import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as LucideIcons from 'lucide-react';
import * as FaIcons from 'react-icons/fa';
import * as MdIcons from 'react-icons/md';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { renderToStaticMarkup } from 'react-dom/server';
import { createPortal } from 'react-dom';
import ButtonConfigModal from './components/ButtonConfigModal';
import WidgetConfigModal from './components/WidgetConfigModal';
import {
  SidebarItem,
  ModelosPanel,
  ElementosPanel,
  PluginPanel,
  MyApiPanel,
  UploadsPanel,
  ProjetosPanel,
  ProjectsTreeModal,
  // MidiasPanel, // Painel «Mídias» desativado na UI; componente segue em LayoutPanels.jsx — descomente aqui + item do nav + bloco activePanel === "midias"
  IndiquePanel,
  TemplatesPanel,
} from './components/LayoutPanels';
import {
  ALL_ELEMENTS,
  ALL_GRIDS as PLAN_GRID_CATALOG,
  getMergedElementTiers,
  getMergedGridTiers,
  getMergedModuleTiers,
  getPlanSaveSlotLimit,
  planAllowsMyApiMenuClient,
  planPluginModuleActiveClient,
  planUploadModuleActiveClient,
  planCricketModuleActiveClient,
  resolvePlanAllowedElements,
} from './config/planFeatures';
import {
  getEffectiveVideoUrl,
  getEffectiveH5pUrl,
  DEFAULT_PLACEHOLDER_VIDEO_URL,
  DEFAULT_PLACEHOLDER_H5P_URL,
} from './config/mediaDefaults';
import { PANDA_OFFICIAL_LOGO_SVG } from './config/pandaBrand';
import MasterDashboard from './pages/MasterDashboard';
import PluginUpdateBalloon from './components/PluginUpdateBalloon';
import { readPluginUpdateSeenEtag, writePluginUpdateSeenEtag } from './utils/pluginUpdateNotice';
import { getWaveSvgDataUrl, getWaveHtml } from './utils/waveUtils';
import RowBackgroundImageLayer from './components/RowBackgroundImageLayer';
import { buildRowBackgroundImageLayerHtml } from './utils/rowBackgroundImage';
import {
  normalizeColumnConfig,
  columnResponsiveClassNames,
  columnAlignToFlex,
  DEFAULT_COLUMN_CONFIG,
  rowColumnsBetweenGapPx,
  buildColumnGapLayoutStyle,
  buildRowContainerGapStyle,
  getRowColumnGapMeta,
  pushRowGridGapStyleRules,
  COLUMN_GRID_BASE_PADDING_PX,
} from './utils/columnLayoutUtils';
import {
  listIconAlignToFlex,
  listIconAlignToTextAlign,
  listIconRowJustify,
  listIconMobileAlignClass,
} from './utils/listIconAlignUtils';
import {
  SECTION_HEADER_TITLE_CLASS,
  resolveSectionHeaderTitleTag,
  resolveSectionHeaderTitleFontPx,
  resolveSectionHeaderSubtitleFontSizeCss,
  resolveSectionHeaderBackground,
  isSectionHeaderLeftBarEnabled,
  resolveSectionHeaderTypography,
  resolveSectionHeaderColors,
  resolveSectionHeaderTextAlign,
  buildSectionHeaderExportStyles,
} from './utils/sectionHeaderTypography';
import { buildImageImgStyleObject, buildImageImgInlineStyle } from './utils/imageWidgetStyles';
import { sanitizeHtml, sanitizeHtmlWithIframes, prepareHtmlTemplateForEmbed, applyNonBreakingHyphenBetweenLetters } from './utils/sanitizeHtml.js';
import {
  contentLooksLikeH5P,
  buildH5pExportInnerHtml,
  buildH5pPreviewEmbedHtml,
  embedLikelyRequiresMoodleLoginInBuilder,
} from './utils/embedContentUtils.js';
import ProfilePage from './pages/ProfilePage';
import CricketBoardPage from './pages/CricketBoardPage';
import CricketDrawer from './components/cricket/CricketDrawer';
import FeedbackFloatingWidget from './components/FeedbackFloatingWidget';
import LanguageSwitcher from './components/LanguageSwitcher';
import SectionsLibraryModal from './components/SectionsLibraryModal';
import AiLibraryPanel from './components/AiLibraryPanel';
import AiLayoutGenerateModal from './components/AiLayoutGenerateModal';
import BuilderTopbarActions from './components/BuilderTopbarActions';
import { EDIT_BREAKPOINT_DESKTOP, EDIT_BREAKPOINT_MOBILE, MOBILE_STAGE_WIDTH_PX } from './constants/responsiveBreakpoints';
import {
  resolveRowConfigForEdit,
  getColumnMobileOverrides,
  pushRowMobileLayoutRules,
  pushColumnMobileLayoutRules,
  isHiddenOnMobile,
  HIDE_ON_MOBILE_CLASS,
  buildRowFloatingImageHtml,
  resolveFloatingImage,
} from './utils/responsiveLayout';
import {
  resolveWidgetContentForEdit,
  resolveGlobalConfigForEdit,
  pushWidgetMobileTypographyRules,
  pushGlobalMobileTypographyRules,
} from './utils/responsiveTypography';
import SidebarMoreMenu from './components/SidebarMoreMenu';
import LearningReportDocs, { isLearningReportDocsPath, LEARNING_REPORT_DOCS_PATH } from './pages/LearningReportDocs';
import { buildSectionPresetSchema, parseSectionPresetRows } from './utils/sectionPresetUtils';
import { resolveRowBackgroundColor } from './utils/rowConfigUtils';
import { elementAdvancedCss, appendElementAdvancedCss } from './utils/advancedCss';
import TubesCursor from './components/ui/tubes-curor';
import { API_BASE } from './config/api';
import { dataTransferLooksLikeGepettoMediaUrl, parseGepettoMediaDropUrl } from './utils/gepettoMediaDrag';
import { collectPageBreakExportConfig, buildSpaPaginationContentHtml } from './utils/pageBreakExport';

const APP_SESSION_STORAGE_KEY = 'gepetto_app_session_id';

/**
 * Lê o ID de sessão analytics onde estiver (token pode existir em localStorage e sessionStorage ao mesmo tempo,
 * ex.: após escolher plano — antes só sessionStorage era consultado e ping/logout não encontravam o ID).
 */
function getStoredAppSessionId() {
  try {
    return localStorage.getItem(APP_SESSION_STORAGE_KEY) || sessionStorage.getItem(APP_SESSION_STORAGE_KEY);
  } catch (_) {
    return null;
  }
}

/** Token JWT onde estiver (localStorage ou sessionStorage) — leitura síncrona na hidratação. */
function getStoredAuthToken() {
  try {
    return localStorage.getItem('gepetto_token') || sessionStorage.getItem('gepetto_token') || null;
  } catch (_) {
    return null;
  }
}

/** Token de convite na URL: /invite/TOKEN ou ?invite_token=TOKEN */
function getInviteTokenFromUrl() {
  try {
    const path = window.location.pathname.replace(/\/$/, '');
    const inviteMatch = path.match(/^\/invite\/([a-f0-9]+)$/i);
    const params = new URLSearchParams(window.location.search);
    return inviteMatch?.[1] || params.get('invite_token') || null;
  } catch (_) {
    return null;
  }
}

/** Grava em ambos os storages para não dessincronizar com gepetto_token duplicado. */
function persistAppSessionId(sessionId) {
  if (!sessionId) return;
  try {
    localStorage.setItem(APP_SESSION_STORAGE_KEY, sessionId);
    sessionStorage.setItem(APP_SESSION_STORAGE_KEY, sessionId);
  } catch (_) { /* noop */ }
}

function clearAppSessionId() {
  try {
    sessionStorage.removeItem(APP_SESSION_STORAGE_KEY);
    localStorage.removeItem(APP_SESSION_STORAGE_KEY);
  } catch (_) { /* noop */ }
}

async function endAppAnalyticsSession(apiBase, token) {
  const sessionId = getStoredAppSessionId();
  if (!token || !sessionId) return;
  try {
    await fetch(`${apiBase}/analytics/session-end`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ sessionId }),
      keepalive: true,
    });
  } catch (_) { /* noop */ }
}

/** Projeto criado só no navegador (plano Free): registra no servidor para relatório. */
function reportFreeProjectCreated(authToken, projectId, courseName) {
  if (!authToken || !projectId) return;
  fetch(`${API_BASE}/analytics/event`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
    body: JSON.stringify({
      type: 'free_project_created',
      payload: { project_id: projectId, course_name: courseName || '' },
    }),
  }).catch(() => {});
}
import { GEPPETTO_COMMENTS_EXPORT_SCRIPT } from './constants/geppettoCommentsExportScript';
import {
  buildSiteIndexDocument,
  buildSiteReadme,
  buildSiteOmittedNoticeHtml,
  buildSiteRuntimeBundle,
  buildSiteSyncScript,
  injectSiteSyncScript,
  finalizeSiteExportHtml,
  isSiteExportRuntime,
  looksLikeMoodleBoundEmbed,
  resolveSitePublishApiBase,
  SITE_EXPORT_RUNTIME,
} from './utils/siteExport';
import { isSiteOnlyWidget, readDeliveryMode } from './utils/deliveryMode';
import { buildMoodleCourseDocument, injectMoodleCourseSyncMeta } from './utils/coursePublish';
import { GEPPETTO_MICROQUIZ_EXPORT_SCRIPT } from './constants/geppettoMicroquizExportScript';
import { GEPPETTO_MOODLE_ACTIVITY_EXPORT_SCRIPT, extractMoodleActivityCmidFromHref } from './constants/geppettoMoodleActivityExportScript';
import { buildGeppettoExportMarkerBundle, GEPPETTO_IFRAME_PARENT_FIT_JS } from './constants/geppettoMoodleExportMarker';
import { GEPPETTO_ASAAS_PAYMENT_CHECKPOINT_SNIPPET } from './constants/geppettoAsaasPaymentCheckpointSnippet';
import { GEPPETTO_MERCADOPAGO_PAYMENT_CHECKPOINT_SNIPPET } from './constants/geppettoMercadopagoPaymentCheckpointSnippet';
import { buildSpacerDecorationHtml } from './constants/spacerDecorations';
import { buildSpacerInnerHtml, buildResponsiveTableInnerHtml, buildImageGalleryInnerHtml, buildTitleBasicInnerHtml, sanitizeTitleBasicAnchorId, resolveTitleBasicTitleColor, resolveTitleBasicSubtitleColor, resolveTitleBasicTitleFontPx, resolveTitleBasicSubtitleFontPx, resolveTitleBasicLineHeight, resolveWidgetExportWrapperColor, normalizeResponsiveTableCell, resolveTableHeaderTextColor, resolveTableBodyTextColor, renderTimelineLucideIconSvg, buildChartExportInnerHtml, buildFlowchartExportInnerHtml, buildAsaasPaymentExportInnerHtml, buildMercadopagoPaymentExportInnerHtml, buildPostItInnerHtml } from './constants/widgetHtmlSnippets';
import { buildSiteMenuInnerHtml, createDefaultSiteMenuContent } from './constants/siteMenuWidget';
import { buildSiteFooterInnerHtml, createDefaultSiteFooterContent } from './constants/siteFooterWidget';
import { buildLoginPageInnerHtml, createDefaultSiteLoginContent } from './constants/loginPageWidget';
import { buildMaintenancePageInnerHtml, createDefaultMaintenanceContent } from './constants/maintenancePageWidget';
import { buildCountUpInnerHtml, createDefaultCountUpContent } from './constants/countUpWidget';
import { MOTION_CSS, applyMotionToInnerHtml, ensureBuilderEffectCss, motionClassName, motionInlineStyle, normalizeMotion } from './constants/motionEffects';
import MotionStage from './components/MotionStage';
import RenderCountUp from './components/RenderCountUp';
import { buildCardSliderInnerHtml, createDefaultCardSliderContent } from './constants/cardSliderWidget';
import { buildCardShopInnerHtml, createDefaultCardShopContent } from './constants/cardShopWidget';
import { buildVideoCardSliderInnerHtml, createDefaultVideoCardSliderContent } from './constants/videoCardSliderWidget';
import RenderCardSlider from './components/RenderCardSlider';
import RenderCardShop from './components/RenderCardShop';
import RenderVideoCardSlider from './components/RenderVideoCardSlider';
import RenderSiteMenu from './components/RenderSiteMenu';
import RenderSiteFooter from './components/RenderSiteFooter';
import RenderSiteLogin from './components/RenderSiteLogin';
import RenderSiteMaintenance from './components/RenderSiteMaintenance';
import SiteOnlyCourseNotice from './components/SiteOnlyCourseNotice';
import { buildTocInnerHtml } from './constants/tocWidgetSnippets';
import { collectTocEntriesFromRows, isIncludedInToc, resolveTocAnchorId, wrapHtmlWithTocAnchorIfNeeded } from './utils/tocRegistry';
import { resolvePostItFontFamilyCss, collectPostItGoogleFontUrls } from './utils/courseFontOptions';
import { resolveTimelineCardBg, resolveTimelineCardBorderCss } from './utils/timelineColors';
import { buildMadriChartOption } from './constants/chartWidgetUtils';
import { DEFAULT_MERMAID_FLOWCHART, buildFlowchartMermaidInitConfig, isMermaidFlowchartLR, sanitizeFlowchartHex, sanitizeMermaidSource } from './constants/flowchartWidgetUtils';
import { loadMadriEcharts } from './utils/madriEchartsCdn';
import { loadMadriMermaid } from './utils/madriMermaidCdn';
import { getRecaptchaToken } from './utils/recaptcha';
import { clearGepettoBuilderLocalCache } from './utils/clearGepettoBrowserCache';
import {
  listFreeLocalProjectsForUser,
  registerFreeLocalProject,
  updateFreeLocalProjectMeta,
  removeFreeLocalProject,
  isFreeLocalProjectId,
} from './utils/freeLocalProjects';
import { buildCricketWidgetBadgeMap } from './utils/cricketWidgetMap';
import {
  buildMicroquizExportInnerHtml,
  buildMicroquizFeedbackManifest,
  collectCheckpointOptionsFromLayout,
  enumerateLayoutCheckpointIds,
  createDefaultMicroquizContent,
  getMicroquizPassingScore,
  resolveCheckpointDisplayLabel,
  shouldShowMicroquizDetailedFeedback,
  summarizeMicroquizzesInLayout,
  MICROQUIZ_MAX_QUESTIONS,
} from './utils/microquizUtils';
import {
  CRICKET_FREE_STANDALONE_LAYOUT_ID,
  isCricketFreeStandaloneLayout,
} from './utils/cricketFreeStandalone';

/** Conteúdo inicial do Template HTML (palco visível até o autor editar). */
const DEFAULT_HTML_TEMPLATE_MOCKUP = `<div style="padding:18px 20px;border:1px dashed #94a3b8;border-radius:10px;background:#f8fafc;color:#475569;font-size:14px;line-height:1.55;max-width:100%;box-sizing:border-box"><strong style="color:#334155">Template HTML</strong><p style="margin:10px 0 0">Edite este texto nas configurações do elemento. Você pode usar títulos, parágrafos, listas, links e imagens permitidas pelo editor.</p></div>`;

/** Último projeto de layout aberto no builder — usado para reidratar palco após F5 (rascunho fica em gepetto_draft_<id>). */
const STORAGE_LAST_OPEN_LAYOUT = 'gepetto_last_open_layout';

/** Ocultar ícones de lâmpada do Cricket no palco (preferência local). */
const STORAGE_CRICKET_STAGE_LAMPS_HIDDEN = 'gepetto_cricket_stage_lamps_hidden';

function persistLastOpenLayoutMeta(id, name) {
  try {
    if (!id) {
      localStorage.removeItem(STORAGE_LAST_OPEN_LAYOUT);
      return;
    }
    localStorage.setItem(STORAGE_LAST_OPEN_LAYOUT, JSON.stringify({
      id,
      name: name || '',
      updatedAt: Date.now()
    }));
  } catch (_) { /* storage cheio / privado */ }
}

/**
 * Hidrata rows/headerWidgets/courseInfo após recarregar a página.
 * Ordem: (1) último projeto aberto + gepetto_draft_<id> — nunca perder id por causa de scratch antigo;
 * (2) migração sem meta; (3) scratch só com conteúdo e sem id (trabal local); (4) scratch genérico.
 */
function readPersistedBuilderDraft() {
  try {
    const metaRaw = localStorage.getItem(STORAGE_LAST_OPEN_LAYOUT);
    if (metaRaw) {
      const meta = JSON.parse(metaRaw);
      const layoutId = meta?.id;
      if (layoutId && typeof layoutId === 'string') {
        const draftRaw = localStorage.getItem(`gepetto_draft_${layoutId}`);
        if (draftRaw) {
          const p = JSON.parse(draftRaw);
          const rows = p?.rows;
          if (Array.isArray(rows)) {
            const ci = p.courseInfo || {};
            return {
              rows,
              headerWidgets: Array.isArray(p.headerWidgets) ? p.headerWidgets : [],
              courseInfo: {
                name: (ci.name != null && ci.name !== '') ? ci.name : (meta.name || ''),
                id: layoutId,
                theme: ci.theme || 'blue',
                globalConfig: ci.globalConfig || {}
              }
            };
          }
        }
        try { localStorage.removeItem(STORAGE_LAST_OPEN_LAYOUT); } catch (_) { /* noop */ }
      }
    }
  } catch (_) { /* noop */ }

  // Migração: sem meta ainda (deploy antigo), mas existe gepetto_draft_<id> com courseInfo coerente — escolhe o rascunho mais “recente” pelo tamanho salvo
  try {
    let best = null;
    let bestScore = -1;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith('gepetto_draft_')) continue;
      const layoutId = key.slice('gepetto_draft_'.length);
      if (!layoutId || layoutId === 'scratch') continue;
      const draftRaw = localStorage.getItem(key);
      if (!draftRaw) continue;
      let p;
      try { p = JSON.parse(draftRaw); } catch { continue; }
      if (!Array.isArray(p?.rows)) continue;
      const ci = p.courseInfo || {};
      if (ci.id && String(ci.id) !== String(layoutId)) continue;
      const score = draftRaw.length;
      if (score > bestScore) {
        bestScore = score;
        best = { layoutId, p };
      }
    }
    if (best) {
      const p = best.p;
      const ci = p.courseInfo || {};
      const name = (ci.name != null && ci.name !== '') ? ci.name : '';
      persistLastOpenLayoutMeta(best.layoutId, name);
      return {
        rows: p.rows,
        headerWidgets: Array.isArray(p.headerWidgets) ? p.headerWidgets : [],
        courseInfo: {
          name,
          id: best.layoutId,
          theme: ci.theme || 'blue',
          globalConfig: ci.globalConfig || {}
        }
      };
    }
  } catch (_) { /* noop */ }

  // Trabalho local sem projeto salvo (scratch com conteúdo explícito e sem id)
  try {
    const scratchRaw = localStorage.getItem('gepetto_draft_scratch');
    if (scratchRaw) {
      const sp = JSON.parse(scratchRaw);
      const hasScratchContent = (Array.isArray(sp?.rows) && sp.rows.length > 0)
        || (Array.isArray(sp?.headerWidgets) && sp.headerWidgets.length > 0);
      const scratchNoProject = !sp?.courseInfo?.id;
      if (hasScratchContent && scratchNoProject) {
        const ci = sp.courseInfo || {};
        return {
          rows: Array.isArray(sp.rows) ? sp.rows : [],
          headerWidgets: Array.isArray(sp.headerWidgets) ? sp.headerWidgets : [],
          courseInfo: {
            name: ci.name || '',
            id: '',
            theme: ci.theme || 'blue',
            globalConfig: ci.globalConfig || {}
          }
        };
      }
    }
  } catch (_) { /* noop */ }

  try {
    const draft = localStorage.getItem('gepetto_draft_scratch');
    if (!draft) return null;
    const p = JSON.parse(draft);
    const ci = p.courseInfo || {};
    return {
      rows: Array.isArray(p.rows) ? p.rows : [],
      headerWidgets: Array.isArray(p.headerWidgets) ? p.headerWidgets : [],
      courseInfo: {
        name: ci.name || '',
        id: ci.id || '',
        theme: ci.theme || 'blue',
        globalConfig: ci.globalConfig || {}
      }
    };
  } catch (_) {
    return null;
  }
}

/** Uma única leitura do localStorage por carregamento da página (courseInfo/rows/headerWidgets alinhados). */
let _hydratedBuilderDraftOnce;
function getHydratedBuilderDraftOnce() {
  if (typeof window === 'undefined') return null;
  if (_hydratedBuilderDraftOnce === undefined) {
    _hydratedBuilderDraftOnce = readPersistedBuilderDraft();
  }
  return _hydratedBuilderDraftOnce;
}

// === Extrair Ícones Lucide sem duplicatas (evita Home/HomeIcon/LucideHome) ===
// Ícones Lucide são ForwardRefExoticComponent (objetos), não funções puras.
const lucideIconMap = new Map();
Object.keys(LucideIcons).forEach(name => {
  if (name === 'createLucideIcon' || name === 'default' || name === 'Icon' || !/^[A-Z]/.test(name)) return;
  const comp = LucideIcons[name];
  if (!comp || (typeof comp !== 'function' && typeof comp !== 'object')) return;
  const existing = lucideIconMap.get(comp);
  const prefer = !name.startsWith('Lucide') && !name.endsWith('Icon');
  if (!existing || (prefer && (existing.startsWith('Lucide') || existing.endsWith('Icon')))) {
    lucideIconMap.set(comp, name);
  }
});
const validIconNames = [...new Set(lucideIconMap.values())];

// === Helper: Resolve ícone por nome (Lucide, fa:, md:) ===
const getIconComponent = (iconName) => {
  if (!iconName || iconName === 'default') return null;
  const ic = String(iconName);
  if (ic.startsWith('fa:')) {
    const pascal = ic.slice(3).split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join('');
    return FaIcons[pascal] || null;
  }
  if (ic.startsWith('md:')) {
    const pascal = ic.slice(3).split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join('');
    return MdIcons[pascal] || null;
  }
  return LucideIcons[ic] || null;
};

// Puxando Destrucuturings Padrões para Elementos Fixos da UI
const {
  Plus, Trash2, GripVertical, Layout, Settings, Type, Video, AlertCircle, Layers,
  Download, ChevronDown, ChevronRight, Save, FileSignature,
  Info, CheckCircle, AlertTriangle, XCircle, Search, ArrowLeft,
  LayoutTemplate, Shapes, Puzzle, UploadCloud, FolderOpen, Lamp, /* Image: ImageIcon, */ BarChart2, Crown, // ImageIcon: item «Mídias» desativado no menu
  Sparkles, Zap, Lock, Heart, Rocket, LayoutGrid, Share2, RotateCcw, Moon, Sun, CreditCard,
  Undo2, Redo2, Pencil, CopyPlus, FolderPlus, PanelLeft, Database, ToggleRight
} = LucideIcons;
// Eye/EyeOff usados via LucideIcons.Eye e LucideIcons.EyeOff para evitar TDZ no bundle (minificador pode renomear para E).

/** ID seguro para embutir em <script> (JSON.stringify(undefined) quebra o JS gerado). */
function jsStringLiteralForId(id) {
  const s = id != null && String(id).trim() !== '' ? String(id) : 'course';
  return JSON.stringify(s);
}

function normalizeFeatureFlags(f) {
  const base = {
    expert: [], premium: [], trial: [], premiumGrids: [], trialGrids: [], premiumModules: [], trialModules: [],
    elementTiers: {}, gridTiers: {}, moduleTiers: {},
  };
  if (!f || typeof f !== 'object') {
    return {
      ...base,
      elementTiers: getMergedElementTiers({}),
      gridTiers: getMergedGridTiers({}),
      moduleTiers: getMergedModuleTiers({}),
    };
  }
  const raw = f;
  return {
    ...base,
    ...raw,
    expert: Array.isArray(raw.expert) ? raw.expert : [],
    premium: Array.isArray(raw.premium) ? raw.premium : [],
    trial: Array.isArray(raw.trial) ? raw.trial : [],
    premiumGrids: Array.isArray(raw.premiumGrids) ? raw.premiumGrids : [],
    trialGrids: Array.isArray(raw.trialGrids) ? raw.trialGrids : [],
    premiumModules: Array.isArray(raw.premiumModules) ? raw.premiumModules : [],
    trialModules: Array.isArray(raw.trialModules) ? raw.trialModules : [],
    elementTiers: getMergedElementTiers(raw),
    gridTiers: getMergedGridTiers(raw),
    moduleTiers: getMergedModuleTiers(raw),
  };
}

const HEADER_ELEMENTS = ['progress-bar', 'header-menu']; // Elementos que vão direto para a zona Header (fixa no topo)

/* Full width: a página e o fundo da faixa ocupam a tela. O miolo fica em até 1200px. */
const FULL_WIDTH_LAYOUT_CSS = '.madri-moodle-layout.madri-layout-full{max-width:none !important;width:100% !important;margin:0 !important;padding:0 !important;overflow-x:clip !important;overflow-y:visible !important}.madri-moodle-layout.madri-layout-full .madri-row{margin-left:0 !important;margin-right:0 !important;width:100%}.madri-moodle-layout.madri-layout-full .madri-row-inner{max-width:1200px;width:100%;margin-left:auto;margin-right:auto;padding-left:24px;padding-right:24px;box-sizing:border-box;position:relative;z-index:0}.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner{display:flex !important;flex-wrap:wrap !important;align-items:flex-start}@media (min-width:768px){.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner{flex-wrap:nowrap !important}}.madri-moodle-layout.madri-layout-full .madri-row-bleed .madri-row-inner{max-width:none;padding-left:0;padding-right:0}@media (min-width:768px){.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner > .madri-col-md-6{flex:0 1 50% !important;max-width:50% !important}.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner > .madri-col-md-4{flex:0 1 33.333333% !important;max-width:33.333333% !important}.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner > .madri-col-md-8{flex:0 1 66.666667% !important;max-width:66.666667% !important}.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner > .madri-col-md-3{flex:0 1 25% !important;max-width:25% !important}}@media (max-width:767px){.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner > .madri-col-12,.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner > .madri-col-md-6,.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner > .madri-col-md-4,.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner > .madri-col-md-8,.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner > .madri-col-md-3,.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner > .madri-col-dynamic,.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner > [class*="madri-col-"]{flex:0 0 100% !important;max-width:100% !important;width:100% !important}}';

function layoutExportStyleTag(layoutWidth) {
  if (layoutWidth !== 'full-width') return '';
  return `<style data-geppetto-layout-width="full">${FULL_WIDTH_LAYOUT_CSS}</style>\n`;
}

const BUILDER_EDIT_PENCIL = {
  row: '#7c3aed',
  widget: '#2563eb',
  column: '#059669',
};

const BASIC_GRIDS = ['1/1', '1/2', '1/3'];
const ALL_GRIDS = ['1/1', '1/2', '1/3', '1/4', '2/3', '33/66', 'dynamic'];

// === Export CSS Base Moodle ===
// Blindagem contra herança de temas dos clientes. Usado tanto para o Motor de Preview Visual (.html iframe) quanto para Export .Zip.
const exportCss = `/* Template Base Moodle - Geppetto SaaS */
.madri-moodle-layout { 
  max-width: 1200px; 
  margin: 0 auto; 
  padding: 0 15px 10px 15px; 
  font-family: inherit; 
  overflow-x: clip;
}
.madri-moodle-layout *, .madri-moodle-layout *::before, .madri-moodle-layout *::after {
  box-sizing: border-box !important;
}

/* Grid custom blindado para não herdar regras do Tema do cliente */
.madri-moodle-layout .madri-row {
  display: flex !important;
  flex-wrap: wrap !important;
  margin-right: -15px !important;
  margin-left: -15px !important;
  box-sizing: border-box !important;
}
.madri-moodle-layout .madri-col-12, 
.madri-moodle-layout .madri-col-md-6, 
.madri-moodle-layout .madri-col-md-4, 
.madri-moodle-layout .madri-col-md-8,
.madri-moodle-layout .madri-col-md-3,
.madri-moodle-layout .madri-col-dynamic {
  position: relative !important;
  width: 100% !important;
  padding-right: 15px !important;
  padding-left: 15px !important;
  box-sizing: border-box !important;
  min-width: 0 !important;
  word-wrap: break-word !important;
  overflow-wrap: break-word !important;
  word-break: normal !important;
}
@media (min-width: 768px) {
  .madri-moodle-layout .madri-row > .madri-col-md-6 { flex: 0 0 50% !important; max-width: 50% !important; }
  .madri-moodle-layout .madri-row > .madri-col-md-4 { flex: 0 0 33.333333% !important; max-width: 33.333333% !important; }
  .madri-moodle-layout .madri-row > .madri-col-md-8 { flex: 0 0 66.666667% !important; max-width: 66.666667% !important; }
  .madri-moodle-layout .madri-row > .madri-col-md-3 { flex: 0 0 25% !important; max-width: 25% !important; }
  /* Grid interno dentro de coluna — preview/export (larguras por linha em layoutStyleRules) */
  .madri-moodle-layout .madri-row--nested {
    display: flex !important;
    flex-wrap: nowrap !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  .madri-moodle-layout .madri-row--nested > [class*="madri-col-"] {
    min-width: 0 !important;
    width: auto !important;
    margin-right: 0 !important;
    padding-left: 15px !important;
    padding-right: 15px !important;
  }
}
@media (max-width: 767px) {
  .madri-moodle-layout {
    padding-left: 10px !important;
    padding-right: 10px !important;
  }
  .madri-moodle-layout .madri-row {
    margin-left: -8px !important;
    margin-right: -8px !important;
  }
  .madri-moodle-layout .madri-row--nested {
    margin-left: 0 !important;
    margin-right: 0 !important;
    flex-wrap: wrap !important;
    row-gap: 12px !important;
  }
  .madri-moodle-layout .madri-row--nested > [class*="madri-col-"] {
    padding-left: 8px !important;
    padding-right: 8px !important;
  }
  .madri-moodle-layout .madri-row > .madri-col-12,
  .madri-moodle-layout .madri-row > .madri-col-md-6,
  .madri-moodle-layout .madri-row > .madri-col-md-4,
  .madri-moodle-layout .madri-row > .madri-col-md-8,
  .madri-moodle-layout .madri-row > .madri-col-md-3,
  .madri-moodle-layout .madri-row > .madri-col-dynamic,
  .madri-moodle-layout .madri-row > [class*="madri-col-"] {
    flex: 0 0 100% !important;
    max-width: 100% !important;
    width: 100% !important;
    margin-right: 0 !important;
    padding-left: 8px !important;
    padding-right: 8px !important;
  }
  .madri-moodle-layout .madri-row > .madri-col-sm-center,
  .madri-moodle-layout .madri-row > [class*="madri-col-"].madri-col-sm-center {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    text-align: center !important;
  }
  .madri-moodle-layout .madri-row > .madri-col-sm-center [class*="madri-widget-"],
  .madri-moodle-layout .madri-row > .madri-col-sm-center .widget-block,
  .madri-moodle-layout .madri-row > [class*="madri-col-"].madri-col-sm-center [class*="madri-widget-"],
  .madri-moodle-layout .madri-row > [class*="madri-col-"].madri-col-sm-center .widget-block {
    text-align: center !important;
  }
  .madri-moodle-layout .madri-row > .madri-col-sm-order-1 { order: 1 !important; }
  .madri-moodle-layout .madri-row > .madri-col-sm-order-2 { order: 2 !important; }
  .madri-moodle-layout .madri-row > .madri-col-sm-order-3 { order: 3 !important; }
  .madri-moodle-layout .madri-row > .madri-col-sm-order-4 { order: 4 !important; }
  .madri-moodle-layout .madri-row > .madri-col-sm-order-5 { order: 5 !important; }
  .madri-moodle-layout .madri-row > .madri-col-sm-order-6 { order: 6 !important; }
  .madri-moodle-layout .madri-row > .madri-col-sm-order-7 { order: 7 !important; }
  .madri-moodle-layout .madri-row > .madri-col-sm-order-8 { order: 8 !important; }
  .madri-moodle-layout .madri-row > .madri-col-sm-order-9 { order: 9 !important; }
  .madri-moodle-layout .madri-row > .madri-col-sm-order-10 { order: 10 !important; }
  .madri-moodle-layout .madri-row > .madri-col-sm-order-11 { order: 11 !important; }
  .madri-moodle-layout .madri-row > .madri-col-sm-order-12 { order: 12 !important; }
  /* Colunas empilhadas: respiro vertical entre caixas/subgrids */
  .madri-moodle-layout .madri-row > [class*="madri-col-"] {
    margin-bottom: 12px !important;
  }
  .madri-moodle-layout .madri-row > [class*="madri-col-"]:last-child {
    margin-bottom: 0 !important;
  }
  .madri-moodle-layout .fake-alert {
    padding: 0.75rem 0.5rem !important;
  }
  .madri-moodle-layout .video-responsive-container {
    width: calc(100% + 36px) !important;
    max-width: none !important;
    margin-left: -18px !important;
    margin-right: -18px !important;
    margin-bottom: 4px !important;
    padding-bottom: 0 !important;
    height: auto !important;
    aspect-ratio: 16 / 9 !important;
    min-height: 200px !important;
    border-radius: 4px !important;
  }
  .madri-moodle-layout .video-responsive-container iframe {
    position: absolute !important;
    inset: 0 !important;
    width: 100% !important;
    height: 100% !important;
    max-width: none !important;
  }
  .madri-moodle-layout .geppetto-moodle-activity-row {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 8px !important;
  }
  .madri-moodle-layout .madri-header {
    padding: 1rem !important;
  }
  .madri-moodle-layout .madri-card {
    padding: 1rem !important;
  }
  .madri-moodle-layout .madri-hide-mobile {
    display: none !important;
  }
}

/* Componentes */
.madri-moodle-layout .widget-block { width: 100%; box-sizing: border-box !important; }

/* Abrir atividade Moodle — linha estilo curso (preview/export) */
.madri-moodle-layout .geppetto-moodle-activity-row {
  display: flex !important;
  align-items: center !important;
  gap: 10px !important;
  padding: 12px 14px !important;
  background: #fff !important;
  border: 1px solid #dee2e6 !important;
  border-radius: 4px !important;
  box-sizing: border-box !important;
  width: 100% !important;
  max-width: 100% !important;
  align-content: center !important;
}
.madri-moodle-layout .geppetto-moodle-activity-row .gma-title { font-weight: 500 !important; color: #1d2125 !important; }
.madri-moodle-layout .geppetto-moodle-activity-row .gma-meta { color: #6a737b !important; }

/* Template HTML embutido — fragmento sem casca html/head/body */
.madri-moodle-layout .geppetto-html-template-embed {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}
.madri-moodle-layout .geppetto-html-template-embed img,
.madri-moodle-layout .geppetto-html-template-embed iframe,
.madri-moodle-layout .geppetto-html-template-embed video {
  max-width: 100%;
}

/* Aviso discreto quando export Geppetto é usado no mod_resource padrão do Moodle */
.madri-moodle-layout .geppetto-mod-resource-warning {
  display: none;
  position: relative;
  z-index: 50;
  margin: 0 0 12px 0;
  width: 100%;
  box-sizing: border-box;
}
.madri-moodle-layout .geppetto-mod-resource-warning.is-visible {
  display: block;
}
.madri-moodle-layout .geppetto-mod-resource-warning__inner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-left: 4px solid #f59e0b;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.45;
  color: #78350f;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.madri-moodle-layout .geppetto-mod-resource-warning__badge {
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: #f59e0b;
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.madri-moodle-layout .geppetto-mod-resource-warning__texts {
  flex: 1 1 auto;
  min-width: 0;
}
.madri-moodle-layout .geppetto-mod-resource-warning__title {
  display: block;
  font-weight: 600;
  color: #92400e;
  margin-bottom: 2px;
}
.madri-moodle-layout .geppetto-mod-resource-warning__body {
  display: block;
  color: #78350f;
}
.madri-moodle-layout .geppetto-mod-resource-warning__link {
  flex: 0 0 auto;
  align-self: center;
  color: #b45309;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
}
.madri-moodle-layout .geppetto-mod-resource-warning__link:hover {
  text-decoration: underline;
}
.madri-moodle-layout .geppetto-mod-resource-warning__dismiss {
  flex: 0 0 auto;
  border: none;
  background: transparent;
  color: #92400e;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 0 2px;
  margin: -2px 0 0 0;
}
.madri-moodle-layout .geppetto-mod-resource-warning__dismiss:hover {
  color: #78350f;
}
@media (max-width: 767px) {
  .madri-moodle-layout .geppetto-mod-resource-warning__inner {
    flex-wrap: wrap;
  }
  .madri-moodle-layout .geppetto-mod-resource-warning__link {
    width: 100%;
    margin-left: 34px;
  }
}

.madri-moodle-layout .madri-card {
  background: var(--bg-card, #ffffff);
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  border-top: 4px solid var(--course-primary, #3b82f6);
  width: 100%;
}
.madri-moodle-layout .madri-card h3 {
  color: var(--text-main, inherit);
  font-size: 1.25rem;
  margin-top: 0;
  border-bottom: 2px solid #e2e8f0;
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
}

.madri-moodle-layout .madri-header {
  background-color: var(--bg-card, #f8fafc);
  padding: 2rem;
  border-radius: 8px;
  border-left: 5px solid var(--course-primary, #3b82f6);
  width: 100%;
}
/* h1/subtítulo herdam do .widget-block (color inline do builder), como o elemento Texto — evita var(--text-main) fixo que ignorava a aba Colors */
.madri-moodle-layout .widget-block .madri-header h1:not(.madri-gc-section-header__heading) {
  color: inherit !important;
  margin-top: 0;
  font-size: 2rem;
  font-weight: 700;
}
.madri-moodle-layout .widget-block .madri-header .subtitle:not(.madri-gc-section-header__sub) {
  color: inherit !important;
  opacity: 0.88;
  font-size: 1.1rem;
  margin-top: 0.5rem;
}
.madri-moodle-layout .widget-block .madri-header .madri-gc-section-header__heading {
  margin-top: 0;
  font-weight: 700;
  overflow-wrap: break-word !important;
  word-wrap: break-word !important;
  word-break: normal !important;
  hyphens: auto;
  -webkit-hyphens: auto;
  min-width: 0;
  max-width: 100%;
}
.madri-moodle-layout .madri-header .tag {
  color: var(--course-primary, #3b82f6);
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.85rem;
  margin-bottom: 0.5rem;
  display: block;
}
/* Título (básico): mesmo padrão do Texto — HTML exportado envolve em .fake-text com color inline; heading herda desse div (temas costumam forçar h2/h3) */
.madri-moodle-layout .widget-block .madri-gc-title-basic .fake-text .madri-gc-title-basic-h {
  color: inherit !important;
}

/* Lista de ícones — texto longo quebra dentro da coluna */
.madri-moodle-layout .madri-list-icon-root {
  max-width: 100% !important;
  min-width: 0 !important;
  width: 100% !important;
  box-sizing: border-box !important;
}
.madri-moodle-layout .madri-list-icon-body {
  min-width: 0 !important;
  max-width: 100% !important;
  overflow-wrap: anywhere !important;
  word-break: break-word !important;
}
.madri-moodle-layout .madri-list-icon-body img,
.madri-moodle-layout .madri-list-icon-body iframe,
.madri-moodle-layout .madri-list-icon-body video {
  max-width: 100% !important;
  height: auto !important;
}
/* Lista de ícones: no Moodle o tema costuma inflar h2/p (margin + font-size) — alinhar ao palco / export */
.madri-moodle-layout .madri-list-icon-body h1,
.madri-moodle-layout .madri-list-icon-body h2,
.madri-moodle-layout .madri-list-icon-body h3,
.madri-moodle-layout .madri-list-icon-body h4,
.madri-moodle-layout .madri-list-icon-body h5,
.madri-moodle-layout .madri-list-icon-body h6 {
  margin: 0 !important;
  padding: 0 !important;
  font-size: inherit !important;
  line-height: inherit !important;
}
.madri-moodle-layout .madri-list-icon-body p {
  margin: 0 !important;
  font-size: inherit !important;
  line-height: inherit !important;
}

/* Linha do tempo horizontal — no mobile vira vertical via CSS */
.madri-moodle-layout .madri-gc-timeline--horizontal {
  max-width: 100%;
  box-sizing: border-box;
}

/* Lista de ícones em linha — no mobile vira coluna; alinhamento via classe madri-list-icon-mob-* */
@media (max-width: 767px) {
  .madri-moodle-layout .madri-list-icon-root--row {
    flex-direction: column !important;
    justify-content: flex-start !important;
  }
  .madri-moodle-layout .madri-list-icon-mob-start {
    align-items: flex-start !important;
    text-align: left !important;
  }
  .madri-moodle-layout .madri-list-icon-mob-left {
    align-items: flex-start !important;
    text-align: left !important;
  }
  .madri-moodle-layout .madri-list-icon-mob-center {
    align-items: center !important;
    text-align: center !important;
  }
  .madri-moodle-layout .madri-list-icon-mob-right {
    align-items: flex-end !important;
    text-align: right !important;
  }
  .madri-moodle-layout .madri-list-icon-root--row.madri-list-icon-mob-start > div:first-child,
  .madri-moodle-layout .madri-list-icon-root--row.madri-list-icon-mob-left > div:first-child {
    align-self: flex-start !important;
  }
  .madri-moodle-layout .madri-list-icon-root--row.madri-list-icon-mob-center > div:first-child {
    align-self: center !important;
  }
  .madri-moodle-layout .madri-list-icon-root--row.madri-list-icon-mob-right > div:first-child {
    align-self: flex-end !important;
  }
  .madri-moodle-layout .madri-gc-timeline--horizontal {
    overflow: visible !important;
    overflow-x: visible !important;
    padding-bottom: 0 !important;
  }
  .madri-moodle-layout .madri-gc-timeline--horizontal .madri-gc-timeline-h-track {
    display: flex !important;
    flex-direction: column !important;
    align-items: stretch !important;
    min-width: 0 !important;
    width: 100% !important;
    padding: 0 0 0 2.5rem !important;
    position: relative !important;
  }
  .madri-moodle-layout .madri-gc-timeline--horizontal .madri-gc-timeline-h-track::before {
    content: '' !important;
    position: absolute !important;
    left: 14px !important;
    top: 8px !important;
    bottom: 8px !important;
    width: 2px !important;
    background: var(--madri-tl-line, #e2e8f0) !important;
  }
  .madri-moodle-layout .madri-gc-timeline--horizontal .madri-gc-timeline-h-connector {
    display: none !important;
  }
  .madri-moodle-layout .madri-gc-timeline--horizontal .madri-gc-timeline-h-item {
    flex: none !important;
    width: 100% !important;
    max-width: 100% !important;
    flex-direction: row !important;
    align-items: flex-start !important;
    gap: 1rem !important;
    padding: 0 0 1.5rem 0 !important;
  }
  .madri-moodle-layout .madri-gc-timeline--horizontal .madri-gc-timeline-h-item:last-child {
    padding-bottom: 0 !important;
  }
  .madri-moodle-layout .madri-gc-timeline--horizontal .madri-gc-timeline-h-icon {
    position: absolute !important;
    left: -38px !important;
    top: 2px !important;
    width: 36px !important;
    height: 36px !important;
    border-width: 2px !important;
    margin: 0 !important;
  }
  .madri-moodle-layout .madri-gc-timeline--horizontal .madri-gc-timeline-h-body {
    margin-top: 0 !important;
    width: 100% !important;
    flex: 1 !important;
    min-width: 0 !important;
  }
  .madri-moodle-layout .madri-gc-timeline--horizontal .madri-gc-timeline-h-body > div {
    text-align: left !important;
  }
}

/* Alertas — mesmo padrão de fluxo do Post-it: bloco externo + flex interno (ícone + HTML Quill) */
.madri-moodle-layout .fake-alert {
  padding: 1rem 1.25rem;
  border-radius: 6px;
  font-weight: 500;
  line-height: 1.5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  margin-bottom: 15px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}
.madri-moodle-layout .fake-alert p {
  margin: 0 0 0.5rem;
  white-space: normal;
}
.madri-moodle-layout .fake-alert p:last-child {
  margin-bottom: 0;
}
.madri-moodle-layout .alert-warning { background-color: #fffbeb; color: #b45309; border: 1px solid #fde68a; }
.madri-moodle-layout .alert-success { background-color: #ecfdf5; color: #047857; border: 1px solid #a7f3d0; }
.madri-moodle-layout .alert-danger { background-color: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }
.madri-moodle-layout .alert-info { background-color: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; }

/* Midia */
.madri-moodle-layout .video-responsive-container {
  width: 100%;
  position: relative;
  aspect-ratio: 16 / 9;
  height: auto !important;
  padding-bottom: 0 !important;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}
.madri-moodle-layout .video-responsive-container iframe {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  max-width: 100% !important;
  height: 100% !important;
  border: 0 !important;
}
.madri-moodle-layout .video-responsive-container video {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  object-fit: contain !important;
  border: 0 !important;
}
.madri-moodle-layout .h5p-embed-wrapper,
.course-wrapper .h5p-embed-wrapper {
  width: 100% !important;
  max-width: 100% !important;
  overflow: hidden;
  box-sizing: border-box;
}
.madri-moodle-layout .h5p-embed-wrapper iframe,
.course-wrapper .h5p-embed-wrapper iframe,
.madri-moodle-layout iframe.h5p-player,
.course-wrapper iframe.h5p-player,
.madri-moodle-layout .h5p-embed-html-host iframe,
.course-wrapper .h5p-embed-html-host iframe {
  width: 100% !important;
  max-width: 100% !important;
  display: block !important;
  border: 0 !important;
  box-sizing: border-box !important;
}

/* Defaults Moodle Cleanup */
.madri-moodle-layout p {
  color: inherit;
  margin-bottom: 1rem;
}
/* Texto rico: listas — recuo antes do marcador; li sem padding (bolinha + texto juntos) */
.madri-moodle-layout :is(.fake-text, .madri-list-icon-body, .madri-card, .fake-alert, .widget-block) ul,
.madri-moodle-layout :is(.fake-text, .madri-list-icon-body, .madri-card, .fake-alert, .widget-block) ol {
  margin-left: 1.5em;
  padding-left: 1.5em;
  margin-top: 0.5em;
  margin-bottom: 0.75em;
  margin-right: 0;
}
.madri-moodle-layout :is(.fake-text, .madri-list-icon-body, .madri-card, .fake-alert, .widget-block) ul {
  list-style-type: disc;
  list-style-position: outside;
}
.madri-moodle-layout :is(.fake-text, .madri-list-icon-body, .madri-card, .fake-alert, .widget-block) ol {
  list-style-type: decimal;
  list-style-position: outside;
}
.madri-moodle-layout :is(.fake-text, .madri-list-icon-body, .madri-card, .fake-alert, .widget-block) li:not(.madri-toc__item) {
  display: list-item;
  list-style-position: outside;
  margin: 0.15em 0;
  padding-left: 0;
}
.madri-moodle-layout :is(.fake-text, .madri-list-icon-body, .madri-card, .fake-alert, .widget-block) li.ql-indent-1 { margin-left: 3em; }
.madri-moodle-layout :is(.fake-text, .madri-list-icon-body, .madri-card, .fake-alert, .widget-block) li.ql-indent-2 { margin-left: 6em; }
.madri-moodle-layout :is(.fake-text, .madri-list-icon-body, .madri-card, .fake-alert, .widget-block) li.ql-indent-3 { margin-left: 9em; }
.madri-moodle-layout :is(.fake-text, .madri-list-icon-body, .madri-card, .fake-alert, .widget-block) li.ql-indent-4 { margin-left: 12em; }
.madri-moodle-layout :is(.fake-text, .madri-list-icon-body, .madri-card, .fake-alert, .widget-block) li.ql-indent-5 { margin-left: 15em; }
.madri-moodle-layout :is(.fake-text, .madri-list-icon-body, .madri-card, .fake-alert, .widget-block) li.ql-indent-6 { margin-left: 18em; }
.madri-moodle-layout :is(.fake-text, .madri-list-icon-body, .madri-card, .fake-alert, .widget-block) li.ql-indent-7 { margin-left: 21em; }
.madri-moodle-layout :is(.fake-text, .madri-list-icon-body, .madri-card, .fake-alert, .widget-block) li.ql-indent-8 { margin-left: 24em; }
.madri-moodle-layout :is(.fake-text, .madri-list-icon-body, .madri-card, .fake-alert, .widget-block) ul ul {
  list-style-type: circle;
  margin-top: 0.25em;
  margin-bottom: 0.25em;
}
.madri-moodle-layout :is(.fake-text, .madri-list-icon-body, .madri-card, .fake-alert, .widget-block) ul ul ul {
  list-style-type: square;
}
/* Sumário (table-of-contents) — isolar das regras globais ol/li do texto rico (.widget-block) */
.madri-moodle-layout .widget-block .madri-toc ol,
.madri-moodle-layout .madri-toc ol,
.madri-moodle-layout .madri-toc__list {
  list-style: none !important;
  list-style-type: none !important;
  margin: 0 !important;
  padding: 0 !important;
  margin-left: 0 !important;
  padding-left: 0 !important;
}
.madri-moodle-layout .widget-block .madri-toc li,
.madri-moodle-layout .madri-toc li,
.madri-moodle-layout .madri-toc__item {
  display: flex !important;
  list-style: none !important;
  list-style-type: none !important;
}
.madri-moodle-layout .madri-toc { width: 100%; box-sizing: border-box; }
.madri-moodle-layout .madri-toc__title { font-weight: 700; margin-bottom: 0.75rem; color: inherit; }
.madri-moodle-layout .madri-toc__list { list-style: none; margin: 0; padding: 0; }
.madri-moodle-layout .madri-toc__item { display: flex; align-items: center; flex-wrap: nowrap; gap: 0.35rem; width: 100%; min-width: 0; margin: var(--madri-toc-item-gap, 0.35rem) 0; }
.madri-moodle-layout .madri-toc__prefix { display: inline-flex; align-items: center; gap: 0.25rem; flex-shrink: 0; }
.madri-moodle-layout .madri-toc__body { display: flex; align-items: center; flex: 1; min-width: 0; gap: 0.2rem; flex-wrap: nowrap; overflow: hidden; }
.madri-moodle-layout .madri-toc--leaders .madri-toc__link { flex: 0 1 auto; min-width: 0; max-width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.madri-moodle-layout .madri-toc--leaders .madri-toc__leader { flex: 1 1 auto; min-width: 0.5rem; border-bottom: 1px dotted currentColor; opacity: 0.42; margin: 0 0.15rem; transform: translateY(-0.1em); height: 0; align-self: center; }
.madri-moodle-layout .madri-toc--leaders .madri-toc__page { margin-left: 0; padding-left: 0; flex-shrink: 0; }
.madri-moodle-layout .madri-toc__item--level-2 { padding-left: 1.25rem !important; }
.madri-moodle-layout .madri-toc__item--level-3 { padding-left: 2.5rem !important; }
.madri-moodle-layout .madri-toc__num { flex-shrink: 0; font-variant-numeric: tabular-nums; opacity: 0.85; min-width: 1.5em; }
.madri-moodle-layout .madri-toc__link { color: #2563eb; text-decoration: none; flex: 1; min-width: 0; }
.madri-moodle-layout .madri-toc__link:hover { text-decoration: underline; }
.madri-moodle-layout .madri-toc__icon { flex-shrink: 0; display: inline-flex; align-items: center; color: inherit; opacity: 0.88; line-height: 0; }
.madri-moodle-layout .madri-toc__icon svg { display: block; flex-shrink: 0; }
.madri-moodle-layout .madri-toc__page { flex-shrink: 0; margin-left: auto; padding-left: 0.75rem; font-variant-numeric: tabular-nums; opacity: 0.72; min-width: 1.25em; text-align: right; white-space: nowrap; }
.madri-moodle-layout .madri-toc--modern .madri-toc__title { border-bottom: 2px solid #e2e8f0; padding-bottom: 0.5rem; }
.madri-moodle-layout .madri-toc--modern .madri-toc__link { color: #0f172a; font-weight: 500; }
.madri-moodle-layout .madri-toc--minimal .madri-toc__title { font-size: 0.92em; text-transform: uppercase; letter-spacing: 0.06em; opacity: 0.72; }
.madri-moodle-layout .madri-toc--minimal .madri-toc__link { color: inherit; }
.madri-moodle-layout .madri-toc--formal { font-family: Georgia, 'Times New Roman', Times, serif; }
.madri-moodle-layout .madri-toc--formal .madri-toc__title { text-align: center; text-transform: uppercase; letter-spacing: 0.08em; border-bottom: 1px solid #cbd5e1; padding-bottom: 0.55rem; margin-bottom: 0.85rem; }
.madri-moodle-layout .madri-toc--formal .madri-toc__link { color: inherit; font-weight: 500; }
.madri-moodle-layout .madri-toc--boxed { border: 1px solid #e2e8f0; border-radius: 10px; padding: 1rem 1.15rem; background: #f8fafc; }
.madri-moodle-layout .madri-toc--boxed .madri-toc__title { border-bottom: 1px dashed #cbd5e1; padding-bottom: 0.45rem; }
.madri-moodle-layout .madri-toc--sidebar { border-left: 4px solid #2563eb; padding: 0.65rem 0 0.65rem 1rem; background: linear-gradient(90deg, rgba(37,99,235,0.07) 0%, transparent 58%); }
.madri-moodle-layout .madri-toc--sidebar .madri-toc__title { color: #1e40af; font-size: 0.95em; }
.madri-moodle-layout .madri-toc--sidebar .madri-toc__link { color: #1d4ed8; }
.madri-moodle-layout .madri-toc--empty .madri-toc__empty { margin: 0; opacity: 0.75; font-style: italic; }
/* Paginação SPA (page-break) — proteger layout no Moodle/preview */
.madri-moodle-layout .madri-spa-pagination {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  flex-wrap: wrap !important;
  gap: 8px !important;
  margin-top: 3rem !important;
  padding-top: 1.5rem !important;
  border-top: 1px solid #e2e8f0 !important;
  width: 100% !important;
  box-sizing: border-box !important;
}
.madri-moodle-layout .madri-spa-pagination button {
  font-family: inherit !important;
  box-sizing: border-box !important;
  line-height: normal !important;
}
.madri-moodle-layout .madri-spa-pagination__nums {
  display: flex !important;
  flex-wrap: wrap !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 8px !important;
  margin: 0 4px !important;
}
.madri-moodle-layout .madri-spa-pagination__nums--mobile {
  display: none !important;
  flex-wrap: nowrap !important;
}
.madri-moodle-layout .madri-spa-pagination__ellipsis {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  min-width: 1.25rem !important;
  padding: 0 2px !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  line-height: 1 !important;
  opacity: 0.55 !important;
  user-select: none !important;
  flex-shrink: 0 !important;
}
.madri-moodle-layout .madri-spa-pagination__page-badge,
.madri-moodle-layout .madri-spa-pagination__num {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-sizing: border-box !important;
  flex-shrink: 0 !important;
}
.madri-moodle-layout .madri-spa-pagination__label,
.madri-moodle-layout .madri-spa-pagination__fraction {
  flex-shrink: 0 !important;
}
@media (max-width: 767px) {
  .madri-moodle-layout .madri-spa-pagination--arrows-numbers {
    flex-wrap: nowrap !important;
    gap: 4px !important;
  }
  .madri-moodle-layout .madri-spa-pagination--arrows-numbers .madri-spa-pagination__prev,
  .madri-moodle-layout .madri-spa-pagination--arrows-numbers .madri-spa-pagination__next,
  .madri-moodle-layout .madri-spa-pagination--arrows-numbers .madri-spa-pagination__redirect {
    padding: 8px 12px !important;
    margin: 0 2px !important;
    flex-shrink: 0 !important;
  }
  .madri-moodle-layout .madri-spa-pagination__nums--desktop {
    display: none !important;
  }
  .madri-moodle-layout .madri-spa-pagination__nums--mobile {
    display: flex !important;
    flex-wrap: nowrap !important;
    gap: 4px !important;
    margin: 0 2px !important;
    max-width: calc(100% - 96px) !important;
    overflow: hidden !important;
  }
  .madri-moodle-layout .madri-spa-pagination__nums--mobile .madri-spa-pagination__num {
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
    font-size: 13px !important;
  }
}
@media (max-width: 640px) {
  .madri-moodle-layout .madri-toc__item--level-2 { padding-left: 0.75rem !important; }
  .madri-moodle-layout .madri-toc__item--level-3 { padding-left: 1.35rem !important; }
  .madri-moodle-layout .madri-toc--sidebar { padding-left: 0.65rem; }
}
.madri-moodle-layout img {
  max-width: 100%;
  height: auto;
  border-radius: 6px;
}
` + MOTION_CSS;

// === CONSTANTS & MOCKS ===

const COLUMN_TYPES = [
  { id: '1/1', label: '100%', class: 'col-1-1' },
  { id: '1/2', label: '50% / 50%', class: 'col-1-2' },
  { id: '1/3', label: '33% / 33% / 33%', class: 'col-1-3' },
  { id: '1/4', label: '25% / 25% / 25% / 25%', class: 'col-1-4' },
  { id: '2/3', label: '66% / 33%', class: 'col-2-3' },
  { id: '33/66', label: '33% / 66%', class: 'col-1-3' },
];

const PandaBrandMark = ({ size = 18 }) => (
  <img
    src={PANDA_OFFICIAL_LOGO_SVG}
    alt=""
    width={size}
    height={size}
    style={{ objectFit: 'contain', display: 'block' }}
    loading="lazy"
    decoding="async"
  />
);

const WIDGET_TYPES = [
  { type: 'text', icon: Type, label: 'Text Block' },
  { type: 'title-basic', icon: LucideIcons.Anchor, label: 'Título âncora' },
  { type: 'table-of-contents', icon: LucideIcons.ListTree, label: 'Sumário (Expert)' },
  { type: 'image', icon: LucideIcons.Image, label: 'Image' },
  { type: 'video', icon: Video, label: 'Video Embed' },
  { type: 'panda-video', icon: PandaBrandMark, label: 'Panda Video (Native)' },
  { type: 'alert', icon: AlertCircle, label: 'Notice Board' },
  { type: 'styled-card', icon: Type, label: 'Styled Card (Premium)' },
  { type: 'section-header', icon: Layout, label: 'Section Header (Premium)' },
  { type: 'styled-button', icon: Layout, label: 'CTA Button' },
  { type: 'list-icon', icon: LucideIcons.List, label: 'Text Icon (List)' },
  { type: 'page-break', icon: LucideIcons.ChevronsLeftRight, label: 'Paginação' },
  { type: 'tabs', icon: LucideIcons.Layers, label: 'Abas (Tabs)' },
  { type: 'accordion', icon: LucideIcons.ChevronDown, label: 'Accordion' },
  { type: 'modal-trigger', icon: LucideIcons.Maximize2, label: 'Modal (Premium)' },
  { type: 'carousel', icon: LucideIcons.GalleryHorizontal, label: 'Carrossel (Premium)' },
  { type: 'card-slider', icon: LucideIcons.GalleryHorizontalEnd, label: 'Carrossel de cards (Premium)' },
  { type: 'card-shop', icon: LucideIcons.ShoppingBag, label: 'Card Shop (Premium)' },
  { type: 'video-card-slider', icon: LucideIcons.Clapperboard, label: 'Carrossel de vídeos (Premium)' },
  { type: 'image-card', icon: LucideIcons.CreditCard, label: 'Image Card (Premium)' },
  { type: 'social-card', icon: LucideIcons.UserCircle, label: 'Social Card (Premium)' },
  { type: 'post-it', icon: LucideIcons.StickyNote, label: 'Post-it (Premium)' },
  { type: 'divider', icon: LucideIcons.Minus, label: 'Linha Divisora' },
  { type: 'spacer', icon: LucideIcons.SeparatorVertical, label: 'Espaçador' },
  { type: 'responsive-table', icon: LucideIcons.Table, label: 'Tabela responsiva (Expert)' },
  { type: 'image-gallery', icon: LucideIcons.LayoutGrid, label: 'Galeria de imagens (Premium)' },
  { type: 'chart-widget', icon: LucideIcons.PieChart, label: 'Gráficos (Premium)' },
  { type: 'flowchart-widget', icon: LucideIcons.Workflow, label: 'Fluxograma (Premium)' },
  { type: 'h5p', icon: LucideIcons.Puzzle, label: 'H5P ou Genially' },
  { type: 'blockquote', icon: LucideIcons.Quote, label: 'Caixa de Citação (Premium)' },
  { type: 'moodle-activity', icon: LucideIcons.ExternalLink, label: 'Abrir atividade Moodle (Premium)' },
  { type: 'timeline', icon: LucideIcons.GitCommitHorizontal, label: 'Linha do Tempo (Premium)' },
  { type: 'checkpoint', icon: LucideIcons.Flag, label: 'Checkpoint (Premium)' },
  { type: 'reinforcement-quiz', icon: LucideIcons.HelpCircle, label: 'Quiz de reforço (Premium)' },
  { type: 'progress-bar', icon: LucideIcons.Gauge, label: 'Barra de Progresso (Premium)' },
  { type: 'header-menu', icon: LucideIcons.Menu, label: 'Menu do cabeçalho (Premium)' },
  { type: 'site-menu', icon: LucideIcons.PanelTop, label: 'Menu Site (Premium)' },
  { type: 'site-footer', icon: LucideIcons.PanelBottom, label: 'Rodapé do site (Premium)' },
  { type: 'site-login', icon: LucideIcons.LogIn, label: 'Página de login (Premium)' },
  { type: 'site-maintenance', icon: LucideIcons.Construction, label: 'Modo manutenção (Expert)' },
  { type: 'count-up', icon: LucideIcons.Hash, label: 'Número crescente (Premium)' },
  { type: 'comments', icon: LucideIcons.MessageCircle, label: 'Comentários (Premium)' },
];

const PREMADE_TEMPLATES = [
  { id: 'hero', label: 'Hero Header Elegante', icon: Layout },
  { id: 'cards', label: '3 Cards (Destaques)', icon: Layout },
  { id: 'text-video', label: 'Texto + Vídeo Lateral', icon: Layout }
];

// Funções para gerador ID basico
const uid = () => 'id_' + Math.random().toString(36).substr(2, 9);

/** Coleta âncoras dos elementos Título (básico) para o menu do cabeçalho. */
function collectTitleBasicAnchorsFromRows(rows) {
  const out = [];
  const visitCol = (col) => {
    if (!col) return;
    for (const w of col.widgets || []) {
      if (w.type === 'title-basic') {
        const anchorId = sanitizeTitleBasicAnchorId(w.content?.anchorId, w.id);
        const label = String(w.content?.title || '').trim() || anchorId;
        out.push({ anchorId, label });
      }
    }
    for (const nr of col.nestedRows || []) {
      for (const c2 of nr.columns || []) visitCol(c2);
    }
  };
  for (const row of rows || []) {
    for (const col of row.columns || []) visitCol(col);
  }
  return out;
}

const cloneTemplateRows = (rows) => {
  if (!Array.isArray(rows)) return [];
  const mapCol = (col) => ({
    ...col,
    id: uid(),
    widgets: (col.widgets || []).map(w => ({ ...w, id: uid() })),
    nestedRows: Array.isArray(col.nestedRows) && col.nestedRows.length
      ? cloneTemplateRows(col.nestedRows)
      : col.nestedRows,
  });
  return rows.map(row => ({
    ...row,
    id: uid(),
    columns: (row.columns || []).map(mapCol),
  }));
};

function findRowInTree(rowList, rowId) {
  for (const row of rowList || []) {
    if (row.id === rowId) return row;
    for (const col of row.columns || []) {
      if (col.nestedRows?.length) {
        const f = findRowInTree(col.nestedRows, rowId);
        if (f) return f;
      }
    }
  }
  return null;
}

function findRowContext(rows, rowId) {
  const topIdx = rows.findIndex((r) => r.id === rowId);
  if (topIdx >= 0) return { scope: 'root', row: rows[topIdx], index: topIdx, parentList: rows };
  for (const row of rows) {
    for (const col of row.columns || []) {
      if (!col.nestedRows?.length) continue;
      const nIdx = col.nestedRows.findIndex((r) => r.id === rowId);
      if (nIdx >= 0) {
        return { scope: 'nested', row: col.nestedRows[nIdx], index: nIdx, parentList: col.nestedRows, hostColId: col.id };
      }
      const deep = findRowContext(col.nestedRows, rowId);
      if (deep) return deep;
    }
  }
  return null;
}

function findColumnOwner(rowList, colId) {
  for (const row of rowList || []) {
    for (const col of row.columns || []) {
      if (col.id === colId) return { row, col };
      if (col.nestedRows?.length) {
        const hit = findColumnOwner(col.nestedRows, colId);
        if (hit) return hit;
      }
    }
  }
  return null;
}

function addNestedRowToColumnTree(rows, hostColId, insertIndex, newRow) {
  return rows.map((row) => ({
    ...row,
    columns: row.columns.map((col) => {
      if (col.id === hostColId) {
        const list = [...(col.nestedRows || [])];
        const idx = Math.max(0, Math.min(insertIndex ?? list.length, list.length));
        list.splice(idx, 0, newRow);
        return { ...col, nestedRows: list };
      }
      if (col.nestedRows?.length) {
        return { ...col, nestedRows: addNestedRowToColumnTree(col.nestedRows, hostColId, insertIndex, newRow) };
      }
      return col;
    }),
  }));
}

function removeNestedRowFromTree(rows, rowIdToRemove) {
  return rows
    .filter((row) => row.id !== rowIdToRemove)
    .map((row) => ({
      ...row,
      columns: row.columns.map((col) => ({
        ...col,
        nestedRows: col.nestedRows?.length ? removeNestedRowFromTree(col.nestedRows, rowIdToRemove) : col.nestedRows,
      })),
    }));
}

function replaceRowInTree(rowList, rowId, fn) {
  return rowList.map((row) => {
    if (row.id === rowId) return fn(row);
    return {
      ...row,
      columns: row.columns.map((col) => ({
        ...col,
        nestedRows: col.nestedRows?.length ? replaceRowInTree(col.nestedRows, rowId, fn) : col.nestedRows,
      })),
    };
  });
}

function patchColumnInTree(rowList, colId, patchFn) {
  return rowList.map((row) => ({
    ...row,
    columns: row.columns.map((col) => {
      if (col.id === colId) return patchFn(col);
      return {
        ...col,
        nestedRows: col.nestedRows?.length ? patchColumnInTree(col.nestedRows, colId, patchFn) : col.nestedRows,
      };
    }),
  }));
}

function addWidgetToRowColumn(rows, targetRowId, colIdx, widget) {
  return rows.map((row) => {
    if (row.id === targetRowId) {
      return {
        ...row,
        columns: row.columns.map((c, i) => (i === colIdx ? { ...c, widgets: [...c.widgets, widget] } : c)),
      };
    }
    return {
      ...row,
      columns: row.columns.map((col) => ({
        ...col,
        nestedRows: col.nestedRows?.length ? addWidgetToRowColumn(col.nestedRows, targetRowId, colIdx, widget) : col.nestedRows,
      })),
    };
  });
}

function removeWidgetFromTree(rows, rowId, colId, widgetId) {
  return rows.map((row) => {
    if (row.id === rowId) {
      return {
        ...row,
        columns: row.columns.map((c) =>
          c.id === colId ? { ...c, widgets: c.widgets.filter((w) => w.id !== widgetId) } : c
        ),
      };
    }
    return {
      ...row,
      columns: row.columns.map((col) => ({
        ...col,
        nestedRows: col.nestedRows?.length ? removeWidgetFromTree(col.nestedRows, rowId, colId, widgetId) : col.nestedRows,
      })),
    };
  });
}

function updateWidgetInTree(rows, rowId, colId, widgetId, newContent) {
  return rows.map((row) => {
    if (row.id === rowId) {
      return {
        ...row,
        columns: row.columns.map((c) =>
          c.id === colId
            ? {
                ...c,
                widgets: c.widgets.map((w) => (w.id === widgetId ? { ...w, content: newContent } : w)),
              }
            : c
        ),
      };
    }
    return {
      ...row,
      columns: row.columns.map((col) => ({
        ...col,
        nestedRows: col.nestedRows?.length ? updateWidgetInTree(col.nestedRows, rowId, colId, widgetId, newContent) : col.nestedRows,
      })),
    };
  });
}

function duplicateWidgetInTree(rows, rowId, colId, widget) {
  const newWidget = {
    ...widget,
    id: uid(),
    content: JSON.parse(JSON.stringify(widget.content)),
  };
  return rows.map((row) => {
    if (row.id === rowId) {
      return {
        ...row,
        columns: row.columns.map((c) => {
          if (c.id !== colId) return c;
          const widgetIndex = c.widgets.findIndex((w) => w.id === widget.id);
          const newWidgets = [...c.widgets];
          newWidgets.splice(widgetIndex + 1, 0, newWidget);
          return { ...c, widgets: newWidgets };
        }),
      };
    }
    return {
      ...row,
      columns: row.columns.map((col) => ({
        ...col,
        nestedRows: col.nestedRows?.length ? duplicateWidgetInTree(col.nestedRows, rowId, colId, widget) : col.nestedRows,
      })),
    };
  });
}

function insertRowAfterInTree(rowList, targetRowId, rowToInsert) {
  const walk = (list) => {
    let inserted = false;
    let out = null;

    for (let i = 0; i < (list || []).length; i++) {
      const row = list[i];
      let nextRow = row;

      let colsChanged = false;
      const nextCols = (row.columns || []).map((col) => {
        if (!col.nestedRows?.length) return col;
        if (inserted) return col;
        const r = walk(col.nestedRows);
        if (r.inserted) inserted = true;
        if (r.list !== col.nestedRows) {
          colsChanged = true;
          return { ...col, nestedRows: r.list };
        }
        return col;
      });
      if (colsChanged) nextRow = { ...row, columns: nextCols };

      if (out) {
        out.push(nextRow);
      } else if (nextRow !== row) {
        out = [...list.slice(0, i), nextRow];
      }

      if (!inserted && row.id === targetRowId) {
        if (!out) out = list.slice(0, i + 1);
        out.push(rowToInsert);
        inserted = true;
      }
    }

    return { list: out || list, inserted };
  };
  return walk(rowList || []).list;
}

function findColByIdInTree(rowList, colId) {
  for (const row of rowList || []) {
    for (const col of row.columns || []) {
      if (col.id === colId) return col;
      if (col.nestedRows?.length) {
        const hit = findColByIdInTree(col.nestedRows, colId);
        if (hit) return hit;
      }
    }
  }
  return null;
}

function forEachRowInTree(rowList, fn) {
  (rowList || []).forEach((row) => {
    fn(row);
    (row.columns || []).forEach((col) => {
      if (col.nestedRows?.length) forEachRowInTree(col.nestedRows, fn);
    });
  });
}

function walkAllWidgetsInRows(rowList, cb) {
  (rowList || []).forEach((row) => {
    (row.columns || []).forEach((col) => {
      (col.widgets || []).forEach(cb);
      if (col.nestedRows?.length) walkAllWidgetsInRows(col.nestedRows, cb);
    });
  });
}

/** Tipo de grid do plano a partir da estrutura da row (espelha checkPremiumUsage). */
function getRowPlanGridType(row) {
  if (!row?.columns?.length) return '1/1';
  if (row.columns[0]?.width === 'dynamic') return 'dynamic';
  if (row.columns.length === 2) {
    if (row.columns[0].width === '2/3') return '2/3';
    if (row.columns[0].width === '1/3') return '33/66';
    return '1/2';
  }
  if (row.columns.length === 3) return '1/3';
  if (row.columns.length === 4) return '1/4';
  return '1/1';
}

/** Estilo de destaque vermelho (elementos/grids fora do plano). */
function getPlanRestrictedHighlightStyle() {
  return {
    boxShadow: '0 0 0 1px rgba(239, 68, 68, 0.55), 0 0 10px rgba(239, 68, 68, 0.18)',
    outline: '1px solid rgba(239, 68, 68, 0.35)',
    outlineOffset: '1px',
    animation: 'gepettoPlanRestrictedPulse 2s ease-in-out infinite',
    zIndex: 5,
    transition: 'box-shadow 0.25s ease, opacity 0.25s ease',
  };
}

function collectWidgetsInRow(r) {
  let out = [];
  (r?.columns || []).forEach((c) => {
    out.push(...(c.widgets || []));
    (c.nestedRows || []).forEach((nr) => {
      out = out.concat(collectWidgetsInRow(nr));
    });
  });
  return out;
}

function buildColumnsForLayout(layoutType, customPercentages) {
  let newCols = [];
  if (layoutType === '1/1') {
    newCols = [{ id: uid(), width: '1/1', widgets: [] }];
  } else if (layoutType === '1/2') {
    newCols = [
      { id: uid(), width: '1/2', widgets: [] },
      { id: uid(), width: '1/2', widgets: [] },
    ];
  } else if (layoutType === '1/3') {
    newCols = [
      { id: uid(), width: '1/3', widgets: [] },
      { id: uid(), width: '1/3', widgets: [] },
      { id: uid(), width: '1/3', widgets: [] },
    ];
  } else if (layoutType === '2/3') {
    newCols = [
      { id: uid(), width: '2/3', widgets: [] },
      { id: uid(), width: '1/3', widgets: [] },
    ];
  } else if (layoutType === '33/66') {
    newCols = [
      { id: uid(), width: '1/3', widgets: [] },
      { id: uid(), width: '2/3', widgets: [] },
    ];
  } else if (layoutType === '1/4') {
    newCols = [
      { id: uid(), width: '1/4', widgets: [] },
      { id: uid(), width: '1/4', widgets: [] },
      { id: uid(), width: '1/4', widgets: [] },
      { id: uid(), width: '1/4', widgets: [] },
    ];
  } else if (layoutType === 'dynamic' && Array.isArray(customPercentages) && customPercentages.length >= 2) {
    newCols = customPercentages.map((pct) => ({ id: uid(), width: 'dynamic', customWidth: pct, widgets: [] }));
  }
  return newCols;
}

function createRowFromLayout(layoutType, customPercentages, opts = {}) {
  const newCols = buildColumnsForLayout(layoutType, customPercentages);
  if (!newCols.length) return null;
  const compact = !!opts.compact;
  const rowConfig = { padding: compact ? '0.5rem' : '1rem', backgroundColor: 'transparent' };
  if (layoutType === 'dynamic') rowConfig.layoutType = 'dynamic';
  if (layoutType === 'dynamic' && customPercentages) rowConfig.dynamicColumns = [...customPercentages];
  return { id: uid(), config: rowConfig, columns: newCols };
}

const toCssStr = (obj, def) => {
  if (!obj) return def;
  return `${obj.top || 0}px ${obj.right || 0}px ${obj.bottom || 0}px ${obj.left || 0}px`;
};

const cssStringToObject = (css) => {
  if (!css || !String(css).trim()) return {};
  const obj = {};
  const safe = String(css).replace(/expression\s*\(|javascript\s*:/gi, '');
  safe.split(';').forEach(part => {
    const idx = part.indexOf(':');
    if (idx > 0) {
      const key = part.slice(0, idx).trim().replace(/-([a-z])/g, (_, c) => c.toUpperCase());
      const val = part.slice(idx + 1).trim();
      if (key && val) obj[key] = val;
    }
  });
  return obj;
};

/** Injeta regras com seletor na folha do documento. Declarações simples não entram aqui. */
function AdvancedCssStyleTag({ styleId, css }) {
  useEffect(() => {
    const id = `gepetto-advcss-${styleId}`;
    const text = String(css || '').trim();
    const existing = document.getElementById(id);
    if (text && text.includes('{')) {
      if (existing) {
        existing.textContent = text;
      } else {
        const el = document.createElement('style');
        el.id = id;
        el.textContent = text;
        document.head.appendChild(el);
      }
    } else if (existing) {
      existing.remove();
    }
    return () => {
      document.getElementById(id)?.remove();
    };
  }, [styleId, css]);
  return null;
}

// === COMPONENTES DO BUILDER ===

const IconPickerModal = ({ onSelect, onClose }) => {
  const { t } = useTranslation('builder');
  const [search, setSearch] = useState('');

  const filteredIcons = validIconNames
    .filter(name => name.toLowerCase().includes(search.toLowerCase()))
    .slice(0, 100);

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ backgroundColor: 'var(--bg-card)', padding: '1.5rem', borderRadius: '8px', width: '100%', maxWidth: '600px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', border: '1px solid var(--border)' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem', alignItems: 'center' }}>
          <h3 style={{ margin: 0, color: 'var(--text-light)', fontSize: '1.25rem' }}>{t('searchIcon')}</h3>
          <button onClick={onClose} style={{ color: 'var(--text-muted)', fontSize: '1.5rem', lineHeight: 1 }}>&times;</button>
        </div>

        <div style={{ position: 'relative', marginBottom: '1rem' }}>
          <LucideIcons.Search size={18} style={{ position: 'absolute', left: '12px', top: '12px', color: 'var(--text-muted)' }} />
          <input
            autoFocus
            placeholder={t('filterPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', padding: '0.75rem 1rem 0.75rem 2.5rem', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-dark)', color: 'white', fontSize: '1rem' }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(64px, 1fr))', gap: '0.5rem', overflowY: 'auto', flex: 1, paddingRight: '0.5rem' }}>
          <button
            onClick={() => onSelect('default')}
            style={{ padding: '0.75rem', borderRadius: '4px', border: '1px dashed var(--border)', backgroundColor: 'var(--bg-dark)', color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', cursor: 'pointer', gridColumn: '1 / -1', marginBottom: '0.5rem' }}
          >
            <span>{t('themeAuto')}</span>
          </button>

          {filteredIcons.map(name => {
            const IconComponent = LucideIcons[name];
            return (
              <button
                key={name}
                onClick={() => onSelect(name)}
                style={{ padding: '0.75rem', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-dark)', color: 'var(--text-light)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s', '--hover-bg': 'var(--primary)' }}
                onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--primary)'; }}
                onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-light)'; }}
                title={name}
              >
                <IconComponent size={24} />
              </button>
            );
          })}
        </div>

      </div>
    </div>
  );
};


// === RENDERERS (Fake Components for the Builder Canvas) ===

const RenderVideo = ({ content, t }) => {
  const urlRaw = (content && content.url) ? String(content.url).trim() : '';
  const url = getEffectiveVideoUrl(urlRaw);

  let embedUrl = url;
  const matchTube = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/);
  if (matchTube) {
    embedUrl = `https://www.youtube.com/embed/${matchTube[1]}?autoplay=0`;
  } else if (url.includes('vimeo.com')) {
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      embedUrl = `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=0`;
    }
  } else if (url.startsWith('<iframe')) {
    const srcMatch = url.match(/src\s*=\s*["']([^"']+)["']/i);
    if (srcMatch) {
      embedUrl = srcMatch[1].replace(/([?&])autoplay=1(&|$)/gi, '$1autoplay=0$2').replace(/\bautoplay=1\b/gi, 'autoplay=0');
      if (!embedUrl.includes('autoplay=')) embedUrl += (embedUrl.includes('?') ? '&' : '?') + 'autoplay=0';
    } else {
      return <div className="video-responsive-container" style={{ position: 'relative', aspectRatio: '16 / 9', height: 'auto', overflow: 'hidden', borderRadius: '8px' }} dangerouslySetInnerHTML={{ __html: url }} />;
    }
  }

  return (
    <div className="video-responsive-container" style={{ position: 'relative', aspectRatio: '16 / 9', height: 'auto', overflow: 'hidden', borderRadius: '8px', backgroundColor: '#000' }}>
      <iframe src={embedUrl} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} frameBorder="0" allow="autoplay; fullscreen; picture-in-picture"></iframe>
    </div>
  );
};

const RenderPandaVideo = ({ content, t }) => {
  const urlRaw = (content && content.url) ? String(content.url).trim() : '';
  let embedUrl = urlRaw;
  
  if (!embedUrl) {
    return (
      <div className="video-responsive-container" style={{ position: 'relative', aspectRatio: '16 / 9', height: 'auto', overflow: 'hidden', borderRadius: '8px', backgroundColor: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
        <div style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', width: '100%', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <PandaBrandMark size={52} />
          <span>{t('builder:noVideo', { defaultValue: 'Sem vídeo configurado' })} (Panda)</span>
        </div>
      </div>
    );
  }

  const ck = content.checkpoint;
  const checkpointStripControls =
    ck?.enabled && ck?.completionMode === 'video' && ck.showTimeline === false;
  if (content.hideControls || checkpointStripControls) {
    embedUrl += (embedUrl.includes('?') ? '&' : '?') + 'controls=0';
  }

  return (
    <div className="video-responsive-container" style={{ position: 'relative', aspectRatio: '16 / 9', height: 'auto', overflow: 'hidden', borderRadius: '8px', backgroundColor: '#000' }}>
      <iframe src={embedUrl} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen></iframe>
    </div>
  );
};

/** Injeta HTML do embed uma vez por mudança de código — evita que re-renders (ex.: polling 5s) recriem o iframe (Genially “piscando”). */
const H5pEmbedCodeHost = React.memo(function H5pEmbedCodeHost({ embedCode }) {
  const hostRef = useRef(null);
  const lastHtmlRef = useRef('');
  const normalizedEmbed = useMemo(() => buildH5pPreviewEmbedHtml(embedCode), [embedCode]);
  useEffect(() => {
    if (!normalizedEmbed || !hostRef.current) return;
    if (normalizedEmbed === lastHtmlRef.current) return;
    lastHtmlRef.current = normalizedEmbed;
    hostRef.current.innerHTML = normalizedEmbed;
  }, [normalizedEmbed]);
  return (
    <div
      ref={hostRef}
      style={{ width: '100%', minHeight: '400px' }}
      className="h5p-embed-html-host"
    />
  );
});

const H5pMoodleProtectedNotice = ({ t }) => (
  <div className="h5p-moodle-protected-notice fake-alert alert-warning" role="status">
    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
      <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: '2px' }} aria-hidden />
      <div>
        <p style={{ fontWeight: 600, marginBottom: '0.35rem' }}>
          {t ? t('builder:h5pMoodleProtectedTitle') : 'Seu conteúdo aparecerá no Moodle'}
        </p>
        <p style={{ fontWeight: 400, fontSize: '0.875rem', lineHeight: 1.55 }}>
          {t ? t('builder:h5pMoodleProtectedBody') : 'Embeds de H5P ou Genially copiados do seu Moodle não são exibidos aqui no Geppetto porque exigem login no seu ambiente. Isso é normal no editor. Após exportar e importar de volta no Moodle, o conteúdo aparecerá normalmente para os alunos.'}
        </p>
      </div>
    </div>
  </div>
);

const RenderH5P = ({ content, t }) => {
  const urlRaw = (content.url || '').trim();
  const embedCode = (content.embedCode || '').trim();
  const useEmbed = embedCode && embedCode.includes('<iframe');
  const hasContent = urlRaw !== '' || useEmbed;
  const iframeSrc = getEffectiveH5pUrl(urlRaw);
  const loadH5pResizer = hasContent && contentLooksLikeH5P({ url: urlRaw, embedCode });
  const showMoodleProtectedNotice = hasContent && embedLikelyRequiresMoodleLoginInBuilder({ url: urlRaw, embedCode });

  useEffect(() => {
    if (!loadH5pResizer) return;
    if (document.querySelector('script[src*="h5p-resizer"]')) return;
    const script = document.createElement('script');
    script.src = 'https://h5p.org/sites/all/modules/h5p/library/js/h5p-resizer.js';
    script.charset = 'UTF-8';
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [loadH5pResizer]);

  if (!hasContent) {
    return (
      <div className="fake-video" style={{ backgroundColor: '#f0f9ff' }}>
        <div style={{ color: '#94a3b8', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
          <LucideIcons.Puzzle size={24} />
          <span>{t ? t('builder:h5pPasteHint') : 'Cole a URL do embed H5P ou o código completo'}</span>
        </div>
      </div>
    );
  }

  const embedFrame = useEmbed ? (
    <div style={{ width: '100%', borderRadius: '8px', overflow: 'hidden', position: 'relative' }} className="h5p-embed-wrapper" data-h5p-container>
      <H5pEmbedCodeHost embedCode={embedCode} />
    </div>
  ) : (
    <div style={{ width: '100%', borderRadius: '8px', overflow: 'hidden', position: 'relative' }} className="h5p-embed-wrapper" data-h5p-container>
      <iframe src={iframeSrc} width="100%" height="400" frameBorder="0" allow="fullscreen; geolocation *; microphone *; camera *; midi *; encrypted-media *" title={t ? t('builder:elH5p') : 'Embed'} style={{ minHeight: '400px', border: 'none' }} />
    </div>
  );

  return (
    <div className="h5p-embed-block" style={{ width: '100%' }}>
      {embedFrame}
      {showMoodleProtectedNotice ? <H5pMoodleProtectedNotice t={t} /> : null}
    </div>
  );
};

const RenderImage = ({ content, t }) => {
  if (!content.url) {
    return (
      <div className="fake-video" style={{ backgroundColor: '#f1f5f9' }}>
        <div style={{ color: '#94a3b8', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
          <LucideIcons.Image size={24} />
          <span>{t ? t('builder:noImage') : 'Sem imagem configurada'}</span>
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', justifyContent: content.align === 'left' ? 'flex-start' : (content.align === 'right' ? 'flex-end' : 'center') }}>
      <img
        src={content.url}
        alt={content.alt || ''}
        style={buildImageImgStyleObject(content)}
      />
    </div>
  );
};

const CheckpointIconSvg = ({ type, done, position }) => {
  const pos = position || 'top-right';
  const posStyle = pos === 'top-left' ? { top: 6, left: 6 } : pos === 'top-right' ? { top: 6, right: 6 } : pos === 'bottom-left' ? { bottom: 6, left: 6 } : { bottom: 6, right: 6 };
  const size = 26;
  const stroke = done ? '#22c55e' : '#94a3b8';
  const fill = done ? '#22c55e' : 'none';
  const checkEl = <path d="M8 12l3 3 5-6" stroke={done ? 'white' : stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />;
  const circleEl = <circle cx="12" cy="12" r="10" fill={fill} stroke={stroke} strokeWidth="2" />;
  const svgMap = {
    check: <svg width={size} height={size} viewBox="0 0 24 24">{circleEl}{checkEl}</svg>,
    circle: <svg width={size} height={size} viewBox="0 0 24 24">{circleEl}{checkEl}</svg>,
    square: <svg width={size} height={size} viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="2" fill={fill} stroke={stroke} strokeWidth="2" />{checkEl}</svg>,
    badge: <svg width={size} height={size} viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill={done ? '#22c55e' : '#e2e8f0'} />{checkEl}</svg>,
    shield: <svg width={size} height={size} viewBox="0 0 24 24"><path d="M12 2L4 5v7c0 5.5 3.8 9.6 8 11 4.2-1.4 8-5.5 8-11V5l-8-3z" fill={fill} stroke={stroke} strokeWidth="2" strokeLinejoin="round" />{checkEl}</svg>
  };
  const emojiTypes = ['flag', 'star', 'bookmark', 'trophy'];
  if (emojiTypes.includes(type)) {
    const ico = { flag: { n: '🏳' }, star: { n: '☆' }, bookmark: { n: '📑' }, trophy: { n: '🎖' } }[type];
    return <span style={{ position: 'absolute', ...posStyle, fontSize: '1.2em', color: stroke }}>{ico?.n || '○'}</span>;
  }
  return <span className="madri-ck-icon" style={{ position: 'absolute', ...posStyle, width: size, height: size, opacity: done ? 1 : 0.8, zIndex: 5 }}>{svgMap[type] || svgMap.circle}</span>;
};

const RenderGeppettoComments = ({ t }) => (
  <div style={{
    fontFamily: 'system-ui, sans-serif',
    fontSize: 14,
    color: '#1e293b',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: 12,
    background: '#f8fafc',
  }}
  >
    <div style={{ fontWeight: 600, marginBottom: 8 }}>
      {t('builder:commentsPreviewTitle', { defaultValue: 'Comentários' })}
    </div>
    <p style={{ margin: 0, color: '#64748b', fontSize: 13, lineHeight: 1.5 }}>
      {t('builder:commentsPreviewHint', { defaultValue: 'No Moodle, os comentários ficam salvos por aluno e por esta atividade. Visual estilo lista recolhível.' })}
    </p>
  </div>
);

const RenderCheckpoint = ({ content, showEditorWatermark = true, t }) => {
  const durMs = Math.min(120000, Math.max(1000, parseInt(content.durationMs, 10) || 5000));
  const sec = durMs / 1000;
  const label = t
    ? t('builder:checkpointEditorWatermark', { sec, defaultValue: `Checkpoint ${sec}s` })
    : `Checkpoint ${sec}s`;
  return (
    <div
      style={{
        position: showEditorWatermark ? 'relative' : undefined,
        minHeight: 48,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {showEditorWatermark ? (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            fontWeight: 500,
            color: '#cbd5e1',
            opacity: 1,
            pointerEvents: 'none',
            userSelect: 'none',
            zIndex: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
      ) : null}
    </div>
  );
};

const RenderProgressBar = ({ content, t }) => {
  const accent = content.accentColor || '#3b82f6';
  const track = content.trackColor || '#e2e8f0';
  const textClr = content.progressBarTextColor || '#64748b';
  const widthPct = content.widthPct ?? 100;
  const align = content.barAlign || 'center';
  const barStyle = content.barStyle || 'simple';
  const CheckIcon = LucideIcons.Check;
  const customBg = content.progressBarBg || content.computedBgRGBA || content.wrapperBg || '#f8fafc';

  const barHeight = barStyle === 'segmented' ? 10 : barStyle === 'thick' ? 16 : barStyle === 'pill' ? 14 : 6;
  const barRadius = barStyle === 'segmented' ? 5 : barStyle === 'pill' ? 999 : barStyle === 'thick' ? 10 : 3;
  const containerAlign = align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center';

  // Estilo do container: igual ao visualizar/export (WYSIWYG)
  const containerStyle = {
    padding: '10px 16px',
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: customBg || '#f8fafc',
    width: '100%',
    boxSizing: 'border-box'
  };

  // Stepper: círculos conectados
  if (barStyle === 'stepper') {
    const steps = [
      { status: 'completed', label: '1' },
      { status: 'current', label: '2' },
      { status: 'upcoming', label: '3' },
      { status: 'upcoming', label: '4' }
    ];
    return (
      <div style={containerStyle}>
        <div style={{ display: 'flex', justifyContent: containerAlign, width: '100%' }}>
          <div style={{ width: `${widthPct}%`, maxWidth: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
            <div style={{ position: 'absolute', top: 14, left: 0, right: 0, height: 2, background: track, zIndex: 0 }} />
            <div style={{ position: 'absolute', top: 14, left: 0, width: '33%', height: 2, background: accent, zIndex: 0 }} />
            {steps.map((s, i) => {
              const filled = s.status === 'completed' || s.status === 'current';
              return (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, zIndex: 1 }}>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: filled ? accent : track, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {filled && CheckIcon && <CheckIcon size={12} style={{ color: '#fff' }} />}
                  </div>
                  <span style={{ fontSize: 10, color: filled ? accent : textClr }}>{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Mínima: só a barra, sem ícone nem texto
  if (barStyle === 'minimal') {
    return (
      <div style={containerStyle}>
        <div style={{ display: 'flex', justifyContent: containerAlign, width: '100%' }}>
          <div style={{ width: `${widthPct}%`, maxWidth: '100%', height: 8, background: track, borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: '45%', height: '100%', background: accent, borderRadius: 4 }} />
          </div>
        </div>
      </div>
    );
  }

  const renderBar = () => (
    <div style={{ flex: 1, minWidth: 0, height: barHeight, background: track, borderRadius: barRadius, overflow: 'hidden' }}>
      <div style={{ width: '45%', height: '100%', background: accent, borderRadius: barRadius, transition: 'width 0.3s' }} />
    </div>
  );

  const renderSegmentedBar = () => (
    <div style={{ flex: 1, minWidth: 0, position: 'relative', height: barHeight, background: track, borderRadius: barRadius, overflow: 'hidden' }}>
      <div style={{ width: '45%', height: '100%', background: accent, borderRadius: barRadius }} />
      {[1, 2, 3, 4].map(i => (
        <div key={i} style={{ position: 'absolute', left: `${i * 20}%`, top: '50%', transform: 'translateY(-50%)', width: 7, height: 7, borderRadius: 5, background: i <= 2 ? 'rgba(255,255,255,0.9)' : accent, border: '1px solid rgba(0,0,0,0.1)' }} />
      ))}
    </div>
  );

  const segSuffix = content.segmentedCompletionText || 'para completar';
  const segClr = content.segmentedCompletionColor || textClr;
  const showSegText = content.showSegmentedCompletion !== false;
  const textFontSize = content.wrapperFont ? `${content.wrapperFont}px` : '0.85em';
  const textLineHeight = content.wrapperLineHeight ?? 1.5;

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: containerAlign, width: '100%' }}>
        <div style={{ width: `${widthPct}%`, maxWidth: '100%', display: 'flex', alignItems: 'center', gap: '12px' }}>
          {barStyle === 'segmented' ? renderSegmentedBar() : renderBar()}
          {barStyle === 'segmented' && showSegText && (
            <span style={{ fontSize: textFontSize, lineHeight: textLineHeight, fontWeight: 600, color: segClr, flexShrink: 0 }}>75% {segSuffix}</span>
          )}
          {barStyle !== 'segmented' && content.showPercent !== false && (
            <span style={{ fontSize: textFontSize, lineHeight: textLineHeight, color: textClr, minWidth: '50px', flexShrink: 0 }}>0 / N</span>
          )}
        </div>
      </div>
    </div>
  );
};

const RenderHeaderMenu = ({ content, t }) => {
  const items = (content.items || []).filter((it) => String(it.label || '').trim());
  const menuStyle = content.menuStyle || 'horizontal';
  const linkColor = content.linkColor || '#334155';
  const fontSize = content.fontSize ?? 14;
  const gap = content.gap ?? 16;
  const showIcons = content.showIcons !== false;
  const menuAlign = content.menuAlign || 'center';
  const justify = menuAlign === 'left' ? 'flex-start' : menuAlign === 'right' ? 'flex-end' : 'center';
  const flexDir = menuStyle === 'vertical' ? 'column' : 'row';
  const pills = menuStyle === 'pills';
  const minimal = menuStyle === 'minimal';
  const underlined = menuStyle === 'underlined';
  const breadcrumb = menuStyle === 'breadcrumb';
  const mbg = content.menuBg;
  const hoverColor = content.linkHoverColor || '#0f172a';

  const navStyle = {
    display: 'flex',
    flexDirection: breadcrumb ? 'row' : flexDir,
    flexWrap: breadcrumb || menuStyle !== 'vertical' ? 'wrap' : 'nowrap',
    alignItems: menuStyle === 'vertical' && !breadcrumb ? 'stretch' : 'center',
    justifyContent: justify,
    gap: breadcrumb ? 0 : gap,
    width: '100%',
    boxSizing: 'border-box',
    padding: `${content.paddingObj?.top ?? 4}px ${content.paddingObj?.right ?? 0}px ${content.paddingObj?.bottom ?? 4}px ${content.paddingObj?.left ?? 0}px`,
    margin: `${content.marginObj?.top ?? 0}px ${content.marginObj?.right ?? 0}px ${content.marginObj?.bottom ?? 0}px ${content.marginObj?.left ?? 0}px`,
    backgroundColor: mbg && mbg !== 'transparent' ? mbg : 'transparent',
    borderRadius: 8,
  };

  const linkStyle = (base = {}) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize,
    fontWeight: 500,
    textDecoration: 'none',
    color: linkColor,
    ...base,
  });

  const renderLink = (it) => {
    const IconComp = showIcons ? (getIconComponent(it.icon) || LucideIcons.Circle) : null;
    const href = (it.href || '#').trim() || '#';
    const isFragment = href.startsWith('#') && href.length > 1;
    const iconSz = breadcrumb ? Math.min(14, fontSize + 1) : Math.min(18, fontSize + 2);
    const extra =
      breadcrumb ? { padding: '2px 4px' } :
      pills ? { padding: '6px 14px', borderRadius: 999, background: 'rgba(15,23,42,0.06)' } :
      minimal ? { padding: '4px 8px' } :
      underlined ? { padding: '4px 2px', borderBottom: '2px solid transparent' } :
      { padding: '4px 8px' };
    return (
      <a
        href={isFragment ? '#' : href}
        target={isFragment ? undefined : (it.openNewTab ? '_blank' : undefined)}
        rel={isFragment ? undefined : (it.openNewTab ? 'noopener noreferrer' : undefined)}
        style={linkStyle(extra)}
        onClick={(e) => {
          if (!isFragment) return;
          e.preventDefault();
          const id = href.slice(1).trim().replace(/[^a-zA-Z0-9_-]/g, '');
          if (!id) return;
          const el = document.getElementById(id);
          if (el) {
            try { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (err) { el.scrollIntoView(true); }
          }
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = hoverColor; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = linkColor; }}
      >
        {IconComp && <IconComp size={iconSz} style={{ flexShrink: 0 }} />}
        <span>{it.label}</span>
      </a>
    );
  };

  return (
    <nav style={navStyle} aria-label={t ? t('builder:headerMenuAria', { defaultValue: 'Menu do cabeçalho' }) : 'Menu'}>
      {items.length === 0 && (
        <span style={{ fontSize: 12, color: '#94a3b8' }}>{t ? t('builder:headerMenuEmpty', { defaultValue: 'Adicione itens ao menu nas configurações.' }) : '…'}</span>
      )}
      {breadcrumb
        ? items.map((it, i) => (
            <React.Fragment key={i}>
              {i > 0 && (
                <span style={{ color: '#94a3b8', fontSize: Math.max(11, fontSize - 1), padding: '0 6px', userSelect: 'none', flexShrink: 0, lineHeight: 1 }} aria-hidden>/</span>
              )}
              {renderLink(it)}
            </React.Fragment>
          ))
        : items.map((it, i) => (
            <React.Fragment key={i}>{renderLink(it)}</React.Fragment>
          ))}
    </nav>
  );
};

const RenderPageBreak = ({ content, t }) => {
  const navStyle = content?.navStyle || 'simple-text';
  const bg = content?.btnBgColor || '#3b82f6';
  const textClr = content?.btnTextColor || '#ffffff';
  const hasLastPageRedirect = content?.lastPageAction === 'redirect' && (content?.lastPageUrl || '').trim();
  const lastPageLabel = content?.lastPageLabel || 'Concluir';

  const btnStyle = {
    backgroundColor: bg,
    color: textClr,
    padding: '8px 16px',
    borderRadius: '4px',
    fontSize: '14px',
    border: 'none',
    margin: '0 4px',
    display: 'inline-block',
    fontWeight: '500'
  };

  const textColor = content?.textColor || '#64748b';
  const textStyle = { color: textColor, fontSize: '14px', margin: '0 8px', fontWeight: 'bold' };

  const renderPreview = () => {
    if (navStyle === 'arrows-fraction') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={btnStyle}>&lt;</div>
          <span style={textStyle}>01/10</span>
          <div style={btnStyle}>&gt;</div>
        </div>
      );
    }
    if (navStyle === 'arrows-numbers') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={btnStyle}>&lt;</div>
          <div style={{ ...btnStyle, opacity: 0.6, backgroundColor: 'transparent', color: bg, border: `1px solid ${bg}` }}>1</div>
          <div style={btnStyle}>2</div>
          <div style={{ ...btnStyle, opacity: 0.6, backgroundColor: 'transparent', color: bg, border: `1px solid ${bg}` }}>3</div>
          <span style={textStyle}>...</span>
          <div style={btnStyle}>&gt;</div>
        </div>
      );
    }
    if (navStyle === 'simple-arrows') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          <div style={btnStyle}>&lsaquo; {t ? t('builder:navPrevious') : 'Anterior'}</div>
          <span style={{ width: 36, height: 36, minWidth: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 4, backgroundColor: 'transparent', color: bg, border: `1px solid ${bg}`, opacity: 0.6, fontSize: 14, fontWeight: 500 }}>2</span>
          <div style={btnStyle}>{t ? t('builder:navNext') : 'Próxima'} &rsaquo;</div>
        </div>
      );
    }

    // simple-text (default) - Clássico: Anterior | Página X de Y | Próxima
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={btnStyle}>{t ? t('builder:navPrevious') : 'Anterior'}</div>
        <span style={textStyle}>{t ? t('builder:page') : 'Página'} 1 de 10</span>
        <div style={btnStyle}>{t ? t('builder:navNext') : 'Próxima'}</div>
      </div>
    );
  };

  return (
    <div style={{ padding: '20px', textAlign: 'center', border: '2px dashed #cbd5e1', color: '#64748b', borderRadius: '8px', margin: '15px 0', backgroundColor: '#f8fafc' }}>
      <LucideIcons.Scissors size={18} style={{ marginBottom: '12px', opacity: 0.5 }} />
      <div style={{ marginBottom: '16px', fontSize: '0.9em', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600, color: '#94a3b8' }}>{t ? t('builder:pageBreakLabel') : 'Divisor de Página SPA'}</div>

      {/* Container de Preview Visual */}
      <div style={{ padding: '15px', backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', display: 'inline-block', minWidth: '300px' }}>
        <p style={{ margin: '0 0 10px 0', fontSize: '0.75em', color: '#94a3b8' }}>{t ? t('builder:browserPreview') : 'Preview do Navegador'}:</p>
        {renderPreview()}
        {hasLastPageRedirect && (
          <div style={{ marginTop: '10px', padding: '6px 10px', background: '#ecfdf5', color: '#059669', borderRadius: '6px', fontSize: '0.8em', fontWeight: 600 }}>
            {t ? t('builder:lastPageRedirectPreview') : 'Última página →'} {lastPageLabel}
          </div>
        )}
      </div>
    </div>
  );
};

const RenderText = ({ content }) => {
  const cleanHtml = sanitizeHtml((content.html || '').replace(/&nbsp;/g, ' '));
  const style = {
    color: content.textColor || 'inherit',
    ...(content.wrapperFont && { fontSize: `${content.wrapperFont}px` }),
    ...(content.wrapperLineHeight && { lineHeight: content.wrapperLineHeight })
  };
  return <div className="fake-text" style={style} dangerouslySetInnerHTML={{ __html: cleanHtml }} />;
};

const RenderAlert = ({ content }) => {
  const theme = content.theme || 'warning';
  const iconName = content.icon || 'default';
  const iconSize = content.alertIconSize ?? 24;

  const getIcon = () => {
    // Escolhas Diretas do Usuário via Picker Dinâmico (Lucide, fa:, md:)
    const CustomIcon = getIconComponent(iconName);
    if (CustomIcon) {
      return <CustomIcon size={iconSize} style={{ flexShrink: 0 }} />;
    }

    // Fallback nativo:
    switch (theme) {
      case 'success': return <CheckCircle size={iconSize} style={{ flexShrink: 0 }} />;
      case 'danger': return <XCircle size={iconSize} style={{ flexShrink: 0 }} />;
      case 'info': return <Info size={iconSize} style={{ flexShrink: 0 }} />;
      case 'warning':
      default: return <AlertTriangle size={iconSize} style={{ flexShrink: 0 }} />;
    }
  };

  const customBg = (content.wrapperBg && content.wrapperBg !== 'transparent') ? (content.computedBgRGBA || content.wrapperBg) : undefined;
  const customColor = content.textColor || undefined;
  const cleanHtml = sanitizeHtml((content.text || '').replace(/&nbsp;/g, ' '));

  return (
    <div className={`fake-alert alert-${theme}`} style={{ backgroundColor: customBg, color: customColor, borderColor: customBg }}>
      <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'flex-start', marginTop: '2px' }}>
          {getIcon()}
        </div>
        <div
          style={{
            color: customColor,
            fontSize: content.wrapperFont ? `${content.wrapperFont}px` : undefined,
            lineHeight: content.wrapperLineHeight || 1.5,
          }}
          className="fake-text"
          dangerouslySetInnerHTML={{ __html: cleanHtml }}
        />
      </div>
    </div>
  );
};

const getDecorationHTML = (styleType, primaryStr, secondaryStr) => {
  const primary = primaryStr || '#f97316';
  const secondary = secondaryStr || '#1e293b';
  const baseSpacing = 'margin-top: 0.75rem; margin-bottom: 1.25rem;';

  switch (styleType) {
    case 'none':
      return `<div style="height: 1rem;"></div>`;
    case 'simple':
      return `<div style="width: 100%; height: 2px; background-color: ${primary}; ${baseSpacing}"></div>`;
    case 'pill':
      return `<div style="width: 50px; height: 4px; background-color: ${primary}; border-radius: 4px; ${baseSpacing}"></div>`;
    case 'gradient':
      return `<div style="width: 100%; height: 2px; background: linear-gradient(90deg, ${secondary} 0%, ${primary} 50%, transparent 100%); ${baseSpacing}"></div>`;
    case 'faded':
      return `<div style="width: 100%; height: 1px; background-color: #e2e8f0; position: relative; ${baseSpacing}"><div style="width: 60%; height: 2px; background: linear-gradient(90deg, ${primary} 0%, transparent 100%); position: absolute; top: -1px; left: 0;"></div></div>`;
    case 'dash-dot':
      return `<div style="width: 100%; border-bottom: 1px solid #e2e8f0; position: relative; ${baseSpacing}"><div style="position: absolute; top: -1px; left: 0; display: flex; gap: 4px; align-items: center;"><div style="width: 32px; height: 3px; background-color: ${secondary};"></div><div style="width: 4px; height: 3px; background-color: ${primary}; border-radius: 50%;"></div></div></div>`;
    case 'double-color':
      return `<div style="width: 100%; border-bottom: 1px solid #e2e8f0; position: relative; ${baseSpacing}"><div style="position: absolute; top: -1px; left: 0; display: flex;"><div style="width: 30px; height: 3px; background-color: ${secondary};"></div><div style="width: 30px; height: 3px; background-color: ${primary};"></div></div></div>`;
    case 'lateral':
      return `<div style="height: 1rem;"></div>`;
    case 'dotted-mixed':
      return `<div style="width: 100%; border-bottom: 2px dotted #cbd5e1; position: relative; ${baseSpacing}"><div style="width: 50px; height: 2px; background-color: ${primary}; position: absolute; top: -2px; left: 0;"></div></div>`;
    case 'classic':
    default:
      return `<div style="width: 100%; height: 1px; background-color: #e2e8f0; position: relative; ${baseSpacing}"><div style="width: 60px; height: 2px; background-color: ${primary}; position: absolute; top: -1px; left: 0;"></div></div>`;
  }
};

const hexToRgbaCard = (hex, transparencyPct) => {
  if (!hex) return 'transparent';
  hex = String(hex).replace('#', '');
  let r = 0, g = 0, b = 0;
  if (hex.length === 3) { r = parseInt(hex[0] + hex[0], 16); g = parseInt(hex[1] + hex[1], 16); b = parseInt(hex[2] + hex[2], 16); }
  else if (hex.length === 6) { r = parseInt(hex.slice(0, 2), 16); g = parseInt(hex.slice(2, 4), 16); b = parseInt(hex.slice(4, 6), 16); }
  const alpha = (100 - (transparencyPct ?? 0)) / 100;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const toCssStrCard = (obj, def) => {
  if (!obj) return def;
  return `${obj.top ?? 0}px ${obj.right ?? 0}px ${obj.bottom ?? 0}px ${obj.left ?? 0}px`;
};

const RenderStyledCard = ({ content, globalCardBg, widgetId }) => {
  const cleanHtml = sanitizeHtml((content.html || '').replace(/&nbsp;/g, ' '));
  const decStyle = content.decorationStyle || 'classic';
  const cPrim = content.lineColorPrimary;
  const cSec = content.lineColorSecondary;
  const tagColor = content.tagColor || 'var(--course-primary, #3b82f6)';
  const borderTopWidth = content.borderTopWidth ?? 4;
  // Cor do card: com transparência (cardBgAlpha) ou sólido; fallback: global, branco (sem wrapperBg para evitar duplicidade)
  const hasCardBg = content.cardBg && String(content.cardBg).trim() !== '';
  const cardBgBase = (hasCardBg ? content.cardBg : undefined) ?? globalCardBg ?? '#ffffff';
  const cardBgComputed = (content.cardBgAlpha !== undefined && content.cardBgAlpha !== null)
    ? (content.computedCardBgRGBA || hexToRgbaCard(content.cardBg || cardBgBase, content.cardBgAlpha))
    : cardBgBase;

  const titleStyle = decStyle === 'lateral'
    ? { borderLeft: `4px solid ${cPrim || '#f97316'}`, paddingLeft: '12px', marginBottom: '0', borderBottom: 'none', paddingBottom: '0', marginTop: '0' }
    : { borderBottom: 'none', marginBottom: '0', paddingBottom: '0', marginTop: '0' };

  const decoHTML = getDecorationHTML(decStyle, cPrim, cSec);

  const borderTopStyle = borderTopWidth > 0 ? { borderTop: `${borderTopWidth}px solid ${tagColor}` } : { borderTop: 'none' };
  const cardPadding = content.paddingObj ? toCssStrCard(content.paddingObj, '1rem') : '1rem';
  const cardInner = (
    <div className="madri-card" style={{ background: cardBgComputed, ...borderTopStyle, padding: cardPadding }}>
      {content.tag && <span className="tag" style={{ display: 'block', color: tagColor, fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.8rem', marginBottom: '0.5rem' }}>{content.tag}</span>}
      {content.title && <h3 style={titleStyle}>{content.title}</h3>}
      {content.title && <div dangerouslySetInnerHTML={{ __html: decoHTML }} />}
      <div className="fake-text" dangerouslySetInnerHTML={{ __html: cleanHtml }} />
    </div>
  );

  if (!isIncludedInToc(content) || !widgetId) return cardInner;

  const anchorId = resolveTocAnchorId(content, widgetId);
  const smRaw = parseInt(content.scrollMarginTop, 10);
  const scrollMargin = Math.min(240, Math.max(0, Number.isFinite(smRaw) ? smRaw : 72));

  return (
    <div
      id={anchorId}
      className="madri-gc-toc-anchor"
      style={{ scrollMarginTop: scrollMargin, width: '100%', boxSizing: 'border-box' }}
    >
      {cardInner}
    </div>
  );
};

const RenderSectionHeader = ({ content, globalConfig, widgetId }) => {
  const c = content || {};
  const barEnabled = isSectionHeaderLeftBarEnabled(c);
  const barColor = String(c.headerLeftBarColor || '').trim();
  const lh = c.wrapperLineHeight;
  const titleTag = resolveSectionHeaderTitleTag(c);
  const titlePx = resolveSectionHeaderTitleFontPx(c, globalConfig);
  const subtitleFs = resolveSectionHeaderSubtitleFontSizeCss(c, globalConfig);

  const headerBg = resolveSectionHeaderBackground(c);
  const textAlign = resolveSectionHeaderTextAlign(c);
  const headerStyle = {
    textAlign,
    ...(!barEnabled
      ? { borderLeft: 'none' }
      : (barColor ? { borderLeft: `5px solid ${barColor}` } : {})),
    ...(headerBg ? { backgroundColor: headerBg } : {}),
  };
  const typo = resolveSectionHeaderTypography(c);
  const partColors = resolveSectionHeaderColors(c);

  const titleStyle = {
    marginTop: 0,
    textAlign,
    fontWeight: typo.title.fontWeight,
    fontStyle: typo.title.fontStyle,
    ...(partColors.title ? { color: partColors.title } : { color: 'inherit' }),
    ...(titlePx != null ? { fontSize: `${titlePx}px` } : {}),
    ...(lh ? { lineHeight: lh } : {}),
  };

  const subtitleStyle = {
    marginTop: typo.gap,
    marginBottom: 0,
    textAlign,
    fontSize: subtitleFs,
    fontWeight: typo.subtitle.fontWeight,
    fontStyle: typo.subtitle.fontStyle,
    ...(partColors.subtitle
      ? { color: partColors.subtitle, opacity: partColors.subtitleExplicit ? 1 : 0.88 }
      : { color: 'inherit', opacity: 0.88 }),
    ...(lh ? { lineHeight: lh } : {}),
  };

  const tagStyle = {
    textAlign,
    fontWeight: typo.tag.fontWeight,
    fontStyle: typo.tag.fontStyle,
    textTransform: 'uppercase',
    fontSize: '0.85rem',
    marginBottom: '0.5rem',
    display: 'block',
    ...(partColors.tag ? { color: partColors.tag } : {}),
  };

  const titleEl = React.createElement(
    titleTag,
    { className: SECTION_HEADER_TITLE_CLASS, style: titleStyle },
    c.title != null && c.title !== '' ? c.title : '\u00a0'
  );

  const headerInner = (
    <div className="madri-header" style={headerStyle}>
      {c.tag ? <span className="tag" style={tagStyle}>{c.tag}</span> : null}
      {titleEl}
      {c.subtitle ? <p className="subtitle madri-gc-section-header__sub" style={subtitleStyle}>{c.subtitle}</p> : null}
    </div>
  );

  if (!isIncludedInToc(c) || !widgetId) return headerInner;

  const anchorId = resolveTocAnchorId(c, widgetId);
  const smRaw = parseInt(c.scrollMarginTop, 10);
  const scrollMargin = Math.min(240, Math.max(0, Number.isFinite(smRaw) ? smRaw : 72));

  return (
    <div
      id={anchorId}
      className="madri-gc-toc-anchor"
      style={{ scrollMarginTop: scrollMargin, width: '100%', boxSizing: 'border-box' }}
    >
      {headerInner}
    </div>
  );
};

const RenderTableOfContents = ({ content, availableEntries, emptyMessage }) => {
  const html = useMemo(
    () => buildTocInnerHtml(content, availableEntries, { emptyMessage }),
    [content, availableEntries, emptyMessage],
  );
  return <div className="madri-toc-root" dangerouslySetInnerHTML={{ __html: html }} />;
};

const RenderTitleBasic = ({ content, widgetId }) => {
  const c = content || {};
  const anchorId = sanitizeTitleBasicAnchorId(c.anchorId, widgetId);
  const lvl = String(c.headingLevel || 'h2').toLowerCase();
  const level = lvl === 'h1' || lvl === 'h3' ? lvl : 'h2';
  const align = ['left', 'center', 'right'].includes(c.textAlign) ? c.textAlign : 'left';
  const ta = align === 'center' ? 'center' : align === 'right' ? 'right' : 'left';
  const fw = Math.min(900, Math.max(100, parseInt(c.titleFontWeight, 10) || 700));
  const titleSize = resolveTitleBasicTitleFontPx(c);
  const subtitleSize = resolveTitleBasicSubtitleFontPx(c, titleSize);
  const lhTitle = resolveTitleBasicLineHeight(c, 1.2);
  const lhSub = resolveTitleBasicLineHeight(c, 1.45);
  const smRaw = parseInt(c.scrollMarginTop, 10);
  const scrollMargin = Math.min(240, Math.max(0, Number.isFinite(smRaw) ? smRaw : 72));
  const titleEl = React.createElement(
    level,
    {
      className: 'madri-gc-title-basic-h',
      style: {
        margin: 0,
        color: resolveTitleBasicTitleColor(c),
        fontSize: titleSize,
        fontWeight: fw,
        lineHeight: lhTitle,
        textAlign: ta,
      },
    },
    c.title ? c.title : '\u00a0'
  );
  return (
    <div
      id={anchorId}
      className="madri-gc-title-basic"
      style={{ scrollMarginTop: scrollMargin, textAlign: ta, width: '100%', boxSizing: 'border-box' }}
    >
      {titleEl}
      {c.subtitle ? (
        <p
          className="madri-gc-title-basic-sub"
          style={{
            margin: '0.35rem 0 0 0',
            color: resolveTitleBasicSubtitleColor(c),
            fontSize: subtitleSize,
            lineHeight: lhSub,
            textAlign: ta,
          }}
        >
          {c.subtitle}
        </p>
      ) : null}
    </div>
  );
};

const RenderMadriChart = ({ content, widgetId }) => {
  const elRef = useRef(null);
  const chartRef = useRef(null);
  const resizeObsRef = useRef(null);
  const intersectObsRef = useRef(null);
  const c = content || {};
  const h = Math.min(800, Math.max(200, parseInt(c.height, 10) || 360));

  useEffect(() => {
    let cancelled = false;
    let onResize;
    loadMadriEcharts().then((echarts) => {
      if (cancelled || !elRef.current) return;
      const el = elRef.current;
      const inst = echarts.init(el);
      chartRef.current = inst;
      inst.setOption(buildMadriChartOption(c), true);
      const doResize = () => {
        try {
          inst.resize();
        } catch (_) { /* ignore */ }
      };
      onResize = doResize;
      window.addEventListener('resize', onResize);
      requestAnimationFrame(doResize);
      setTimeout(doResize, 50);
      if (typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(() => doResize());
        ro.observe(el);
        resizeObsRef.current = ro;
      }
      if (typeof IntersectionObserver !== 'undefined') {
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) doResize();
            });
          },
          { root: null, threshold: 0.02 }
        );
        io.observe(el);
        intersectObsRef.current = io;
      }
    });
    return () => {
      cancelled = true;
      if (onResize) window.removeEventListener('resize', onResize);
      resizeObsRef.current?.disconnect();
      resizeObsRef.current = null;
      intersectObsRef.current?.disconnect();
      intersectObsRef.current = null;
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, [widgetId]);

  useEffect(() => {
    const inst = chartRef.current;
    if (inst) {
      inst.setOption(buildMadriChartOption(content), true);
      requestAnimationFrame(() => {
        try {
          inst.resize();
        } catch (_) { /* ignore */ }
      });
    }
  }, [content]);

  return (
    <div
      ref={elRef}
      className="madri-gc-chart-host"
      style={{ width: '100%', maxWidth: '100%', height: h, minHeight: 200, boxSizing: 'border-box' }}
    />
  );
};

const RenderMermaidFlowchart = ({ content, widgetId }) => {
  const innerRef = useRef(null);
  const c = content || {};
  const code = sanitizeMermaidSource(c.mermaidSource);
  const isLr = isMermaidFlowchartLR(c.mermaidSource);
  const align = c.align === 'right' ? 'right' : c.align === 'left' ? 'left' : 'center';
  const mermaidInit = useMemo(
    () => buildFlowchartMermaidInitConfig(c),
    [
      c.theme,
      c.mermaidSource,
      c.flowDiagramFontPx,
      c.textColor,
      c.flowNodeTextColor,
      c.flowNodeBkg,
      c.flowNodeBorder,
      c.flowEdgeColor,
      c.flowCanvasBg,
      c.flowchartNodePadding,
      c.flowchartRankSpacing,
      c.flowchartNodeSpacing,
    ],
  );
  const diagramCanvasCss = useMemo(() => {
    const hex = sanitizeFlowchartHex(c.flowCanvasBg);
    if (hex) return hex;
    if (c.computedBgRGBA && c.computedBgRGBA !== 'transparent') return c.computedBgRGBA;
    if (c.wrapperBg && c.wrapperBg !== 'transparent') return c.wrapperBg;
    return null;
  }, [c.flowCanvasBg, c.computedBgRGBA, c.wrapperBg]);

  useEffect(() => {
    let cancelled = false;
    if (innerRef.current) innerRef.current.innerHTML = '';
    loadMadriMermaid()
      .then((mermaid) => {
        if (cancelled || !innerRef.current) return;
        try {
          mermaid.initialize(mermaidInit);
        } catch (_) { /* ignore */ }
        const rid = `madri_fc_${String(widgetId).replace(/[^a-zA-Z0-9_-]/g, '_')}_${Date.now()}`;
        return mermaid.render(rid, code);
      })
      .then((result) => {
        if (cancelled || !innerRef.current) return;
        if (result && result.svg) innerRef.current.innerHTML = result.svg;
      })
      .catch(() => {
        if (cancelled || !innerRef.current) return;
        innerRef.current.innerHTML = '<p style="color:#b91c1c;padding:8px;font-size:13px">Fluxograma inválido ou biblioteca indisponível.</p>';
      });
    return () => {
      cancelled = true;
    };
  }, [widgetId, code, mermaidInit]);

  return (
    <div
      className={`madri-gc-flowchart-host${isLr ? ' madri-gc-flowchart-host--lr' : ''}`}
      style={{
        width: '100%',
        maxWidth: '100%',
        overflowX: 'auto',
        boxSizing: 'border-box',
        textAlign: align,
        ...(diagramCanvasCss ? { background: diagramCanvasCss } : {}),
      }}
    >
      <div
        ref={innerRef}
        className="madri-gc-flowchart-inner"
        style={{
          display: 'inline-block',
          textAlign: 'left',
          maxWidth: isLr ? 'none' : '100%',
        }}
      />
    </div>
  );
};

const RenderHtmlTemplate = ({ content }) => {
  const clean = prepareHtmlTemplateForEmbed(content.html || '');
  if (!clean) return null;
  return (
    <div
      className="geppetto-html-template-embed"
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
};

const RenderListIcon = ({ content }) => {
  const IconCmp = getIconComponent(content.icon) || LucideIcons.CheckCircle;
  const isRow = content.display === 'HORIZONTAL' || content.display === 'TWO COLUMNS';
  const deskAlign = content.align || 'NENHUM';
  const sAlignItems = listIconAlignToFlex(deskAlign);
  const sTextAlign = listIconAlignToTextAlign(deskAlign);
  const sJustify = isRow ? listIconRowJustify(deskAlign) : 'flex-start';
  const mobAlignCls = listIconMobileAlignClass(content);

  return (
    <div
      className={`madri-list-icon-root${isRow ? ' madri-list-icon-root--row' : ''} ${mobAlignCls}`.trim()}
      style={{
        display: 'flex',
        flexDirection: isRow ? 'row' : 'column',
        justifyContent: sJustify,
        alignItems: isRow ? 'center' : sAlignItems,
        gap: `${content.spaceBetween ?? 0.45}rem`,
        textAlign: isRow ? (deskAlign === 'RIGHT' ? 'right' : deskAlign === 'CENTER' ? 'center' : 'left') : sTextAlign,
        maxWidth: '100%',
        minWidth: 0,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', alignSelf: isRow ? 'center' : 'auto' }}>
        <IconCmp size={`${content.iconSize ?? 2.65}rem`} color={content.iconColor || '#3b82f6'} />
      </div>
      <div
        className="madri-list-icon-body fake-text"
        style={{
          flex: 1,
          minWidth: 0,
          maxWidth: '100%',
          color: content.textColor || 'inherit',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          fontSize: content.wrapperFont ? `${content.wrapperFont}px` : undefined,
          lineHeight: content.wrapperLineHeight || undefined,
        }}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(content.html || '<p>Seu texto aqui...</p>') }}
      />
    </div>
  );
};

// ===========================================================
// NOVOS WIDGETS: TABS, ACCORDION, MODAL, CAROUSEL, IMAGE-CARD, POST-IT
// ===========================================================

const RenderTabs = ({ content, t }) => {
  const [activeTab, setActiveTab] = React.useState(0);
  const tabs = content.tabs || (t ? [{ label: t('builder:defaultTab1'), body: `<p>${t('builder:defaultTab1Body')}</p>` }, { label: t('builder:defaultTab2'), body: `<p>${t('builder:defaultTab2Body')}</p>` }] : [{ label: 'Tab 1', body: '<p>Tab 1 content</p>' }, { label: 'Tab 2', body: '<p>Tab 2 content</p>' }]);
  const accentColor = content.accentColor || '#3b82f6';
  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
      <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', background: '#f8fafc' }}>
        {tabs.map((t, i) => (
          <button key={i} onClick={() => setActiveTab(i)} style={{ padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', fontWeight: activeTab === i ? '700' : '400', color: activeTab === i ? accentColor : '#64748b', borderBottom: activeTab === i ? `2px solid ${accentColor}` : '2px solid transparent', marginBottom: '-2px', transition: 'all 0.2s' }}>{t.label}</button>
        ))}
      </div>
      <div style={{ padding: '1rem' }} className="fake-text" dangerouslySetInnerHTML={{ __html: sanitizeHtml(tabs[activeTab]?.body || '') }} />
    </div>
  );
};

const RenderAccordion = ({ content, t }) => {
  const [open, setOpen] = React.useState(null);
  const items = content.items || (t ? [{ question: t('builder:defaultAccordionQ1'), answer: t('builder:defaultAccordionA1') }, { question: t('builder:defaultAccordionQ2'), answer: t('builder:defaultAccordionA2') }] : [{ question: 'Question 1', answer: 'Answer 1.' }, { question: 'Question 2', answer: 'Answer 2.' }]);
  const accentColor = content.accentColor || '#3b82f6';
  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
      {items.map((item, i) => (
        <div key={i} style={{ borderBottom: i < items.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
          <button onClick={() => setOpen(open === i ? null : i)} style={{ width: '100%', textAlign: 'left', padding: '14px 16px', background: open === i ? '#f0f7ff' : 'white', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: '600', color: open === i ? accentColor : '#1e293b', transition: 'all 0.2s' }}>
            <span>{item.question}</span>
            <LucideIcons.ChevronDown size={18} style={{ transform: open === i ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.3s', color: accentColor }} />
          </button>
          {open === i && <div className="fake-text" style={{ padding: '0 16px 14px', color: '#475569', lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.answer || '') }} />}
        </div>
      ))}
    </div>
  );
};

const BUILDER_WIDGET_MODAL_Z = 99990;

const RenderModalTrigger = ({ content, t }) => {
  const [open, setOpen] = React.useState(false);
  const btnBg = content.btnBg || '#3b82f6';
  const btnText = content.btnText || '#ffffff';
  const btnStyle = content.btnStyle || 'button'; // button | outline | link
  const IconCmp = getIconComponent(content.icon);
  const defaultTrigger = t ? t('builder:defaultModalTrigger') : 'Open Modal';
  const defaultBody = t ? t('builder:defaultModalBody') : 'Modal content...';
  const panelW = Math.min(1200, Math.max(280, parseInt(content.modalPanelWidthPx, 10) || 800));
  const panelMaxH = Math.min(900, Math.max(200, parseInt(content.modalPanelMaxHeightPx, 10) || 600));
  let triggerStyle = { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 22px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', border: 'none', transition: 'all 0.2s' };
  if (btnStyle === 'button') { triggerStyle = { ...triggerStyle, background: btnBg, color: btnText }; }
  else if (btnStyle === 'outline') { triggerStyle = { ...triggerStyle, background: 'transparent', color: btnBg, border: `2px solid ${btnBg}` }; }
  else if (btnStyle === 'link') { triggerStyle = { ...triggerStyle, background: 'transparent', color: btnBg, padding: '0', fontWeight: '600', textDecoration: 'underline' }; }
  return (
    <div style={{ textAlign: content.align || 'left' }}>
      <button style={triggerStyle} onClick={() => setOpen(true)}>
        {IconCmp && <IconCmp size={16} />}{content.triggerLabel || defaultTrigger}
      </button>
      {open && createPortal(
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: BUILDER_WIDGET_MODAL_Z, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }} onClick={() => setOpen(false)}>
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '2rem',
              width: `min(${panelW}px, calc(100vw - 32px))`,
              maxWidth: '100%',
              maxHeight: `min(${panelMaxH}px, 90vh)`,
              boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxSizing: 'border-box',
            }}
            onClick={e => e.stopPropagation()}
          >
            <button onClick={() => setOpen(false)} style={{ position: 'absolute', top: '12px', right: '14px', background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#94a3b8', zIndex: 1 }}>✕</button>
            {content.modalTitle && <h2 style={{ marginTop: 0, marginBottom: '1rem', color: '#1e293b', flexShrink: 0, paddingRight: '36px' }}>{content.modalTitle}</h2>}
            <div className="fake-text" style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(content.modalBody || `<p>${defaultBody}</p>`) }} />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const RenderReinforcementQuiz = ({ content, t, widgetId }) => {
  const [open, setOpen] = React.useState(false);
  const [qIndex, setQIndex] = React.useState(0);
  const [answers, setAnswers] = React.useState({});
  const [finished, setFinished] = React.useState(false);
  const [completedAttempts, setCompletedAttempts] = React.useState(0);
  const [bestScore, setBestScore] = React.useState(0);
  const feedbackManifest = React.useMemo(() => buildMicroquizFeedbackManifest(content), [content]);
  const btnBg = content.btnBg || '#3b82f6';
  const btnText = content.btnText || '#ffffff';
  const btnStyle = content.btnStyle || 'button';
  const questions = (content.questions || []).slice(0, MICROQUIZ_MAX_QUESTIONS);
  const defaultTrigger = t ? t('builder:microquizDefaultTrigger') : 'Teste seu conhecimento';
  const defaultTitle = t ? t('builder:microquizDefaultModalTitle') : 'Quiz de reforço';
  const trueLabel = t ? t('builder:microquizTrue') : 'Verdadeiro';
  const falseLabel = t ? t('builder:microquizFalse') : 'Falso';
  const panelW = Math.min(720, Math.max(320, parseInt(content.modalPanelWidthPx, 10) || 560));
  const panelMaxH = Math.min(720, Math.max(240, parseInt(content.modalPanelMaxHeightPx, 10) || 520));
  const passingScore = getMicroquizPassingScore(feedbackManifest);
  const currentAttemptNum = completedAttempts + 1;

  let triggerStyle = { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 22px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', border: 'none', transition: 'all 0.2s' };
  if (btnStyle === 'button') triggerStyle = { ...triggerStyle, background: btnBg, color: btnText };
  else if (btnStyle === 'outline') triggerStyle = { ...triggerStyle, background: 'transparent', color: btnBg, border: `2px solid ${btnBg}` };
  else if (btnStyle === 'link') triggerStyle = { ...triggerStyle, background: 'transparent', color: btnBg, padding: '0', fontWeight: '600', textDecoration: 'underline' };

  const resetQuiz = () => {
    setQIndex(0);
    setAnswers({});
    setFinished(false);
  };

  const openQuiz = () => {
    resetQuiz();
    setOpen(true);
  };

  const currentQ = questions[qIndex];
  const options = currentQ?.type === 'truefalse'
    ? [trueLabel, falseLabel]
    : (currentQ?.options || []);

  const scorePct = React.useMemo(() => {
    if (!finished || !questions.length) return 0;
    let correct = 0;
    questions.forEach((q, i) => {
      if ((answers[i] ?? -1) === (q.correctIndex ?? 0)) correct += 1;
    });
    return Math.round((correct / questions.length) * 100);
  }, [finished, questions, answers]);

  const showDetailedDuringAttempt = shouldShowMicroquizDetailedFeedback(feedbackManifest, currentAttemptNum, null);
  const showDetailedResults = finished && shouldShowMicroquizDetailedFeedback(feedbackManifest, currentAttemptNum, scorePct);

  const computeScorePct = React.useCallback((ans) => {
    if (!questions.length) return 0;
    let correct = 0;
    questions.forEach((q, i) => {
      if ((ans[i] ?? -1) === (q.correctIndex ?? 0)) correct += 1;
    });
    return Math.round((correct / questions.length) * 100);
  }, [questions]);

  const handlePick = (picked) => {
    if (answers[qIndex] !== undefined || finished) return;
    setAnswers((prev) => ({ ...prev, [qIndex]: picked }));
  };

  const goNext = () => {
    if (qIndex >= questions.length - 1) {
      const pct = computeScorePct(answers);
      setBestScore((prev) => Math.max(prev, pct));
      setCompletedAttempts((prev) => prev + 1);
      setFinished(true);
      return;
    }
    setQIndex((i) => i + 1);
  };

  const feedbackHiddenHint = (() => {
    const policy = feedbackManifest.attemptPolicy || 'best_unlimited';
    if (policy === 'best_limited' || policy === 'single') {
      return t ? t('builder:microquizFeedbackHiddenHintLimited') : 'O gabarito detalhado será exibido na última tentativa.';
    }
    const n = Math.max(0, parseInt(feedbackManifest.showFeedbackFromAttempt, 10) || 0);
    if (n <= 0) {
      return t ? t('builder:microquizFeedbackHiddenHintUnlimitedOff') : 'O gabarito não será exibido durante as tentativas. Defina a partir de qual tentativa liberar ou use a nota mínima.';
    }
    return t ? t('builder:microquizFeedbackHiddenHintUnlimited', { n }) : `O gabarito detalhado será exibido a partir da ${n}ª tentativa.`;
  })();

  return (
    <div style={{ textAlign: content.align || 'left' }}>
      <button type="button" style={triggerStyle} onClick={openQuiz}>
        {content.triggerLabel || defaultTrigger}
      </button>
      {content.graded && (parseInt(content.weight, 10) || 0) > 0 && (
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
          {t ? t('builder:microquizWeight') : 'Peso'}: {content.weight}%
        </div>
      )}
      {open && createPortal(
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: BUILDER_WIDGET_MODAL_Z, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }} onClick={() => setOpen(false)}>
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '1.5rem',
              width: `min(${panelW}px, calc(100vw - 32px))`,
              maxWidth: '100%',
              maxHeight: `min(${panelMaxH}px, 90vh)`,
              boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxSizing: 'border-box',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" onClick={() => setOpen(false)} style={{ position: 'absolute', top: '12px', right: '14px', background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#94a3b8', zIndex: 1 }}>✕</button>
            <h2 style={{ marginTop: 0, marginBottom: '1rem', color: '#1e293b', flexShrink: 0, paddingRight: '36px', fontSize: '1.25rem' }}>
              {content.modalTitle || defaultTitle}
            </h2>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {!finished && currentQ && (
                <>
                  <p style={{ fontWeight: 600, color: '#1e293b', margin: '0 0 12px', lineHeight: 1.45 }}>{currentQ.prompt}</p>
                  {options.map((opt, oi) => {
                    const picked = answers[qIndex];
                    const isCorrectOption = oi === (currentQ.correctIndex ?? 0);
                    const pickedCorrect = picked === (currentQ.correctIndex ?? 0);
                    const showGabarito = picked !== undefined && showDetailedDuringAttempt;
                    let optStyle = { display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px', marginBottom: 8, border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: picked === undefined ? 'pointer' : 'default', fontSize: 15, color: '#1e293b' };
                    if (showGabarito && pickedCorrect && picked === oi) {
                      optStyle = { ...optStyle, borderColor: '#16a34a', background: '#f0fdf4' };
                    } else if (showGabarito && !pickedCorrect && picked === oi) {
                      optStyle = { ...optStyle, borderColor: '#dc2626', background: '#fef2f2' };
                    } else if (showGabarito && !pickedCorrect && isCorrectOption) {
                      optStyle = { ...optStyle, borderColor: '#16a34a', background: '#f0fdf4' };
                    } else if (!showGabarito && picked !== undefined && picked === oi && !pickedCorrect) {
                      optStyle = { ...optStyle, borderColor: '#dc2626', background: '#fef2f2' };
                    } else if (!showGabarito && picked !== undefined && picked === oi && pickedCorrect) {
                      optStyle = { ...optStyle, borderColor: '#16a34a', background: '#f0fdf4' };
                    }
                    return (
                      <button key={`${widgetId}_q${qIndex}_o${oi}`} type="button" style={optStyle} disabled={picked !== undefined} onClick={() => handlePick(oi)}>
                        {opt}
                      </button>
                    );
                  })}
                  {answers[qIndex] !== undefined && (
                    <div style={{ marginTop: 12, padding: 12, borderRadius: 8, fontSize: 14, lineHeight: 1.5, background: showDetailedDuringAttempt ? (answers[qIndex] === (currentQ.correctIndex ?? 0) ? '#f0fdf4' : '#fef2f2') : '#f8fafc', color: showDetailedDuringAttempt ? (answers[qIndex] === (currentQ.correctIndex ?? 0) ? '#166534' : '#991b1b') : '#475569' }}>
                      {showDetailedDuringAttempt
                        ? (currentQ.explanation || (answers[qIndex] === (currentQ.correctIndex ?? 0) ? 'Correto!' : 'Resposta incorreta.'))
                        : (t ? t('builder:microquizAnswerRecorded') : 'Resposta registrada.')}
                    </div>
                  )}
                  {answers[qIndex] !== undefined && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                      <button type="button" onClick={goNext} style={{ padding: '8px 16px', borderRadius: 6, fontWeight: 600, fontSize: 14, cursor: 'pointer', border: 'none', background: '#3b82f6', color: '#fff' }}>
                        {qIndex >= questions.length - 1 ? 'Ver resultado' : 'Próxima'}
                      </button>
                    </div>
                  )}
                </>
              )}
              {finished && (
                <div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e293b', margin: '8px 0' }}>
                    {Object.keys(answers).filter((k) => answers[k] === (questions[k]?.correctIndex ?? 0)).length} / {questions.length} ({scorePct}%)
                  </div>
                  <p style={{ color: '#475569', margin: '0 0 12px', lineHeight: 1.5 }}>
                    {scorePct >= passingScore ? 'Ótimo trabalho! Continue o módulo.' : 'Revise o conteúdo e tente novamente quando quiser.'}
                  </p>
                  {!showDetailedResults && (
                    <>
                      <p style={{ color: '#64748b', margin: '0 0 8px', lineHeight: 1.5 }}>
                        {t ? t('builder:microquizBestAttempt') : 'Sua melhor tentativa'}: <strong>{Math.max(bestScore, scorePct)}%</strong>
                      </p>
                      <p style={{ fontSize: 13, color: '#94a3b8', margin: 0, lineHeight: 1.5 }}>{feedbackHiddenHint}</p>
                    </>
                  )}
                  {showDetailedResults && questions.map((q, qi) => {
                    const ok = (answers[qi] ?? -1) === (q.correctIndex ?? 0);
                    return (
                      <div key={`${widgetId}_fb_${qi}`} style={{ marginTop: 8, padding: 12, borderRadius: 8, fontSize: 14, lineHeight: 1.5, background: ok ? '#f0fdf4' : '#fef2f2', color: ok ? '#166534' : '#991b1b' }}>
                        <strong>{q.prompt}</strong>
                        {q.explanation ? <div style={{ marginTop: 4 }}>{q.explanation}</div> : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const RenderCarousel = ({ content }) => {
  const [idx, setIdx] = React.useState(0);
  const slides = content.slides || [{ url: '', caption: 'Slide 1' }, { url: '', caption: 'Slide 2' }];
  const accentColor = content.accentColor || '#3b82f6';
  const prev = () => setIdx(i => (i - 1 + slides.length) % slides.length);
  const next = () => setIdx(i => (i + 1) % slides.length);
  const slide = slides[idx] || {};
  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden', position: 'relative', background: '#f8fafc' }}>
      <div style={{ width: '100%', minHeight: '200px', background: slide.url ? 'none' : '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {slide.url ? <img src={slide.url} alt={slide.caption || ''} style={{ width: '100%', maxHeight: '320px', objectFit: 'cover' }} /> : <LucideIcons.Image size={48} color="#94a3b8" />}
      </div>
      {slide.caption && <div style={{ padding: '10px 16px', textAlign: 'center', color: '#475569', fontStyle: 'italic', fontSize: '14px' }}>{slide.caption}</div>}
      {slides.length > 1 && (
        <>
          <button onClick={prev} style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', background: accentColor, color: '#fff', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><LucideIcons.ChevronLeft size={18} /></button>
          <button onClick={next} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: accentColor, color: '#fff', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><LucideIcons.ChevronRight size={18} /></button>
          <div style={{ position: 'absolute', bottom: '8px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '6px' }}>
            {slides.map((_, i) => <div key={i} onClick={() => setIdx(i)} style={{ width: '8px', height: '8px', borderRadius: '50%', background: i === idx ? accentColor : '#cbd5e1', cursor: 'pointer' }} />)}
          </div>
        </>
      )}
    </div>
  );
};

const RenderImageCard = ({ content, t }) => {
  const btnBg = content.btnBg || '#3b82f6';
  const defaultBtn = t ? t('builder:defaultLearnMore') : 'Learn More';
  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', background: content.cardBg || '#ffffff', maxWidth: content.maxWidth || '100%' }}>
      {content.imageUrl ? <img src={content.imageUrl} alt={content.imageAlt || ''} style={{ width: '100%', height: `${content.imageHeight || 200}px`, objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '180px', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><LucideIcons.Image size={40} color="#94a3b8" /></div>}
      <div style={{ padding: '1.2rem' }}>
        {content.cardTag && <span style={{ fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', color: btnBg, letterSpacing: '1px' }}>{content.cardTag}</span>}
        {content.cardTitle && <h3 style={{ marginTop: '0.4rem', marginBottom: '0.6rem', color: content.titleColor || '#1e293b', fontSize: '1.1rem' }}>{content.cardTitle}</h3>}
        {content.cardText && <p style={{ color: content.textColor || '#64748b', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: content.showBtn ? '1rem' : 0 }}>{content.cardText}</p>}
        {content.showBtn && <a href={content.btnLink || '#'} onClick={e => e.preventDefault()} style={{ display: 'inline-block', padding: '8px 18px', background: btnBg, color: content.btnTextColor || '#fff', borderRadius: '6px', textDecoration: 'none', fontSize: '14px', fontWeight: '600' }}>{content.btnLabel || defaultBtn}</a>}
      </div>
    </div>
  );
};

/** SVG mockup de avatar (canvas); no Moodle, tokens [[GEPPETTO_*]] são resolvidos pelo plugin. */
const SOCIAL_CARD_MOCKUP_DATA_URI =
  'data:image/svg+xml;charset=utf-8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96"><circle cx="48" cy="48" r="48" fill="#e2e8f0"/><circle cx="48" cy="36" r="16" fill="#94a3b8"/><ellipse cx="48" cy="82" rx="28" ry="22" fill="#94a3b8"/></svg>'
  );

const RenderSocialCard = ({ content, t }) => {
  const btnBg = content.btnBg || '#2563eb';
  const btnOutline = (content.btnStyle || 'outline') === 'outline';
  const defaultBtn = t ? t('builder:defaultSocialCardBtn') : 'View profile';
  const avSize = Math.min(120, Math.max(56, parseInt(content.avatarSize, 10) || 88));
  const mode = content.avatarMode || 'mockup';
  const mockSrc = (content.mockupAvatarUrl || '').trim() || SOCIAL_CARD_MOCKUP_DATA_URI;
  const bannerH = content.bannerHeight || 160;
  const cardBg = content.cardBg || '#ffffff';
  const tagColor = content.tagColor || btnBg;
  const titleColor = content.titleColor || '#0f172a';
  const subtitleColor = content.subtitleColor || '#64748b';
  const textColor = content.textColor || '#64748b';
  const iconsColor = content.socialIconsColor || '#334155';
  const tagFontPx = Math.min(22, Math.max(10, parseInt(content.tagFontPx, 10) || 11));
  const titleFontPx = Math.min(34, Math.max(12, parseInt(content.titleFontPx, 10) || 18));
  const subtitleFontPx = Math.min(26, Math.max(10, parseInt(content.subtitleFontPx, 10) || 14));
  const textFontPx = Math.min(22, Math.max(10, parseInt(content.textFontPx, 10) || 13));
  const iconsSizePx = Math.min(32, Math.max(12, parseInt(content.socialIconsSizePx, 10) || 18));
  const bannerZoomPct = Math.min(200, Math.max(50, parseInt(content.bannerZoomPct, 10) || 100));
  const bannerPosX = Math.min(50, Math.max(-50, parseInt(content.bannerPosX, 10) || 0));
  const bannerPosY = Math.min(50, Math.max(-50, parseInt(content.bannerPosY, 10) || 0));

  let avatarInner;
  const previewMoodle = (key, fb) => (
    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e0e7ef', color: '#64748b', fontSize: '11px', textAlign: 'center', padding: '6px', lineHeight: 1.3 }}>
      {t ? t(key) : fb}
    </div>
  );
  if (mode === 'moodle_viewer_native' || mode === 'moodle_self') {
    avatarInner = previewMoodle('builder:socialCardAvatarPreviewViewerNative', 'Moodle: você');
  } else if (mode === 'moodle_teacher_native') {
    avatarInner = previewMoodle('builder:socialCardAvatarPreviewTeacherNative', 'Moodle: professor');
  } else if (mode === 'moodle_user_id') {
    const uid = Math.max(0, parseInt(String(content.avatarMoodleUserId ?? '').trim(), 10) || 0);
    avatarInner = (
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e0e7ef', color: '#64748b', fontSize: '11px', textAlign: 'center', padding: '6px', lineHeight: 1.3 }}>
        {t ? t('builder:socialCardAvatarPreviewUserId', { id: uid > 0 ? String(uid) : '—' }) : `Moodle: ID ${uid > 0 ? uid : '—'}`}
      </div>
    );
  } else if (mode === 'moodle_custom') {
    avatarInner = previewMoodle('builder:socialCardAvatarPreviewFiltercodes', 'FilterCodes');
  } else {
    avatarInner = <img src={mockSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />;
  }

  const btnStyle = btnOutline
    ? { display: 'inline-block', padding: '10px 24px', background: 'transparent', color: btnBg, border: `2px solid ${btnBg}`, borderRadius: '9999px', textDecoration: 'none', fontSize: '14px', fontWeight: '600' }
    : { display: 'inline-block', padding: '10px 24px', background: btnBg, color: content.btnTextColor || '#ffffff', border: 'none', borderRadius: '9999px', textDecoration: 'none', fontSize: '14px', fontWeight: '600' };

  const icons = (content.socialIconsEnabled ? (content.socialIcons || []) : [])
    .filter((it) => (it?.url || '').trim())
    .slice(0, 5);

  const SocialIconsRow = icons.length ? (
    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', margin: '10px 0 12px 0' }}>
      {icons.map((it, i) => {
        const IconCmp = getIconComponent(it.icon) || LucideIcons.Link;
        return (
          <a
            key={i}
            href={it.url}
            target={it.newTab === false ? '_self' : '_blank'}
            rel="noopener noreferrer"
            onClick={(e) => e.preventDefault()}
            title={it.url}
            style={{
              width: 34,
              height: 34,
              borderRadius: 999,
              border: '1px solid #e2e8f0',
              background: '#ffffff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 1px 4px rgba(15,23,42,0.08)',
              textDecoration: 'none',
            }}
          >
            <IconCmp size={iconsSizePx} color={iconsColor} />
          </a>
        );
      })}
    </div>
  ) : null;

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: '0 2px 10px rgba(15,23,42,0.08)', background: cardBg, maxWidth: content.maxWidth || '100%', overflow: 'visible' }}>
      <div style={{ borderRadius: '12px 12px 0 0', overflow: 'hidden', lineHeight: 0 }}>
        {content.bannerUrl ? (
          <img
            src={content.bannerUrl}
            alt=""
            style={{
              width: '100%',
              height: `${bannerH}px`,
              objectFit: 'cover',
              display: 'block',
              objectPosition: `calc(50% + ${bannerPosX}%) calc(50% + ${bannerPosY}%)`,
              transform: `scale(${bannerZoomPct / 100})`,
              transformOrigin: `calc(50% + ${bannerPosX}%) calc(50% + ${bannerPosY}%)`,
            }}
          />
        ) : (
          <div style={{ width: '100%', height: `${bannerH}px`, background: 'linear-gradient(135deg,#dc2626 0%,#b91c1c 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.9)', fontSize: '14px', fontWeight: 600 }}>
            {t ? t('builder:socialCardBannerPlaceholder') : 'Banner'}
          </div>
        )}
      </div>
      <div style={{ position: 'relative', padding: `${avSize / 2 + 10}px 20px 20px 20px` }}>
        <div
          style={{
            position: 'absolute',
            left: 20,
            top: -avSize / 2,
            width: avSize,
            height: avSize,
            borderRadius: '50%',
            overflow: 'hidden',
            border: '3px solid #ffffff',
            boxShadow: '0 2px 8px rgba(15,23,42,0.15)',
            background: '#f1f5f9',
            zIndex: 2,
          }}
        >
          {avatarInner}
        </div>
        {content.cardTag ? (
          <span style={{ fontSize: `${tagFontPx}px`, fontWeight: 700, textTransform: 'uppercase', color: tagColor, letterSpacing: '0.08em' }}>{content.cardTag}</span>
        ) : null}
        {content.cardTitle ? (
          <h3 className="madri-social-card-title" style={{ margin: content.cardTag ? '6px 0 4px 0' : '0 0 4px 0', color: titleColor, fontSize: `${titleFontPx}px`, fontWeight: 700 }}>{content.cardTitle}</h3>
        ) : null}
        {content.cardSubtitle ? <p className="madri-social-card-subtitle" style={{ margin: '0 0 8px 0', color: subtitleColor, fontSize: `${subtitleFontPx}px`, lineHeight: 1.45 }}>{content.cardSubtitle}</p> : null}
        {content.cardText ? <p className="madri-social-card-text" style={{ margin: '0 0 14px 0', color: textColor, fontSize: `${textFontPx}px`, lineHeight: 1.6 }}>{content.cardText}</p> : null}
        {content.socialIconsEnabled && (content.socialIconsPosition || 'above') === 'above' ? SocialIconsRow : null}
        {content.showBtn ? (
          <div style={{ textAlign: 'center', marginTop: '4px' }}>
            <a href={content.btnLink || '#'} onClick={e => e.preventDefault()} style={btnStyle}>
              {content.btnLabel || defaultBtn}
            </a>
          </div>
        ) : null}
        {content.socialIconsEnabled && (content.socialIconsPosition || 'above') === 'below' ? SocialIconsRow : null}
      </div>
    </div>
  );
};

const RenderPostIt = ({ content, t }) => {
  const bg = content.bg || '#fef08a';
  const IconCmp = getIconComponent(content.icon) || LucideIcons.StickyNote;
  const defaultPostIt = t ? t('builder:defaultPostIt') : 'Your note here...';
  const fontFamily = resolvePostItFontFamilyCss(content);
  return (
    <div style={{ background: bg, borderRadius: '2px 12px 12px 2px', boxShadow: '3px 4px 12px rgba(0,0,0,0.18), inset 0 -3px 6px rgba(0,0,0,0.08)', padding: '1.4rem 1.4rem 2rem', position: 'relative', minHeight: '120px', fontFamily }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '8px', background: 'rgba(0,0,0,0.06)', borderRadius: '2px 12px 0 0' }} />
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
        <IconCmp size={24} style={{ color: content.iconColor || '#854d0e', flexShrink: 0, marginTop: '2px' }} />
        <div className="fake-text" style={{ color: content.textColor || '#422006', fontSize: '15px', lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(content.html || `<p>${defaultPostIt}</p>`) }} />
      </div>
    </div>
  );
};

// ===========================================================
// NOVOS WIDGETS: LINHA DIVISORA, BLOCKQUOTE, MOODLE-ACTIVITY, TIMELINE
// ===========================================================

const RenderDivider = ({ content }) => {
  const style = content.dividerStyle || 'solid';
  const color = content.color || '#cbd5e1';
  const thickness = content.thickness || 2;
  const width = content.width || 100;
  const IconCmp = getIconComponent(content.icon);

  const lineBase = {
    width: '100%', height: `${thickness}px`, background: 'none',
    display: 'block', margin: '0 auto',
  };

  const containerStyle = {
    display: 'flex', alignItems: 'center', gap: '12px',
    width: `${width}%`, margin: '0 auto', padding: '12px 0',
    justifyContent: content.align === 'right' ? 'flex-end' : content.align === 'center' ? 'center' : 'flex-start'
  };

  if (style === 'icon' || style === 'scissors') {
    const IconEl = style === 'scissors' ? LucideIcons.Scissors : (IconCmp || LucideIcons.Star);
    return (
      <div style={containerStyle}>
        <div style={{ flex: 1, height: `${thickness}px`, background: color, borderRadius: '4px' }} />
        <IconEl size={20} color={color} style={style === 'scissors' ? { transform: 'rotate(-45deg)' } : {}} />
        <div style={{ flex: 1, height: `${thickness}px`, background: color, borderRadius: '4px' }} />
      </div>
    );
  }

  if (style === 'double') {
    return (
      <div style={{ width: `${width}%`, margin: '0 auto', padding: '12px 0', display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div style={{ ...lineBase, height: `${thickness}px`, background: color }} />
        <div style={{ ...lineBase, height: `${thickness}px`, background: color }} />
      </div>
    );
  }

  if (style === 'wavy') {
    const wavyId = 'wavy_' + Math.random().toString(36).substr(2, 5);
    return (
      <div style={{ width: `${width}%`, margin: '0 auto', padding: '2px 0', overflow: 'hidden' }}>
        <svg width="100%" height="20" viewBox="0 0 400 20" preserveAspectRatio="none">
          <path d="M0,10 Q25,0 50,10 T100,10 T150,10 T200,10 T250,10 T300,10 T350,10 T400,10" fill="none" stroke={color} strokeWidth={thickness} />
        </svg>
      </div>
    );
  }

  const borderStyle = style === 'dashed' ? 'dashed' : style === 'dotted' ? 'dotted' : 'solid';
  const isDotDash = style === 'dashed' || style === 'dotted';
  return (
    <div style={{ width: `${width}%`, margin: '0 auto', padding: '12px 0' }}>
      <div style={{
        width: '100%',
        height: isDotDash ? '0' : `${thickness}px`,
        background: style === 'gradient' ? `linear-gradient(to right, transparent, ${color}, transparent)` : (isDotDash ? 'none' : color),
        borderTop: isDotDash ? `${thickness}px ${borderStyle} ${color}` : 'none',
      }} />
    </div>
  );
};

const RenderSpacer = ({ content, showEditorWatermark = true, t }) => {
  const h = Math.min(240, Math.max(8, parseInt(content.heightPx, 10) || 40));
  const rawBg = content.spacerBg;
  const bg = rawBg === 'transparent' || rawBg === '' || rawBg == null ? 'transparent' : (rawBg || 'transparent');
  const deco = content.decoration || 'none';
  const decoColor = content.decoColor || '#94a3b8';
  const html = buildSpacerDecorationHtml(deco, decoColor);
  const label = t
    ? t('builder:spacerEditorWatermark', { px: h, defaultValue: `Espaçador ${h}px` })
    : `Espaçador ${h}px`;
  return (
    <div
      style={{
        minHeight: h,
        background: bg,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'stretch',
        width: '100%',
        boxSizing: 'border-box',
        position: showEditorWatermark ? 'relative' : undefined,
      }}
    >
      {showEditorWatermark ? (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            fontWeight: 500,
            color: '#cbd5e1',
            opacity: 1,
            pointerEvents: 'none',
            userSelect: 'none',
            zIndex: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
      ) : null}
      {html ? (
        <div style={{ width: '100%', flexShrink: 0, position: 'relative', zIndex: 1 }} dangerouslySetInnerHTML={{ __html: html }} />
      ) : null}
    </div>
  );
};

const RenderResponsiveTable = ({ content, t }) => {
  const cols = Math.min(12, Math.max(1, parseInt(content.columns, 10) || 3));
  const rowCount = Math.min(40, Math.max(1, parseInt(content.rowCount, 10) || 3));
  const headerRow = content.headerRow !== false;
  let rows = Array.isArray(content.rows) ? content.rows : [];
  rows = rows.slice(0, rowCount);
  while (rows.length < rowCount) {
    rows.push(Array(cols).fill(''));
  }
  const borderWRaw = parseInt(content.borderWidth, 10);
  const borderW = Math.min(6, Math.max(0, Number.isFinite(borderWRaw) ? borderWRaw : 1));
  const borderColor = content.borderColor || '#e2e8f0';
  const borderStyle = content.borderStyle || 'solid';
  const headerBg = content.headerBg || '#f1f5f9';
  const cellBg = content.cellBg || '#ffffff';
  const zebra = !!content.zebra;
  const zebraBg = content.zebraBg || '#f8fafc';
  const headerTextColor = resolveTableHeaderTextColor(content);
  const bodyTextColor = resolveTableBodyTextColor(content);
  const widthPct = Math.min(100, Math.max(30, parseInt(content.widthPct, 10) || 100));
  const pad = Math.min(24, Math.max(4, parseInt(content.cellPadding, 10) || 10));
  const fs = Math.min(22, Math.max(11, parseInt(content.fontSize, 10) || 14));
  const colBgs = Array.isArray(content.columnBgs) ? content.columnBgs : [];

  const ph = t ? t('builder:tablePlaceholderCell') : '—';

  return (
    <div style={{ width: `${widthPct}%`, maxWidth: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: fs, color: bodyTextColor, minWidth: cols * 80 }}>
        <tbody>
          {rows.map((row, ri) => {
            const cells = Array.isArray(row) ? row : [];
            const isHead = headerRow && ri === 0;
            const rowBg = !isHead && zebra && ri % 2 === (headerRow ? 1 : 0) ? zebraBg : null;
            return (
              <tr key={ri}>
                {Array.from({ length: cols }, (_, ci) => {
                  const text = normalizeResponsiveTableCell(cells[ci]);
                  const colTint = colBgs[ci] && String(colBgs[ci]).trim() ? String(colBgs[ci]).trim() : null;
                  const tdBg = colTint || (isHead ? headerBg : rowBg || cellBg);
                  const cellColor = isHead ? headerTextColor : bodyTextColor;
                  const Tag = isHead ? 'th' : 'td';
                  const cellLabel = text || (isHead ? ph : '\u00a0');
                  return (
                    <Tag
                      key={ci}
                      style={{
                        border: borderW > 0 ? `${borderW}px ${borderStyle} ${borderColor}` : 'none',
                        padding: pad,
                        background: tdBg,
                        color: cellColor,
                        fontWeight: isHead ? 600 : 400,
                        textAlign: (content.textAlign || 'left'),
                        verticalAlign: 'top',
                      }}
                    >
                      <span style={{ color: cellColor }}>{cellLabel}</span>
                    </Tag>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const RenderImageGallery = ({ content, t }) => {
  const [lightbox, setLightbox] = useState(null);
  const images = Array.isArray(content.images) ? content.images.filter((x) => x && String(x.url || '').trim()) : [];
  const cols = Math.min(6, Math.max(2, parseInt(content.columns, 10) || 3));
  const gap = Math.min(24, Math.max(0, parseInt(content.gap, 10) || 8));
  const br = Math.min(32, Math.max(0, parseInt(content.borderRadius, 10) || 10));
  const layout = content.layout === 'mosaic' ? 'mosaic' : 'grid';
  const animation = content.animation === 'fade' ? 'fade' : content.animation === 'zoom' ? 'zoom' : 'none';
  const hoverZoom = content.hoverZoom !== false;
  const showLb = content.lightbox !== false;

  const gridStyle = layout === 'mosaic'
    ? { display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap, gridAutoRows: 'minmax(100px, auto)' }
    : { display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap };

  return (
    <>
      <div className="madri-gc-gallery-preview" style={gridStyle}>
        {images.length === 0 ? (
          <div style={{ gridColumn: '1 / -1', padding: '2rem', textAlign: 'center', color: '#94a3b8', border: '1px dashed #cbd5e1', borderRadius: br }}>
            {t ? t('builder:galleryEmpty') : 'Adicione imagens na configuração.'}
          </div>
        ) : images.map((img, i) => {
          const spanStyle = layout === 'mosaic' && i === 0 && images.length >= 3
            ? { gridColumn: 'span 2', gridRow: 'span 2' }
            : {};
          const animCls = animation === 'fade' ? 'madri-gal-anim-fade' : animation === 'zoom' ? 'madri-gal-anim-zoom' : '';
          return (
            <button
              key={i}
              type="button"
              onClick={() => showLb && setLightbox({ url: img.url, alt: img.alt || '' })}
              style={{
                border: 'none', padding: 0, margin: 0, borderRadius: br, overflow: 'hidden', cursor: showLb ? 'zoom-in' : 'default',
                background: '#e2e8f0', position: 'relative', ...spanStyle,
              }}
              className={animCls}
            >
              <img
                src={img.url}
                alt={img.alt || ''}
                style={{
                  width: '100%', height: '100%', objectFit: 'cover', display: 'block',
                  transition: hoverZoom ? 'transform 0.35s ease' : undefined,
                  minHeight: layout === 'mosaic' && i === 0 ? 200 : 120,
                }}
                onMouseEnter={(e) => { if (hoverZoom) e.currentTarget.style.transform = 'scale(1.05)'; }}
                onMouseLeave={(e) => { if (hoverZoom) e.currentTarget.style.transform = 'scale(1)'; }}
              />
            </button>
          );
        })}
      </div>
      {lightbox && (
        <div
          role="presentation"
          style={{ position: 'fixed', inset: 0, zIndex: 99998, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          onClick={() => setLightbox(null)}
        >
          <img src={lightbox.url} alt={lightbox.alt} style={{ maxWidth: '96vw', maxHeight: '92vh', objectFit: 'contain', borderRadius: 8 }} onClick={(e) => e.stopPropagation()} />
          <button type="button" style={{ position: 'fixed', top: 16, right: 20, background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontWeight: 700 }} onClick={() => setLightbox(null)}>✕</button>
        </div>
      )}
    </>
  );
};

const RenderBlockquote = ({ content, t }) => {
  const accentColor = content.accentColor || '#3b82f6';
  const quoteStyle = content.quoteStyle || 'classic';
  const bg = content.bg ?? (content.wrapperBg && content.wrapperBg !== 'transparent' ? (content.computedBgRGBA || content.wrapperBg) : undefined) ?? '#f8fafc';
  const defaultQuote = t ? t('builder:defaultBlockquotePlaceholder') : 'Insert your quote here...';

  if (quoteStyle === 'bordered') return (
    <div style={{ borderLeft: `5px solid ${accentColor}`, paddingLeft: '1.5rem', margin: '1rem 0', background: bg, padding: '1.2rem 1.5rem', borderRadius: '0 8px 8px 0' }}>
      <p style={{ fontSize: content.fontSize || 16, fontStyle: 'italic', color: content.textColor || '#334155', lineHeight: 1.8, margin: 0 }}>&ldquo;{content.text || defaultQuote}&rdquo;</p>
      {content.author && <cite style={{ display: 'block', marginTop: '0.8rem', fontSize: '0.85rem', color: accentColor, fontWeight: 700 }}>— {content.author}</cite>}
    </div>
  );

  if (quoteStyle === 'card') return (
    <div style={{ background: accentColor, borderRadius: '12px', padding: '1.8rem', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: '-20px', left: '10px', fontSize: '8rem', color: 'rgba(255,255,255,0.15)', fontFamily: 'Georgia', lineHeight: 1 }}>&ldquo;</div>
      <p style={{ fontSize: content.fontSize || 16, fontStyle: 'italic', color: '#fff', lineHeight: 1.8, margin: 0, position: 'relative', zIndex: 1 }}>{content.text || defaultQuote}</p>
      {content.author && <cite style={{ display: 'block', marginTop: '0.8rem', fontSize: '0.85rem', color: 'rgba(255,255,255,0.8)', fontWeight: 700, position: 'relative', zIndex: 1 }}>— {content.author}</cite>}
    </div>
  );

  // classic (default)
  return (
    <div style={{ textAlign: 'center', padding: '1.5rem 2rem', margin: '1rem 0' }}>
      <LucideIcons.Quote size={32} color={accentColor} style={{ margin: '0 auto 0.8rem', display: 'block', opacity: 0.7 }} />
      <p style={{ fontSize: content.fontSize || 18, fontStyle: 'italic', color: content.textColor || '#334155', lineHeight: 1.8, margin: '0 0 1rem' }}>&ldquo;{content.text || defaultQuote}&rdquo;</p>
      {content.author && <cite style={{ fontSize: '0.9rem', color: accentColor, fontWeight: 700 }}>— {content.author}</cite>}
    </div>
  );
};

/** Palco do builder: mesmo cartão da exportação/preview (sem tokens Moodle nem script). */
const RenderAsaasPayment = ({ content, t, gatewayConfigured, onOpenGatewaySettings }) => {
  const rawAmt = parseFloat(content?.amount);
  const amount = Number.isFinite(rawAmt) ? Math.min(999999.99, Math.max(0.01, rawAmt)) : 9.9;
  const title = (content?.title || '').trim() || (t ? t('builder:asaasPaymentDefaultTitle') : 'Pagamento');
  const desc = (content?.description || '').trim();
  const btnLabel = (content?.buttonLabel || '').trim() || (t ? t('builder:asaasPaymentDefaultBtn') : 'Pagar agora');
  const align = content?.align === 'left' ? 'left' : content?.align === 'right' ? 'right' : 'center';
  const btnBg = content?.buttonBg || '#16a34a';
  const btnFg = content?.buttonTextColor || '#ffffff';
  const cardBg = content?.cardBg || '#f8fafc';
  const br = Math.min(24, Math.max(0, parseInt(content?.borderRadius, 10) || 8));
  const brFmt = amount.toFixed(2).replace('.', ',');
  const ck = content?.checkpoint || {};
  const iconType = ck.iconType || 'check';
  const position = ck.position || 'top-right';
  const showGatewayWarning = gatewayConfigured === false && typeof onOpenGatewaySettings === 'function';
  return (
    <div style={{ position: 'relative', minHeight: 72, boxSizing: 'border-box' }}>
      <CheckpointIconSvg type={iconType} done={false} position={position} />
      <div style={{ textAlign: align, padding: '18px', border: '1px solid #e2e8f0', borderRadius: br, background: cardBg, fontFamily: 'system-ui, sans-serif', maxWidth: 420, margin: '0 auto', boxSizing: 'border-box' }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '1.15rem', color: '#0f172a', fontWeight: 700 }}>{title}</h3>
        {desc ? <p style={{ margin: '0 0 12px 0', fontSize: '0.95rem', color: '#64748b', lineHeight: 1.45 }}>{desc}</p> : null}
        <p style={{ margin: '0 0 16px 0', fontSize: '1.35rem', fontWeight: 800, color: '#0f172a' }}>R$ {brFmt}</p>
        <button type="button" tabIndex={-1} style={{ display: 'inline-block', padding: '12px 22px', border: 'none', borderRadius: 8, background: btnBg, color: btnFg, fontSize: '1rem', fontWeight: 600, cursor: 'default', pointerEvents: 'none' }}>
          {btnLabel}
        </button>
        {showGatewayWarning ? (
          <p style={{ margin: '14px 0 0 0', fontSize: '0.85rem', color: '#b45309', lineHeight: 1.45 }}>
            {t('builder:asaasPaymentGatewayWarningText', { defaultValue: 'Você precisa configurar o gateway antes de usar.' })}{' '}
            <button
              type="button"
              style={{
                margin: 0,
                padding: 0,
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                color: '#92400e',
                fontWeight: 700,
                textDecoration: 'underline',
                fontSize: 'inherit',
                fontFamily: 'inherit',
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onOpenGatewaySettings();
              }}
            >
              {t('builder:asaasPaymentGatewayWarningLink', { defaultValue: 'Clique aqui' })}
            </button>
          </p>
        ) : null}
      </div>
    </div>
  );
};

const RenderMercadoPagoPayment = ({ content, t, gatewayConfigured, onOpenGatewaySettings }) => {
  const rawAmt = parseFloat(content?.amount);
  const amount = Number.isFinite(rawAmt) ? Math.min(999999.99, Math.max(0.01, rawAmt)) : 9.9;
  const title = (content?.title || '').trim() || (t ? t('builder:mpPaymentDefaultTitle') : 'Pagamento');
  const desc = (content?.description || '').trim();
  const btnLabel = (content?.buttonLabel || '').trim() || (t ? t('builder:mpPaymentDefaultBtn') : 'Pagar com Mercado Pago');
  const align = content?.align === 'left' ? 'left' : content?.align === 'right' ? 'right' : 'center';
  const btnBg = content?.buttonBg || '#009ee3';
  const btnFg = content?.buttonTextColor || '#ffffff';
  const cardBg = content?.cardBg || '#f8fafc';
  const br = Math.min(24, Math.max(0, parseInt(content?.borderRadius, 10) || 8));
  const brFmt = amount.toFixed(2).replace('.', ',');
  const ck = content?.checkpoint || {};
  const iconType = ck.iconType || 'check';
  const position = ck.position || 'top-right';
  const showGatewayWarning = gatewayConfigured === false && typeof onOpenGatewaySettings === 'function';
  return (
    <div style={{ position: 'relative', minHeight: 72, boxSizing: 'border-box' }}>
      <CheckpointIconSvg type={iconType} done={false} position={position} />
      <div style={{ textAlign: align, padding: '18px', border: '1px solid #e2e8f0', borderRadius: br, background: cardBg, fontFamily: 'system-ui, sans-serif', maxWidth: 420, margin: '0 auto', boxSizing: 'border-box' }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '1.15rem', color: '#0f172a', fontWeight: 700 }}>{title}</h3>
        {desc ? <p style={{ margin: '0 0 12px 0', fontSize: '0.95rem', color: '#64748b', lineHeight: 1.45 }}>{desc}</p> : null}
        <p style={{ margin: '0 0 16px 0', fontSize: '1.35rem', fontWeight: 800, color: '#0f172a' }}>R$ {brFmt}</p>
        <button type="button" tabIndex={-1} style={{ display: 'inline-block', padding: '12px 22px', border: 'none', borderRadius: 8, background: btnBg, color: btnFg, fontSize: '1rem', fontWeight: 600, cursor: 'default', pointerEvents: 'none' }}>
          {btnLabel}
        </button>
        {showGatewayWarning ? (
          <p style={{ margin: '14px 0 0 0', fontSize: '0.85rem', color: '#b45309', lineHeight: 1.45 }}>
            {t('builder:mpPaymentGatewayWarningText', { defaultValue: 'Você precisa configurar o Mercado Pago antes de usar.' })}{' '}
            <button
              type="button"
              style={{
                margin: 0,
                padding: 0,
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                color: '#92400e',
                fontWeight: 700,
                textDecoration: 'underline',
                fontSize: 'inherit',
                fontFamily: 'inherit',
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onOpenGatewaySettings();
              }}
            >
              {t('builder:mpPaymentGatewayWarningLink', { defaultValue: 'Clique aqui' })}
            </button>
          </p>
        ) : null}
      </div>
    </div>
  );
};

const MOODLE_ACTIVITY_TYPES = ['moodle-activity', 'moodle-quiz', 'moodle-forum'];

const RenderMoodleActivityRow = ({ content, t }) => {
  const IconCmp = getIconComponent(content.activityIcon) || LucideIcons.MessageCircle;
  const iconBg = content.activityIconBg || '#e5f4f9';
  const iconFg = content.activityIconColor || '#006378';
  const title = content.activityTitle || (t ? t('builder:defaultActivityTitle') : 'Atividade');
  const hrefPreview = String(content.activityUrl || '').trim() || (t ? t('builder:activityUrlPlaceholderShort') : '/mod/…');
  return (
    <div className="geppetto-moodle-activity-row" style={{ cursor: 'default' }}>
      <span className="gma-icon-wrap" style={{ background: iconBg }}>
        <IconCmp size={22} color={iconFg} strokeWidth={2} />
      </span>
      <span className="gma-body" style={{ flex: 1, minWidth: 0 }}>
        <span className="gma-title">{title}</span>
        {content.activityDesc ? <span className="gma-meta">{content.activityDesc}</span> : null}
        <span className="gma-meta" style={{ display: 'block', marginTop: content.activityDesc ? 6 : 4, fontFamily: 'ui-monospace, monospace', fontSize: '0.8rem' }}>{hrefPreview}</span>
      </span>
      {content.showActivityStatus ? (
        <span
          className="gma-badge"
          title={extractMoodleActivityCmidFromHref(String(content.activityUrl || '').trim()) && t ? t('builder:activityCompletionLiveHint') : undefined}
        >
          {(content.activityStatusLabel || '').trim() || (t ? t('builder:activityStatusPendingDefault') : 'Pendente')}
        </span>
      ) : null}
    </div>
  );
};

const RenderTimeline = ({ content, t }) => {
  const items = content.items || (t ? [
    { date: '2024', title: `${t('builder:defaultEvent')} 1`, desc: t('builder:defaultEventDesc'), icon: 'Circle' },
    { date: '2025', title: `${t('builder:defaultEvent')} 2`, desc: t('builder:defaultEventDesc'), icon: 'Circle' },
  ] : [
    { date: '2024', title: 'Event 1', desc: 'Event description', icon: 'Circle' },
    { date: '2025', title: 'Event 2', desc: 'Event description', icon: 'Circle' },
  ]);
  const direction = content.direction || 'vertical';
  const accentColor = content.accentColor || '#3b82f6';
  const lineColor = content.lineColor || '#e2e8f0';
  const dateColor = content.timelineDateColor || accentColor;
  const titleColor = content.timelineTitleColor || '#1e293b';
  const descColor = content.timelineDescColor || '#64748b';
  const cardBg = resolveTimelineCardBg(content.timelineCardBg);
  const cardBorderCss = resolveTimelineCardBorderCss(content.timelineCardBorder);
  const scalePct = Math.min(200, Math.max(50, parseInt(content.timelineFontScalePct, 10) || 100));
  const scale = scalePct / 100;
  const horiz = direction === 'horizontal';
  const remDate = `${0.75 * scale}rem`;
  const remTitle = `${(horiz ? 0.9 : 0.95) * scale}rem`;
  const remDesc = `${(horiz ? 0.8 : 0.85) * scale}rem`;

  if (direction === 'horizontal') {
    return (
      <div className="madri-gc-timeline madri-gc-timeline--horizontal" style={{ overflowX: 'auto', paddingBottom: '1rem', ['--madri-tl-line']: lineColor }}>
        <div className="madri-gc-timeline-h-track" style={{ display: 'flex', alignItems: 'flex-start', minWidth: `${items.length * 180}px`, position: 'relative', padding: '1rem 0' }}>
          <div className="madri-gc-timeline-h-connector" style={{ position: 'absolute', top: '36px', left: '48px', right: '48px', height: '2px', background: lineColor }} />
          {items.map((item, i) => {
            const IconEl = (() => {
              const ic = item.icon || 'Circle';
              if (ic.startsWith('fa:')) {
                const pascal = ic.slice(3).split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join('');
                return FaIcons[pascal] || LucideIcons.Circle;
              }
              if (ic.startsWith('md:')) {
                const pascal = ic.slice(3).split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join('');
                return MdIcons[pascal] || LucideIcons.Circle;
              }
              return LucideIcons[ic] || LucideIcons.Circle;
            })();
            return (
              <div key={i} className="madri-gc-timeline-h-item" style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 8px', position: 'relative' }}>
                <div className="madri-gc-timeline-h-icon" style={{ width: '48px', height: '48px', borderRadius: '50%', background: accentColor, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '3px solid #fff', boxShadow: `0 0 0 2px ${accentColor}`, zIndex: 1, flexShrink: 0 }}>
                  <IconEl size={24} color="#fff" />
                </div>
                <div className="madri-gc-timeline-h-body" style={{ marginTop: '0.8rem', width: '100%', boxSizing: 'border-box' }}>
                  <div style={{
                    background: cardBg,
                    borderRadius: '8px',
                    padding: '0.9rem 1.1rem',
                    border: cardBorderCss,
                    textAlign: 'center',
                    boxSizing: 'border-box',
                  }}
                  >
                    {item.date && <span className="madri-gc-timeline-date" style={{ fontSize: remDate, fontWeight: 700, color: dateColor, letterSpacing: '0.5px' }}>{item.date}</span>}
                    <div className="madri-gc-timeline-title" style={{ fontWeight: 700, color: titleColor, fontSize: remTitle, marginTop: '2px' }}>{item.title}</div>
                    {item.desc && <p className="madri-gc-timeline-desc" style={{ fontSize: remDesc, color: descColor, marginTop: '4px', lineHeight: 1.5 }}>{item.desc}</p>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // vertical
  return (
    <div style={{ position: 'relative', paddingLeft: '2.5rem' }}>
      <div style={{ position: 'absolute', left: '14px', top: '8px', bottom: '8px', width: '2px', background: lineColor }} />
      {items.map((item, i) => {
        const IconEl = (() => {
          const ic = item.icon || 'Circle';
          if (ic.startsWith('fa:')) {
            const pascal = ic.slice(3).split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join('');
            return FaIcons[pascal] || LucideIcons.Circle;
          }
          if (ic.startsWith('md:')) {
            const pascal = ic.slice(3).split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join('');
            return MdIcons[pascal] || LucideIcons.Circle;
          }
          return LucideIcons[ic] || LucideIcons.Circle;
        })();
        return (
          <div key={i} style={{ display: 'flex', gap: '1rem', marginBottom: i < items.length - 1 ? '1.8rem' : 0, position: 'relative' }}>
            <div style={{ position: 'absolute', left: '-38px', top: '2px', width: '36px', height: '36px', borderRadius: '50%', background: accentColor, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff', boxShadow: `0 0 0 2px ${accentColor}`, flexShrink: 0 }}>
              <IconEl size={20} color="#fff" />
            </div>
            <div style={{ background: cardBg, borderRadius: '8px', padding: '0.9rem 1.1rem', border: cardBorderCss, flex: 1 }}>
              {item.date && <span className="madri-gc-timeline-date" style={{ fontSize: remDate, fontWeight: 700, color: dateColor, letterSpacing: '0.5px' }}>{item.date}</span>}
              <div className="madri-gc-timeline-title" style={{ fontWeight: 700, color: titleColor, fontSize: remTitle, marginTop: item.date ? '2px' : 0 }}>{item.title}</div>
              {item.desc && <p className="madri-gc-timeline-desc" style={{ fontSize: remDesc, color: descColor, marginTop: '4px', lineHeight: 1.6, marginBottom: 0 }}>{item.desc}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ===========================================================

const RenderButton = ({ content }) => {
  const { text, size, rounded, border, fullWidth, center, width, fontWeight, letterSpacing, wordSpacing, uppercase, colorsNormal } = content || {};

  let btnPad = '10px 20px'; let btnFont = '14px';
  if (size === 'S') { btnPad = '6px 12px'; btnFont = '12px'; }
  if (size === 'L') { btnPad = '14px 28px'; btnFont = '16px'; }
  if (size === 'XL') { btnPad = '18px 36px'; btnFont = '18px'; }

  const btnFontNum = parseInt(btnFont, 10) || 14;
  const iconPx = Number.isFinite(content?.buttonIconSizePx) && content.buttonIconSizePx >= 12
    ? Math.min(48, Math.max(12, content.buttonIconSizePx))
    : btnFontNum;
  const iconColorExplicit = content?.iconColor && String(content.iconColor).trim();

  let br = '4px';
  if (rounded === 'Yes (Pill)') br = '50px';
  if (rounded === 'No (Square)') br = '0px';

  let fw = 'normal';
  if (fontWeight === 'Light') fw = '300';
  if (fontWeight === 'Medium') fw = '500';
  if (fontWeight === 'Semi-bold') fw = '600';
  if (fontWeight === 'Bold') fw = 'bold';

  const styleObj = {
    display: fullWidth ? 'block' : 'inline-block',
    width: fullWidth ? '100%' : `${width || 100}%`,
    textAlign: 'center',
    padding: btnPad,
    fontSize: btnFont,
    borderRadius: br,
    backgroundColor: border ? 'transparent' : (colorsNormal?.bg || '#f97316'),
    color: colorsNormal?.text || '#ffffff',
    border: border ? `2px solid ${colorsNormal?.bg || '#f97316'}` : `1px solid ${colorsNormal?.border || 'transparent'}`,
    fontWeight: fw,
    letterSpacing: `${letterSpacing || 0}px`,
    wordSpacing: `${wordSpacing || 0}px`,
    textTransform: uppercase ? 'uppercase' : 'none',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  };

  const uid_btn = 'btn_' + (content?.id || 'preview');
  const hoverCss = `
    #${uid_btn}:hover {
      background-color: ${border && content?.colorsHover ? content.colorsHover.bg : (content?.colorsHover?.bg || '#ea580c')} !important;
      color: ${content?.colorsHover?.text || '#ffffff'} !important;
      border-color: ${content?.colorsHover?.border || 'transparent'} !important;
    }
  `;

  const IconCmp = getIconComponent(content?.icon);

  return (
    <div style={{ textAlign: center ? 'center' : 'left', width: '100%' }}>
      <style>{hoverCss}</style>
      <a id={uid_btn} href="#" style={styleObj} onClick={(e) => e.preventDefault()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          {content?.showIcon && IconCmp ? (
            <IconCmp size={iconPx} {...(iconColorExplicit ? { color: iconColorExplicit } : {})} style={{ flexShrink: 0 }} />
          ) : (content?.showIcon && <span>★</span>)}
          <span>{text || "Button"}</span>
        </div>
      </a>
    </div>
  );
};

// COMPONENTE: CONTROLE VISUAL DE MARGIN/PADDING (Desenhado para o Professor)
// =========================================================================
const SpacingControl = ({ label, values, onChange }) => {
  const currentValues = values || { top: 0, right: 0, bottom: 0, left: 0 };

  const handleSliderChange = (side, val) => {
    onChange({ ...currentValues, [side]: parseInt(val) || 0 });
  };

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <h5 style={{ margin: '0 0 1rem 0', color: '#1e293b', fontSize: '1rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem' }}>{label}</h5>
      {['top', 'bottom', 'left', 'right'].map((side) => (
        <div key={side} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem', gap: '1rem' }}>
          <div style={{ width: '80px', color: '#64748b', fontSize: '0.85rem', textTransform: 'capitalize' }}>{label} {side}</div>
          <input
            type="range"
            min="0"
            max="100"
            value={currentValues[side] || 0}
            onChange={(e) => handleSliderChange(side, e.target.value)}
            style={{ flex: 1, accentColor: 'var(--primary)' }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', width: '70px' }}>
            <input
              type="number"
              value={currentValues[side] || 0}
              onChange={(e) => handleSliderChange(side, e.target.value)}
              style={{ width: '50px', padding: '0.25rem', border: '1px solid var(--border)', borderRadius: '4px', textAlign: 'center' }}
            />
            <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>px</span>
          </div>
        </div>
      ))}
    </div>
  );
};

const WidgetRenderer = ({
  widget,
  onRemove,
  onUpdate,
  onDuplicate,
  onBeforeEdit,
  onRequestEdit,
  onReleaseEdit,
  onAfterConfigSave,
  dragHandleProps,
  globalCardBg,
  globalTypographyConfig,
  stagePosition,
  stagePositionTotal,
  onStagePositionChange,
  isHeaderWidget,
  lockInfo,
  currentUserId,
  rowLockInfo,
  parentRowId,
  activeEditKey,
  setActiveEditKey,
  titleBasicAnchorsForMenu = [],
  tocAvailableEntries = [],
  readOnly = false,
  showToast,
  asaasGatewayConfigured = null,
  onOpenAsaasGatewaySettings,
  mercadopagoGatewayConfigured = null,
  onOpenMercadoPagoGatewaySettings,
  cricketLinkEnabled = false,
  cricketLayoutId = null,
  cricketAuthToken = null,
  cricketStageBadge = null,
  cricketStageLampsHidden = false,
  onCricketOverlayInvalidate = null,
  cricketFocusHighlight = false,
  onCricketLampClick = null,
  editBreakpoint = EDIT_BREAKPOINT_DESKTOP,
  microquizCheckpointOptions = [],
  planRestrictedHighlight = false,
  planAllowedDimOthers = false,
}) => {
  const { t } = useTranslation(['builder', 'cricket']);
  const [showAdvancedDesign, setShowAdvancedDesign] = useState(false);
  const [imageDropActive, setImageDropActive] = useState(false);
  const wrapperRef = useRef(null);
  const lockedByOther = !!(lockInfo?.user_id && String(lockInfo.user_id) !== String(currentUserId || ''));
  const rowLocked = !!(rowLockInfo?.user_id);
  const rowLockKey = parentRowId ? `row:${parentRowId}` : null;
  const rowEditingHere = !!(rowLockKey && activeEditKey === rowLockKey);
  const blockedByRow = !!(parentRowId && rowLocked && !rowEditingHere);
  const lockLabel = lockInfo?.user_name || lockInfo?.user_id || t('builder:anotherUser', { defaultValue: 'Outro usuário' });
  const rowLockLabel = rowLockInfo?.user_name || rowLockInfo?.user_id || t('builder:anotherUser', { defaultValue: 'Outro usuário' });
  const widgetCss = elementAdvancedCss(widget.content.advancedCss, widget.content.customClass);
  const motion = normalizeMotion(widget.content);
  useEffect(() => {
    ensureBuilderEffectCss();
    const el = wrapperRef.current;
    if (!el) return undefined;
    if (motion.enter === 'none') {
      el.classList.remove('is-in');
      return undefined;
    }
    el.classList.remove('is-in');
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || typeof IntersectionObserver === 'undefined') {
      el.classList.add('is-in');
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          el.classList.add('is-in');
          io.disconnect();
        }
      });
    }, { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, [motion.enter, motion.delay, widget.id]);

  const imageMediaDropHandlers =
    widget.type === 'image' && !readOnly && !lockedByOther && !blockedByRow
      ? {
          onDragOver: (e) => {
            if (!dataTransferLooksLikeGepettoMediaUrl(e.dataTransfer)) return;
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = 'copy';
            setImageDropActive(true);
          },
          onDragEnter: (e) => {
            if (dataTransferLooksLikeGepettoMediaUrl(e.dataTransfer)) {
              e.preventDefault();
              setImageDropActive(true);
            }
          },
          onDragLeave: (e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) setImageDropActive(false);
          },
          onDrop: (e) => {
            e.preventDefault();
            e.stopPropagation();
            setImageDropActive(false);
            const url = parseGepettoMediaDropUrl(e.dataTransfer);
            if (!url) return;
            onBeforeEdit?.();
            onUpdate(widget.id, { ...widget.content, url });
            showToast?.(
              t('imageUpdatedFromMedia', { defaultValue: 'Imagem atualizada a partir da biblioteca.' }),
              'success',
              2200
            );
          },
        }
      : {};

  const stageContent = resolveWidgetContentForEdit(widget.content, widget.type, editBreakpoint);
  const stageGlobalTypography = resolveGlobalConfigForEdit(globalTypographyConfig || {}, editBreakpoint);

  return (
    <>
      <AdvancedCssStyleTag styleId={widget.id} css={widgetCss.stylesheet} />
      {showAdvancedDesign && (
        widget.type === 'styled-button' ? (
          <ButtonConfigModal
            initialContent={widget.content}
            onChange={(config) => {
              onUpdate(widget.id, config);
            }}
            onSave={(config) => {
              onUpdate(widget.id, config);
              setShowAdvancedDesign(false);
              onReleaseEdit?.(widget.id);
              setActiveEditKey?.(prev => (prev === widget.id ? null : prev));
              onAfterConfigSave?.();
            }}
            onClose={() => {
              setShowAdvancedDesign(false);
              onReleaseEdit?.(widget.id);
              setActiveEditKey?.(prev => (prev === widget.id ? null : prev));
            }}
            currentPosition={stagePosition}
            totalCount={stagePositionTotal}
            onPositionChange={onStagePositionChange}
          />
        ) : (
          <WidgetConfigModal
            widgetType={widget.type}
            initialContent={widget.content}
            onChange={(config) => {
              onUpdate(widget.id, config);
            }}
            onSave={(config) => {
              onUpdate(widget.id, config);
              setShowAdvancedDesign(false);
              onReleaseEdit?.(widget.id);
              setActiveEditKey?.(prev => (prev === widget.id ? null : prev));
              onAfterConfigSave?.();
            }}
            onClose={() => {
              setShowAdvancedDesign(false);
              onReleaseEdit?.(widget.id);
              setActiveEditKey?.(prev => (prev === widget.id ? null : prev));
            }}
            currentPosition={stagePosition}
            totalCount={stagePositionTotal}
            onPositionChange={onStagePositionChange}
            titleBasicAnchorsForMenu={titleBasicAnchorsForMenu}
            tocAvailableEntries={tocAvailableEntries}
            editingWidgetId={widget.id}
            courseGlobalConfig={globalTypographyConfig || {}}
            cricketLinkEnabled={cricketLinkEnabled}
            cricketLayoutId={cricketLayoutId}
            cricketAuthToken={cricketAuthToken}
            cricketReadOnly={readOnly}
            onCricketOverlayInvalidate={onCricketOverlayInvalidate}
            editBreakpoint={editBreakpoint}
            microquizCheckpointOptions={microquizCheckpointOptions}
          />
        )
      )}
      <div
        ref={wrapperRef}
        className={`widget-wrapper madri-widget-${widget.id} ${widgetCss.classNames}${planRestrictedHighlight ? ' gepetto-plan-restricted' : ''}${isHiddenOnMobile(widget.content) ? ` ${HIDE_ON_MOBILE_CLASS}` : ''}${motion.enter !== 'none' ? ` madri-motion madri-motion--${motion.enter}` : ''}`}
        data-geppetto-plan-restricted={planRestrictedHighlight ? '1' : undefined}
        style={{
          position: 'relative',
          border: lockedByOther ? '1px solid #ef4444' : '1px solid transparent',
          padding: widget.type === 'styled-card' || widget.type === 'header-menu' || widget.type === 'site-menu' || widget.type === 'site-footer' || widget.type === 'site-login' || widget.type === 'site-maintenance' ? 0 : toCssStr(widget.content.paddingObj, '10px'),
          margin: isHeaderWidget || widget.type === 'site-login' || widget.type === 'site-maintenance' ? '0' : toCssStr(widget.content.marginObj, '0 0 20px 0'),
          backgroundColor: widget.type === 'site-menu' || widget.type === 'site-footer' || widget.type === 'site-login' || widget.type === 'site-maintenance' ? 'transparent' : widget.type === 'progress-bar' ? (widget.content.progressBarBg || widget.content.computedBgRGBA || widget.content.wrapperBg || '#f8fafc') : widget.type === 'header-menu' ? (widget.content.menuBg && widget.content.menuBg !== 'transparent' ? widget.content.menuBg : (widget.content.computedBgRGBA || widget.content.wrapperBg || '#f8fafc')) : (widget.content.computedBgRGBA || widget.content.wrapperBg || 'transparent'),
          ...(widget.type === 'title-basic' || widget.type === 'section-header' || widget.type === 'table-of-contents' || widget.type === 'responsive-table'
            ? {}
            : {
                color: resolveWidgetExportWrapperColor(widget.type, stageContent) || 'inherit',
                fontSize: stageContent.wrapperFont ? `${stageContent.wrapperFont}px` : 'inherit',
              }),
          lineHeight: stageContent.wrapperLineHeight ? stageContent.wrapperLineHeight : 'inherit',
          borderRadius: widget.type === 'site-menu' || widget.type === 'site-footer' || widget.type === 'site-login' || widget.type === 'site-maintenance' ? 0 : '8px',
          overflow: widget.type === 'site-menu' || widget.type === 'site-footer' || widget.type === 'site-login' || widget.type === 'site-maintenance' ? 'visible' : undefined,
          ...(widget.type === 'image' && imageDropActive ? { outline: '3px dashed #2563eb', outlineOffset: '2px', backgroundColor: 'rgba(37, 99, 235, 0.06)' } : {}),
          ...(widgetCss.inline ? cssStringToObject(widgetCss.inline) : {}),
          ...(cricketFocusHighlight
            ? { boxShadow: '0 0 0 3px rgba(245, 158, 11, 0.9), 0 0 22px rgba(245, 158, 11, 0.35)', transition: 'box-shadow 0.25s ease' }
            : {}),
          ...(planRestrictedHighlight
            ? getPlanRestrictedHighlightStyle()
            : {}),
          ...(planAllowedDimOthers && !planRestrictedHighlight
            ? { opacity: 0.55, transition: 'opacity 0.25s ease' }
            : {}),
          ...(motion.enter !== 'none' || motion.loop !== 'none' ? { '--madri-motion-delay': `${motion.delay}ms` } : {}),
        }}
        {...imageMediaDropHandlers}
      >
        {isHiddenOnMobile(widget.content) && (
          <div className="madri-hide-mobile-note">{t('builder:hiddenOnMobileNote', { defaultValue: 'Oculto no celular' })}</div>
        )}
        {cricketLinkEnabled && !cricketStageLampsHidden && cricketStageBadge && (() => {
          const b = cricketStageBadge;
          const chirp = (b.body || '').trim();
          const tip = [b.listTitle, b.title, chirp ? chirp.slice(0, 220) : ''].filter(Boolean).join(' — ');
          const lampTitle = `${tip}${typeof onCricketLampClick === 'function' ? ` — ${t('cricket:lampScrollHint')}` : ''}`;
          return (
            <button
              type="button"
              className="absolute left-1 top-1 z-[12] flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border-0 bg-white/95 p-0 shadow ring-1 ring-slate-200/80 hover:bg-amber-50 dark:bg-slate-900/95 dark:ring-slate-600/80 dark:hover:bg-slate-800"
              title={lampTitle}
              aria-label={lampTitle}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onCricketLampClick?.(widget.id);
              }}
            >
              <Lamp size={15} style={{ color: b.lampColor || '#a855f7' }} aria-hidden />
            </button>
          );
        })()}
        {lockedByOther && (
          <div style={{ position: 'absolute', right: 8, top: 8, zIndex: 5, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
            {lockLabel} {t('builder:isEditing', { defaultValue: 'editando' })}
          </div>
        )}
        {!readOnly && (
        <div className="widget-controls">
          <div className="widget-btn" title={t('builder:btnDrag')} {...dragHandleProps} style={{ cursor: 'grab' }}><LucideIcons.GripVertical size={14} /></div>
          <button
            className="widget-btn"
            title={
              blockedByRow
                ? `${rowLockLabel} ${t('builder:isEditing', { defaultValue: 'editando seção' })}`
                : (lockedByOther ? `${lockLabel} ${t('builder:isEditing', { defaultValue: 'editando' })}` : t('builder:btnConfig'))
            }
            disabled={lockedByOther || blockedByRow}
            onClick={async () => {
              if (blockedByRow) return;
              if (activeEditKey && activeEditKey !== widget.id) {
                alert(t('builder:finishCurrentEditFirst', { defaultValue: 'Finalize a edição atual antes de abrir outra configuração.' }));
                return;
              }
              const ok = await (onRequestEdit ? onRequestEdit(widget.id) : true);
              if (!ok) return;
              onBeforeEdit?.();
              setActiveEditKey?.(widget.id);
              setShowAdvancedDesign(true);
            }}
          >
            <LucideIcons.Pencil size={14} color={BUILDER_EDIT_PENCIL.widget} strokeWidth={2.2} />
          </button>
          <button className="widget-btn" title={t('builder:btnDuplicate')} onClick={onDuplicate}><LucideIcons.Copy size={14} /></button>
          <button className="widget-btn danger" title={t('builder:btnRemove')} onClick={onRemove}><Trash2 size={14} /></button>
        </div>
        )}

        <MotionStage content={widget.content}>
        {widget.type === 'text' && <RenderText content={stageContent} />}
        {widget.type === 'image' && <RenderImage content={stageContent} t={t} />}
        {widget.type === 'video' && <RenderVideo content={stageContent} t={t} />}
        {widget.type === 'panda-video' && <RenderPandaVideo content={stageContent} t={t} />}
        {widget.type === 'h5p' && <RenderH5P content={stageContent} t={t} />}
        {widget.type === 'alert' && <RenderAlert content={stageContent} />}
        {widget.type === 'htmlTemplate' && <RenderHtmlTemplate content={stageContent} />}
        {widget.type === 'styled-card' && <RenderStyledCard content={stageContent} globalCardBg={globalCardBg} widgetId={widget.id} />}
        {widget.type === 'section-header' && <RenderSectionHeader content={stageContent} globalConfig={stageGlobalTypography} widgetId={widget.id} />}
        {widget.type === 'title-basic' && <RenderTitleBasic content={stageContent} widgetId={widget.id} />}
        {widget.type === 'table-of-contents' && (
          <RenderTableOfContents
            content={stageContent}
            availableEntries={tocAvailableEntries}
            emptyMessage={t('builder:tocEmptyHint', { defaultValue: 'Nenhuma entrada no sumário. Marque «Incluir no sumário» em Título âncora ou Cabeçalho.' })}
          />
        )}
        {widget.type === 'chart-widget' && <RenderMadriChart content={stageContent} widgetId={widget.id} />}
        {widget.type === 'flowchart-widget' && <RenderMermaidFlowchart content={stageContent} widgetId={widget.id} />}
        {widget.type === 'asaas-payment' && (
          <RenderAsaasPayment
            content={stageContent}
            t={t}
            gatewayConfigured={asaasGatewayConfigured}
            onOpenGatewaySettings={onOpenAsaasGatewaySettings}
          />
        )}
        {widget.type === 'mercadopago-payment' && (
          <RenderMercadoPagoPayment
            content={stageContent}
            t={t}
            gatewayConfigured={mercadopagoGatewayConfigured}
            onOpenGatewaySettings={onOpenMercadoPagoGatewaySettings}
          />
        )}
        {widget.type === 'page-break' && <RenderPageBreak content={stageContent} t={t} />}
        {widget.type === 'checkpoint' && <RenderCheckpoint content={stageContent} t={t} />}
        {widget.type === 'reinforcement-quiz' && <RenderReinforcementQuiz content={stageContent} t={t} widgetId={widget.id} />}
        {widget.type === 'progress-bar' && <RenderProgressBar content={stageContent} t={t} />}
        {widget.type === 'header-menu' && <RenderHeaderMenu content={stageContent} t={t} />}
        {widget.type === 'site-menu' && (
          <>
            <SiteOnlyCourseNotice />
            <RenderSiteMenu content={stageContent} fixedHint={t('builder:siteMenuFixedHint')} />
          </>
        )}
        {widget.type === 'site-footer' && (
          <>
            <SiteOnlyCourseNotice />
            <RenderSiteFooter content={stageContent} />
          </>
        )}
        {widget.type === 'site-login' && (
          <>
            <SiteOnlyCourseNotice />
            <RenderSiteLogin content={stageContent} widgetId={widget.id} />
          </>
        )}
        {widget.type === 'site-maintenance' && (
          <>
            <SiteOnlyCourseNotice />
            <RenderSiteMaintenance content={stageContent} widgetId={widget.id} />
          </>
        )}
        {widget.type === 'count-up' && <RenderCountUp content={stageContent} />}
        {widget.type === 'comments' && <RenderGeppettoComments t={t} />}
        {widget.type === 'styled-button' && <RenderButton content={stageContent} />}
        {widget.type === 'list-icon' && <RenderListIcon content={stageContent} />}
        {widget.type === 'tabs' && <RenderTabs content={stageContent} t={t} />}
        {widget.type === 'accordion' && <RenderAccordion content={stageContent} t={t} />}
        {widget.type === 'modal-trigger' && <RenderModalTrigger content={stageContent} t={t} />}
        {widget.type === 'carousel' && <RenderCarousel content={stageContent} />}
        {widget.type === 'card-slider' && <RenderCardSlider content={stageContent} />}
        {widget.type === 'card-shop' && <RenderCardShop content={stageContent} />}
        {widget.type === 'video-card-slider' && <RenderVideoCardSlider content={stageContent} />}
        {widget.type === 'image-card' && <RenderImageCard content={stageContent} t={t} />}
        {widget.type === 'social-card' && <RenderSocialCard content={stageContent} t={t} />}
        {widget.type === 'post-it' && <RenderPostIt content={stageContent} t={t} />}
        {widget.type === 'divider' && <RenderDivider content={stageContent} />}
        {widget.type === 'spacer' && <RenderSpacer content={stageContent} t={t} />}
        {widget.type === 'responsive-table' && <RenderResponsiveTable content={stageContent} t={t} />}
        {widget.type === 'image-gallery' && <RenderImageGallery content={stageContent} t={t} />}
        {widget.type === 'blockquote' && <RenderBlockquote content={stageContent} t={t} />}
        {MOODLE_ACTIVITY_TYPES.includes(widget.type) && (
          <RenderMoodleActivityRow content={stageContent} t={t} />
        )}
        {widget.type === 'timeline' && <RenderTimeline content={stageContent} t={t} />}
        {widget.content?.checkpoint?.enabled && ['video', 'text', 'image', 'h5p', 'styled-card', 'section-header', 'title-basic', 'blockquote', 'alert'].includes(widget.type) && (
          <CheckpointIconSvg type={widget.content.checkpoint.iconType || 'check'} done={false} position={widget.content.checkpoint.position || 'top-right'} />
        )}
        </MotionStage>
      </div >
    </>
  );
};

// === MAIN APP COMPONENT ===

function App() {
  const { t, i18n } = useTranslation(['login', 'common', 'builder', 'profile', 'cricket']);
  // Persistência: localStorage (30 dias) se "lembrar" ativo, senão sessionStorage
  const savedUser = (() => {
    try {
      const fromLocal = localStorage.getItem('gepetto_user');
      const fromSession = sessionStorage.getItem('gepetto_user');
      const u = fromLocal || fromSession;
      return u ? JSON.parse(u) : null;
    } catch { return null; }
  })();
  const savedToken = localStorage.getItem('gepetto_token') || sessionStorage.getItem('gepetto_token') || null;
  // Sem plano ativo = sem tenant ou sem user.plan (assinatura ativa)
  const hasActivePlan = (u) => !!(u?.tenant_id && u?.plan);
  const hasBuilderAccess = (u) => hasActivePlan(u) || !!(u?.isCollaborator && u?.plan);
  // Com token: começar em 'checking' e só definir builder/plans/master depois do /auth/me (evita storage desatualizado)
  const [appState, setAppState] = useState(savedToken ? 'checking' : (savedUser ? (savedUser.role === 'super_admin' ? 'master' : (hasBuilderAccess(savedUser) ? 'builder' : 'plans')) : 'login'));
  const [currentUser, setCurrentUser] = useState(savedUser);
  const [authToken, setAuthToken] = useState(savedToken);
  const skipNextAuthCheckRef = useRef(false); // evita revalidar logo após escolher plano (free) e deslogar
  const inviteFlowActiveRef = useRef(false); // convite em processamento — /auth/me não deve mudar appState
  const inviteAcceptInFlightRef = useRef(false); // evita POST duplicado em /invites/:token/accept
  const [courseInfo, setCourseInfo] = useState(() => {
    const d = getHydratedBuilderDraftOnce();
    if (d?.courseInfo) {
      return {
        name: d.courseInfo.name || '',
        id: d.courseInfo.id || '',
        theme: d.courseInfo.theme || 'blue',
        globalConfig: d.courseInfo.globalConfig || {}
      };
    }
    return { name: '', id: '', theme: 'blue' };
  });
  /** Permissões ao abrir projeto como convidado (null = dono / acesso total ao projeto). */
  const [layoutCollabPermissions, setLayoutCollabPermissions] = useState(null);
  const isCanvasReadOnly = !!(layoutCollabPermissions && layoutCollabPermissions.canEdit === false);

  // Login/Register states
  const [loginMode, setLoginMode] = useState('login'); // login | register
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginName, setLoginName] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginSuccessMessage, setLoginSuccessMessage] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => {
    try { return localStorage.getItem('gepetto_token') !== null; } catch { return false; }
  });

  // Dashboard states
  const [projectsList, setProjectsList] = useState([]);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [showNewProjectConfirmModal, setShowNewProjectConfirmModal] = useState(false);
  const [showResetStageConfirmModal, setShowResetStageConfirmModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectParentId, setNewProjectParentId] = useState(null);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderParentId, setNewFolderParentId] = useState(null);
  const [showProjectsModal, setShowProjectsModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [projectToRename, setProjectToRename] = useState(null);
  const [renameProjectName, setRenameProjectName] = useState('');
  const [projectsModalExpanded, setProjectsModalExpanded] = useState(new Set());
  const [dashboardLoading, setDashboardLoading] = useState(false);
  /** Layout em nuvem ao abrir o quadro Cricket (tela dedicada). */
  const [cricketBoardLayoutId, setCricketBoardLayoutId] = useState(null);
  const [cricketDrawerOpen, setCricketDrawerOpen] = useState(false);

  const [activePanel, setActivePanel] = useState(null);
  const [activeInsertIndex, setActiveInsertIndex] = useState(null); // índice para inserir grid (null = fim)
  /** Inserção de grid dentro de uma coluna: escolhe modelo e grava em col.nestedRows */
  const [gridInsertTarget, setGridInsertTarget] = useState(null); // null | { type: 'nested', hostColId, index }
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [activeRowSettings, setActiveRowSettings] = useState(null);
  /** { rowId, colId } — configuração por coluna (slot do grid) */
  const [activeColumnSettings, setActiveColumnSettings] = useState(null);
  const [showGlobalSettings, setShowGlobalSettings] = useState(false);

  const [uiToast, setUiToast] = useState(null);
  const toastTimerRef = useRef(null);
  const showToast = useCallback((message, type = 'warning', ms = 2600) => {
    setUiToast({ message, type });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setUiToast(null), ms);
  }, []);

  const logoutAndClear = useCallback(async () => {
    try {
      const tok = localStorage.getItem('gepetto_token') || sessionStorage.getItem('gepetto_token');
      await endAppAnalyticsSession(API_BASE, tok);
    } catch (_) { /* noop */ }
    clearAppSessionId();
    setCurrentUser(null);
    setAuthToken(null);
    sessionStorage.removeItem('gepetto_user');
    sessionStorage.removeItem('gepetto_token');
    localStorage.removeItem('gepetto_user');
    localStorage.removeItem('gepetto_token');
    localStorage.removeItem('gepetto_rows');
    localStorage.removeItem('gepetto_courseInfo');
    try { Object.keys(localStorage).filter(k => k.startsWith('gepetto_draft_')).forEach(k => localStorage.removeItem(k)); } catch (_) {}
    try { localStorage.removeItem(STORAGE_LAST_OPEN_LAYOUT); } catch (_) {}
    setAppState('login');
    setRows([]);
    setCourseInfo({ name: '', id: '', theme: 'blue' });
  }, []);

  const togglePanel = (panel) => {
    if (isCanvasReadOnly && panel !== 'projetos') {
      showToast(t('builder:canvasReadOnlyHint'), 'info', 4200);
      return;
    }
    if (panel === 'modelos' && activePanel !== panel) {
      setActiveInsertIndex(null);
      setGridInsertTarget(null);
    }
    setActivePanel(activePanel === panel ? null : panel);
  };

  const handleSlotClick = (slotIdx, colIdx, rowId) => {
    if (isCanvasReadOnly) {
      showToast(t('builder:canvasReadOnlyHint'), 'info', 4200);
      return;
    }
    setSelectedSlotId({ rowId, colIdx });
    if (activePanel !== "elementos") {
      setActivePanel("elementos");
    }
  };

  const getDraftKey = (projectId) => `gepetto_draft_${projectId || 'scratch'}`;
  const [rows, setRows] = useState(() => {
    const d = getHydratedBuilderDraftOnce();
    return Array.isArray(d?.rows) ? d.rows : [];
  });
  const [headerWidgets, setHeaderWidgets] = useState(() => {
    const d = getHydratedBuilderDraftOnce();
    return Array.isArray(d?.headerWidgets) ? d.headerWidgets : [];
  });
  // Refs para permitir polling sem depender de "rows/headerWidgets" no dependency array
  const rowsStateRef = useRef(rows);
  const headerWidgetsStateRef = useRef(headerWidgets);
  const courseInfoStateRef = useRef(courseInfo);
  /** Mantém referência estável para agendar save após fechar modais (evita closure obsoleta). */
  const saveLayoutToDBRef = useRef(async () => {});
  useEffect(() => { rowsStateRef.current = rows; }, [rows]);
  useEffect(() => { headerWidgetsStateRef.current = headerWidgets; }, [headerWidgets]);
  useEffect(() => { courseInfoStateRef.current = courseInfo; }, [courseInfo]);

  const titleBasicAnchorsList = useMemo(() => collectTitleBasicAnchorsFromRows(rows), [rows]);
  const tocEntriesList = useMemo(() => collectTocEntriesFromRows(rows), [rows]);
  const microquizCheckpointOptionsList = useMemo(
    () => collectCheckpointOptionsFromLayout(rows, t),
    [rows, t]
  );
  const microquizLayoutSummary = useMemo(() => summarizeMicroquizzesInLayout(rows), [rows]);
  const postItFontUrls = useMemo(
    () => collectPostItGoogleFontUrls(rows, courseInfo.globalConfig, headerWidgets),
    [rows, courseInfo.globalConfig, headerWidgets]
  );

  const [showExportModal, setShowExportModal] = useState(false);
  const [exportModalKind, setExportModalKind] = useState('moodle');
  const [isExporting, setIsExporting] = useState(false);
  const [exportKind, setExportKind] = useState('');
  const [isPublishingSite, setIsPublishingSite] = useState(false);
  const [isPublishingMoodle, setIsPublishingMoodle] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  const courseWrapperRef = useRef(null);

  // Undo/Redo: histórico das últimas ações (até 50 estados)
  const HISTORY_MAX = 50;
  const historyRef = useRef([]);
  const historyIndexRef = useRef(-1);
  const isRestoringRef = useRef(false);
  const pushHistory = useCallback(() => {
    if (isRestoringRef.current) return;
    const snap = {
      rows: JSON.parse(JSON.stringify(rows)),
      headerWidgets: JSON.parse(JSON.stringify(headerWidgets)),
      courseInfo: JSON.parse(JSON.stringify(courseInfo))
    };
    const arr = historyRef.current;
    const idx = historyIndexRef.current;
    if (idx < arr.length - 1) arr.splice(idx + 1);
    arr.push(snap);
    if (arr.length > HISTORY_MAX) {
      arr.shift();
      historyIndexRef.current = arr.length - 1;
    } else {
      historyIndexRef.current = arr.length - 1;
    }
    setHistoryState({ canUndo: arr.length > 0 && historyIndexRef.current > 0, canRedo: false });
  }, [rows, headerWidgets, courseInfo]);
  const undo = useCallback(() => {
    const arr = historyRef.current;
    const idx = historyIndexRef.current;
    if (idx <= 0) return;
    const prev = arr[idx - 1];
    isRestoringRef.current = true;
    setRows(prev.rows);
    setHeaderWidgets(prev.headerWidgets || []);
    setCourseInfo(prev.courseInfo || { name: '', id: '', theme: 'blue' });
    historyIndexRef.current = idx - 1;
    setHistoryState({ canUndo: idx - 1 > 0, canRedo: true });
    setTimeout(() => { isRestoringRef.current = false; }, 0);
  }, []);
  const redo = useCallback(() => {
    const arr = historyRef.current;
    const idx = historyIndexRef.current;
    if (idx >= arr.length - 1) return;
    const next = arr[idx + 1];
    isRestoringRef.current = true;
    setRows(next.rows);
    setHeaderWidgets(next.headerWidgets || []);
    setCourseInfo(next.courseInfo || { name: '', id: '', theme: 'blue' });
    historyIndexRef.current = idx + 1;
    setHistoryState({ canUndo: true, canRedo: idx + 1 < arr.length - 1 });
    setTimeout(() => { isRestoringRef.current = false; }, 0);
  }, []);
  const [historyState, setHistoryState] = useState({ canUndo: false, canRedo: false });

  // States de Live Preview e Visualização
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  /** 'desktop' | 'mobile' — largura do iframe no modal Visualizar */
  const [previewViewport, setPreviewViewport] = useState('desktop');
  /** 'desktop' | 'mobile' — modo de edição do palco (botão na topbar) */
  const [editBreakpoint, setEditBreakpoint] = useState(EDIT_BREAKPOINT_DESKTOP);

  const stageGlobalConfig = useMemo(
    () => resolveGlobalConfigForEdit(courseInfo.globalConfig || {}, editBreakpoint),
    [courseInfo.globalConfig, editBreakpoint]
  );

  // === Banco de Dados Lógica ===
  const [isSaving, setIsSaving] = useState(false);

  // Feature Flags (configuração global de premium/trial do admin)
  const [featureFlags, setFeatureFlags] = useState({
    expert: [], premium: [], trial: [], premiumGrids: [], trialGrids: [], premiumModules: [], trialModules: [],
    elementTiers: {}, gridTiers: {}, moduleTiers: {},
  });
  /** null = ainda não carregou / sem tenant; false = sem chave Asaas; true = configurado */
  const [asaasGatewayConfigured, setAsaasGatewayConfigured] = useState(null);
  /** Idem para Mercado Pago (elemento mercadopago-payment). */
  const [mercadopagoGatewayConfigured, setMercadopagoGatewayConfigured] = useState(null);
  const [profileEntryTab, setProfileEntryTab] = useState(null);
  const [profilePaymentGatewayProvider, setProfilePaymentGatewayProvider] = useState(null);

  const [darkMode, setDarkMode] = useState(() => {
    try { return localStorage.getItem('gepetto_dark_mode') !== '0'; } catch { return true; }
  });
  const [plansList] = useState([]);
  const [presenceLocks, setPresenceLocks] = useState({});
  const [presenceOnline, setPresenceOnline] = useState([]);
  const heldWidgetLocksRef = useRef(new Set());
  const currentUserId = currentUser?.id || currentUser?.userId || null;
  const [activeEditKey, setActiveEditKey] = useState(null);

  useEffect(() => {
    if (!isCanvasReadOnly) return;
    setActivePanel(null);
    setActiveInsertIndex(null);
    setGridInsertTarget(null);
    setActiveRowSettings(null);
    setActiveColumnSettings(null);
    setShowGlobalSettings(false);
    setSelectedSlotId(null);
    setActiveEditKey(null);
  }, [isCanvasReadOnly]);

  /** Abre o painel GRID a partir do palco e orienta o usuário (toast + destaque + faixa no palco). */
  const openGridCatalogForStageInsert = useCallback((spec) => {
    if (isCanvasReadOnly) {
      showToast(t('builder:canvasReadOnlyHint'), 'info', 4200);
      return;
    }
    if (spec?.nested) {
      setGridInsertTarget({ type: 'nested', hostColId: spec.hostColId, index: spec.index });
      setActiveInsertIndex(null);
    } else {
      setActiveInsertIndex(spec.insertIndex);
      setGridInsertTarget(null);
    }
    setActivePanel('modelos');
    showToast(t('builder:gridPickerOpenedHint'), 'info', 5200);
  }, [showToast, t, isCanvasReadOnly]);

  const guidedGridPickerActive = useMemo(
    () => activePanel === 'modelos' && (activeInsertIndex !== null || gridInsertTarget !== null),
    [activePanel, activeInsertIndex, gridInsertTarget]
  );

  const acquireWidgetLock = useCallback(async (widgetId) => {
    if (!authToken || !courseInfo?.id || !widgetId) return true;
    if (currentUser?.id && isFreeLocalProjectId(currentUser.id, String(courseInfo.id))) return true;
    try {
      const res = await fetch(`${API_BASE}/layouts/${courseInfo.id}/lock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ widget_id: widgetId })
      });
      if (res.ok) {
        heldWidgetLocksRef.current.add(widgetId);
        return true;
      }
      const data = await res.json().catch(() => ({}));
      showToast(data?.error || t('builder:widgetLockedByAnother', { defaultValue: 'Este elemento está sendo editado por outro colaborador.' }), 'warning');
      return false;
    } catch (_) {
      showToast(t('builder:lockUnavailable', { defaultValue: 'Não foi possível validar lock agora. Tente novamente.' }), 'error');
      return false;
    }
  }, [authToken, courseInfo?.id, currentUser?.id, t, showToast]);

  const releaseWidgetLock = useCallback(async (widgetId) => {
    if (!authToken || !courseInfo?.id || !widgetId) return;
    if (currentUser?.id && isFreeLocalProjectId(currentUser.id, String(courseInfo.id))) return;
    if (!heldWidgetLocksRef.current.has(widgetId)) return;
    heldWidgetLocksRef.current.delete(widgetId);
    try {
      await fetch(`${API_BASE}/layouts/${courseInfo.id}/lock/${widgetId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
    } catch (_) {
      // best effort
    }
  }, [authToken, courseInfo?.id, currentUser?.id]);

  // Presença/locks v1 por polling.
  useEffect(() => {
    if (appState !== 'builder' || !authToken || !courseInfo?.id) {
      setPresenceLocks({});
      setPresenceOnline([]);
      return;
    }
    if (currentUser?.id && isFreeLocalProjectId(currentUser.id, String(courseInfo.id))) {
      setPresenceLocks({});
      setPresenceOnline([]);
      return;
    }

    let cancelled = false;
    const stableLocksKey = (m) => {
      const keys = Object.keys(m || {}).sort();
      const o = {};
      keys.forEach((k) => { o[k] = m[k]; });
      return JSON.stringify(o);
    };
    const fetchPresence = async () => {
      try {
        const res = await fetch(`${API_BASE}/layouts/${courseInfo.id}/presence`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;

        const locksArr = Array.isArray(data?.locks) ? data.locks : [];
        const lockMap = {};
        locksArr.forEach(l => {
          if (l?.widget_id) lockMap[l.widget_id] = l;
        });
        const onlineNext = Array.isArray(data?.online) ? data.online : [];
        setPresenceLocks((prev) => (stableLocksKey(prev) === stableLocksKey(lockMap) ? prev : lockMap));
        setPresenceOnline((prev) => (JSON.stringify(prev) === JSON.stringify(onlineNext) ? prev : onlineNext));
      } catch (_) {
        if (!cancelled) {
          setPresenceLocks((prev) => (Object.keys(prev).length === 0 ? prev : {}));
          setPresenceOnline((prev) => (prev.length === 0 ? prev : []));
        }
      }
    };

    fetchPresence();
    const it = setInterval(fetchPresence, 5000);
    return () => {
      cancelled = true;
      clearInterval(it);
    };
  }, [appState, authToken, courseInfo?.id, currentUser?.id]);

  // Libera locks retidos ao trocar de projeto/sair do builder.
  useEffect(() => {
    return () => {
      const held = Array.from(heldWidgetLocksRef.current);
      heldWidgetLocksRef.current.clear();
      held.forEach((wid) => {
        fetch(`${API_BASE}/layouts/${courseInfo?.id || ''}/lock/${wid}`, {
          method: 'DELETE',
          headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {}
        }).catch(() => { });
      });
    };
  }, [authToken, courseInfo?.id]);

  useEffect(() => {
    fetch(`${API_BASE}/feature-flags`)
      .then(r => r.json())
      .then(d => {
        if (!d.flags) return;
        setFeatureFlags(normalizeFeatureFlags(d.flags));
      })
      .catch(() => { });
  }, []);

  useEffect(() => {
    if (appState !== 'builder' || !authToken || !currentUser?.tenant_id) {
      setAsaasGatewayConfigured(null);
      return;
    }
    let cancelled = false;
    fetch(`${API_BASE}/tenant/asaas-gateway-configured`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d || typeof d.configured !== 'boolean') return;
        setAsaasGatewayConfigured(!!d.configured);
      })
      .catch(() => {
        if (!cancelled) setAsaasGatewayConfigured(null);
      });
    return () => { cancelled = true; };
  }, [appState, authToken, currentUser?.tenant_id]);

  useEffect(() => {
    if (appState !== 'builder' || !authToken || !currentUser?.tenant_id) {
      setMercadopagoGatewayConfigured(null);
      return;
    }
    let cancelled = false;
    fetch(`${API_BASE}/tenant/mercadopago-gateway-configured`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d || typeof d.configured !== 'boolean') return;
        setMercadopagoGatewayConfigured(!!d.configured);
      })
      .catch(() => {
        if (!cancelled) setMercadopagoGatewayConfigured(null);
      });
    return () => { cancelled = true; };
  }, [appState, authToken, currentUser?.tenant_id]);

  const openBuilderPaymentGatewaySettings = useCallback((providerTab = 'asaas') => {
    setProfilePaymentGatewayProvider(providerTab === 'mercadopago' ? 'mercadopago' : 'asaas');
    setProfileEntryTab('gateways');
    setAppState('profile');
  }, []);

  // Modo escuro: aplica classe no html e persiste preferência
  useEffect(() => {
    const html = document.documentElement;
    if (darkMode) {
      html.classList.add('dark');
      try { localStorage.setItem('gepetto_dark_mode', '1'); } catch { }
    } else {
      html.classList.remove('dark');
      try { localStorage.setItem('gepetto_dark_mode', '0'); } catch { }
    }
  }, [darkMode]);

  // Builder trava scroll no documento; telas secundárias precisam liberar e resetar posição (evita “zoom”/scroll horizontal no mobile)
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const isBuilder = appState === 'builder';
    if (isBuilder) {
      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
    } else {
      html.style.overflow = '';
      body.style.overflow = '';
      window.scrollTo(0, 0);
    }
    return () => {
      html.style.overflow = '';
      body.style.overflow = '';
    };
  }, [appState]);

  // Convite de colaborador: /invite/TOKEN ou ?invite_token=TOKEN
  useEffect(() => {
    const inviteToken = getInviteTokenFromUrl();
    if (!inviteToken) return;

    const effectiveToken = authToken || getStoredAuthToken();

    // Sem sessão: guardar convite e ir para login (não assumir que o usuário já está autenticado)
    if (!effectiveToken) {
      try { sessionStorage.setItem('gepetto_invite_token', inviteToken); } catch {}
      setAppState('login');
      setLoginMode('login');
      setLoginError('');
      setLoginSuccessMessage(t('login:invitePending', { defaultValue: 'Você tem um convite pendente. Faça login com o e-mail e a senha que enviamos.' }));
      return;
    }

    // Storage tem token mas o estado React ainda não hidratou — sincronizar e reexecutar
    if (!authToken && effectiveToken) {
      setAuthToken(effectiveToken);
      setAppState('checking');
      return;
    }

    if (inviteAcceptInFlightRef.current) return;

    inviteAcceptInFlightRef.current = true;
    inviteFlowActiveRef.current = true;
    setAppState('checking');

    let cancelled = false;

    const restoreAppStateFromSession = async (token) => {
      try {
        const meData = await fetch(`${API_BASE}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null);
        if (!meData?.user) {
          setAppState('login');
          return;
        }
        setCurrentUser(meData.user);
        const storage = localStorage.getItem('gepetto_token') ? localStorage : sessionStorage;
        storage.setItem('gepetto_user', JSON.stringify(meData.user));
        if (meData.featureFlags) setFeatureFlags(normalizeFeatureFlags(meData.featureFlags));
        const next = meData.user.role === 'super_admin' ? 'master' : (hasBuilderAccess(meData.user) ? 'builder' : 'plans');
        setAppState(next);
      } catch {
        setAppState('login');
      }
    };

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/invites/${inviteToken}/accept`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${effectiveToken}`, 'Content-Type': 'application/json' },
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;

        if (!res.ok) {
          inviteFlowActiveRef.current = false;
          inviteAcceptInFlightRef.current = false;

          if (res.status === 403) {
            // Conta logada não é a do convite — deslogar e pedir login com o e-mail correto
            try { sessionStorage.setItem('gepetto_invite_token', inviteToken); } catch {}
            setCurrentUser(null);
            setAuthToken(null);
            sessionStorage.removeItem('gepetto_user');
            sessionStorage.removeItem('gepetto_token');
            localStorage.removeItem('gepetto_user');
            localStorage.removeItem('gepetto_token');
            clearAppSessionId();
            window.history.replaceState({}, '', '/');
            setAppState('login');
            setLoginMode('login');
            setLoginSuccessMessage('');
            setLoginError(data.error || t('login:inviteWrongEmail', { defaultValue: 'Este convite foi enviado para outro e-mail. Saia e entre com o e-mail que recebeu o convite.' }));
            return;
          }

          try { sessionStorage.removeItem('gepetto_invite_token'); } catch {}
          window.history.replaceState({}, '', '/');
          const failMsg = data.error || (res.status === 404
            ? t('login:inviteInvalid', { defaultValue: 'Convite inválido ou expirado.' })
            : t('login:inviteAcceptError', { defaultValue: 'Não foi possível aceitar o convite.' }));
          showToast(failMsg, 'warning', 4000);
          await restoreAppStateFromSession(effectiveToken);
          return;
        }

        try { sessionStorage.removeItem('gepetto_invite_token'); } catch {}
        window.history.replaceState({}, '', '/');

        if (data.layoutId) {
          const meData = await fetch(`${API_BASE}/auth/me`, { headers: { Authorization: `Bearer ${effectiveToken}` } })
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null);
          if (cancelled) return;
          if (meData?.user) {
            setCurrentUser(meData.user);
            const storage = localStorage.getItem('gepetto_token') ? localStorage : sessionStorage;
            storage.setItem('gepetto_user', JSON.stringify(meData.user));
            if (meData.featureFlags) setFeatureFlags(normalizeFeatureFlags(meData.featureFlags));
          }
          inviteFlowActiveRef.current = false;
          inviteAcceptInFlightRef.current = false;
          openProject({ id: data.layoutId, course_name: '' });
          showToast(t('collab:inviteAccepted', { defaultValue: 'Convite aceito! Você já pode acessar o projeto.' }), 'success', 3200);
        } else {
          inviteFlowActiveRef.current = false;
          inviteAcceptInFlightRef.current = false;
          await restoreAppStateFromSession(effectiveToken);
        }
      } catch {
        if (!cancelled) {
          inviteFlowActiveRef.current = false;
          inviteAcceptInFlightRef.current = false;
          try { sessionStorage.removeItem('gepetto_invite_token'); } catch {}
          window.history.replaceState({}, '', '/');
          showToast(t('login:inviteAcceptError', { defaultValue: 'Não foi possível aceitar o convite.' }), 'warning', 4000);
          await restoreAppStateFromSession(effectiveToken);
        }
      }
    })();

    return () => {
      cancelled = true;
      inviteAcceptInFlightRef.current = false;
      inviteFlowActiveRef.current = false;
    };
  }, [authToken, t, showToast]);

  // Após login, verificar se há invite_token pendente e redirecionar
  useEffect(() => {
    if (!authToken) return;
    try {
      const stored = sessionStorage.getItem('gepetto_invite_token');
      if (!stored) return;
      const path = window.location.pathname.replace(/\/$/, '');
      if (path.startsWith('/invite/')) return;
      const params = new URLSearchParams(window.location.search);
      if (params.get('invite_token')) return;
      window.location.href = `${window.location.origin}?invite_token=${stored}`;
    } catch {}
  }, [authToken]);

  // Página /verify-email?token=xxx: redireciona para a API validar e depois volta com ?email_verified=1
  useEffect(() => {
    const path = window.location.pathname.replace(/\/$/, '');
    if (path !== '/verify-email') return;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) {
      const apiBase = API_BASE.replace(/\/$/, '');
      window.location.href = `${apiBase}/auth/verify-email?token=${encodeURIComponent(token)}`;
    }
  }, []);

  // Sempre validar usuário no servidor ao carregar (evita estado desatualizado do storage)
  useEffect(() => {
    if (!authToken) return;
    if (skipNextAuthCheckRef.current) {
      skipNextAuthCheckRef.current = false;
      return; // acabou de vir da escolha de plano free; não revalidar agora para não deslogar
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get('token') || params.get('tenant_token')) return; // deixa o outro effect tratar
    fetch(`${API_BASE}/auth/me`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((r) => {
        if (!r.ok) {
          setCurrentUser(null);
          setAuthToken(null);
          setAppState('login');
          sessionStorage.removeItem('gepetto_user');
          sessionStorage.removeItem('gepetto_token');
          localStorage.removeItem('gepetto_user');
          localStorage.removeItem('gepetto_token');
          clearAppSessionId();
          return null;
        }
        return r.json();
      })
      .then((data) => {
        if (!data?.user) return;
        setCurrentUser(data.user);
        const storage = localStorage.getItem('gepetto_token') ? localStorage : sessionStorage;
        storage.setItem('gepetto_user', JSON.stringify(data.user));
        if (data.featureFlags) setFeatureFlags(normalizeFeatureFlags(data.featureFlags));
        // Convite em andamento controla appState (evita flash de login/planos antes de abrir o projeto)
        if (inviteFlowActiveRef.current) return;
        const next = data.user.role === 'super_admin' ? 'master' : (hasBuilderAccess(data.user) ? 'builder' : 'plans');
        setAppState(next);
      })
      .catch(() => {
        setCurrentUser(null);
        setAuthToken(null);
        setAppState('login');
        sessionStorage.removeItem('gepetto_user');
        sessionStorage.removeItem('gepetto_token');
        localStorage.removeItem('gepetto_user');
        localStorage.removeItem('gepetto_token');
        clearAppSessionId();
      });
  }, [authToken]);

  useEffect(() => {
    if (!authToken || !currentUser?.id) return;
    const sessionId = getStoredAppSessionId();
    if (!sessionId) return;
    const ping = () => {
      fetch(`${API_BASE}/analytics/session-ping`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ sessionId }),
      }).catch(() => {});
    };
    ping();
    const id = setInterval(ping, 60 * 1000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') ping();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [authToken, currentUser?.id]);

  // Defensivo: se estiver no builder mas usuário não tem acesso (plano ou colaborador), forçar tela de planos
  useEffect(() => {
    if (appState === 'builder' && currentUser && currentUser.role !== 'super_admin' && !hasBuilderAccess(currentUser)) {
      setAppState('plans');
    }
  }, [appState, currentUser]);

  useEffect(() => {
    if (appState === 'cricket' && !cricketBoardLayoutId) {
      setAppState('builder');
    }
  }, [appState, cricketBoardLayoutId]);

  useEffect(() => {
    setCricketDrawerOpen(false);
  }, [courseInfo?.id]);

  // Retorno do checkout (Asaas/Stripe): sincroniza pagamento no Asaas e atualiza usuário
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    const path = (window.location.pathname || '/').replace(/\/$/, '') || '/';
    if (checkout !== 'success' || !authToken) return;

    if (path === '/profile') setAppState('profile');

    const finish = (user) => {
      if (!user) return;
      setCurrentUser(user);
      const storage = localStorage.getItem('gepetto_token') ? localStorage : sessionStorage;
      storage.setItem('gepetto_user', JSON.stringify(user));
      const cleanPath = path === '/profile' ? '/profile' : '/';
      window.history.replaceState({}, '', cleanPath);
      if (path !== '/profile') {
        setAppState(hasBuilderAccess(user) ? 'builder' : 'plans');
      }
    };

    fetch(`${API_BASE}/checkout/confirm-return`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .catch(() => null)
      .then(() => fetch(`${API_BASE}/auth/me`, { headers: { Authorization: `Bearer ${authToken}` } }))
      .then((r) => (r && r.ok ? r.json() : null))
      .then((data) => finish(data?.user))
      .catch(() => { });
  }, [authToken]);

  // Auto-login via URL param (tenant_token ou token do Google OAuth) + mensagem email_verified
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token') || params.get('tenant_token');
    const error = params.get('error');
    const emailVerified = params.get('email_verified');
    const rememberOAuth = params.get('remember') === '1';
    const oauthSessionId = params.get('session_id');
    if (emailVerified === '1') {
      window.history.replaceState({}, '', window.location.pathname || '/');
      setAppState('login');
      setLoginSuccessMessage('E-mail confirmado! Agora você pode entrar com sua senha.');
      return;
    }
    if (error) {
      window.history.replaceState({}, '', window.location.pathname);
      setAppState('login');
      setLoginError(error === 'google_oauth_not_configured' ? 'Google OAuth não configurado.' : error === 'google_no_email' ? 'Conta Google sem email.' : error === 'token_invalid_or_expired' ? 'Link inválido ou expirado. Solicite um novo e-mail de verificação.' : 'Falha no login com Google.');
      return;
    }
    if (!token) return;

    window.history.replaceState({}, '', window.location.pathname);

    fetch(`${API_BASE}/auth/me`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => {
        if (data.user) {
          if (rememberOAuth) {
            localStorage.setItem('gepetto_user', JSON.stringify(data.user));
            localStorage.setItem('gepetto_token', token);
            sessionStorage.removeItem('gepetto_user');
            sessionStorage.removeItem('gepetto_token');
            persistAppSessionId(oauthSessionId);
          } else {
            sessionStorage.setItem('gepetto_user', JSON.stringify(data.user));
            sessionStorage.setItem('gepetto_token', token);
            localStorage.removeItem('gepetto_user');
            localStorage.removeItem('gepetto_token');
            persistAppSessionId(oauthSessionId);
          }
          setCurrentUser(data.user);
          setAuthToken(token);
          setAppState(data.user.role === 'super_admin'
            ? 'master'
            : (hasBuilderAccess(data.user) ? 'builder' : 'plans'));
          if (data.featureFlags) setFeatureFlags(normalizeFeatureFlags(data.featureFlags));
        }
      })
      .catch(() => { });
  }, []);

  // Inicializa histórico undo/redo com o estado atual (uma vez)
  const historyInitializedRef = useRef(false);
  // Snapshot do último estado salvo no servidor (usado para calcular "mudanças" por widget)
  const savedSnapshotRef = useRef(null);
  useEffect(() => {
    if (appState !== 'builder' || historyInitializedRef.current) return;
    // Se há projeto aberto, loadLayoutFromDB vai inicializar o histórico após carregar
    if (courseInfo.id) return;
    historyInitializedRef.current = true;
    const snap = { rows: JSON.parse(JSON.stringify(rows)), headerWidgets: JSON.parse(JSON.stringify(headerWidgets)), courseInfo: JSON.parse(JSON.stringify(courseInfo)) };
    historyRef.current = [snap];
    historyIndexRef.current = 0;
  }, [appState, rows, headerWidgets, courseInfo]);

  // Carrega lista de projetos quando entra no builder (para o modal "Ver projetos")
  useEffect(() => {
    if (appState === 'builder' && currentUser) {
      loadProjectsList();
    }
  }, [appState, currentUser]);

  // Carrega o layout quando entra no builder
  useEffect(() => {
    if (appState === 'builder' && courseInfo.id) {
      loadLayoutFromDB(courseInfo.id);
    }
  }, [appState, courseInfo.id]);

  // Autosave: salva no localStorage sempre que houver edições (com ou sem projeto aberto)
  useEffect(() => {
    if (appState !== 'builder') return;
    const t = setTimeout(() => {
      try {
        const key = getDraftKey(courseInfo.id);
        const payload = { rows, headerWidgets, courseInfo: { name: courseInfo.name, id: courseInfo.id, globalConfig: courseInfo.globalConfig } };
        localStorage.setItem(key, JSON.stringify(payload));
        if (courseInfo.id) persistLastOpenLayoutMeta(courseInfo.id, courseInfo.name);
        else persistLastOpenLayoutMeta(null, '');
      } catch (_) {}
    }, 1500);
    return () => clearTimeout(t);
  }, [appState, courseInfo.id, courseInfo.name, rows, headerWidgets, courseInfo.globalConfig]);

  // Atalhos Ctrl+Z (desfazer) e Ctrl+Shift+Z / Ctrl+Y (refazer)
  useEffect(() => {
    if (appState !== 'builder' || isCanvasReadOnly) return;
    const onKeyDown = (e) => {
      const target = e.target;
      const isInput = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (isInput) return; // Deixa o navegador tratar undo/redo em campos de texto
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'z' || e.key === 'Z') {
          e.preventDefault();
          if (e.shiftKey) redo();
          else undo();
        }
        if (e.key === 'y') {
          e.preventDefault();
          redo();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [appState, undo, redo, isCanvasReadOnly]);

  // === Auth Functions ===
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      let recaptchaToken = null;
      try {
        const configRes = await fetch(`${API_BASE}/config/recaptcha`);
        const config = await configRes.json();
        const siteKey = config?.siteKey || '';
        if (siteKey) recaptchaToken = await getRecaptchaToken('login', siteKey);
      } catch (_) { }
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword, rememberMe, recaptchaToken: recaptchaToken || undefined })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setCurrentUser(data.user);
        setAuthToken(data.token);
        const storage = rememberMe ? localStorage : sessionStorage;
        storage.setItem('gepetto_user', JSON.stringify(data.user));
        storage.setItem('gepetto_token', data.token);
        persistAppSessionId(data.sessionId);
        if (!rememberMe) {
          localStorage.removeItem('gepetto_user');
          localStorage.removeItem('gepetto_token');
        }
        // Se o login foi motivado por convite, não mandar para "Planos" antes de aceitar o convite.
        // O effect de /invite/TOKEN + sessionStorage('gepetto_invite_token') cuidará de aceitar e abrir o projeto.
        let hasPendingInvite = false;
        try {
          const params = new URLSearchParams(window.location.search);
          const path = window.location.pathname.replace(/\/$/, '');
          hasPendingInvite = !!(sessionStorage.getItem('gepetto_invite_token') || params.get('invite_token') || path.startsWith('/invite/'));
        } catch (_) { }

        setAppState(
          hasPendingInvite
            ? 'checking'
            : (data.user.role === 'super_admin'
              ? 'master'
              : (hasBuilderAccess(data.user) ? 'builder' : 'plans'))
        );
        setLoginEmail('');
        setLoginPassword('');
      } else {
        setLoginError(data.error || t('login:loginError'));
      }
    } catch (err) {
      setLoginError(t('login:serverOffline'));
    }
    setLoginLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      let recaptchaToken = null;
      try {
        const configRes = await fetch(`${API_BASE}/config/recaptcha`);
        const config = await configRes.json();
        const siteKey = config?.siteKey || '';
        if (siteKey) recaptchaToken = await getRecaptchaToken('register', siteKey);
      } catch (_) { }
      const res = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: loginName, email: loginEmail, password: loginPassword, recaptchaToken: recaptchaToken || undefined })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setLoginError('');
        setLoginSuccessMessage(data.message || 'Conta criada. Verifique seu e-mail e clique no link para ativar.');
        setLoginEmail('');
        setLoginPassword('');
        setLoginName('');
        setLoginMode('login');
      } else {
        setLoginError(data.error || t('login:registerError'));
      }
    } catch (err) {
      setLoginError(t('login:serverOffline'));
    }
    setLoginLoading(false);
  };

  const handleLogout = logoutAndClear;

  // === Dashboard Functions ===
  const loadProjectsList = async () => {
    setDashboardLoading(true);
    try {
      const res = await fetch(`${API_BASE}/layouts?user_id=${currentUser.id}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const data = await res.json();
      const mergeCloudWithFreeLocal = (cloudList) => {
        const uidLocal = currentUser?.id;
        if (!uidLocal || !Array.isArray(cloudList)) return cloudList || [];
        const byId = new Map(cloudList.map((p) => [p.id, p]));
        const out = [...cloudList];
        for (const it of listFreeLocalProjectsForUser(uidLocal)) {
          if (!it?.id || byId.has(it.id)) continue;
          out.unshift({
            id: it.id,
            course_name: it.course_name,
            type: 'canvas',
            local_only: true,
            expires_at: it.expiresAt,
            updated_at: it.createdAt,
          });
          byId.set(it.id, true);
        }
        return out;
      };
      if (res.ok && Array.isArray(data.projects)) {
        setProjectsList(mergeCloudWithFreeLocal(data.projects));
      } else if (!res.ok) {
        console.error('Erro ao carregar projetos:', data?.error || res.status);
        // Não sobrescreve com [] em caso de erro — mantém lista anterior
      } else {
        setProjectsList([]);
      }
    } catch (err) {
      console.error('Erro ao carregar projetos:', err);
      // Em falha de rede, não sobrescreve a lista
    }
    setDashboardLoading(false);
  };

  const createNewProject = async () => {
    if (!newProjectName.trim()) return;
    const parent = newProjectParentId || null;
    const projectId = uid();
    const emptyLayout = { rows: [], globalConfig: {} };

    /** Plano sem slot na nuvem (ex.: Free): projeto só no navegador + lista 24h (freeLocalProjects). */
    if (!planAllowsServerSave(currentUser)) {
      if (!currentUser?.id) {
        alert(t('builder:saveError'));
        return;
      }
      registerFreeLocalProject(currentUser.id, { id: projectId, course_name: newProjectName.trim() });
      reportFreeProjectCreated(authToken, projectId, newProjectName.trim());
      try {
        localStorage.setItem(getDraftKey(projectId), JSON.stringify({
          rows: [],
          headerWidgets: [],
          courseInfo: { name: newProjectName.trim(), id: projectId, globalConfig: {} },
        }));
      } catch (_) { /* noop */ }
      setShowNewProjectModal(false);
      setNewProjectName('');
      setNewProjectParentId(null);
      loadProjectsList();
      setLayoutCollabPermissions(null);
      setCourseInfo({ name: newProjectName.trim(), id: projectId, theme: 'blue' });
      persistLastOpenLayoutMeta(projectId, newProjectName.trim());
      try { localStorage.removeItem('gepetto_draft_scratch'); } catch (_) { /* noop */ }
      setRows([]);
      setHeaderWidgets([]);
      const snap = {
        rows: [],
        headerWidgets: [],
        courseInfo: { name: newProjectName.trim(), id: projectId, theme: 'blue', globalConfig: {} },
      };
      savedSnapshotRef.current = {
        rows: [],
        headerWidgets: [],
        courseInfo: JSON.parse(JSON.stringify(snap.courseInfo)),
      };
      historyInitializedRef.current = true;
      historyRef.current = [snap];
      historyIndexRef.current = 0;
      setHistoryState({ canUndo: false, canRedo: false });
      setAppState('builder');
      setActivePanel('modelos');
      showToast(t('builder:freeLocalSavedToast'), 'success', 2800);
      return;
    }

    try {
      const r = await fetch(`${API_BASE}/layouts/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          id: projectId,
          course_name: newProjectName.trim(),
          schema_json: JSON.stringify(emptyLayout),
          parent_id: parent || null,
          type: 'canvas',
          ...(currentUser?.tenant_access === 'collab' && currentUser?.tenant_id
            ? { host_tenant_id: currentUser.tenant_id }
            : {}),
        })
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        alert(t('builder:saveError') + ': ' + (d.error || 'Dados incompletos.'));
        return;
      }
      setShowNewProjectModal(false);
      setNewProjectName('');
      setNewProjectParentId(null);
      loadProjectsList();
      setLayoutCollabPermissions(null);
      setCourseInfo({ name: newProjectName.trim(), id: projectId, theme: 'blue' });
      persistLastOpenLayoutMeta(projectId, newProjectName.trim());
      try { localStorage.removeItem('gepetto_draft_scratch'); } catch (_) { /* noop */ }
      setRows([]);
      setHeaderWidgets([]);
      setAppState('builder');
      setActivePanel('modelos');
    } catch (err) {
      alert(t('builder:backendConnectionError'));
    }
  };

  const createNewFolder = async () => {
    if (!newFolderName.trim()) return;
    const parent = newFolderParentId || null;
    try {
      const r = await fetch(`${API_BASE}/layouts/folder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          course_name: newFolderName.trim(),
          parent_id: parent || null,
          ...(currentUser?.tenant_access === 'collab' && currentUser?.tenant_id
            ? { host_tenant_id: currentUser.tenant_id }
            : {}),
        })
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        alert(d.error || t('builder:folderMigrationRequired'));
        return;
      }
      setShowNewFolderModal(false);
      setNewFolderName('');
      setNewFolderParentId(null);
      loadProjectsList();
    } catch (err) {
      alert(t('builder:backendConnectionError'));
    }
  };

  const moveProject = async (projectId, targetParentId) => {
    if (currentUser?.id && isFreeLocalProjectId(currentUser.id, projectId)) {
      showToast(t('builder:freeLocalMoveBlocked'), 'warning', 4200);
      return;
    }
    try {
      const r = await fetch(`${API_BASE}/layouts/${projectId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ parent_id: targetParentId })
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Erro ao mover');
      loadProjectsList();
    } catch (err) {
      alert(t('builder:moveProjectError', { defaultValue: 'Erro ao mover projeto.' }));
    }
  };

  const openProject = (project) => {
    try { localStorage.removeItem('gepetto_draft_scratch'); } catch (_) { /* evita scratch antigo “bloquear” id após F5 */ }
    persistLastOpenLayoutMeta(project.id, project.course_name);
    setLayoutCollabPermissions(null);
    setCourseInfo({ name: project.course_name, id: project.id, theme: 'blue' });
    setRows([]);
    setHeaderWidgets([]);
    setActivePanel(null);
    setAppState('builder');
    historyRef.current = [];
    historyIndexRef.current = -1;
    historyInitializedRef.current = false;
    // Reabrir o *mesmo* projeto não altera `courseInfo.id`, então o efeito [appState, courseInfo.id] não dispara
    // e o palco ficava vazio até o autosave gravar rows vazios no draft (crítico no plano Free). Sempre reidratar.
    void loadLayoutFromDB(String(project.id));
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm(t('builder:deleteProjectConfirm'))) return;
    if (currentUser?.id && isFreeLocalProjectId(currentUser.id, projectId)) {
      try {
        removeFreeLocalProject(currentUser.id, projectId);
        localStorage.removeItem(getDraftKey(projectId));
        const metaRaw = localStorage.getItem(STORAGE_LAST_OPEN_LAYOUT);
        if (metaRaw) {
          const meta = JSON.parse(metaRaw);
          if (meta?.id === projectId) persistLastOpenLayoutMeta(null, '');
        }
      } catch (_) { /* noop */ }
      loadProjectsList();
      if (courseInfo.id === projectId) {
        goBackToDashboard();
      }
      return;
    }
    try {
      const r = await fetch(`${API_BASE}/layouts/${projectId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || 'Erro ao deletar');
      }
      loadProjectsList();
      try {
        const metaRaw = localStorage.getItem(STORAGE_LAST_OPEN_LAYOUT);
        if (metaRaw) {
          const meta = JSON.parse(metaRaw);
          if (meta?.id === projectId) persistLastOpenLayoutMeta(null, '');
        }
      } catch (_) { /* noop */ }
      try { localStorage.removeItem(`gepetto_draft_${projectId}`); } catch (_) { /* noop */ }
    } catch (err) {
      alert(t('builder:deleteProjectError'));
    }
  };

  const renameProject = async (projectId, newName) => {
    if (!newName || !newName.trim()) return;
    const name = newName.trim();
    if (currentUser?.id && isFreeLocalProjectId(currentUser.id, projectId)) {
      updateFreeLocalProjectMeta(currentUser.id, projectId, { course_name: name });
      loadProjectsList();
      if (courseInfo.id === projectId) {
        setCourseInfo((prev) => ({ ...prev, name }));
        persistLastOpenLayoutMeta(projectId, name);
        try {
          const dk = getDraftKey(projectId);
          const raw = localStorage.getItem(dk);
          if (raw) {
            const p = JSON.parse(raw);
            localStorage.setItem(dk, JSON.stringify({
              ...p,
              courseInfo: { ...(p.courseInfo || {}), name, id: projectId, globalConfig: (p.courseInfo || {}).globalConfig || courseInfo.globalConfig || {} },
            }));
          }
        } catch (_) { /* noop */ }
      }
      return;
    }
    try {
      let ok = false;
      const r = await fetch(`${API_BASE}/layouts/${projectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ course_name: name })
      });
      if (r.ok) {
        ok = true;
      } else {
        const d = await r.json().catch(() => ({}));
        if (r.status === 404 || r.status === 405) {
          const res = await fetch(`${API_BASE}/layouts/${projectId}`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });
          if (!res.ok) throw new Error(d.error || 'Projeto não encontrado');
          const data = await res.json();
          const raw = data.project?.schema_json || data.layout;
          if (!raw) throw new Error('Projeto sem conteúdo');
          const saveRes = await fetch(`${API_BASE}/layouts/save`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
            body: JSON.stringify({ id: projectId, course_name: name, schema_json: raw })
          });
          if (saveRes.ok) ok = true;
          else throw new Error((await saveRes.json().catch(() => ({}))).error || 'Erro ao renomear');
        } else {
          throw new Error(d.error || 'Erro ao renomear');
        }
      }
      if (ok) {
        loadProjectsList();
        if (courseInfo.id === projectId) {
          setCourseInfo(prev => ({ ...prev, name }));
          persistLastOpenLayoutMeta(projectId, name);
        }
      }
    } catch (err) {
      alert(t('builder:renameProjectError'));
    }
  };

  const reuseProject = async (project) => {
    try {
      let parsed = null;
      if (currentUser?.id && isFreeLocalProjectId(currentUser.id, project.id)) {
        const raw = localStorage.getItem(getDraftKey(project.id));
        if (!raw) throw new Error('Projeto sem conteúdo');
        const p = JSON.parse(raw);
        parsed = {
          rows: p.rows || [],
          headerWidgets: p.headerWidgets || [],
          globalConfig: (p.courseInfo && p.courseInfo.globalConfig) || {},
        };
      } else {
        const res = await fetch(`${API_BASE}/layouts/${project.id}`, {
          headers: { 'Authorization': `Bearer ${authToken}` },
        });
        if (!res.ok) throw new Error('Projeto não encontrado');
        const data = await res.json();
        const raw = data.project?.schema_json || data.layout;
        if (!raw) throw new Error('Projeto sem conteúdo');
        parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      }
      const newName = (t('builder:reuseProjectNamePrefix', { name: project.course_name || t('builder:untitled') }) || `Cópia de ${project.course_name || 'Sem título'}`).trim();
      const projectId = uid();
      const payloadLayout = {
        rows: parsed.rows || [],
        headerWidgets: parsed.headerWidgets || [],
        globalConfig: parsed.globalConfig || {},
      };
      if (!planAllowsServerSave(currentUser)) {
        if (!currentUser?.id) throw new Error('no user');
        registerFreeLocalProject(currentUser.id, { id: projectId, course_name: newName });
        reportFreeProjectCreated(authToken, projectId, newName);
        try {
          localStorage.setItem(getDraftKey(projectId), JSON.stringify({
            rows: payloadLayout.rows,
            headerWidgets: payloadLayout.headerWidgets,
            courseInfo: { name: newName, id: projectId, globalConfig: payloadLayout.globalConfig },
          }));
        } catch (_) { /* noop */ }
        loadProjectsList();
        setShowProjectsModal(false);
        openProject({ id: projectId, course_name: newName });
        showToast(t('builder:freeLocalSavedToast'), 'success', 2400);
        return;
      }
      const r = await fetch(`${API_BASE}/layouts/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({
          id: projectId,
          course_name: newName,
          schema_json: JSON.stringify(payloadLayout),
          ...(currentUser?.tenant_access === 'collab' && currentUser?.tenant_id
            ? { host_tenant_id: currentUser.tenant_id }
            : {}),
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || 'Erro ao criar cópia');
      }
      loadProjectsList();
      setShowProjectsModal(false);
      openProject({ id: projectId, course_name: newName });
    } catch (err) {
      alert(t('builder:reuseProjectError'));
    }
  };

  const goBackToDashboard = () => {
    persistLastOpenLayoutMeta(null, '');
    setLayoutCollabPermissions(null);
    setCourseInfo({ name: '', id: '', theme: 'blue' });
    setRows([]);
    setHeaderWidgets([]);
    setActivePanel(null);
    historyRef.current = [];
    historyIndexRef.current = -1;
    historyInitializedRef.current = false;
  };

  const isFreePlan = () => {
    if (!currentUser) return true;
    if (currentUser.role === 'super_admin') return false;
    if (!currentUser.plan) return true;
    return currentUser.plan.price_brl <= 0;
  };

  /**
   * Pode persistir layout no servidor (alinha ao backend: parsePlanSaveSlotsFromFeatures).
   * - features.save === true = ilimitado (legado)
   * - features.save número > 0 = slots (ex.: planos Pro / corporativos)
   * plan_free continua bloqueado mesmo com flag errada.
   */
  const planAllowsServerSave = (u) => {
    if (!u) return false;
    if (u.role === 'super_admin') return true;
    if (!u.plan) return false;
    const pid = String(u.plan.id || '');
    if (pid === 'plan_free') return false;
    let feat = u.plan.features;
    if (typeof feat === 'string') {
      try {
        feat = JSON.parse(feat);
      } catch {
        feat = null;
      }
    }
    const saveSlots = getPlanSaveSlotLimit(feat);
    if (saveSlots === 0) return false;
    if (saveSlots === Infinity) return true;
    if (Number.isFinite(saveSlots) && saveSlots > 0) return true;
    return false;
  };

  const moduleTierMap = useMemo(() => getMergedModuleTiers(featureFlags), [featureFlags]);

  /** Módulos laterais: alinhado à tabela de planos (features.modules.* === true); super_admin ignora. */
  const planUploadsModuleEnabled = useMemo(() => {
    if (!currentUser || currentUser.role === 'super_admin') return true;
    return planUploadModuleActiveClient(currentUser.plan?.features);
  }, [currentUser]);

  const planPluginModuleEnabled = useMemo(() => {
    if (!currentUser || currentUser.role === 'super_admin') return true;
    return planPluginModuleActiveClient(currentUser.plan?.features);
  }, [currentUser]);

  /** Minha API + gateways: mesma regra do backend (`planAllowsMyApiMenu`) e do perfil. */
  const planMyApiMenuEnabled = useMemo(() => {
    if (!currentUser || currentUser.role === 'super_admin') return true;
    return planAllowsMyApiMenuClient(currentUser.plan?.features);
  }, [currentUser]);

  const planCricketModuleEnabled = useMemo(() => {
    if (!currentUser || currentUser.role === 'super_admin') return true;
    return planCricketModuleActiveClient(currentUser.plan?.features);
  }, [currentUser]);

  const cricketWidgetLinkEnabled = useMemo(
    () =>
      !!(
        planCricketModuleEnabled &&
        authToken &&
        currentUser?.id &&
        courseInfo?.id &&
        !isFreeLocalProjectId(currentUser.id, String(courseInfo.id))
      ),
    [planCricketModuleEnabled, authToken, currentUser, courseInfo?.id],
  );

  /**
   * Plano Free: mesma gaveta que o pago, mas dados num único quadro local (não ligado ao projeto).
   * Ícone pode aparecer sem projeto salvo na nuvem; não entra em conflito com Cricket na nuvem.
   */
  const planFreeCricketDrawerStandalone = useMemo(
    () =>
      !!(
        authToken &&
        currentUser?.id &&
        currentUser.role !== 'super_admin' &&
        String(currentUser?.plan?.id || '') === 'plan_free' &&
        !cricketWidgetLinkEnabled
      ),
    [authToken, currentUser, cricketWidgetLinkEnabled],
  );

  const showCricketDrawerInBuilder = cricketWidgetLinkEnabled || planFreeCricketDrawerStandalone;

  const [cricketStageLampsHidden, setCricketStageLampsHidden] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_CRICKET_STAGE_LAMPS_HIDDEN) === '1';
    } catch {
      return false;
    }
  });

  const setCricketStageLampsHiddenPersist = useCallback((hidden) => {
    setCricketStageLampsHidden(!!hidden);
    try {
      if (hidden) localStorage.setItem(STORAGE_CRICKET_STAGE_LAMPS_HIDDEN, '1');
      else localStorage.removeItem(STORAGE_CRICKET_STAGE_LAMPS_HIDDEN);
    } catch {
      /* ignore */
    }
  }, []);

  const [cricketOverlaySnapshot, setCricketOverlaySnapshot] = useState({ lists: [], cards: [] });

  const loadCricketOverlaySnapshot = useCallback(async () => {
    if (!cricketWidgetLinkEnabled || !authToken || !courseInfo?.id) {
      setCricketOverlaySnapshot({ lists: [], cards: [] });
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/layouts/${encodeURIComponent(String(courseInfo.id))}/cricket`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) {
        setCricketOverlaySnapshot({ lists: [], cards: [] });
        return;
      }
      const data = await res.json();
      setCricketOverlaySnapshot({ lists: data.lists || [], cards: data.cards || [] });
    } catch {
      setCricketOverlaySnapshot({ lists: [], cards: [] });
    }
  }, [cricketWidgetLinkEnabled, authToken, courseInfo?.id]);

  const cricketWidgetBadgeByWidgetId = useMemo(
    () => buildCricketWidgetBadgeMap(cricketOverlaySnapshot.lists, cricketOverlaySnapshot.cards),
    [cricketOverlaySnapshot],
  );

  const moodleSimulatedExportStrings = useMemo(
    () => ({
      htmlLang: i18n?.language === 'en' ? 'en' : 'pt-BR',
      navPrevious: t('builder:navPrevious'),
      navNext: t('builder:navNext'),
      page: t('builder:page'),
      layoutEmpty: t('builder:layoutEmpty'),
      moodleNoActivityUrl: t('builder:moodleNoActivityUrl'),
      moodleActivityPopupBlocked: t('builder:moodleActivityPopupBlocked'),
      activityStatusPendingDefault: t('builder:activityStatusPendingDefault'),
      activityStatusCompleteDefault: t('builder:activityStatusCompleteDefault'),
      modResourceWarningTitle: t('builder:modResourceWarningTitle'),
      modResourceWarningBody: t('builder:modResourceWarningBody'),
      modResourceWarningLink: t('builder:modResourceWarningLink'),
      modResourceWarningLinkLabel: t('builder:modResourceWarningLinkLabel'),
      modResourceWarningDismiss: t('builder:modResourceWarningDismiss'),
      siteOmittedNotice: t('builder:siteOmittedNotice'),
      siteEmbedNotice: t('builder:siteEmbedNotice'),
      sitePaymentNotice: t('builder:sitePaymentNotice'),
      siteCommentName: t('builder:siteCommentName'),
      siteCommentPlaceholder: t('builder:siteCommentPlaceholder'),
      siteCommentSend: t('builder:siteCommentSend'),
      siteCommentEmpty: t('builder:siteCommentEmpty'),
      siteLinkNotice: t('builder:siteLinkNotice'),
      tocEmpty: t('builder:tocEmptyHint'),
    }),
    [t, i18n?.language],
  );

  useEffect(() => {
    if (appState !== 'builder') return;
    loadCricketOverlaySnapshot();
  }, [appState, loadCricketOverlaySnapshot]);

  const cricketFocusTimerRef = useRef(null);
  const [cricketFocusHighlightWidgetId, setCricketFocusHighlightWidgetId] = useState(null);
  const [pendingCricketFocusWidgetId, setPendingCricketFocusWidgetId] = useState(null);

  const runCricketScrollAndHighlight = useCallback((widgetId) => {
    const id = String(widgetId || '');
    if (!id) return;
    let el = null;
    try {
      el = document.querySelector(`.${CSS.escape(`madri-widget-${id}`)}`);
    } catch {
      el = document.querySelector(`.madri-widget-${id}`);
    }
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setCricketFocusHighlightWidgetId(id);
    if (cricketFocusTimerRef.current) window.clearTimeout(cricketFocusTimerRef.current);
    cricketFocusTimerRef.current = window.setTimeout(() => {
      setCricketFocusHighlightWidgetId(null);
      cricketFocusTimerRef.current = null;
    }, 2200);
  }, []);

  const focusCricketLinkedWidgetOnStage = useCallback(
    (widgetId) => {
      const id = String(widgetId || '');
      if (!id) return;
      if (appState !== 'builder') {
        setPendingCricketFocusWidgetId(id);
        setCricketBoardLayoutId(null);
        setAppState('builder');
        return;
      }
      runCricketScrollAndHighlight(id);
    },
    [appState, runCricketScrollAndHighlight],
  );

  useEffect(() => {
    if (appState !== 'builder' || !pendingCricketFocusWidgetId) return undefined;
    const id = pendingCricketFocusWidgetId;
    const t = window.setTimeout(() => {
      runCricketScrollAndHighlight(id);
      setPendingCricketFocusWidgetId(null);
    }, 160);
    return () => window.clearTimeout(t);
  }, [appState, pendingCricketFocusWidgetId, runCricketScrollAndHighlight]);

  /** Mesma regra do perfil: quota do plano + % vinda do /auth/me. */
  const sidebarDiskStorage = useMemo(() => {
    const u = currentUser;
    if (!u?.tenant_id || !u?.plan) {
      return { visible: false, storagePct: null, unlimited: false, storageUsage: null };
    }
    const planHasStorageQuota = [u.plan.storage_projects_mb, u.plan.storage_logs_mb, u.plan.storage_media_mb].some(
      (v) => v != null && Number(v) > 0,
    );
    const storagePct = planHasStorageQuota
      ? typeof u.storage_used_percent === 'number'
        ? Math.min(100, Math.max(0, u.storage_used_percent))
        : 0
      : null;
    return {
      visible: true,
      storagePct,
      unlimited: !planHasStorageQuota,
      storageUsage: u.storage_usage || null,
    };
  }, [currentUser]);

  /** Salvar no servidor: feature «Salvar» no plano + não é plan_free (contrato/corporativo pode ter preço 0). */
  const canSaveLayout = () => {
    if (!currentUser) return false;
    if (layoutCollabPermissions && layoutCollabPermissions.canEdit === false) return false;
    return planAllowsServerSave(currentUser);
  };

  /** Atualiza currentUser com dados frescos do servidor (útil após admin alterar plano) */
  const refreshCurrentUser = async () => {
    if (!authToken) return null;
    try {
      const r = await fetch(`${API_BASE}/auth/me`, { headers: { 'Authorization': `Bearer ${authToken}` } });
      const data = await r.json();
      if (data.user) {
        sessionStorage.setItem('gepetto_user', JSON.stringify(data.user));
        setCurrentUser(data.user);
        if (data.featureFlags) setFeatureFlags(normalizeFeatureFlags(data.featureFlags));
        return data.user;
      }
    } catch (e) { /* ignora */ }
    return null;
  };

  /** Plano Free / projeto só no navegador: limpa rascunhos locais (ver `clearGepettoBrowserCache.js`) e recarrega. */
  const clearBuilderBrowserCacheAndReload = useCallback(() => {
    if (!window.confirm(t('builder:clearBrowserCacheConfirm'))) return;
    clearGepettoBuilderLocalCache();
    showToast(t('builder:clearBrowserCacheDone'), 'success', 2400);
    window.setTimeout(() => {
      window.location.reload();
    }, 500);
  }, [t, showToast]);

  const saveLayoutToDB = async () => {
    const rowsCur = rowsStateRef.current;
    const headerWidgetsCur = headerWidgetsStateRef.current;
    const courseInfoCur = courseInfoStateRef.current;
    let serverSaveUser = currentUser;

    if (!canSaveLayout()) {
      if (layoutCollabPermissions && layoutCollabPermissions.canEdit === false) {
        alert(t('builder:readOnlyCollaborator', { defaultValue: 'Você só tem permissão de visualização neste projeto.' }));
        return;
      }
      const freshUser = await refreshCurrentUser();
      serverSaveUser = freshUser || currentUser;
      if (!planAllowsServerSave(serverSaveUser)) {
        const layoutIdStr = String(courseInfoCur.id || '').trim();
        const hasLocalWork = rowsCur.length > 0 || (headerWidgetsCur || []).length > 0;
        if (layoutIdStr && currentUser?.id && isFreeLocalProjectId(currentUser.id, layoutIdStr)) {
          setIsSaving(true);
          try {
            const nextInfo = { ...courseInfoCur, id: layoutIdStr, name: courseInfoCur.name || '' };
            try {
              localStorage.setItem(getDraftKey(layoutIdStr), JSON.stringify({
                rows: rowsCur,
                headerWidgets: headerWidgetsCur,
                courseInfo: { name: nextInfo.name, id: layoutIdStr, globalConfig: courseInfoCur.globalConfig || {} },
              }));
            } catch (_) { /* noop */ }
            savedSnapshotRef.current = {
              rows: JSON.parse(JSON.stringify(rowsCur)),
              headerWidgets: JSON.parse(JSON.stringify(headerWidgetsCur)),
              courseInfo: JSON.parse(JSON.stringify(nextInfo)),
            };
            loadProjectsList();
            showToast(t('builder:freeLocalSavedToast'), 'success', 2600);
          } finally {
            setIsSaving(false);
          }
          return;
        }
        if (!layoutIdStr && hasLocalWork) {
          // primeiro “nome + id” só em cache (plano Free / sem slot nuvem)
        } else {
          setShowFreeSaveModal(true);
          return;
        }
      }
      // Plano atualizado: refresh já atualizou o state, segue para salvar
    }

    if (!courseInfoCur.id) {
      if (rowsCur.length === 0) {
        setShowNewProjectModal(true);
        return;
      }
      const name = prompt(t('builder:newProjectName') + ':', t('builder:newProjectNamePlaceholder'));
      if (!name || !name.trim()) return;
      const projectId = uid();
      const payloadLayout = { rows: rowsCur, headerWidgets: headerWidgetsCur, globalConfig: courseInfoCur.globalConfig || {} };
      setIsSaving(true);
      try {
        if (!planAllowsServerSave(serverSaveUser)) {
          if (!currentUser?.id) {
            alert(t('builder:saveError'));
            setIsSaving(false);
            return;
          }
          registerFreeLocalProject(currentUser.id, { id: projectId, course_name: name.trim() });
          reportFreeProjectCreated(authToken, projectId, name.trim());
          const nextInfo = { ...courseInfoCur, id: projectId, name: name.trim() };
          setCourseInfo(prev => ({ ...prev, id: projectId, name: name.trim() }));
          persistLastOpenLayoutMeta(projectId, name.trim());
          try { localStorage.removeItem('gepetto_draft_scratch'); } catch (_) { /* noop */ }
          loadProjectsList();
          savedSnapshotRef.current = {
            rows: JSON.parse(JSON.stringify(rowsCur)),
            headerWidgets: JSON.parse(JSON.stringify(headerWidgetsCur)),
            courseInfo: JSON.parse(JSON.stringify(nextInfo)),
          };
          try {
            localStorage.setItem(getDraftKey(projectId), JSON.stringify({
              rows: rowsCur,
              headerWidgets: headerWidgetsCur,
              courseInfo: { name: name.trim(), id: projectId, globalConfig: courseInfoCur.globalConfig || {} },
            }));
          } catch (_) { /* noop */ }
          const violations = checkPremiumUsage();
          if (violations.length > 0) { setPaidWarningItems(violations); setShowPaidWarning(true); }
          showToast(t('builder:freeLocalSavedToast'), 'success', 2800);
        } else {
          const r = await fetch(`${API_BASE}/layouts/save`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
            body: JSON.stringify({ id: projectId, course_name: name.trim(), schema_json: JSON.stringify(payloadLayout) }),
          });
          if (r.ok) {
            const nextInfo = { ...courseInfoCur, id: projectId, name: name.trim() };
            setCourseInfo(prev => ({ ...prev, id: projectId, name: name.trim() }));
            persistLastOpenLayoutMeta(projectId, name.trim());
            try { localStorage.removeItem('gepetto_draft_scratch'); } catch (_) { /* noop */ }
            loadProjectsList();
            savedSnapshotRef.current = {
              rows: JSON.parse(JSON.stringify(rowsCur)),
              headerWidgets: JSON.parse(JSON.stringify(headerWidgetsCur)),
              courseInfo: JSON.parse(JSON.stringify(nextInfo)),
            };
            try {
              localStorage.setItem(getDraftKey(projectId), JSON.stringify({
                rows: rowsCur,
                headerWidgets: headerWidgetsCur,
                courseInfo: { name: name.trim(), id: projectId, globalConfig: courseInfoCur.globalConfig || {} },
              }));
            } catch (_) { /* noop */ }
            const violations = checkPremiumUsage();
            if (violations.length > 0) { setPaidWarningItems(violations); setShowPaidWarning(true); }
          } else {
            const d = await r.json().catch(() => ({}));
            alert(t('builder:saveError') + ': ' + (d.error || 'Erro desconhecido'));
          }
        }
      } catch (err) {
        alert(t('builder:backendConnectionError'));
      }
      setIsSaving(false);
      return;
    }

    // Projeto criado no Free (lista 24h no navegador): sem plano com save na nuvem, persiste só em gepetto_draft_*.
    // Com Premium/Pro, deve ir ao servidor — senão o utilizador “salva” mas o DB nunca atualiza e o rascunho velho pode mascarar o palco.
    if (
      currentUser?.id &&
      isFreeLocalProjectId(currentUser.id, String(courseInfoCur.id)) &&
      !planAllowsServerSave(currentUser)
    ) {
      setIsSaving(true);
      try {
        try {
          localStorage.setItem(getDraftKey(courseInfoCur.id), JSON.stringify({
            rows: rowsCur,
            headerWidgets: headerWidgetsCur,
            courseInfo: { name: courseInfoCur.name, id: courseInfoCur.id, globalConfig: courseInfoCur.globalConfig || {} },
          }));
        } catch (_) { /* noop */ }
        savedSnapshotRef.current = {
          rows: JSON.parse(JSON.stringify(rowsCur)),
          headerWidgets: JSON.parse(JSON.stringify(headerWidgetsCur)),
          courseInfo: JSON.parse(JSON.stringify(courseInfoCur)),
        };
        loadProjectsList();
        showToast(t('builder:freeLocalSavedToast'), 'success', 2600);
        setTimeout(() => setIsSaving(false), 500);
      } catch (err) {
        setIsSaving(false);
      }
      return;
    }

    setIsSaving(true);
    try {
      const collectWidgets = (rowsArr, headerArr) => {
        const map = new Map();
        const walkWidgets = (widgets) => {
          (widgets || []).forEach(w => {
            if (w && w.id) map.set(w.id, w);
          });
        };
        walkWidgets(headerArr);
        (rowsArr || []).forEach(row => {
          (row.columns || []).forEach(col => {
            walkWidgets(col.widgets);
          });
        });
        return map;
      };

      const computeChangedWidgetIds = (base, current) => {
        if (!base) return null;
        const baseMap = collectWidgets(base.rows, base.headerWidgets);
        const curMap = collectWidgets(current.rows, current.headerWidgets);
        const idsBase = new Set(baseMap.keys());
        const idsCur = new Set(curMap.keys());
        const changed = new Set();

        // adições/remoções
        for (const id of idsBase) if (!idsCur.has(id)) changed.add(id);
        for (const id of idsCur) if (!idsBase.has(id)) changed.add(id);

        // alterações de conteúdo
        for (const id of idsCur) {
          if (!idsBase.has(id)) continue;
          const b = baseMap.get(id);
          const c = curMap.get(id);
          try {
            if (JSON.stringify(b) !== JSON.stringify(c)) changed.add(id);
          } catch {
            changed.add(id);
          }
        }
        return Array.from(changed);
      };

      const payloadLayout = {
        rows: rowsCur,
        headerWidgets: headerWidgetsCur,
        globalConfig: courseInfoCur.globalConfig || {}
      };

      const changedWidgetIds = computeChangedWidgetIds(
        savedSnapshotRef.current,
        { rows: rowsCur, headerWidgets: headerWidgetsCur }
      );

      const response = await fetch(`${API_BASE}/layouts/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          id: courseInfoCur.id,
          course_name: courseInfoCur.name,
          schema_json: JSON.stringify(payloadLayout),
          changedWidgetIds: Array.isArray(changedWidgetIds) && changedWidgetIds.length > 0 ? changedWidgetIds : undefined
        })
      });

      if (response.ok) {
        // Atualiza snapshot "base" pós-save para evitar sobrescrever alterações feitas por outro usuário
        savedSnapshotRef.current = {
          rows: JSON.parse(JSON.stringify(rowsCur)),
          headerWidgets: JSON.parse(JSON.stringify(headerWidgetsCur)),
          courseInfo: JSON.parse(JSON.stringify(courseInfoCur))
        };
        try {
          localStorage.setItem(getDraftKey(courseInfoCur.id), JSON.stringify({
            rows: rowsCur,
            headerWidgets: headerWidgetsCur,
            courseInfo: { name: courseInfoCur.name, id: courseInfoCur.id, globalConfig: courseInfoCur.globalConfig || {} },
          }));
        } catch (_) { /* noop */ }
        if (currentUser?.id && courseInfoCur.id) {
          try {
            removeFreeLocalProject(currentUser.id, String(courseInfoCur.id));
          } catch (_) { /* noop */ }
        }

        const violations = checkPremiumUsage();
        if (violations.length > 0) {
          setPaidWarningItems(violations);
          setShowPaidWarning(true);
        }
        setTimeout(() => setIsSaving(false), 800);
      } else {
        const errData = await response.json().catch(() => ({}));
        alert(t('builder:saveError') + ": " + (errData.error || 'Erro desconhecido'));
        setIsSaving(false);
      }
    } catch (err) {
      console.error("Erro na API de DB:", err);
      alert(t('builder:backendConnectionError'));
      setIsSaving(false);
    }
  };

  saveLayoutToDBRef.current = saveLayoutToDB;

  /** Após "Salvar" no modal de elemento/seção: envia ao servidor no próximo tick (refs já refletem o `setState`). */
  const schedulePersistLayoutAfterModal = useCallback(() => {
    window.setTimeout(() => { void saveLayoutToDBRef.current(); }, 0);
  }, []);

  const initHistoryFromLoaded = (loadedRows, loadedHeaderWidgets, info) => {
    historyInitializedRef.current = true;
    const snap = {
      rows: JSON.parse(JSON.stringify(loadedRows)),
      headerWidgets: JSON.parse(JSON.stringify(loadedHeaderWidgets || [])),
      courseInfo: JSON.parse(JSON.stringify(info))
    };
    historyRef.current = [snap];
    historyIndexRef.current = 0;
    setHistoryState({ canUndo: false, canRedo: false });
    // Considera o estado carregado do servidor como "último salvo"
    savedSnapshotRef.current = snap;
  };

  const loadLayoutFromDB = async (layoutIdParam, opts = {}) => {
    // Por defeito prioriza draft do localStorage sobre o DB (dono). Com plano que grava na nuvem e projeto
    // não é só-local, abre-se primeiro o servidor para não “reverter” ao template por rascunho antigo.
    // Convidados: `shouldUseDraft` falso. `forceDb` força leitura do servidor (sync).
    const { forceDb = false } = opts;
    const shouldUseDraft = !forceDb && !(currentUser?.isCollaborator);
    const layoutId = String(layoutIdParam || '');

    const mergeCourseForHistory = (patch) => ({
      ...courseInfo,
      id: patch.id ?? layoutId,
      name: patch.name != null ? patch.name : (courseInfo.name || ''),
      theme: patch.theme || courseInfo.theme || 'blue',
      globalConfig: patch.globalConfig != null ? patch.globalConfig : (courseInfo.globalConfig || {}),
    });

    const loadFromLocalDraft = () => {
      if (!shouldUseDraft) return false;
      try {
        const draftRaw = localStorage.getItem(getDraftKey(layoutId));
        if (!draftRaw) return false;
        const p = JSON.parse(draftRaw);
        if (!p?.rows || !Array.isArray(p.rows)) return false;
        const ciDraft = p.courseInfo || {};
        const mergedInfo = mergeCourseForHistory({
          id: layoutId,
          name: (ciDraft.name != null && ciDraft.name !== '') ? ciDraft.name : (courseInfo.name || ''),
          theme: ciDraft.theme,
          globalConfig: ciDraft.globalConfig != null ? ciDraft.globalConfig : courseInfo.globalConfig,
        });
        setRows(p.rows);
        setHeaderWidgets(p.headerWidgets || []);
        setCourseInfo(mergedInfo);
        try { persistLastOpenLayoutMeta(layoutId, mergedInfo.name || ''); } catch (_) { /* noop */ }
        initHistoryFromLoaded(p.rows, p.headerWidgets || [], mergedInfo);
        return true;
      } catch (_) {
        return false;
      }
    };

    /** Com plano que grava na nuvem e projeto não é “só local”, não preferir rascunho ao abrir — evita palco “voltar” ao template quando o DB está correto. */
    const skipLocalDraftBecauseTrustedCloud =
      shouldUseDraft &&
      !!layoutId &&
      !!currentUser?.id &&
      planAllowsServerSave(currentUser) &&
      !isFreeLocalProjectId(currentUser.id, layoutId);

    /** Projetos só em `gepetto_draft_*` + registro 24h — não chamar API (404 limpava o palco). */
    if (layoutId && currentUser?.id && isFreeLocalProjectId(currentUser.id, layoutId)) {
      try {
        if (shouldUseDraft) {
          const draftRaw = localStorage.getItem(getDraftKey(layoutId));
          if (draftRaw) {
            const p = JSON.parse(draftRaw);
            if (p?.rows && Array.isArray(p.rows)) {
              const ciDraft = p.courseInfo || {};
              const mergedInfo = mergeCourseForHistory({
                id: layoutId,
                name: (ciDraft.name != null && ciDraft.name !== '') ? ciDraft.name : (courseInfo.name || ''),
                theme: ciDraft.theme,
                globalConfig: ciDraft.globalConfig != null ? ciDraft.globalConfig : courseInfo.globalConfig,
              });
              setRows(p.rows);
              setHeaderWidgets(p.headerWidgets || []);
              setCourseInfo(mergedInfo);
              try { persistLastOpenLayoutMeta(layoutId, mergedInfo.name || ''); } catch (_) { /* noop */ }
              initHistoryFromLoaded(p.rows, p.headerWidgets || [], mergedInfo);
              return;
            }
          }
        }
        const mergedInfo = mergeCourseForHistory({
          id: layoutId,
          name: courseInfo.name || '',
          globalConfig: courseInfo.globalConfig || {},
        });
        setCourseInfo(mergedInfo);
        setRows([]);
        setHeaderWidgets([]);
        try { persistLastOpenLayoutMeta(layoutId, mergedInfo.name || ''); } catch (_) { /* noop */ }
        initHistoryFromLoaded([], [], mergedInfo);
        return;
      } catch (_) { /* cai no fluxo normal */ }
    }

    if (!skipLocalDraftBecauseTrustedCloud && loadFromLocalDraft()) {
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/layouts/${layoutId}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLayoutCollabPermissions(data.collabPermissions != null ? data.collabPermissions : null);
        const row = data.project;
        const serverName = row?.course_name != null ? row.course_name : '';
        const raw = row?.schema_json || data.layout;
        if (!raw) {
          const mergedInfo = mergeCourseForHistory({
            id: layoutId,
            name: serverName || courseInfo.name || ''
          });
          setCourseInfo(mergedInfo);
          try { persistLastOpenLayoutMeta(layoutId, mergedInfo.name || ''); } catch (_) { /* noop */ }
          initHistoryFromLoaded([], [], mergedInfo);
          return;
        }
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        let loadedRows = [];
        let loadedHeaderWidgets = [];
        const gc = parsed?.globalConfig;
        if (Array.isArray(parsed) && parsed.length > 0) {
          loadedRows = parsed;
          setRows(parsed);
        } else if (parsed?.rows) {
          loadedRows = parsed.rows;
          loadedHeaderWidgets = parsed.headerWidgets || [];
          setRows(parsed.rows);
          setHeaderWidgets(parsed.headerWidgets || []);
        }
        const mergedInfo = mergeCourseForHistory({
          id: row?.id || layoutId,
          name: serverName || courseInfo.name || '',
          globalConfig: gc != null ? gc : courseInfo.globalConfig
        });
        setCourseInfo(mergedInfo);
        try { persistLastOpenLayoutMeta(layoutId, mergedInfo.name || ''); } catch (_) { /* noop */ }
        initHistoryFromLoaded(loadedRows, loadedHeaderWidgets, mergedInfo);
        try {
          localStorage.setItem(getDraftKey(layoutId), JSON.stringify({
            rows: loadedRows,
            headerWidgets: loadedHeaderWidgets || [],
            courseInfo: { name: mergedInfo.name, id: layoutId, globalConfig: mergedInfo.globalConfig || {} },
          }));
        } catch (_) { /* noop */ }
      } else {
        setLayoutCollabPermissions(null);
        if (!loadFromLocalDraft()) {
          const mergedInfo = mergeCourseForHistory({ id: layoutId });
          setCourseInfo(mergedInfo);
          initHistoryFromLoaded([], [], mergedInfo);
        }
      }
    } catch (err) {
      console.warn("Sem backup prévio no Banco de Dados ou Backend Offline.", err);
      setLayoutCollabPermissions(null);
      if (!loadFromLocalDraft()) {
        const mergedInfo = mergeCourseForHistory({ id: layoutId });
        setCourseInfo(mergedInfo);
        initHistoryFromLoaded([], [], mergedInfo);
      }
    }
  };

  const hasPendingLayoutChanges = useCallback((baseSnapshot) => {
    if (!baseSnapshot) return false;
    try {
      const baseSignature = JSON.stringify({
        rows: baseSnapshot.rows || [],
        headerWidgets: baseSnapshot.headerWidgets || []
      });
      const currentSignature = JSON.stringify({
        rows: rowsStateRef.current || [],
        headerWidgets: headerWidgetsStateRef.current || []
      });
      return baseSignature !== currentSignature;
    } catch {
      // Em caso de erro de serialização, assume que há mudanças para evitar sobrescrever edição local.
      return true;
    }
  }, []);

  // Sincronização simples para colaboradores: se ele não tiver alterações pendentes,
  // recarrega periodicamente o layout do servidor para refletir mudanças do anfitrião.
  useEffect(() => {
    if (appState !== 'builder') return;
    if (!currentUser?.isCollaborator) return;
    if (!courseInfo?.id) return;
    if (!authToken) return;
    if (currentUser?.id && isFreeLocalProjectId(currentUser.id, String(courseInfo.id))) return;

    const layoutId = courseInfo.id;
    const interval = setInterval(async () => {
      try {
        const base = savedSnapshotRef.current;
        if (!base) return;
        if (hasPendingLayoutChanges(base)) return;

        await loadLayoutFromDB(layoutId, { forceDb: true });
      } catch (_) {
        // Se falhar a sync, não interrompe a edição do usuário.
      }
    }, 7000);

    return () => clearInterval(interval);
  }, [appState, currentUser?.isCollaborator, courseInfo?.id, authToken, hasPendingLayoutChanges]);

  // Sincronização para o anfitrião (não-colaborador):
  // se ele estiver "idle" (sem alterações pendentes), recarrega do DB
  // para refletir mudanças feitas pelos colaboradores.
  useEffect(() => {
    if (appState !== 'builder') return;
    if (currentUser?.isCollaborator) return;
    if (!courseInfo?.id) return;
    if (!authToken) return;
    if (currentUser?.id && isFreeLocalProjectId(currentUser.id, String(courseInfo.id))) return;

    const layoutId = courseInfo.id;
    const interval = setInterval(async () => {
      try {
        const base = savedSnapshotRef.current;
        if (!base) return;
        if (hasPendingLayoutChanges(base)) return;

        await loadLayoutFromDB(layoutId, { forceDb: true });
      } catch (_) {
        // Se falhar a sync, não interrompe a edição do usuário.
      }
    }, 7000);

    return () => clearInterval(interval);
  }, [appState, currentUser?.isCollaborator, courseInfo?.id, authToken, hasPendingLayoutChanges]);

  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [premiumViolationItems, setPremiumViolationItems] = useState([]);
  const [showFreeSaveModal, setShowFreeSaveModal] = useState(false);
  const [showPaidWarning, setShowPaidWarning] = useState(false);
  const [paidWarningItems, setPaidWarningItems] = useState([]);
  const [highlightPlanRestricted, setHighlightPlanRestricted] = useState(false);
  const [pluginManifest, setPluginManifest] = useState(null);
  const [showPluginUpdateBalloon, setShowPluginUpdateBalloon] = useState(false);
  const [pluginUpdateDot, setPluginUpdateDot] = useState(false);

  const planAllowedElementTypes = useMemo(() => {
    if (!currentUser || currentUser.role === 'super_admin') return null;
    const pf = (currentUser.plan?.features && typeof currentUser.plan.features === 'object')
      ? currentUser.plan.features
      : {};
    const tierMap = getMergedElementTiers(featureFlags);
    return resolvePlanAllowedElements(pf, tierMap);
  }, [currentUser, featureFlags]);

  /** Grids liberados pelo plano; null = não aplica restrição de grid no destaque/export gate interno. */
  const planAllowedGridIds = useMemo(() => {
    if (!currentUser || currentUser.role === 'super_admin') return null;
    const pf = (currentUser.plan?.features && typeof currentUser.plan.features === 'object')
      ? currentUser.plan.features
      : {};
    const raw = pf.grids;
    if (!Array.isArray(raw) || raw.length === 0) return null;
    return new Set(raw);
  }, [currentUser]);

  const isPlanGridRestricted = useCallback((row) => {
    if (!planAllowedGridIds) return false;
    return !planAllowedGridIds.has(getRowPlanGridType(row));
  }, [planAllowedGridIds]);

  const stageHasPlanRestrictedWidgets = useMemo(() => {
    if (planAllowedElementTypes) {
      let found = false;
      walkAllWidgetsInRows(rows, (w) => {
        if (found) return;
        if (w?.type && !planAllowedElementTypes.has(w.type)) found = true;
      });
      if (found) return true;
      for (const w of headerWidgets || []) {
        if (w?.type && !planAllowedElementTypes.has(w.type)) return true;
      }
    }
    if (planAllowedGridIds) {
      let foundGrid = false;
      forEachRowInTree(rows, (row) => {
        if (foundGrid) return;
        if (!planAllowedGridIds.has(getRowPlanGridType(row))) foundGrid = true;
      });
      if (foundGrid) return true;
    }
    return false;
  }, [planAllowedElementTypes, planAllowedGridIds, rows, headerWidgets]);

  useEffect(() => {
    if (highlightPlanRestricted && !stageHasPlanRestrictedWidgets) {
      setHighlightPlanRestricted(false);
    }
  }, [highlightPlanRestricted, stageHasPlanRestrictedWidgets]);

  const activateHighlightPlanRestricted = useCallback(() => {
    if (!stageHasPlanRestrictedWidgets) return;
    setHighlightPlanRestricted(true);
    setShowPremiumModal(false);
    setShowPaidWarning(false);
    showToast(t('builder:highlightPlanRestrictedToast'), 'info', 3200);
    window.setTimeout(() => {
      try {
        document.querySelector('[data-geppetto-plan-restricted="1"]')?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      } catch (_) { /* noop */ }
    }, 120);
  }, [showToast, t, stageHasPlanRestrictedWidgets]);

  const toggleHighlightPlanRestricted = useCallback(() => {
    if (!stageHasPlanRestrictedWidgets && !highlightPlanRestricted) return;
    setHighlightPlanRestricted((prev) => {
      const next = !prev;
      if (next && !stageHasPlanRestrictedWidgets) return false;
      showToast(
        next ? t('builder:highlightPlanRestrictedToast') : t('builder:highlightPlanRestrictedOffToast'),
        'info',
        2600
      );
      if (next) {
        window.setTimeout(() => {
          try {
            document.querySelector('[data-geppetto-plan-restricted="1"]')?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          } catch (_) { /* noop */ }
        }, 120);
      }
      return next;
    });
  }, [showToast, t, stageHasPlanRestrictedWidgets, highlightPlanRestricted]);

  /** Aviso único de atualização de plugins Moodle (changelog do Master). */
  useEffect(() => {
    if (appState !== 'builder' || !authToken || !planPluginModuleEnabled || !currentUserId) {
      return undefined;
    }
    let cancelled = false;
    fetch(`${API_BASE}/moodle-plugins/manifest`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (cancelled || !ok || !d?.manifest_etag) return;
        setPluginManifest(d);
        const seen = readPluginUpdateSeenEtag(currentUserId);
        if (d.manifest_etag !== seen) {
          setPluginUpdateDot(true);
          setShowPluginUpdateBalloon(true);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [appState, authToken, planPluginModuleEnabled, currentUserId]);

  useEffect(() => {
    if (activePanel !== 'plugin' || !pluginManifest?.manifest_etag || !currentUserId) return;
    writePluginUpdateSeenEtag(currentUserId, pluginManifest.manifest_etag);
    setShowPluginUpdateBalloon(false);
    setPluginUpdateDot(false);
  }, [activePanel, pluginManifest?.manifest_etag, currentUserId]);

  const dismissPluginUpdateNotice = useCallback(() => {
    if (pluginManifest?.manifest_etag && currentUserId) {
      writePluginUpdateSeenEtag(currentUserId, pluginManifest.manifest_etag);
    }
    setShowPluginUpdateBalloon(false);
    setPluginUpdateDot(false);
  }, [pluginManifest?.manifest_etag, currentUserId]);

  const openPluginPanelFromUpdateNotice = useCallback(() => {
    setActivePanel('plugin');
  }, []);

  const [showGallerySubmitModal, setShowGallerySubmitModal] = useState(false);
  const [gallerySubmitName, setGallerySubmitName] = useState('');
  const [gallerySubmitDesc, setGallerySubmitDesc] = useState('');
  const [gallerySubmitting, setGallerySubmitting] = useState(false);
  const [showSaveAsTemplateModal, setShowSaveAsTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDesc, setTemplateDesc] = useState('');
  const [templateSaving, setTemplateSaving] = useState(false);
  const [previewTemplateItem, setPreviewTemplateItem] = useState(null);
  const [sectionsModal, setSectionsModal] = useState(null);
  const [showAiLayoutModal, setShowAiLayoutModal] = useState(false);

  const isServerLayoutProject = !!(
    courseInfo?.id
    && currentUser?.id
    && !isFreeLocalProjectId(currentUser.id, String(courseInfo.id))
  );

  const planSectionsLibraryEnabled = !!(
    currentUser?.role === 'super_admin'
    || (isServerLayoutProject && (
      layoutCollabPermissions != null
      || currentUser?.isCollaborator
      || currentUser?.tenant_id
      || planAllowsServerSave(currentUser)
    ))
    || (!isServerLayoutProject && currentUser?.tenant_id)
  );

  useEffect(() => {
    if (!previewTemplateItem) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setPreviewTemplateItem(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [previewTemplateItem]);

  /** Exportação: compara palco com o que o plano cobre (features no admin). Palco em si não bloqueia inserção. */
  const checkPremiumUsage = () => {
    if (!currentUser || currentUser.role === 'super_admin') return [];

    const rows = rowsStateRef.current;
    const headerWidgets = headerWidgetsStateRef.current;
    const violations = [];
    const tierMap = getMergedElementTiers(featureFlags);

    const pf = (currentUser.plan?.features && typeof currentUser.plan.features === 'object')
      ? currentUser.plan.features
      : {};

    const allowedElements = resolvePlanAllowedElements(pf, tierMap);
    const rawGrids = pf.grids;
    const enforceGrids = Array.isArray(rawGrids) && rawGrids.length > 0;

    const getGridType = getRowPlanGridType;

    if (enforceGrids) {
      forEachRowInTree(rows, (row) => {
        const gridType = getGridType(row);
        if (!rawGrids.includes(gridType)) {
          const gridLabel = PLAN_GRID_CATALOG.find((g) => g.id === gridType)?.label;
          violations.push(gridLabel || `Grid (${gridType})`);
        }
      });
    }

    walkAllWidgetsInRows(rows, (w) => {
      if (!allowedElements.has(w.type)) {
        violations.push(`${w.type}`);
      }
    });
    (headerWidgets || []).forEach((w) => {
      if (!allowedElements.has(w.type)) {
        violations.push(`${w.type}`);
      }
    });

    return [...new Set(violations)];
  };

  const handleExportZip = async () => {
    const violations = checkPremiumUsage();
    if (violations.length > 0) {
      if (isFreePlan()) {
        setPremiumViolationItems(violations);
        setShowPremiumModal(true);
        return;
      }
      setPaidWarningItems(violations);
      setShowPaidWarning(true);
      return;
    }

    console.log("=== INICIANDO EXPORTAÇÃO ZIP ===");
    setIsExporting(true);
    setExportProgress(15);

    // Simula 1s de "Build" visual como richiesto pelo usuário para não parecer congelante
    await new Promise(r => setTimeout(r, 600));
    setExportProgress(45);

    try {
      console.log("1. Criando nova instância JSZip...");
      const zip = new JSZip();
      setExportProgress(70);

      console.log("2. Obtendo código simulado (getSimulatedCode)...");
      const rawHtml = getSimulatedCode(undefined, undefined, undefined, moodleSimulatedExportStrings);
      console.log("   -> rawHtml obtido. Tamanho:", rawHtml ? rawHtml.length : 0);
      let syncToken = '';
      const layoutId = String(courseInfo.id || '').trim();
      const canCourseSync = Boolean(
        authToken
        && layoutId
        && !(currentUser?.id && isFreeLocalProjectId(currentUser.id, layoutId)),
      );
      if (canCourseSync) {
        try {
          const tokenRes = await fetch(`${API_BASE}/layouts/${encodeURIComponent(layoutId)}/course-publish/token`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${authToken}` },
          });
          if (tokenRes.ok) {
            const tokenData = await tokenRes.json().catch(() => ({}));
            if (tokenData.token) syncToken = String(tokenData.token);
          }
        } catch (_) { /* ZIP continua válido sem sync */ }
      }
      let completeHtml = buildMoodleCourseDocument({
        title: `${courseInfo.name} - ${t('builder:exportedContent')}`,
        lang: i18n?.language === 'en' ? 'en' : 'pt-BR',
        bodyHtml: rawHtml,
        css: exportCss,
        syncToken,
      });
      if (syncToken) {
        completeHtml = injectMoodleCourseSyncMeta(completeHtml, syncToken);
      }

      console.log("3. Adicionando arquivos ao zip...");
      zip.file("index.html", completeHtml);
      zip.file("style.css", exportCss);
      zip.file("README_USO.txt", `COMO USAR NO MOODLE - Geppetto Cursos
========================================

1. Extraia o ZIP e faça upload dos arquivos (index.html, style.css) no Moodle.
2. Preferível: atividade "Geppetto" (plugin mod_gepettoresource) — melhor experiência.
3. Alternativa: recurso "Arquivo" com opção "Incorporar" (Embed).

No recurso Arquivo (mesma origem do Moodle), o HTML tenta sozinho:
- expandir a altura do iframe conforme o conteúdo (sem barra interna);
- usar largura 100% e remover a borda do iframe.

Se ainda ficar estreito ou com borda em algum tema, use o plugin Geppetto
ou adicione no tema (HTML adicional do head):

<style>
#region-main iframe,.mod_resource iframe,.resourcecontent iframe{width:100%!important;min-width:100%!important;max-width:100%!important;border:0!important;box-sizing:border-box}
</style>
`);
      zip.folder("assets"); // Folder vazio para assets externos se houver no futuro

      setExportProgress(90);
      await new Promise(r => setTimeout(r, 400)); // delay cosmético conclusivo

      console.log("4. Gerando Blob puro via JSZip...");
      const contentBlob = await zip.generateAsync({ type: "blob" });
      const filename = `${courseInfo.name.replace(/\s+/g, "_")}_Exportado.zip`;

      console.log("5. Acionando biblioteca nativa de salvamento...");
      // Restaura o formato tradicional e correto via biblioteca file-saver
      saveAs(contentBlob, filename);
      console.log("=== EXPORTAÇÃO ZIP CONCLUÍDA ===");

      setExportProgress(100);
    } catch (err) {
      console.error("=== ERRO CRÍTICO DURANTE EXPORTAÇÃO ZIP ===");
      console.error(err);
      alert(t('builder:zipExportError'));
    } finally {
      setTimeout(() => {
        setIsExporting(false);
        setExportProgress(0);
        setShowExportModal(false);
      }, 500); // 0.5s pra o usuário ver 100% e fechar
    }
  };

  const handleExportSiteZip = async () => {
    const violations = checkPremiumUsage();
    if (violations.length > 0) {
      if (isFreePlan()) {
        setPremiumViolationItems(violations);
        setShowPremiumModal(true);
        return;
      }
      setPaidWarningItems(violations);
      setShowPaidWarning(true);
      return;
    }

    setExportKind('site');
    setIsExporting(true);
    setExportProgress(15);
    await new Promise((r) => setTimeout(r, 600));
    setExportProgress(45);

    try {
      const zip = new JSZip();
      setExportProgress(70);
      const rawHtml = getSimulatedCode(
        undefined,
        undefined,
        undefined,
        moodleSimulatedExportStrings,
        { runtime: SITE_EXPORT_RUNTIME },
      );
      let completeHtml = buildSiteIndexDocument({
        title: `${courseInfo.name} - ${t('builder:exportedContent')}`,
        lang: i18n?.language === 'en' ? 'en' : 'pt-BR',
        bodyHtml: rawHtml,
        css: exportCss,
      });
      const layoutId = String(courseInfo.id || '').trim();
      const canSync = Boolean(
        authToken
        && layoutId
        && !(currentUser?.id && isFreeLocalProjectId(currentUser.id, layoutId)),
      );
      if (canSync) {
        try {
          const tokenRes = await fetch(`${API_BASE}/layouts/${encodeURIComponent(layoutId)}/site-publish/token`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${authToken}` },
          });
          if (tokenRes.ok) {
            const tokenData = await tokenRes.json().catch(() => ({}));
            if (tokenData.token) {
              completeHtml = injectSiteSyncScript(
                completeHtml,
                buildSiteSyncScript({
                  apiBase: resolveSitePublishApiBase(API_BASE),
                  token: tokenData.token,
                }),
              );
            }
          }
        } catch (_) { /* a página ainda abre com o HTML exportado */ }
      }
      zip.file('index.html', completeHtml);
      zip.file('style.css', exportCss);
      zip.file('LEIA-ME.txt', buildSiteReadme({ readme: t('builder:siteReadme') }));
      zip.folder('assets');
      setExportProgress(90);
      await new Promise((r) => setTimeout(r, 400));
      const contentBlob = await zip.generateAsync({ type: 'blob' });
      const filename = `${String(courseInfo.name || 'geppetto').replace(/\s+/g, '_')}_Site.zip`;
      saveAs(contentBlob, filename);
      setExportProgress(100);
    } catch (err) {
      console.error(err);
      alert(t('builder:zipExportError'));
    } finally {
      setTimeout(() => {
        setIsExporting(false);
        setExportKind('');
        setExportProgress(0);
        setShowExportModal(false);
      }, 500);
    }
  };

  const handlePublishSite = async () => {
    const violations = checkPremiumUsage();
    if (violations.length > 0) {
      if (isFreePlan()) {
        setPremiumViolationItems(violations);
        setShowPremiumModal(true);
        return;
      }
      setPaidWarningItems(violations);
      setShowPaidWarning(true);
      return;
    }

    const layoutId = String(courseInfo.id || '').trim();
    if (!authToken || !layoutId || (currentUser?.id && isFreeLocalProjectId(currentUser.id, layoutId))) {
      alert(t('builder:sitePublishNeedsCloud'));
      return;
    }

    setIsPublishingSite(true);
    try {
      const rawHtml = getSimulatedCode(
        undefined,
        undefined,
        undefined,
        moodleSimulatedExportStrings,
        { runtime: SITE_EXPORT_RUNTIME },
      );
      const html = buildSiteIndexDocument({
        title: `${courseInfo.name} - ${t('builder:exportedContent')}`,
        lang: i18n?.language === 'en' ? 'en' : 'pt-BR',
        bodyHtml: rawHtml,
        css: exportCss,
      });
      const response = await fetch(`${API_BASE}/layouts/${encodeURIComponent(layoutId)}/site-publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ html }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        alert(`${t('builder:sitePublishError')}${errData.error ? `: ${errData.error}` : ''}`);
        return;
      }
      await response.json().catch(() => ({}));
      showToast(t('builder:sitePublishDone'), 'success', 3600);
    } catch (err) {
      console.error(err);
      alert(t('builder:backendConnectionError'));
    } finally {
      setIsPublishingSite(false);
    }
  };

  const handlePublishMoodle = async () => {
    const violations = checkPremiumUsage();
    if (violations.length > 0) {
      if (isFreePlan()) {
        setPremiumViolationItems(violations);
        setShowPremiumModal(true);
        return;
      }
      setPaidWarningItems(violations);
      setShowPaidWarning(true);
      return;
    }

    const layoutId = String(courseInfo.id || '').trim();
    if (!authToken || !layoutId || (currentUser?.id && isFreeLocalProjectId(currentUser.id, layoutId))) {
      alert(t('builder:coursePublishNeedsCloud'));
      return;
    }

    setIsPublishingMoodle(true);
    try {
      const rawHtml = getSimulatedCode(undefined, undefined, undefined, moodleSimulatedExportStrings);
      let token = '';
      try {
        const tokenRes = await fetch(`${API_BASE}/layouts/${encodeURIComponent(layoutId)}/course-publish/token`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (tokenRes.ok) {
          const tokenData = await tokenRes.json().catch(() => ({}));
          if (tokenData.token) token = String(tokenData.token);
        }
      } catch (_) { /* publish ainda pode criar token */ }
      const html = buildMoodleCourseDocument({
        title: `${courseInfo.name} - ${t('builder:exportedContent')}`,
        lang: i18n?.language === 'en' ? 'en' : 'pt-BR',
        bodyHtml: rawHtml,
        css: exportCss,
        syncToken: token,
      });
      const response = await fetch(`${API_BASE}/layouts/${encodeURIComponent(layoutId)}/course-publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ html }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        alert(`${t('builder:coursePublishError')}${errData.error ? `: ${errData.error}` : ''}`);
        return;
      }
      const data = await response.json().catch(() => ({}));
      if (data.token) token = String(data.token);
      showToast(t('builder:coursePublishDone'), 'success', 4200);
      if (token && typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(token);
          showToast(t('builder:coursePublishTokenCopied'), 'info', 5000);
        } catch (_) { /* opcional */ }
      }
    } catch (err) {
      console.error(err);
      alert(t('builder:backendConnectionError'));
    } finally {
      setIsPublishingMoodle(false);
    }
  };

  const PremiumAlertModal = () => (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[2000] flex items-center justify-center p-4 animate-in fade-in duration-300">
      <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden border border-blue-100 dark:border-slate-600 transform animate-in zoom-in-95 duration-300">
        <div className="h-2 bg-[#3b82f6]" />
        <div className="p-8 text-center">
          <div className="w-20 h-20 bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center mx-auto mb-6">
            <Sparkles size={40} />
          </div>
          <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">{t('builder:upgradeRequired')} ✨</h3>
          <p className="text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
            {t('builder:premiumAccountMessage')} <b>{currentUser?.plan?.name || 'FREE'}</b> {t('builder:premiumAccountMessage2')}
          </p>

          <div className="bg-slate-50 dark:bg-slate-900/60 rounded-2xl p-4 mb-8 text-left border border-slate-100 dark:border-slate-600">
            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">{t('builder:blockedItemsLabel')}</p>
            <div className="flex flex-wrap gap-2">
              {premiumViolationItems.map((item) => {
                const elementLabel = ALL_ELEMENTS.find((e) => e.id === item)?.label;
                const label = elementLabel || item;
                return (
                  <span key={item} className="px-3 py-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 text-xs rounded-full font-medium shadow-sm">
                    {label}
                  </span>
                );
              })}
            </div>
            <button
              type="button"
              onClick={activateHighlightPlanRestricted}
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-rose-600 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300"
            >
              <ToggleRight size={15} />
              {t('builder:highlightOutOfPlanOnStage')}
            </button>
          </div>

          <p className="text-sm text-slate-500 dark:text-slate-400 mb-8 italic">
            {t('builder:premiumUpgradeMessage')}
          </p>

          <div className="flex flex-col gap-3">
            <button
              onClick={() => {
                setShowPremiumModal(false);
                setAppState('profile');
              }}
              className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl transition-all shadow-lg shadow-blue-200 dark:shadow-blue-900/40 flex items-center justify-center gap-2 group"
            >
              <Zap size={18} className="group-hover:animate-pulse" />
              {t('builder:upgradeNow')}
            </button>
            <button
              onClick={() => setShowPremiumModal(false)}
              className="w-full py-3 text-slate-500 dark:text-slate-400 font-semibold hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
            >
              {t('builder:removeElementsManually')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const FreeSaveModal = () => (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[2000] flex items-center justify-center p-4 animate-in fade-in duration-300">
      <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden border border-emerald-100 dark:border-emerald-900/40 transform animate-in zoom-in-95 duration-300">
        <div className="h-2 bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400" />
        <div className="p-8 text-center">
          <div className="w-20 h-20 bg-emerald-50 dark:bg-emerald-950/60 text-emerald-500 dark:text-emerald-400 rounded-full flex items-center justify-center mx-auto mb-6">
            <Heart size={40} />
          </div>
          <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">
            {t('builder:amazingLayout')}
          </h3>
          <p className="text-slate-600 dark:text-slate-300 mb-2 leading-relaxed">
            {t('builder:freeSaveMessage')}
          </p>
          <p className="text-slate-600 dark:text-slate-300 mb-4 leading-relaxed">
            {t('builder:freeSaveUpgradeHint')}
          </p>
          <div className="bg-emerald-50 dark:bg-emerald-950/40 rounded-2xl p-5 mb-6 text-left border border-emerald-100 dark:border-emerald-800/60">
            <p className="text-sm font-bold text-emerald-800 dark:text-emerald-300 mb-3">{t('builder:proPlanBenefits')}</p>
            <ul className="text-sm text-slate-600 dark:text-slate-300 space-y-2">
              <li className="flex items-center gap-2"><CheckCircle size={16} className="text-emerald-500 dark:text-emerald-400 flex-shrink-0" /> {t('builder:proBenefit1')}</li>
              <li className="flex items-center gap-2"><CheckCircle size={16} className="text-emerald-500 dark:text-emerald-400 flex-shrink-0" /> {t('builder:proBenefit2')}</li>
              <li className="flex items-center gap-2"><CheckCircle size={16} className="text-emerald-500 dark:text-emerald-400 flex-shrink-0" /> {t('builder:proBenefit3')}</li>
              <li className="flex items-center gap-2"><CheckCircle size={16} className="text-emerald-500 dark:text-emerald-400 flex-shrink-0" /> {t('builder:proBenefit4')}</li>
              <li className="flex items-center gap-2"><CheckCircle size={16} className="text-emerald-500 dark:text-emerald-400 flex-shrink-0" /> {t('builder:proBenefit5')}</li>
              <li className="flex items-center gap-2 pl-7 text-slate-500 dark:text-slate-400 italic">{t('builder:proBenefitMore')}</li>
            </ul>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 italic">
            {t('builder:exportTip')}
          </p>
          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={() => {
                setShowFreeSaveModal(false);
                setAppState('profile');
              }}
              className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-2xl transition-all shadow-lg shadow-emerald-200 dark:shadow-emerald-900/40 flex items-center justify-center gap-2 group"
            >
              <Rocket size={18} className="group-hover:animate-bounce" />
              {t('builder:knowProPlans')}
            </button>
            <button
              type="button"
              onClick={() => setShowFreeSaveModal(false)}
              className="w-full py-3 text-slate-500 dark:text-slate-400 font-semibold hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
            >
              {t('builder:continueEditing')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const PaidWarningToast = () => {
    useEffect(() => {
      const timer = setTimeout(() => setShowPaidWarning(false), 8000);
      return () => clearTimeout(timer);
    }, []);

    return (
      <div className="fixed top-4 right-4 z-[2000] animate-in slide-in-from-right duration-300 max-w-sm">
        <div className="bg-white rounded-2xl shadow-2xl border border-amber-200 overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-amber-400 to-orange-400" />
          <div className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-50 text-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={20} />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-bold text-slate-800 mb-1">{t('builder:paidWarningTitle')}</h4>
                <p className="text-xs text-slate-500 mb-2">
                  {t('builder:paidWarningDesc')}
                </p>
                <div className="flex flex-wrap gap-1">
                  {paidWarningItems.slice(0, 5).map((item) => {
                    const elementLabel = ALL_ELEMENTS.find((e) => e.id === item)?.label;
                    const label = elementLabel || item;
                    return (
                      <span key={item} className="px-2 py-0.5 bg-amber-50 text-amber-700 text-xs rounded-full font-medium border border-amber-200">
                        {label}
                      </span>
                    );
                  })}
                  {paidWarningItems.length > 5 && (
                    <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-xs rounded-full font-medium border border-amber-200">
                      +{paidWarningItems.length - 5} mais
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={activateHighlightPlanRestricted}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-rose-600 hover:text-rose-700"
                >
                  <ToggleRight size={13} />
                  {t('builder:highlightOutOfPlanOnStage')}
                </button>
              </div>
              <button onClick={() => setShowPaidWarning(false)} className="text-slate-400 hover:text-slate-600">
                <XCircle size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Legacy function kept for compatibility
  const startNewCourse = (e) => {
    e.preventDefault();
  };

  const addRowAt = (index, layoutType, customPercentages) => {
    if (isCanvasReadOnly) return;
    const newRow = createRowFromLayout(layoutType, customPercentages, { compact: false });
    if (!newRow) return;
    const idx = Math.max(0, Math.min(index ?? rows.length, rows.length));
    pushHistory();
    setRows([...rows.slice(0, idx), newRow, ...rows.slice(idx)]);
  };

  const addNestedRowToColumn = (hostColId, insertIndex, layoutType, customPercentages) => {
    if (isCanvasReadOnly) return;
    const newRow = createRowFromLayout(layoutType, customPercentages, { compact: true });
    if (!newRow) return;
    pushHistory();
    setRows((prev) => addNestedRowToColumnTree(prev, hostColId, insertIndex, newRow));
  };

  const addRow = (layoutType, customPercentages) => addRowAt(rows.length, layoutType, customPercentages);

  // eslint-disable-next-line no-unused-vars
  const addTemplate = (templateId) => {
    let newRow = {};
    if (templateId === 'hero') {
      newRow = {
        id: uid(),
        config: { padding: '4rem 2rem', backgroundColor: '#0f6cbf', textColor: '#ffffff' },
        columns: [
          {
            id: uid(),
            width: '1/1',
            widgets: [
              {
                id: uid(),
                type: 'htmlTemplate',
                content: {
                  html: `
                  <div style="text-align: center;">
                    <h1 style="font-size: 2.5rem; font-weight: bold; margin-bottom: 1rem;">Bem-vindo à Formação Avançada</h1>
                    <p style="font-size: 1.25rem; opacity: 0.9; max-width: 600px; margin: 0 auto;">Domine as habilidades do futuro através de uma metodologia pragmática e interativa.</p>
                  </div>`
                }
              }
            ]
          }
        ]
      };
    } else if (templateId === 'cards') {
      newRow = {
        id: uid(),
        config: { padding: '3rem 2rem', backgroundColor: '#f8fafc' },
        columns: [
          ...[1, 2, 3].map(i => ({
            id: uid(), width: '1/3',
            widgets: [
              {
                id: uid(), type: 'htmlTemplate',
                content: {
                  html: `
                  <div style="background: white; border-radius: 12px; padding: 2rem; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); text-align: center;">
                    <div style="width: 48px; height: 48px; background: #e0f2fe; color: #0284c7; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 1rem auto; font-size: 24px;">★</div>
                    <h3 style="color: #0f172a; font-size: 1.125rem; margin-bottom: 0.5rem;">Módulo ${i}</h3>
                    <p style="color: #64748b; font-size: 0.875rem;">Acesso rápido aos materiais base desta etapa.</p>
                  </div>`
                }
              }
            ]
          }))
        ]
      };
    } else if (templateId === 'text-video') {
      newRow = {
        id: uid(),
        config: { padding: '3rem 2rem', backgroundColor: '#ffffff' },
        columns: [
          {
            id: uid(), width: '1/2',
            widgets: [
              {
                id: uid(), type: 'text',
                content: { html: '<h2 style="color: #0f172a; margin-bottom: 1rem;">O Contexto Importa.</h2><p style="color: #475569; font-size: 1.125rem; line-height: 1.7;">Ao lado temos o vídeo gravado com o CEO detalhando as estratégias principais do semestre.</p>' }
              }
            ]
          },
          {
            id: uid(), width: '1/2',
            widgets: [
              { id: uid(), type: 'video', content: { url: DEFAULT_PLACEHOLDER_VIDEO_URL } }
            ]
          }
        ]
      };
    } else if (templateId === 'tpl-empreendedorismo') {
      newRow = {
        id: uid(),
        config: { padding: '0', backgroundColor: '#ffffff' },
        columns: [
          {
            id: uid(), width: '1/1',
            widgets: [
              {
                id: uid(), type: 'htmlTemplate',
                content: {
                  html: `
                    <div style="font-family: Arial, sans-serif; max-width: 900px; margin: 0 auto; padding: 2rem;">
                      <header style="background-color: #f8fafc; padding: 2rem; border-radius: 8px; margin-bottom: 2rem; border-left: 5px solid #3b82f6;">
                        <p style="color: #3b82f6; font-weight: bold; margin-bottom: 0.5rem; text-transform: uppercase; font-size: 0.8rem;">Empreendedorismo Digital</p>
                        <h1 style="color: #1e293b; margin-top: 0; font-size: 2rem;">Módulo 1 — Fundamentos</h1>
                        <p style="color: #64748b; font-size: 1.1rem; margin-top: 0.5rem; margin-bottom: 0;">Mercado, modelos e mentalidade</p>
                      </header>
                      
                      <section style="margin-bottom: 2.5rem;">
                        <h3 style="color: #334155; font-size: 1.25rem; border-bottom: 2px solid #e2e8f0; padding-bottom: 0.5rem;">🎯 Objetivo do Módulo</h3>
                        <p style="color: #475569; line-height: 1.6; margin-top: 1rem;">
                          Entender o que é <b>empreendedorismo digital</b>, como funciona o mercado online e quais modelos de negócio existem — para você conseguir enxergar oportunidades reais e escolher um caminho viável para começar.
                        </p>
                      </section>
                      
                      <section style="display: flex; gap: 1rem; margin-bottom: 2.5rem;">
                        <div style="flex: 1; border: 1px solid #e2e8f0; padding: 1.5rem; border-radius: 8px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);">
                          <h4 style="color: #0f172a; margin-top: 0;">Tradicional</h4>
                          <p style="color: #64748b; font-size: 0.9rem; line-height: 1.5;">Depende mais de localização, atendimento presencial e horários. Crescer costuma exigir mais estrutura física, equipe e custos fixos.</p>
                        </div>
                        <div style="flex: 1; border: 1px solid #e2e8f0; padding: 1.5rem; border-radius: 8px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);">
                          <h4 style="color: #0f172a; margin-top: 0;">Digital</h4>
                          <p style="color: #64748b; font-size: 0.9rem; line-height: 1.5;">Usa canais online para atrair e vender. Crescer pode ser mais rápido com automação e processos estruturados.</p>
                        </div>
                      </section>
                      
                      <div style="background-color: #eff6ff; border: 1px solid #bfdbfe; padding: 1.5rem; border-radius: 8px; text-align: center;">
                        <h4 style="color: #1e3a8a; margin-top: 0;">📌 Projeto do Módulo</h4>
                        <p style="color: #1e40af; margin-bottom: 0;">Crie um documento com 3 ideias de negócio digital e uma justificativa de mercado para cada uma.</p>
                      </div>
                    </div>
                  `
                }
              }
            ]
          }
        ]
      };
    } else if (templateId === 'tpl-design-instrucional') {
      newRow = {
        id: uid(),
        config: { padding: '0', backgroundColor: '#fafafa' },
        columns: [
          {
            id: uid(), width: '1/1',
            widgets: [
              {
                id: uid(), type: 'htmlTemplate',
                content: {
                  html: `
                    <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 900px; margin: 0 auto; padding: 2rem;">
                      <header style="text-align: center; margin-bottom: 3rem;">
                        <h1 style="color: #0f172a; font-size: 2.5rem; font-weight: 800; letter-spacing: -0.5px;">Design Instrucional no LMS Moodle</h1>
                        <p style="color: #64748b; font-size: 1.25rem; max-width: 700px; margin: 1rem auto;">
                          A importância de definir metas claras ao planejar um curso e utilizar estrategicamente as ferramentas do Moodle.
                        </p>
                      </header>
                      
                      <div style="background: white; border-radius: 12px; padding: 2rem; box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1); margin-bottom: 2rem; border-top: 4px solid #3b82f6;">
                        <h2 style="color: #1e293b; display: flex; align-items: center; gap: 0.5rem; margin-top: 0;">
                          <span style="display:inline-block; width:32px; height:32px; background:#dbeafe; color:#2563eb; border-radius:50%; text-align:center; line-height:32px; font-size:16px;">1</span>
                          Aula 1: Princípios Essenciais do Design Instrucional
                        </h2>
                        <div style="margin-left: 2.5rem;">
                          <h3 style="color: #334155; font-size: 1.1rem; margin-top: 1.5rem;">O que é Design Instrucional (DI)?</h3>
                          <p style="color: #475569; line-height: 1.6;">O Design Instrucional consiste na aplicação de uma abordagem sistemática e organizada para a criação de cursos eficazes. No contexto da EAD, foca na forma como o conteúdo é apresentado.</p>
                          
                          <h3 style="color: #334155; font-size: 1.1rem; margin-top: 1.5rem;">Princípios Fundamentais</h3>
                          <ul style="color: #475569; line-height: 1.6; padding-left: 1.2rem;">
                            <li style="margin-bottom: 0.5rem;"><strong>Foco nos Objetivos:</strong> Cada elemento do curso deve estar alinhado.</li>
                            <li style="margin-bottom: 0.5rem;"><strong>Sistematização:</strong> Organização clara promove assimilação.</li>
                            <li style="margin-bottom: 0.5rem;"><strong>Personalização:</strong> Uso de fóruns, quizzes adaptativos e trilhas condicionais.</li>
                          </ul>
                          
                          <div style="background-color: #f8fafc; border-left: 4px solid #94a3b8; padding: 1rem 1.5rem; margin-top: 2rem; border-radius: 0 8px 8px 0;">
                            <h4 style="color: #475569; margin-top: 0; margin-bottom: 0.5rem;">📝 Exercício Prático</h4>
                            <p style="color: #64748b; font-size: 0.95rem; margin-bottom: 0;">Criar um esboço de curso online aplicando os princípios do DI.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  `
                }
              }
            ]
          }
        ]
      };
    }
    pushHistory();
    setRows([...rows, newRow]);
  };

  const removeRow = (rowId) => {
    releaseWidgetLock(`row:${rowId}`);
    setActiveEditKey(prev => (prev === `row:${rowId}` ? null : prev));
    pushHistory();
    setRows(rows.filter(r => r.id !== rowId));
    if (selectedSlotId?.rowId === rowId) setSelectedSlotId(null);
  };

  const moveRowToPosition = (rowId, newPosition1Based) => {
    const idx = rows.findIndex(r => r.id === rowId);
    if (idx < 0) return;
    const targetIdx = Math.max(0, Math.min(rows.length - 1, newPosition1Based - 1));
    if (targetIdx === idx) return;
    pushHistory();
    const items = [...rows];
    const [moved] = items.splice(idx, 1);
    items.splice(targetIdx, 0, moved);
    setRows(items);
  };

  const moveNestedRowToPosition = (rowId, newPosition1Based) => {
    const clone = JSON.parse(JSON.stringify(rows));
    const ctx = findRowContext(clone, rowId);
    if (!ctx || ctx.scope !== 'nested') return;
    const targetIdx = Math.max(0, Math.min(ctx.parentList.length - 1, newPosition1Based - 1));
    if (targetIdx === ctx.index) return;
    pushHistory();
    const list = ctx.parentList;
    const [moved] = list.splice(ctx.index, 1);
    list.splice(targetIdx, 0, moved);
    setRows(clone);
  };

  const removeNestedRowById = (rowId) => {
    releaseWidgetLock(`row:${rowId}`);
    setActiveEditKey(prev => (prev === `row:${rowId}` ? null : prev));
    setActiveRowSettings(prev => (prev === rowId ? null : prev));
    setSelectedSlotId(prev => (prev?.rowId === rowId ? null : prev));
    pushHistory();
    setRows(removeNestedRowFromTree(rows, rowId));
  };

  const moveWidgetToPosition = (rowId, colId, widgetId, newPosition1Based) => {
    pushHistory();
    setRows((prev) => {
      const moveIn = (rowList) => rowList.map((row) => {
        if (row.id === rowId) {
          return {
            ...row,
            columns: row.columns.map((c) => {
              if (c.id !== colId) return c;
              const wIdx = c.widgets.findIndex((w) => w.id === widgetId);
              if (wIdx < 0) return c;
              const widgets = [...c.widgets];
              const targetIdx = Math.max(0, Math.min(widgets.length - 1, newPosition1Based - 1));
              if (targetIdx === wIdx) return c;
              const [moved] = widgets.splice(wIdx, 1);
              widgets.splice(targetIdx, 0, moved);
              return { ...c, widgets };
            }),
          };
        }
        return {
          ...row,
          columns: row.columns.map((col) => ({
            ...col,
            nestedRows: col.nestedRows?.length ? moveIn(col.nestedRows) : col.nestedRows,
          })),
        };
      });
      return moveIn(prev);
    });
  };

  /** Coluna do palco + grids aninhados (recursivo). topRowLockedByOther = lock da linha arrastável do canvas raiz. */
  function renderColumnCell(parentRow, col, colIdx, topRowLockedByOther, isNestedRow = false) {
    const isDynamic = col.width === 'dynamic';
    const colClassBase = isNestedRow ? '' : (isDynamic ? '' : (COLUMN_TYPES.find((c0) => c0.id === col.width)?.class || 'col-1-1'));
    const rowColsForGap = parentRow.columns || [];
    const ccPre = normalizeColumnConfig(col.config || {});
    const gapLayoutStyle = buildColumnGapLayoutStyle(col, colIdx, rowColsForGap, { isNested: isNestedRow });
    const colStyle = gapLayoutStyle ? { ...gapLayoutStyle } : {};
    const rowGapMeta = getRowColumnGapMeta(rowColsForGap);
    const colGapLayoutActive = rowGapMeta.totalBetweenGapPx > 0;
    const isSelected = selectedSlotId?.rowId === parentRow.id && selectedSlotId?.colIdx === colIdx;
    const cc = ccPre;
    const colMobileEdit = getColumnMobileOverrides(cc);
    const colRespClasses = columnResponsiveClassNames(cc).join(' ').trim();
    const colCss = elementAdvancedCss(cc.advancedCss, cc.customClass);
    const centerOnSmallActive = !!colMobileEdit.centerOnSmall;
    const colAi = columnAlignToFlex(cc.columnAlign);
    const colLockKey = `column:${parentRow.id}:${col.id}`;
    const colLockInfo = presenceLocks[colLockKey];
    const colLockedByOther = !!(colLockInfo?.user_id && String(colLockInfo.user_id) !== String(currentUserId || ''));
    const colLockLabel = colLockInfo?.user_name || colLockInfo?.user_id || t('builder:anotherUser', { defaultValue: 'Outro usuário' });
    const parentRowScopedLock = presenceLocks[`row:${parentRow.id}`];
    const rowLockedByOtherScoped = !!(parentRowScopedLock?.user_id && String(parentRowScopedLock.user_id) !== String(currentUserId || ''));
    const anyRowLockedByOther = topRowLockedByOther || rowLockedByOtherScoped;
    const outerColumnStyle = {
      position: 'relative',
      ...colStyle,
      ...(centerOnSmallActive && editBreakpoint === EDIT_BREAKPOINT_MOBILE
        ? { alignItems: 'center', textAlign: 'center' }
        : {
            ...(cc.textAlign ? { textAlign: cc.textAlign } : {}),
            ...(colAi ? { alignItems: colAi } : {}),
          }),
      ...(cc.heightPx > 0 ? { minHeight: cc.heightPx } : {}),
      ...(editBreakpoint === EDIT_BREAKPOINT_MOBILE && colMobileEdit.paddingTop > 0
        ? { paddingTop: colMobileEdit.paddingTop }
        : (cc.paddingTop > 0 ? { paddingTop: cc.paddingTop } : {})),
      ...(editBreakpoint === EDIT_BREAKPOINT_MOBILE && colMobileEdit.paddingBottom > 0
        ? { paddingBottom: colMobileEdit.paddingBottom }
        : (cc.paddingBottom > 0 ? { paddingBottom: cc.paddingBottom } : {})),
      ...((cc.backgroundColor && cc.backgroundColor !== 'transparent') ? { backgroundColor: cc.backgroundColor } : {}),
      ...(cc.bgImage ? { backgroundImage: `url(${cc.bgImage})`, backgroundSize: 'cover', backgroundPosition: '50% 50%' } : {}),
      ...cssStringToObject(colCss.inline),
    };

    return (
      <div
        key={col.id}
        className={`builder-column madri-col-${col.id} ${colClassBase} ${colRespClasses} ${colCss.classNames} ${colGapLayoutActive ? 'builder-column--gap-layout' : ''}${isHiddenOnMobile(cc) ? ` ${HIDE_ON_MOBILE_CLASS}` : ''}`.trim()}
        style={outerColumnStyle}
      >
        <AdvancedCssStyleTag styleId={`col-${col.id}`} css={colCss.stylesheet} />
        {isHiddenOnMobile(cc) && (
          <div className="madri-hide-mobile-note">{t('builder:hiddenOnMobileNote', { defaultValue: 'Oculto no celular' })}</div>
        )}
        {cc.waveConfig?.enabled && getWaveSvgDataUrl(cc.waveConfig) && (
          <div style={{ position: 'absolute', left: 0, right: 0, width: '100%', height: cc.waveConfig.height || 60, [cc.waveConfig.direction === 'top' ? 'top' : 'bottom']: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
            <div style={{ width: '100%', height: '100%', backgroundImage: getWaveSvgDataUrl(cc.waveConfig), backgroundSize: '100% 100%', backgroundRepeat: 'no-repeat', transform: cc.waveConfig.direction === 'top' ? 'scaleY(-1)' : 'none' }} />
          </div>
        )}
        {!isCanvasReadOnly && (
        <div className="builder-col-controls" style={{ position: 'absolute', top: 6, right: 6, zIndex: 6, display: 'flex', gap: 4 }}>
          <button
            type="button"
            className="row-control-btn"
            title={colLockedByOther ? `${colLockLabel} ${t('builder:isEditing', { defaultValue: 'editando' })}` : t('builder:columnConfig', { defaultValue: 'Configurar coluna' })}
            onClick={async (e) => {
              e.stopPropagation();
              if (anyRowLockedByOther || colLockedByOther) return;
              const rowWidgetLocks = collectWidgetsInRow(parentRow)
                .map((w) => presenceLocks[w.id])
                .filter(Boolean);
              if (rowWidgetLocks.length > 0) {
                const who = rowWidgetLocks[0]?.user_name || rowWidgetLocks[0]?.user_id || t('builder:anotherUser', { defaultValue: 'Outro usuário' });
                showToast(`${who} ${t('builder:isEditing', { defaultValue: 'editando' })} ${t('builder:element', { defaultValue: 'um elemento' })}.`, 'warning');
                return;
              }
              const headerRowLock = presenceLocks[`row:${parentRow.id}`];
              if (headerRowLock && String(headerRowLock.user_id) !== String(currentUserId || '')) {
                const who = headerRowLock.user_name || headerRowLock.user_id || t('builder:anotherUser', { defaultValue: 'Outro usuário' });
                showToast(`${who} ${t('builder:isEditing', { defaultValue: 'está editando' })} ${t('builder:rowConfigTitle', { defaultValue: 'a seção' })}.`, 'warning');
                return;
              }
              if (activeEditKey && activeEditKey !== colLockKey) {
                showToast(t('builder:finishCurrentEditFirst', { defaultValue: 'Finalize a edição atual antes de abrir outra configuração.' }), 'warning');
                return;
              }
              const ok = await acquireWidgetLock(colLockKey);
              if (!ok) return;
              pushHistory();
              setActiveEditKey(colLockKey);
              setActiveColumnSettings({ rowId: parentRow.id, colId: col.id });
            }}
            style={{ padding: '4px 6px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff' }}
          >
            <Pencil size={14} color={BUILDER_EDIT_PENCIL.column} strokeWidth={2.2} />
          </button>
        </div>
        )}
        {colLockedByOther && (
          <div style={{ position: 'absolute', left: 8, top: 8, zIndex: 6, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 999, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>
            {colLockLabel}
          </div>
        )}
        <Droppable droppableId={col.id} type="widget">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`min-h-[120px] rounded-lg border-2 border-dashed transition-all duration-200 p-3 flex flex-col gap-3 ${isCanvasReadOnly
                ? 'border-slate-200/80 cursor-default'
                : (isSelected
                ? 'border-blue-500 bg-blue-50/50 shadow-[0_0_0_4px_rgba(99,102,241,0.1)]'
                : 'border-slate-200 hover:border-blue-400 cursor-pointer')
                }`}
              style={{
                height: '100%',
                minHeight: '100px',
                position: 'relative',
                zIndex: 1,
                width: '100%',
                ...(centerOnSmallActive && editBreakpoint === EDIT_BREAKPOINT_MOBILE
                  ? { alignItems: 'center', textAlign: 'center' }
                  : {}),
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (!isCanvasReadOnly) handleSlotClick(col.id, colIdx, parentRow.id);
              }}
            >
              {col.widgets.map((w, wIdx) => (
                <Draggable key={w.id} draggableId={w.id} index={wIdx} isDragDisabled={isCanvasReadOnly}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={provided.draggableProps.style}
                      className={snapshot.isDragging ? 'is-dragging-widget relative z-50' : 'relative group/elem'}
                    >
                      <WidgetRenderer
                        widget={w}
                        dragHandleProps={provided.dragHandleProps}
                        globalCardBg={courseInfo.globalConfig?.cardBg}
                        globalTypographyConfig={stageGlobalConfig}
                        onRemove={(e) => { if (e) e.stopPropagation(); removeWidget(parentRow.id, col.id, w.id); }}
                        onUpdate={(wId, newContent) => updateWidget(parentRow.id, col.id, wId, newContent)}
                        onDuplicate={() => duplicateWidget(parentRow.id, col.id, w)}
                        onAfterConfigSave={schedulePersistLayoutAfterModal}
                        onBeforeEdit={pushHistory}
                        onRequestEdit={acquireWidgetLock}
                        onReleaseEdit={releaseWidgetLock}
                        lockInfo={presenceLocks[w.id]}
                        currentUserId={currentUserId}
                        rowLockInfo={presenceLocks[`row:${parentRow.id}`]}
                        parentRowId={parentRow.id}
                        activeEditKey={activeEditKey}
                        setActiveEditKey={setActiveEditKey}
                        stagePosition={wIdx + 1}
                        stagePositionTotal={col.widgets.length}
                        onStagePositionChange={(v) => moveWidgetToPosition(parentRow.id, col.id, w.id, v)}
                        titleBasicAnchorsForMenu={titleBasicAnchorsList}
                        tocAvailableEntries={tocEntriesList}
                        readOnly={isCanvasReadOnly}
                        showToast={showToast}
                        asaasGatewayConfigured={asaasGatewayConfigured}
                        onOpenAsaasGatewaySettings={openBuilderPaymentGatewaySettings}
                        mercadopagoGatewayConfigured={mercadopagoGatewayConfigured}
                        onOpenMercadoPagoGatewaySettings={() => openBuilderPaymentGatewaySettings('mercadopago')}
                        cricketLinkEnabled={cricketWidgetLinkEnabled}
                        cricketLayoutId={cricketWidgetLinkEnabled ? String(courseInfo.id) : null}
                        cricketAuthToken={cricketWidgetLinkEnabled ? authToken : null}
                        cricketStageBadge={cricketWidgetLinkEnabled ? cricketWidgetBadgeByWidgetId[String(w.id)] ?? null : null}
                        cricketStageLampsHidden={cricketStageLampsHidden}
                        onCricketOverlayInvalidate={loadCricketOverlaySnapshot}
                        cricketFocusHighlight={cricketFocusHighlightWidgetId === String(w.id)}
                        onCricketLampClick={cricketWidgetLinkEnabled ? focusCricketLinkedWidgetOnStage : null}
                        editBreakpoint={editBreakpoint}
                        microquizCheckpointOptions={microquizCheckpointOptionsList}
                        planRestrictedHighlight={!!(highlightPlanRestricted && planAllowedElementTypes && !planAllowedElementTypes.has(w.type))}
                        planAllowedDimOthers={!!(highlightPlanRestricted && planAllowedElementTypes)}
                      />
                    </div>
                  )}
                </Draggable>
              ))}

              {provided.placeholder}

              {col.widgets.length === 0 && !isCanvasReadOnly && (
                <div className="flex-1 flex flex-col items-center justify-center text-slate-400 gap-2 opacity-60 hover:opacity-100 transition-opacity">
                  <Plus size={20} className={isSelected ? 'text-blue-500' : ''} />
                  <span className="text-xs font-medium text-center px-1 max-w-full break-words">
                    {isSelected ? t('builder:chooseBlock') : t('builder:selectEmptySlot')}
                  </span>
                </div>
              )}

            </div>
          )}
        </Droppable>

        {(col.nestedRows || []).flatMap((nRow, nRi) => [
          !isCanvasReadOnly && (
          <div key={`nest-ins-${col.id}-${nRi}`} className="flex justify-center py-1">
            <button
              type="button"
              onClick={() => openGridCatalogForStageInsert({ nested: true, hostColId: col.id, index: nRi })}
              className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-violet-600 hover:bg-violet-100 dark:hover:bg-violet-900/30 rounded-md border border-dashed border-violet-300 dark:border-violet-600"
            >
              <Plus size={12} /> {t('builder:addNestedGrid', { defaultValue: 'Grid interno' })}
            </button>
          </div>
          ),
          (() => {
            const nestedRowBg = resolveRowBackgroundColor(nRow.config);
            const nestedRowGap = rowColumnsBetweenGapPx(nRow.columns || []);
            const nestedContainerGapStyle = buildRowContainerGapStyle(nRow.columns || []);
            const hasNestedRowBgImg = !!(nRow.config?.bgImage || '').trim();
            const nestedGridRestricted = !!(highlightPlanRestricted && isPlanGridRestricted(nRow));
            const nestedGridDim = !!(highlightPlanRestricted && planAllowedGridIds && !isPlanGridRestricted(nRow));
            const nestedCss = elementAdvancedCss(nRow.config?.advancedCss, nRow.config?.customClass);
            return (
          <div
            key={nRow.id}
            className={`builder-row builder-row--nested madri-row-${nRow.id} ${nestedCss.classNames} mt-0.5 rounded-lg border-2 border-violet-300 dark:border-violet-700 p-2 relative${nestedGridRestricted ? ' gepetto-plan-restricted' : ''}`}
            data-geppetto-plan-restricted={nestedGridRestricted ? '1' : undefined}
            style={{
              backgroundColor: nestedRowBg !== 'transparent' ? nestedRowBg : 'rgba(139, 92, 246, 0.06)',
              ['--madri-row-gap-total']: `${nestedRowGap}px`,
              ...cssStringToObject(nestedCss.inline),
              ...(nestedGridRestricted ? getPlanRestrictedHighlightStyle() : {}),
              ...(nestedGridDim ? { opacity: 0.55, transition: 'opacity 0.25s ease' } : {}),
            }}
          >
            <AdvancedCssStyleTag styleId={`row-${nRow.id}`} css={nestedCss.stylesheet} />
            {hasNestedRowBgImg && <RowBackgroundImageLayer rc={nRow.config} />}
            {!isCanvasReadOnly && (
            <div className="builder-row-controls" style={{ position: 'relative', zIndex: 2 }}>
              <button
                type="button"
                className="row-control-btn"
                title={t('builder:btnConfig')}
                onClick={async () => {
                  const nRowLockInfo = presenceLocks[`row:${nRow.id}`];
                  if (nRowLockInfo?.user_id && String(nRowLockInfo.user_id) !== String(currentUserId || '')) return;
                  const nwLocks = collectWidgetsInRow(nRow).map((w) => presenceLocks[w.id]).filter(Boolean);
                  if (nwLocks.length > 0) {
                    showToast(t('builder:finishCurrentEditFirst', { defaultValue: 'Finalize a edição atual antes de abrir outra configuração.' }), 'warning');
                    return;
                  }
                  if (activeEditKey && activeEditKey !== `row:${nRow.id}`) {
                    showToast(t('builder:finishCurrentEditFirst', { defaultValue: 'Finalize a edição atual antes de abrir outra configuração.' }), 'warning');
                    return;
                  }
                  const ok = await acquireWidgetLock(`row:${nRow.id}`);
                  if (!ok) return;
                  pushHistory();
                  setActiveEditKey(`row:${nRow.id}`);
                  setActiveRowSettings(nRow.id);
                }}
              >
                <Pencil size={14} color={BUILDER_EDIT_PENCIL.row} strokeWidth={2.2} />
              </button>
              <button type="button" className="row-control-btn" title={t('builder:btnDuplicateRow')} onClick={() => duplicateRowById(nRow.id)}>
                <CopyPlus size={14} />
              </button>
              {planSectionsLibraryEnabled && (
                <>
                  <button
                    type="button"
                    className="row-control-btn"
                    title={t('builder:sectionPresetSaveBtn', { defaultValue: 'Salvar seção na biblioteca' })}
                    onClick={() => openSectionLibraryModal('save', nRow.id)}
                  >
                    <Save size={14} />
                  </button>
                  <button
                    type="button"
                    className="row-control-btn"
                    title={t('builder:sectionPresetInsertBtn', { defaultValue: 'Inserir seção da biblioteca' })}
                    onClick={() => openSectionLibraryModal('insert', nRow.id)}
                  >
                    <Download size={14} />
                  </button>
                </>
              )}
              <button type="button" className="row-control-btn danger" title={t('builder:btnDeleteRow')} onClick={() => removeNestedRowById(nRow.id)}>
                <Trash2 size={14} />
              </button>
            </div>
            )}
            <div
              className={`builder-columns-container flex w-full ${nestedRowGap > 0 ? 'builder-columns-container--col-gap flex-wrap md:flex-nowrap' : 'flex-wrap'}`}
              style={{ position: 'relative', zIndex: 1, ...(nestedContainerGapStyle || {}) }}
            >
              {nRow.columns.map((ncol, ncolIdx) => renderColumnCell(nRow, ncol, ncolIdx, topRowLockedByOther, true))}
            </div>
          </div>
            );
          })(),
        ])}
        {!isCanvasReadOnly && (
        <div className="flex justify-center py-1">
          <button
            type="button"
            onClick={() => openGridCatalogForStageInsert({ nested: true, hostColId: col.id, index: (col.nestedRows || []).length })}
            className="flex items-center gap-1 px-2 py-1.5 text-[11px] font-medium text-violet-600 hover:bg-violet-100 dark:hover:bg-violet-900/30 rounded-md border border-dashed border-violet-300 dark:border-violet-600"
          >
            <Layers size={12} /> {t('builder:addNestedGridHere', { defaultValue: 'Adicionar grid nesta célula' })}
          </button>
        </div>
        )}
      </div>
    );
  }

  const addWidgetToColumn = (type) => {
    if (isCanvasReadOnly) {
      showToast(t('builder:canvasReadOnlyHint'), 'info', 4200);
      return;
    }
    if (isSiteOnlyWidget(type) && readDeliveryMode() !== 'site') return;
    const isHeader = HEADER_ELEMENTS.includes(type);
    if (!isHeader && !selectedSlotId) return;
    if (type === 'header-menu' && headerWidgets.some((w) => w.type === 'header-menu')) {
      alert(t('builder:headerMenuOnlyOne', { defaultValue: 'Só pode existir um menu de cabeçalho. Remova o atual para adicionar outro.' }));
      return;
    }

    // Default content based on type (usa idioma atual)
    const tb = (k, opts) => t(`builder:${k}`, opts);
    let newWidget = { id: uid(), type, content: {}, styles: {} };
    if (type === 'text') newWidget.content = { html: `<h2>${tb('defaultHeading')}</h2><p>${tb('defaultText')}</p>` };
    if (type === 'title-basic') {
      newWidget.content = {
        title: tb('defaultHeading'),
        subtitle: '',
        headingLevel: 'h2',
        anchorId: '',
        includeInToc: false,
        tocLabel: '',
        tocLevel: 1,
        titleColor: '#0f172a',
        subtitleColor: '#64748b',
        textAlign: 'left',
        titleFontSize: 0,
        subtitleFontSize: 0,
        titleFontWeight: 700,
        scrollMarginTop: 72,
      };
    }
    if (type === 'table-of-contents') {
      newWidget.content = {
        title: tb('tocDefaultTitle'),
        tocPreset: 'classic',
        showNumbers: true,
        showPageNumbers: false,
        showLeaders: false,
        showIcons: false,
        linkColor: '',
        itemGap: 0,
        sortMode: 'document',
        fontSize: 15,
        lineHeight: 1.5,
        items: [],
      };
    }
    if (type === 'chart-widget') {
      newWidget.content = {
        chartType: 'column',
        title: '',
        height: 360,
        showLegend: true,
        categories: ['A', 'B', 'C', 'D'],
        series: [
          { name: 'Série 1', data: [120, 200, 150, 180] },
          { name: 'Série 2', data: [80, 160, 110, 140] },
        ],
        items: [
          { label: 'A', value: 40 },
          { label: 'B', value: 32 },
          { label: 'C', value: 28 },
        ],
        gaugeValue: 65,
        gaugeMax: 100,
      };
    }
    if (type === 'flowchart-widget') {
      newWidget.content = {
        mermaidSource: DEFAULT_MERMAID_FLOWCHART,
        theme: 'default',
        align: 'center',
      };
    }
    if (type === 'alert') newWidget.content = { text: 'Important Information' };
    if (type === 'video') newWidget.content = { url: DEFAULT_PLACEHOLDER_VIDEO_URL };
    if (type === 'h5p') newWidget.content = { url: DEFAULT_PLACEHOLDER_H5P_URL };
    if (type === 'styled-card') newWidget.content = { tag: tb('defaultCardTag'), title: tb('defaultCardTitle'), html: `<p>${tb('defaultCardBody')}</p>`, includeInToc: false, tocLabel: '', tocLevel: 1, anchorId: '', tocIcon: '', scrollMarginTop: 72 };
    if (type === 'section-header') newWidget.content = { tag: tb('defaultSectionTag'), title: tb('defaultSectionTitle'), subtitle: tb('defaultSectionSubtitle'), includeInToc: false, tocLabel: '', tocLevel: 1, anchorId: '', scrollMarginTop: 72 };
    if (type === 'htmlTemplate') newWidget.content = { html: DEFAULT_HTML_TEMPLATE_MOCKUP };
    if (type === 'styled-button') newWidget.content = { text: tb('defaultButton'), colorsNormal: { bg: '#3b82f6', text: '#ffffff' }, size: 'M' };
    if (type === 'list-icon') newWidget.content = { html: 'Item 1', iconColor: '#3b82f6' };
    if (type === 'image') newWidget.content = {};
    if (type === 'page-break') newWidget.content = { navStyle: 'simple-text', btnBgColor: '#3b82f6', btnTextColor: '#ffffff' };
    if (type === 'tabs') newWidget.content = { tabs: [{ label: tb('defaultTab1'), body: `<p>${tb('defaultTab1Body')}</p>` }, { label: tb('defaultTab2'), body: `<p>${tb('defaultTab2Body')}</p>` }], accentColor: '#3b82f6' };
    if (type === 'accordion') newWidget.content = { items: [{ question: tb('defaultAccordionQ1'), answer: tb('defaultAccordionA1') }, { question: tb('defaultAccordionQ2'), answer: tb('defaultAccordionA2') }], accentColor: '#3b82f6' };
    if (type === 'modal-trigger') newWidget.content = { triggerLabel: tb('defaultModalTrigger'), btnStyle: 'button', btnBg: '#3b82f6', btnText: '#ffffff', modalTitle: tb('defaultModalTitle'), modalBody: `<p>${tb('defaultModalBody')}</p>`, align: 'left', modalPanelWidthPx: 800, modalPanelMaxHeightPx: 600 };
    if (type === 'carousel') newWidget.content = { slides: [{ url: '', caption: tb('defaultSlide1') }, { url: '', caption: tb('defaultSlide2') }], accentColor: '#3b82f6' };
    if (type === 'card-slider') {
      newWidget.content = createDefaultCardSliderContent({
        card1: tb('cardSliderCard1'),
        card2: tb('cardSliderCard2'),
        card3: tb('cardSliderCard3'),
        card4: tb('cardSliderCard4'),
        cardText: tb('cardSliderCardTextDefault'),
        button: tb('cardSliderButtonDefault'),
      });
    }
    if (type === 'card-shop') {
      newWidget.content = createDefaultCardShopContent({
        card1: tb('cardShopCard1'),
        card2: tb('cardShopCard2'),
        cardText: tb('cardShopCardTextDefault'),
        button: tb('cardShopButtonDefault'),
      });
    }
    if (type === 'video-card-slider') {
      newWidget.content = createDefaultVideoCardSliderContent({
        video1: tb('videoSliderItem1'),
        video2: tb('videoSliderItem2'),
        video3: tb('videoSliderItem3'),
      });
    }
    if (type === 'image-card') newWidget.content = { imageUrl: '', imageHeight: 200, cardTitle: tb('defaultImageCardTitle'), cardText: tb('defaultImageCardText'), showBtn: true, btnLabel: tb('defaultLearnMore'), btnLink: '#', btnBg: '#3b82f6', btnTextColor: '#ffffff', cardBg: '#ffffff' };
    if (type === 'social-card') {
      newWidget.content = {
        bannerUrl: '',
        bannerHeight: 160,
        avatarMode: 'mockup',
        mockupAvatarUrl: '',
        avatarMoodleUserId: '',
        avatarFiltercodes: '{global_social_avatar}',
        avatarSize: 88,
        cardTag: '',
        cardTitle: tb('defaultSocialCardTitle'),
        cardSubtitle: tb('defaultSocialCardSubtitle'),
        cardText: '',
        showBtn: true,
        btnStyle: 'outline',
        btnLabel: tb('defaultSocialCardBtn'),
        btnLink: '#',
        btnBg: '#2563eb',
        btnTextColor: '#ffffff',
        cardBg: '#ffffff',
        titleColor: '#0f172a',
        subtitleColor: '#64748b',
        textColor: '#64748b',
      };
    }
    if (type === 'post-it') newWidget.content = { html: `<p>${tb('defaultPostIt')}</p>`, bg: '#fef08a', iconColor: '#854d0e', textColor: '#422006', icon: 'StickyNote' };
    if (type === 'divider') newWidget.content = { dividerStyle: 'solid', color: '#cbd5e1', thickness: 2, width: 100 };
    if (type === 'spacer') newWidget.content = { heightPx: 48, spacerBg: 'transparent', decoration: 'none', decoColor: '#94a3b8' };
    if (type === 'responsive-table') {
      newWidget.content = {
        columns: 3,
        rowCount: 4,
        headerRow: true,
        rows: [
          [tb('tableCol1'), tb('tableCol2'), tb('tableCol3')],
          ['', '', ''],
          ['', '', ''],
          ['', '', ''],
        ],
        columnBgs: ['', '', ''],
        borderWidth: 1,
        borderColor: '#e2e8f0',
        borderStyle: 'solid',
        headerBg: '#f1f5f9',
        cellBg: '#ffffff',
        zebra: false,
        zebraBg: '#f8fafc',
        headerTextColor: '#334155',
        bodyTextColor: '#334155',
        widthPct: 100,
        cellPadding: 10,
        fontSize: 14,
        textAlign: 'left',
      };
    }
    if (type === 'image-gallery') {
      newWidget.content = {
        images: [{ url: '', alt: '' }, { url: '', alt: '' }, { url: '', alt: '' }],
        columns: 3,
        gap: 8,
        borderRadius: 10,
        layout: 'grid',
        animation: 'none',
        hoverZoom: true,
        lightbox: true,
      };
    }
    if (type === 'checkpoint') newWidget.content = { durationMs: 5000, iconType: 'check', position: 'top-right' };
    if (type === 'reinforcement-quiz') newWidget.content = createDefaultMicroquizContent(tb);
    if (type === 'comments') newWidget.content = {};
    if (type === 'progress-bar') newWidget.content = { accentColor: '#3b82f6', showPercent: true, barStyle: 'simple', widthPct: 100, barAlign: 'center', progressBarBg: '#f8fafc', trackColor: '#e2e8f0', progressBarTextColor: '#64748b', progressBarIcon: 'Gauge', showSegmentedCompletion: true, segmentedCompletionText: 'para completar', segmentedCompletionColor: '#64748b' };
    if (type === 'header-menu') {
      newWidget.content = {
        menuStyle: 'horizontal',
        relativeToProgress: 'above',
        menuAlign: 'center',
        menuBg: 'transparent',
        linkColor: '#334155',
        linkHoverColor: '#0f172a',
        fontSize: 14,
        gap: 16,
        showIcons: true,
        items: [
          { label: tb('headerMenuItemHome'), href: '/', icon: 'Home', openNewTab: false },
          { label: tb('headerMenuItemCourse'), href: '/course/view.php?id=1', icon: 'BookOpen', openNewTab: false },
          { label: tb('headerMenuItemExternal'), href: 'https://moodle.org', icon: 'ExternalLink', openNewTab: true },
        ],
        marginObj: { top: 0, bottom: 0, left: 0, right: 0 },
        paddingObj: { top: 4, bottom: 4, left: 0, right: 0 },
      };
    }
    if (type === 'site-menu') {
      newWidget.content = createDefaultSiteMenuContent({
        logoAlt: tb('siteMenuLogoAlt'),
        logoText: tb('siteMenuLogoText'),
        loginLabel: tb('siteMenuLoginLabel'),
        home: tb('siteMenuItemHome'),
        about: tb('siteMenuItemAbout'),
        courses: tb('siteMenuItemCourses'),
        courseItem: tb('siteMenuItemCourse'),
        contact: tb('siteMenuItemContact'),
      });
    }
    if (type === 'site-footer') {
      newWidget.content = createDefaultSiteFooterContent({
        logoAlt: tb('siteFooterLogoAlt'),
        logoText: tb('siteFooterLogoText'),
        about: tb('siteFooterAboutDefault'),
        phone: tb('siteFooterPhoneDefault'),
        email: tb('siteFooterEmailDefault'),
        address: tb('siteFooterAddressDefault'),
        socialTitle: tb('siteFooterSocialTitleDefault'),
        copyright: tb('siteFooterCopyrightDefault'),
        colInstitution: tb('siteFooterColInstitution'),
        colStudents: tb('siteFooterColStudents'),
        colSupport: tb('siteFooterColSupport'),
        aboutLink: tb('siteFooterLinkAbout'),
        courses: tb('siteFooterLinkCourses'),
        blog: tb('siteFooterLinkBlog'),
        contact: tb('siteFooterLinkContact'),
        student: tb('siteFooterLinkStudent'),
        certificates: tb('siteFooterLinkCertificates'),
        faq: tb('siteFooterLinkFaq'),
        help: tb('siteFooterLinkHelp'),
        talk: tb('siteFooterLinkTalk'),
        jobs: tb('siteFooterLinkJobs'),
        privacy: tb('siteFooterPrivacy'),
        terms: tb('siteFooterTerms'),
      });
    }
    if (type === 'count-up') {
      newWidget.content = createDefaultCountUpContent({
        label: tb('countUpLabelDefault'),
        caption: tb('countUpCaptionDefault'),
      });
    }
    if (type === 'site-maintenance') {
      newWidget.content = createDefaultMaintenanceContent({
        badge: tb('siteMaintenanceBadgeDefault'),
        title: tb('siteMaintenanceTitleDefault'),
        subtitle: tb('siteMaintenanceSubtitleDefault'),
        progressLabel: tb('siteMaintenanceProgressLabel'),
        progressHint: tb('siteMaintenanceProgressHint'),
        diagnosticsTitle: tb('siteMaintenanceDiagnosticsTitle'),
        gameTitle: tb('siteMaintenanceGameTitle'),
        gameText: tb('siteMaintenanceGameText'),
      });
    }
    if (type === 'site-login') {
      newWidget.content = createDefaultSiteLoginContent({
        title: tb('siteLoginTitleDefault'),
        instruction: tb('siteLoginInstructionDefault'),
        usernameLabel: tb('siteLoginUsername'),
        passwordLabel: tb('siteLoginPassword'),
        submitLabel: tb('siteLoginSubmit'),
        forgotLabel: tb('siteLoginForgot'),
        signupLabel: tb('siteLoginSignup'),
        guestLabel: tb('siteLoginGuest'),
        idpHint: tb('siteLoginIdpHint'),
      });
    }
    if (type === 'blockquote') newWidget.content = { text: tb('defaultBlockquote'), author: 'Nelson Mandela', quoteStyle: 'classic', accentColor: '#3b82f6', textColor: '#334155', fontSize: 18 };
    if (type === 'moodle-activity') {
      newWidget.content = {
        activityTitle: tb('defaultActivityTitle'),
        activityDesc: '',
        activityUrl: '/mod/forum/view.php?id=',
        activityIcon: 'MessageCircle',
        activityIconBg: '#e5f4f9',
        activityIconColor: '#006378',
        showActivityStatus: false,
        activityStatusLabel: tb('activityStatusPendingDefault'),
        activityStatusLabelComplete: tb('activityStatusCompleteDefault'),
        activityOpenInNewTab: false,
      };
    }
    if (type === 'timeline') newWidget.content = { direction: 'vertical', accentColor: '#3b82f6', lineColor: '#e2e8f0', timelineFontScalePct: 100, items: [{ date: '2024', title: tb('defaultTimeline2024'), desc: tb('defaultTimeline2024Desc'), icon: 'BookOpen' }, { date: '2025', title: tb('defaultTimeline2025'), desc: tb('defaultTimeline2025Desc'), icon: 'Pencil' }, { date: tb('defaultTimelineDateToday'), title: tb('defaultTimelineToday'), desc: tb('defaultTimelineTodayDesc'), icon: 'Trophy' }] };
    if (type === 'asaas-payment') {
      newWidget.content = {
        title: tb('asaasPaymentDefaultTitle'),
        description: '',
        amount: 9.9,
        buttonLabel: tb('asaasPaymentDefaultBtn'),
        align: 'center',
        cardBg: '#f8fafc',
        buttonBg: '#16a34a',
        buttonTextColor: '#ffffff',
        borderRadius: 8,
        checkpoint: { enabled: true, completionMode: 'payment', iconType: 'check', position: 'top-right' },
      };
    }
    if (type === 'mercadopago-payment') {
      newWidget.content = {
        title: tb('mpPaymentDefaultTitle'),
        description: '',
        amount: 9.9,
        buttonLabel: tb('mpPaymentDefaultBtn'),
        align: 'center',
        cardBg: '#f8fafc',
        buttonBg: '#009ee3',
        buttonTextColor: '#ffffff',
        borderRadius: 8,
        checkpoint: { enabled: true, completionMode: 'payment', iconType: 'check', position: 'top-right' },
      };
    }

    if (isHeader) {
      pushHistory();
      setHeaderWidgets(prev => [...prev, newWidget]);
      return;
    }

    const { rowId, colIdx } = selectedSlotId;
    pushHistory();
    setRows(addWidgetToRowColumn(rows, rowId, colIdx, newWidget));
  };

  const removeHeaderWidget = (widgetId) => {
    releaseWidgetLock(widgetId);
    pushHistory();
    setHeaderWidgets(prev => prev.filter(w => w.id !== widgetId));
  };

  const updateHeaderWidget = (widgetId, newContent) => {
    setHeaderWidgets(prev => prev.map(w => w.id === widgetId ? { ...w, content: newContent } : w));
  };

  const removeWidget = (rowId, colId, widgetId) => {
    releaseWidgetLock(widgetId);
    pushHistory();
    setRows(removeWidgetFromTree(rows, rowId, colId, widgetId));
  }

  const duplicateWidget = (rowId, colId, widget) => {
    pushHistory();
    setRows(duplicateWidgetInTree(rows, rowId, colId, widget));
  };

  const duplicateRowById = (rowId) => {
    if (isCanvasReadOnly) return;
    pushHistory();
    setRows((prev) => {
      const row = findRowInTree(prev, rowId);
      if (!row) return prev;
      const cloned = cloneTemplateRows([row])[0];
      if (!cloned) return prev;
      return insertRowAfterInTree(prev, rowId, cloned);
    });
  };

  const openSectionLibraryModal = (mode, rowId) => {
    if (isCanvasReadOnly) return;
    if (!planSectionsLibraryEnabled) {
      showToast(
        t('sectionPresetTenantRequired', {
          defaultValue: 'Biblioteca de seções disponível em projetos na nuvem (equipe ou projeto compartilhado com você).',
        }),
        'warning',
        4200
      );
      return;
    }
    if (!findRowInTree(rows, rowId)) return;
    setSectionsModal({ mode, rowId });
  };

  const handleSaveSectionPreset = async ({
    name,
    description,
    overwriteId,
    folderId,
    saveTarget = 'tenant',
    sectionRole,
    tags,
    tierMin,
    mood,
    status,
  }) => {
    const rowId = sectionsModal?.rowId;
    const row = findRowInTree(rows, rowId);
    if (!row) throw new Error(t('sectionPresetRowMissing', { defaultValue: 'Linha não encontrada no palco.' }));
    const schema = buildSectionPresetSchema(row);

    if (saveTarget === 'ai_global') {
      if (!currentUser?.aiLibraryCurator) {
        throw new Error(t('sectionPresetAiCuratorRequired', {
          defaultValue: 'Somente a conta Mestra pode salvar na biblioteca IA.',
        }));
      }
      const r = await fetch(`${API_BASE}/section-presets/ai-global`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({
          name,
          description,
          schema_json: JSON.stringify(schema),
          source_layout_id: courseInfo?.id || null,
          source_row_id: rowId,
          overwrite_id: overwriteId || null,
          section_role: sectionRole,
          tags,
          tier_min: tierMin,
          mood,
          status,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || t('sectionPresetSaveError', { defaultValue: 'Erro ao salvar seção.' }));
      showToast(
        d.updated
          ? t('sectionPresetAiUpdatedToast', { defaultValue: 'Seção atualizada na biblioteca IA (Global).' })
          : t('sectionPresetAiSavedToast', { defaultValue: 'Seção salva na biblioteca IA (Global).' }),
        'success',
        2800
      );
      return;
    }

    const r = await fetch(`${API_BASE}/section-presets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({
        name,
        description,
        schema_json: JSON.stringify(schema),
        source_layout_id: courseInfo?.id || null,
        source_row_id: rowId,
        overwrite_id: overwriteId,
        folder_id: folderId,
        layout_id: courseInfo?.id || null,
      }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || t('sectionPresetSaveError', { defaultValue: 'Erro ao salvar seção.' }));
    showToast(
      d.updated
        ? t('sectionPresetUpdatedToast', { defaultValue: 'Seção atualizada na biblioteca da equipe.' })
        : t('sectionPresetSavedToast', { defaultValue: 'Seção salva na biblioteca da equipe.' }),
      'success',
      2800
    );
  };

  const handleInsertSectionPreset = (section) => {
    const targetRowId = sectionsModal?.rowId;
    if (!targetRowId) return;
    const presetRows = parseSectionPresetRows(section?.schema_json);
    if (!presetRows.length) {
      alert(t('sectionPresetInsertEmpty', { defaultValue: 'Esta seção não contém conteúdo válido.' }));
      return;
    }
    pushHistory();
    const cloned = cloneTemplateRows(presetRows);
    if (!cloned[0]) return;
    setRows((prev) => insertRowAfterInTree(prev, targetRowId, cloned[0]));
    showToast(t('sectionPresetInsertedToast', { defaultValue: 'Seção inserida após a linha selecionada.' }), 'success', 2800);
  };

  /** Insere seção Global da Biblioteca IA no final do palco (validação da curadoria). */
  const handleInsertAiLibrarySection = (section) => {
    const presetRows = parseSectionPresetRows(section?.schema_json);
    if (!presetRows.length) {
      alert(t('sectionPresetInsertEmpty', { defaultValue: 'Esta seção não contém conteúdo válido.' }));
      return;
    }
    pushHistory();
    const cloned = cloneTemplateRows(presetRows);
    if (!cloned[0]) return;
    setRows((prev) => [...(Array.isArray(prev) ? prev : []), cloned[0]]);
    showToast(
      t('sectionPresetAiInsertedToast', { defaultValue: 'Seção Global inserida no final do palco para validação.' }),
      'success',
      2800
    );
  };

  /** Aplica layout montado pelo assembler (só Mestra — modal gated). Substitui o palco. */
  const handleApplyAiAssembledLayout = (payload) => {
    if (!currentUser?.aiLibraryCurator) {
      alert(t('aiLayoutMasterOnly', {
        defaultValue: 'Montagem de layout IA disponível apenas para a conta Mestra nesta fase.',
      }));
      return;
    }
    const incoming = Array.isArray(payload?.rows) ? payload.rows : [];
    if (!incoming.length) {
      alert(t('aiLayoutEmptyResult', { defaultValue: 'Layout vazio — nada a aplicar.' }));
      return;
    }
    const hasContent = Array.isArray(rows) && rows.length > 0;
    if (hasContent) {
      const ok = window.confirm(
        t('aiLayoutReplaceConfirm', {
          defaultValue: 'Substituir o conteúdo atual do palco pelo layout gerado?',
        })
      );
      if (!ok) return;
    }
    pushHistory();
    const cloned = cloneTemplateRows(incoming);
    setRows(cloned);
    if (Array.isArray(payload.headerWidgets)) {
      setHeaderWidgets(payload.headerWidgets);
    }
    if (payload.globalConfig && typeof payload.globalConfig === 'object') {
      setCourseInfo((prev) => ({
        ...prev,
        globalConfig: {
          ...(prev.globalConfig || {}),
          ...payload.globalConfig,
        },
      }));
    }
    setShowAiLayoutModal(false);
    const n = payload?.meta?.picked?.length || cloned.length;
    showToast(
      t('aiLayoutAppliedToast', {
        defaultValue: `Layout montado aplicado (${n} peça(s)).`,
        count: n,
      }),
      'success',
      3200
    );
  };

  const updateWidget = (rowId, colId, widgetId, newContent) => {
    const newRows = updateWidgetInTree(rows, rowId, colId, widgetId, newContent);
    setRows(newRows);
  };

// Helper: SVG para ícones da timeline na exportação (Lucide paths)
const getTimelineIconSvg = (iconName, color = '#fff', size = 20) => {
    const stroke = color;
    const sw = size >= 20 ? '2.5' : '2';
    const icons = {
        Circle: `<circle cx="12" cy="12" r="8" fill="${stroke}"/>`,
        BookOpen: `<path d="M12 7v14" fill="none" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>` +
            `<path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" fill="none" stroke="${stroke}" stroke-width="${sw}"/>`,
        Pencil: `<path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" fill="none" stroke="${stroke}" stroke-width="${sw}"/>` +
            `<path d="m15 5 4 4" fill="none" stroke="${stroke}" stroke-width="${sw}"/>`,
        Trophy: `<path d="M10 14.66v1.626a2 2 0 0 1-.976 1.696A5 5 0 0 0 7 21.978" fill="none" stroke="${stroke}" stroke-width="${sw}"/>` +
            `<path d="M14 14.66v1.626a2 2 0 0 0 .976 1.696A5 5 0 0 1 17 21.978" fill="none" stroke="${stroke}" stroke-width="${sw}"/>` +
            `<path d="M18 9h1.5a1 1 0 0 0 0-5H18" fill="none" stroke="${stroke}" stroke-width="${sw}"/>` +
            `<path d="M4 22h16" fill="none" stroke="${stroke}" stroke-width="${sw}"/>` +
            `<path d="M6 9a6 6 0 0 0 12 0V3a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1z" fill="none" stroke="${stroke}" stroke-width="${sw}"/>` +
            `<path d="M6 9H4.5a1 1 0 0 1 0-5H6" fill="none" stroke="${stroke}" stroke-width="${sw}"/>`,
        Award: `<circle cx="12" cy="8" r="6" fill="none" stroke="${stroke}" stroke-width="${sw}"/>` +
            `<path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" fill="none" stroke="${stroke}" stroke-width="${sw}"/>`,
        Check: `<path d="M20 6 9 17l-5-5" fill="none" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>`,
        Flag: `<path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" fill="none" stroke="${stroke}" stroke-width="${sw}"/>` +
            `<path d="M4 22V15" fill="none" stroke="${stroke}" stroke-width="${sw}"/>`,
        Star: `<path d="m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="none" stroke="${stroke}" stroke-width="${sw}"/>`,
    };
    const svg = icons[iconName] || icons.Circle;
    return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="flex-shrink:0">${svg}</svg>`;
};
// Helper: renderiza ícones FA/MD como SVG inline (sem CDN) para preview/export
const renderFaMdIconSvg = (iconName, color, size) => {
    const ic = iconName || '';
    const toPascal = (s) => s.split('-').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('');
    if (ic.startsWith('fa:')) {
        const pascal = toPascal(ic.slice(3)); // "fa-home" -> "FaHome"
        const IconComp = (typeof FaIcons !== 'undefined' && FaIcons[pascal]) ? FaIcons[pascal] : null;
        if (IconComp) return renderToStaticMarkup(React.createElement(IconComp, { size, color }));
    }
    if (ic.startsWith('md:')) {
        const pascal = toPascal(ic.slice(3)); // "md-home" -> "MdHome"
        const IconComp = (typeof MdIcons !== 'undefined' && MdIcons[pascal]) ? MdIcons[pascal] : null;
        if (IconComp) return renderToStaticMarkup(React.createElement(IconComp, { size, color }));
    }
    return null;
};

// Helper: SVG para ícones do menu flutuante na exportação (Lucide paths)
const getFloatingMenuIconSvg = (iconName, color = '#475569', size = 20) => {
    const stroke = color;
    const sw = '2';
    const icons = {
        Home: `<path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="none" stroke="${stroke}" stroke-width="${sw}"/><polyline points="9 22 9 12 15 12 15 22" fill="none" stroke="${stroke}" stroke-width="${sw}"/>`,
        BookOpen: `<path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" fill="none" stroke="${stroke}" stroke-width="${sw}"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" fill="none" stroke="${stroke}" stroke-width="${sw}"/>`,
        ExternalLink: `<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" fill="none" stroke="${stroke}" stroke-width="${sw}"/><polyline points="15 3 21 3 21 9" fill="none" stroke="${stroke}" stroke-width="${sw}"/><line x1="10" y1="14" x2="21" y2="3" fill="none" stroke="${stroke}" stroke-width="${sw}"/>`,
        ArrowLeft: `<line x1="19" y1="12" x2="5" y2="12" fill="none" stroke="${stroke}" stroke-width="${sw}"/><polyline points="12 19 5 12 12 5" fill="none" stroke="${stroke}" stroke-width="${sw}"/>`,
        Circle: `<circle cx="12" cy="12" r="8" fill="none" stroke="${stroke}" stroke-width="${sw}"/>`,
        MessageCircle: `<path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" fill="none" stroke="${stroke}" stroke-width="${sw}"/>`,
        Pencil: `<path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" fill="none" stroke="${stroke}" stroke-width="${sw}"/><path d="m15 5 4 4" fill="none" stroke="${stroke}" stroke-width="${sw}"/>`,
        Trophy: `<path d="M6 9H4.5a1 1 0 0 1 0-5H6" fill="none" stroke="${stroke}" stroke-width="${sw}"/><path d="M6 9V3" fill="none" stroke="${stroke}" stroke-width="${sw}"/><path d="M18 9h1.5a1 1 0 0 0 0-5H18" fill="none" stroke="${stroke}" stroke-width="${sw}"/><path d="M18 9V3" fill="none" stroke="${stroke}" stroke-width="${sw}"/><path d="M6 9a6 6 0 0 0 12 0" fill="none" stroke="${stroke}" stroke-width="${sw}"/><path d="M4 22h16" fill="none" stroke="${stroke}" stroke-width="${sw}"/>`,
        Award: `<circle cx="12" cy="8" r="6" fill="none" stroke="${stroke}" stroke-width="${sw}"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" fill="none" stroke="${stroke}" stroke-width="${sw}"/>`,
        Check: `<path d="M20 6 9 17l-5-5" fill="none" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>`,
        Flag: `<path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" fill="none" stroke="${stroke}" stroke-width="${sw}"/><path d="M4 22V15" fill="none" stroke="${stroke}" stroke-width="${sw}"/>`,
        Star: `<path d="m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="none" stroke="${stroke}" stroke-width="${sw}"/>`,
        Mail: `<rect width="20" height="16" x="2" y="4" rx="2" fill="none" stroke="${stroke}" stroke-width="${sw}"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" fill="none" stroke="${stroke}" stroke-width="${sw}"/>`,
        Phone: `<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" fill="none" stroke="${stroke}" stroke-width="${sw}"/>`,
        Globe: `<circle cx="12" cy="12" r="10" fill="none" stroke="${stroke}" stroke-width="${sw}"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" fill="none" stroke="${stroke}" stroke-width="${sw}"/><path d="M2 12h20" fill="none" stroke="${stroke}" stroke-width="${sw}"/>`,
    };
    const svg = icons[iconName] || icons.Home;
    return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="flex-shrink:0">${svg}</svg>`;
};

// Export Moodle: href /… — mesmo critério da paginação (M.cfg.wwwroot); script só em página única quando necessário.
function isMoodleRootRelativeHref(link) {
    const raw = String(link ?? '').trim();
    if (!raw || raw === '#') return false;
    if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(raw) || raw.slice(0, 2) === '//') return false;
    return raw.charAt(0) === '/';
}
function widgetNeedsMadriRedirectForHref(w) {
    if (!w || !w.content) return false;
    const c = w.content;
    if (w.type === 'styled-button') return isMoodleRootRelativeHref(c.link);
    if (w.type === 'image-card' && c.showBtn !== false) return isMoodleRootRelativeHref(c.btnLink);
    if (w.type === 'social-card' && c.showBtn !== false) return isMoodleRootRelativeHref(c.btnLink);
    if (w.type === 'header-menu' && Array.isArray(c.items)) {
        return c.items.some((it) => isMoodleRootRelativeHref(it && it.href));
    }
    if (w.type === 'card-slider' && Array.isArray(c.cards)) {
        return c.cards.some((card) => isMoodleRootRelativeHref(card && card.href));
    }
    return false;
}
function layoutNeedsMadriMoodleRedirectScript(rows, headerWidgets) {
    if (Array.isArray(headerWidgets)) {
        for (const w of headerWidgets) {
            if (widgetNeedsMadriRedirectForHref(w)) return true;
        }
    }
    if (!Array.isArray(rows)) return false;
    for (const r of rows) {
        for (const col of r.columns || []) {
            for (const w of col.widgets || []) {
                if (widgetNeedsMadriRedirectForHref(w)) return true;
            }
        }
    }
    return false;
}
const MADRI_MOODLE_REDIRECT_SCRIPT = `<script>
  function madriStripTrailingSlash(s) {
    var t = String(s || '');
    while (t.length > 0 && t.charAt(t.length - 1) === '/') { t = t.slice(0, -1); }
    return t;
  }
  function madriResolveRedirectUrl(u) {
    if (!u) return u;
    var raw = String(u).trim();
    if (!raw) return raw;
    var _absProto = new RegExp('^[a-zA-Z][a-zA-Z0-9+.-]*:');
    if (_absProto.test(raw) || raw.slice(0, 2) === '//') return raw;

    var ctx = null;
    try { ctx = window.top || window; } catch (e0) { ctx = window; }

    try {
      var m = ctx && ctx.M;
      var wwwroot = m && m.cfg && m.cfg.wwwroot ? String(m.cfg.wwwroot) : '';
      if (wwwroot && raw.charAt(0) === '/') {
        return madriStripTrailingSlash(wwwroot) + raw;
      }
    } catch (e1) {}

    try {
      return new URL(raw, (ctx && ctx.location && ctx.location.href) ? ctx.location.href : window.location.href).toString();
    } catch (e2) {
      return raw;
    }
  }

  function madriGoRedirect(u) {
    var target = madriResolveRedirectUrl(u);
    if (!target) return;
    try {
      (window.top || window).location.href = target;
    } catch (e3) {
      window.location.href = target;
    }
  }
</script>
`;

/** Menu + âncoras: em iframe com srcDoc, href="#id" pode resolver à URL do documento pai. Usar JS. */
const MADRI_SCROLL_ANCHOR_SCRIPT = `<script>
(function(){
function madriScrollToAnchor(rawId){
  var id=String(rawId||'').replace(/^#/,'').trim();
  if(!id)return;
  try{id=decodeURIComponent(id);}catch(e0){}
  id=String(id).replace(/[^a-zA-Z0-9_-]/g,'');
  if(!id)return;
  var el=document.getElementById(id);
  if(!el)return;
  var n=el,pageIdx=-1;
  while(n&&n!==document.body){
    if(n.id&&/^madri-page-\\d+$/.test(n.id)){
      var mm=n.id.match(/^madri-page-(\\d+)$/);
      if(mm)pageIdx=parseInt(mm[1],10);
      break;
    }
    n=n.parentElement;
  }
  var needSwitch=false;
  if(pageIdx>=0&&typeof madriGoToPage==='function'){
    if(typeof madriGetCurrentPageIdx==='function'){needSwitch=madriGetCurrentPageIdx()!==pageIdx;}
    else{needSwitch=true;}
    if(needSwitch){madriGoToPage(pageIdx);}
  }
  setTimeout(function(){
    var t=document.getElementById(id);
    if(t&&t.scrollIntoView){
      try{t.scrollIntoView({behavior:'smooth',block:'start'});}catch(e1){t.scrollIntoView(true);}
    }
    if(typeof sendHeightToMoodle==='function')setTimeout(sendHeightToMoodle,80);
  },needSwitch?60:0);
}
window.madriScrollToAnchor=madriScrollToAnchor;
})();
</script>`;

// Renderizador de DOM para a Exportação (Transforma JSON do Builder em HTML Final Moodle)
const getSimulatedCode = (rowsOverride, globalConfigOverride, headerWidgetsOverride, exportStrings = {}, exportOptions = {}) => {
    const siteExport = isSiteExportRuntime(exportOptions);
    const siteOmittedNotice = exportStrings.siteOmittedNotice || 'Este bloco usa o Moodle e não entra na página publicada.';
    const siteEmbedNotice = exportStrings.siteEmbedNotice || 'Este conteúdo está hospedado no Moodle e não abre nesta página.';
    const sitePaymentNotice = exportStrings.sitePaymentNotice || 'O pagamento (Asaas ou Mercado Pago) confirma a cobrança pelo Moodle e não entra na página publicada.';
    const rowsToUse = rowsOverride ?? rows;
    const layoutCheckpointIds = enumerateLayoutCheckpointIds(rowsToUse);
    const prev = exportStrings.navPrevious ?? 'Anterior';
    const next = exportStrings.navNext ?? 'Próxima';
    const pageLabel = exportStrings.page ?? 'Página';
    const activityPendingDef = exportStrings.activityStatusPendingDefault ?? 'Pendente';
    const activityCompleteDef = exportStrings.activityStatusCompleteDefault ?? 'Concluído';
    const htmlLang = exportStrings.htmlLang === 'en' ? 'en' : 'pt-BR';
    const gConfigSource = globalConfigOverride ?? courseInfo?.globalConfig;
    const headerWidgetsToUse = headerWidgetsOverride !== undefined ? headerWidgetsOverride : headerWidgets;
    const layoutStyleRules = [];
    const typographyStyleRules = [];
    let pages = [];
    let hasComments = false;
    let hasMicroquizzes = false;
    let hasMoodleActivitySlots = false;
    // eslint-disable-next-line no-unused-vars
    const updateRowConfig = (rowId, field, value) => {
        setRows(rows.map(r => r.id === rowId ? { ...r, config: { ...r.config, [field]: value } } : r));
    };

    // eslint-disable-next-line no-unused-vars
    const toCssStr = (obj, def) => {
        if (!obj) return def;
        return `${obj.top || 0}px ${obj.right || 0}px ${obj.bottom || 0}px ${obj.left || 0}px`;
    };
    const sanitizeCss = (s) => (s || '').replace(/expression\s*\(|javascript\s*:/gi, '').trim();
    /** Literal JS seguro para embutir em <script> (aspas, quebras de linha, etc.). */
    const jsStringLiteral = (v) => JSON.stringify(v == null ? '' : String(v));
    /** Cores/tokens em style="" ou CSS inline — remove aspas e caracteres que quebram atributos. */
    const safeCssColorToken = (c, fb) => {
        const s = String(c ?? '').replace(/["'<>`\\]/g, '').trim();
        return s || fb;
    };
    const extractPandaVideoIdFromUrl = (url) => {
        if (!url || typeof url !== 'string') return '';
        const s = url.trim();
        const m = s.match(/[?&]v=([^&\s"'<>]+)/i);
        if (m) {
            try {
                return decodeURIComponent(m[1]);
            } catch (e) {
                return m[1];
            }
        }
        const m2 = s.match(/\/embed\/?\?v=([^&\s"'<>]+)/i);
        if (m2) {
            try {
                return decodeURIComponent(m2[1]);
            } catch (e) {
                return m2[1];
            }
        }
        return '';
    };

    let currentPageHTML = [];
    const checkpointList = [];
    let ckExportIndex = 0;
    const quizList = [];
    let progressBarConfig = null;

    const buildExportedHeaderMenuInnerHtml = (content, widgetId) => {
        const escapeUrlForAttr = (u) => String(u).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const c = content || {};
        const rawItems = Array.isArray(c.items) ? c.items : [];
        const items = rawItems.filter((it) => String(it.label || '').trim() && String(it.href || '').trim());
        if (!items.length) return '';
        const menuStyle = c.menuStyle || 'horizontal';
        const linkColor = c.linkColor || '#334155';
        const hoverColor = c.linkHoverColor || '#0f172a';
        const fontSize = c.fontSize ?? 14;
        const gap = c.gap ?? 16;
        const showIcons = c.showIcons !== false;
        const menuAlign = c.menuAlign || 'center';
        const justify = menuAlign === 'left' ? 'flex-start' : menuAlign === 'right' ? 'flex-end' : 'center';
        const flexDir = menuStyle === 'vertical' ? 'column' : 'row';
        const wrap = menuStyle === 'vertical' ? 'nowrap' : 'wrap';
        const alignItems = menuStyle === 'vertical' ? 'stretch' : 'center';
        const pills = menuStyle === 'pills';
        const minimal = menuStyle === 'minimal';
        const underlined = menuStyle === 'underlined';
        const breadcrumb = menuStyle === 'breadcrumb';
        const uid = 'madri-hmenu-' + String(widgetId || 'x').replace(/[^a-zA-Z0-9_-]/g, '_');

        const makeLinkHtml = (it) => {
            const label = esc(String(it.label || ''));
            const rawHref = String(it.href || '#').trim();
            const isFragment = rawHref.startsWith('#') && rawHref.length > 1;
            const rootRel = isMoodleRootRelativeHref(rawHref);
            const openNew = !isFragment && it.openNewTab === true;
            const iconSz = breadcrumb ? Math.min(14, fontSize + 1) : Math.min(18, fontSize + 2);
            const IconComp = showIcons ? (getIconComponent(it.icon) || LucideIcons.Circle) : null;
            const iconHtml = IconComp ? renderToStaticMarkup(React.createElement(IconComp, { size: iconSz, color: linkColor, style: { flexShrink: 0 } })) : '';
            let baseStyle = 'display:inline-flex;align-items:center;gap:6px;font-size:' + fontSize + 'px;font-weight:500;text-decoration:none;color:' + linkColor + ';';
            if (breadcrumb) baseStyle += 'padding:2px 4px;';
            else if (pills) baseStyle += 'padding:6px 14px;border-radius:999px;background:rgba(15,23,42,0.06);';
            else if (minimal) baseStyle += 'padding:4px 8px;';
            else if (underlined) baseStyle += 'padding:4px 2px;border-bottom:2px solid transparent;';
            else baseStyle += 'padding:4px 8px;';
            const inner = iconHtml + '<span>' + label + '</span>';
            if (isFragment) {
                const rawFrag = rawHref.slice(1).trim();
                const safeFrag = rawFrag.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 80);
                if (!safeFrag) {
                    return '<a href="#" class="madri-hmenu-link" style="' + baseStyle + '" onclick="return false;">' + inner + '</a>';
                }
                const onclk = 'event.preventDefault();if(typeof madriScrollToAnchor===\'function\'){madriScrollToAnchor(\'' + safeFrag + '\');}return false;';
                return '<a href="#" onclick="' + onclk + '" class="madri-hmenu-link" style="' + baseStyle + '">' + inner + '</a>';
            }
            if (rootRel) {
                const escHref = escapeUrlForAttr(rawHref);
                if (openNew) {
                    return '<a href="#" data-madri-href="' + escHref + '" onclick="event.preventDefault();var u=this.getAttribute(\'data-madri-href\');var t=(typeof madriResolveRedirectUrl===\'function\'?madriResolveRedirectUrl(u):u);window.open(t,\'_blank\');" rel="noopener noreferrer" class="madri-hmenu-link" style="' + baseStyle + '">' + inner + '</a>';
                }
                return '<a href="#" data-madri-href="' + escHref + '" onclick="event.preventDefault();var u=this.getAttribute(\'data-madri-href\');if(u){if(typeof madriGoRedirect===\'function\'){madriGoRedirect(u);}else{try{(window.top||window).location.href=u;}catch(e){location.href=u;}}}" class="madri-hmenu-link" style="' + baseStyle + '">' + inner + '</a>';
            }
            const safeHref = escapeUrlForAttr(rawHref || '#');
            const relAttr = openNew ? ' rel="noopener noreferrer" target="_blank"' : '';
            return '<a href="' + safeHref + '"' + relAttr + ' class="madri-hmenu-link" style="' + baseStyle + '">' + inner + '</a>';
        };

        let links;
        let navWrap;
        if (breadcrumb) {
            const sepFs = Math.max(11, fontSize - 1);
            const sep = '<span style="color:#94a3b8;font-size:' + sepFs + 'px;padding:0 6px;user-select:none;flex-shrink:0;line-height:1" aria-hidden="true">/</span>';
            const parts = [];
            items.forEach((it, idx) => {
                if (idx > 0) parts.push(sep);
                parts.push(makeLinkHtml(it));
            });
            links = parts.join('');
            navWrap = 'display:flex;flex-direction:row;flex-wrap:wrap;align-items:center;justify-content:' + justify + ';gap:0;width:100%;box-sizing:border-box;';
        } else {
            links = items.map((it) => makeLinkHtml(it)).join('');
            navWrap = 'display:flex;flex-direction:' + flexDir + ';flex-wrap:' + wrap + ';align-items:' + alignItems + ';justify-content:' + justify + ';gap:' + gap + 'px;width:100%;box-sizing:border-box;';
        }

        let hoverCss = '#' + uid + ' a.madri-hmenu-link:hover{color:' + hoverColor + ' !important;opacity:0.92;}';
        if (underlined) hoverCss += '#' + uid + ' a.madri-hmenu-link:hover{border-bottom-color:' + linkColor + ' !important;}';
        return '<style>' + hoverCss + '</style><nav id="' + uid + '" class="madri-header-menu-nav" aria-label="Menu" style="' + navWrap + '">' + links + '</nav>';
    };

    // Helper: render function for individual widgets
    const renderWidgetHtml = (w, currentPageIdx) => {
        let innerHtml = '';
        if (w.type === 'text') {
            const textHtml = sanitizeHtml((w.content.html || '').replace(/&nbsp;/g, ' '));
            innerHtml = `<div class="fake-text">${textHtml}</div>`;
        }
        if (w.type === 'image') {
            const alignFlex = w.content.align === 'left' ? 'flex-start' : (w.content.align === 'right' ? 'flex-end' : 'center');
            const imgSt = buildImageImgInlineStyle(w.content || {});
            innerHtml = `<div style="display: flex; justify-content: ${alignFlex};"><img src="${w.content.url || ''}" alt="${(w.content.alt || '').replace(/"/g, '&quot;')}" style="${imgSt}" /></div>`;
        }
        if (w.type === 'video') {
            const ck = w.content.checkpoint;
            const isVideoCk = ck?.enabled && ck?.completionMode === 'video';
            const rawVideoUrl = (w.content.url || '').trim();
            let u = '';
            if (/^\s*<iframe\b/i.test(rawVideoUrl)) {
                const srcMatch = rawVideoUrl.match(/src\s*=\s*(?:"([^"]+)"|'([^']+)'|([^\s>]+))/i);
                u = srcMatch ? (srcMatch[1] || srcMatch[2] || srcMatch[3] || '') : '';
            } else {
                u = getEffectiveVideoUrl(rawVideoUrl);
            }
            if (!u) {
                innerHtml = rawVideoUrl.replace(/autoplay=1/gi, 'autoplay=0');
            } else {
                const ytId = u.match(/(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:shorts\/|live\/|embed\/|watch\?(?:.*&)?v=|v\/|e\/))([A-Za-z0-9_-]{11})/i);
                const vimeoId = u.match(/(?:player\.)?vimeo\.com\/(?:video\/)?(\d+)/i);
                if (ytId) u = 'https://www.youtube.com/embed/' + ytId[1];
                else if (vimeoId) u = 'https://player.vimeo.com/video/' + vimeoId[1];
                else u = u.replace('youtube.com/watch?v=', 'youtube.com/embed/').replace('vimeo.com/', 'player.vimeo.com/video/');
                u = u.replace(/([?&])autoplay=1(&|$)/gi, '$1autoplay=0$2').replace(/\bautoplay=1\b/gi, 'autoplay=0');
                if (!/([?&])autoplay=/.test(u)) u += (u.includes('?') ? '&' : '?') + 'autoplay=0';
                if (u.includes('youtube.com/embed/')) {
                    if (!/([?&])enablejsapi=/.test(u)) u += '&enablejsapi=1';
                    // Omitting &origin= intentionally: baking in geppetto-creator.com breaks
                    // the YouTube IFrame API when the HTML is hosted on any other domain
                    // (e.g. Moodle). Without origin, YouTube uses the actual page origin at
                    // runtime, which is always correct regardless of where the HTML is hosted.
                }
                const iframeId = isVideoCk ? ` id="madri-video-${w.id}"` : '';
                innerHtml = `<div class="video-responsive-container" style="position: relative; aspect-ratio: 16 / 9; height: auto; overflow: hidden; border-radius: 8px;"><iframe${iframeId} src="${u}" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;" frameborder="0" allow="autoplay; fullscreen; picture-in-picture"></iframe></div>`;
            }
        }
        if (w.type === 'panda-video') {
            const ck = w.content.checkpoint;
            const isVideoCk = ck?.enabled && ck?.completionMode === 'video';
            const rawVideoUrl = (w.content.url || '').trim();
            let u = rawVideoUrl;
            const checkpointStripControls = ck?.enabled && ck?.completionMode === 'video' && ck.showTimeline === false;
            if (w.content.hideControls || checkpointStripControls) {
                u += (u.includes('?') ? '&' : '?') + 'controls=0';
            }
            const pandaVid = extractPandaVideoIdFromUrl(u);
            const iframeId = isVideoCk && pandaVid
                ? ` id="panda-${pandaVid.replace(/"/g, '')}"`
                : (isVideoCk ? ` id="madri-video-${w.id}"` : '');
            innerHtml = `<div class="video-responsive-container" style="position: relative; aspect-ratio: 16 / 9; height: auto; overflow: hidden; border-radius: 8px; background-color: #000;"><iframe${iframeId} src="${u.replace(/"/g, '&quot;')}" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe></div>`;
        }
        if (w.type === 'h5p') {
            const h5pUrl = (w.content && w.content.url) || '';
            const h5pEmbed = (w.content && w.content.embedCode) || '';
            if (siteExport && looksLikeMoodleBoundEmbed(h5pUrl, h5pEmbed)) {
                innerHtml = buildSiteOmittedNoticeHtml(siteEmbedNotice);
            } else {
                innerHtml = buildH5pExportInnerHtml({ url: w.content.url, embedCode: w.content.embedCode });
            }
        }
        if (w.type === 'htmlTemplate') {
            const clean = prepareHtmlTemplateForEmbed(w.content.html || '');
            innerHtml = clean
                ? `<div class="geppetto-html-template-embed">${clean}</div>`
                : '';
        }
        if (w.type === 'alert') {
            const theme = w.content.theme || 'warning';
            const iconName = w.content.icon;
            const iconSize = w.content.alertIconSize ?? 24;
            const IconComp = getIconComponent(iconName) || (theme === 'success' ? CheckCircle : theme === 'danger' ? XCircle : theme === 'info' ? Info : AlertTriangle);
            const iconSvgMarkup = renderToStaticMarkup(<IconComp size={iconSize} style={{ flexShrink: 0 }} />);
            const customBg = (w.content.wrapperBg && w.content.wrapperBg !== 'transparent') ? (w.content.computedBgRGBA || w.content.wrapperBg) : '';
            const customColor = w.content.textColor || '';

            const bgStyle = customBg ? ` background-color: ${customBg} !important; border-color: ${customBg} !important;` : '';
            const colorStyle = customColor ? ` color: ${customColor} !important;` : '';
            const lineHeightStr = w.content.wrapperLineHeight ? ` line-height: ${w.content.wrapperLineHeight};` : '';
            const fontSizeStr = w.content.wrapperFont ? ` font-size: ${w.content.wrapperFont}px;` : '';

            const alertHtml = sanitizeHtml((w.content.text || '').replace(/&nbsp;/g, ' '));
            const alertLh = w.content.wrapperLineHeight ? (w.content.wrapperLineHeight) : '1.5';
            innerHtml = `<div class="fake-alert alert-${theme}" style="width:100%;max-width:100%;box-sizing:border-box;${bgStyle}${colorStyle}">
          <div style="display:flex;gap:15px;align-items:flex-start">
            <div style="flex-shrink:0;display:flex;align-items:flex-start;margin-top:2px">${iconSvgMarkup}</div>
            <div class="fake-text" style="line-height:${alertLh};${fontSizeStr}${colorStyle}">${alertHtml}</div>
          </div>
        </div>`;
        }
        if (w.type === 'styled-card') {
            const decStyle = w.content.decorationStyle || 'classic';
            const cPrim = w.content.lineColorPrimary;
            const cSec = w.content.lineColorSecondary;
            const tagColor = w.content.tagColor || 'var(--course-primary, #3b82f6)';
            const borderTopWidth = w.content.borderTopWidth ?? 4;
            const hasCardBg = w.content.cardBg && String(w.content.cardBg).trim() !== '';
            const cardBgBase = (hasCardBg ? w.content.cardBg : undefined) ?? gConfigSource?.cardBg ?? '#ffffff';
            const hexToRgbaCard = (hex, pct) => {
                if (!hex) return 'transparent';
                hex = String(hex).replace('#', '');
                let r = 0, g = 0, b = 0;
                if (hex.length === 3) { r = parseInt(hex[0] + hex[0], 16); g = parseInt(hex[1] + hex[1], 16); b = parseInt(hex[2] + hex[2], 16); }
                else if (hex.length === 6) { r = parseInt(hex.slice(0, 2), 16); g = parseInt(hex.slice(2, 4), 16); b = parseInt(hex.slice(4, 6), 16); }
                const alpha = (100 - (pct ?? 0)) / 100;
                return `rgba(${r}, ${g}, ${b}, ${alpha})`;
            };
            const cardBg = (w.content.cardBgAlpha !== undefined && w.content.cardBgAlpha !== null)
                ? (w.content.computedCardBgRGBA || hexToRgbaCard(w.content.cardBg || cardBgBase, w.content.cardBgAlpha))
                : cardBgBase;

            let tStyle = "margin-top:0; border-bottom:none; padding-bottom:0; margin-bottom:0;";
            if (decStyle === 'lateral') {
                tStyle += ` border-left: 4px solid ${cPrim || '#f97316'}; padding-left: 12px;`;
            }
            const decoHTML = w.content.title ? getDecorationHTML(decStyle, cPrim, cSec) : '';

            const borderTopCss = borderTopWidth > 0 ? `border-top:${borderTopWidth}px solid ${tagColor}` : 'border-top:none';
            const cardPad = w.content.paddingObj ? `padding: ${w.content.paddingObj.top || 0}px ${w.content.paddingObj.right || 0}px ${w.content.paddingObj.bottom || 0}px ${w.content.paddingObj.left || 0}px;` : 'padding: 1rem;';
            innerHtml = `<div class="madri-card" style="background:${cardBg};${borderTopCss};${cardPad}">
          ${w.content.tag ? `<span class="tag" style="display: block; color: ${tagColor}; font-weight: bold; text-transform: uppercase; font-size: 0.8rem; margin-bottom: 0.5rem;">${w.content.tag}</span>` : ''}
          ${w.content.title ? `<h3 style="${tStyle}">${w.content.title}</h3>${decoHTML}` : ''}
          <div class="fake-text">${sanitizeHtml((w.content.html || '').replace(/&nbsp;/g, ' '))}</div>
        </div>`;
            innerHtml = wrapHtmlWithTocAnchorIfNeeded(innerHtml, w.content, w.id);
        }
        if (w.type === 'section-header') {
            const c = w.content || {};
            const escSh = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
            const barEnabled = isSectionHeaderLeftBarEnabled(c);
            const barRaw = String(c.headerLeftBarColor || '').trim();
            const lh = c.wrapperLineHeight;
            const titleTag = resolveSectionHeaderTitleTag(c);
            const titlePx = resolveSectionHeaderTitleFontPx(c, gConfigSource);
            const subFs = resolveSectionHeaderSubtitleFontSizeCss(c, gConfigSource);
            const titleFs = titlePx != null ? `font-size:${titlePx}px;` : '';
            const titleLh = lh ? `line-height:${lh};` : '';
            const shStyles = buildSectionHeaderExportStyles(c, safeCssColorToken);
            const subStyle = `${shStyles.subColor}${shStyles.subOpacity}${lh ? `line-height:${lh};` : ''}font-size:${subFs};${shStyles.subtitleTypography}`;
            const headerBar = !barEnabled
                ? 'border-left:none;'
                : (barRaw ? `border-left:5px solid ${safeCssColorToken(barRaw, '#3b82f6')};` : '');
            const headerBgRaw = resolveSectionHeaderBackground(c);
            const headerBgStyle = headerBgRaw ? `background-color:${safeCssColorToken(headerBgRaw, headerBgRaw)} !important;` : '';
            const headerStyleCombined = `${shStyles.textAlign}${headerBar}${headerBgStyle}`.trim();
            const tagHtml = c.tag
                ? `<span class="tag" style="${shStyles.textAlign}${shStyles.tagTypography}${shStyles.tagColor}">${escSh(c.tag)}</span>`
                : '';
            innerHtml = `<div class="madri-header" style="${headerStyleCombined}">
          ${tagHtml}
          <${titleTag} class="${SECTION_HEADER_TITLE_CLASS}" style="${shStyles.textAlign}${shStyles.titleColor}${titleFs}${titleLh}${shStyles.titleTypography}margin-top:0;">${escSh(c.title || '')}</${titleTag}>
          ${c.subtitle ? `<p class="subtitle madri-gc-section-header__sub" style="${shStyles.textAlign}${subStyle}margin-bottom:0;">${escSh(c.subtitle)}</p>` : ''}
        </div>`;
            innerHtml = wrapHtmlWithTocAnchorIfNeeded(innerHtml, c, w.id);
        }
        if (w.type === 'title-basic') {
            innerHtml = buildTitleBasicInnerHtml(w.content, w.id);
        }
        if (w.type === 'table-of-contents') {
            innerHtml = buildTocInnerHtml(
                w.content,
                collectTocEntriesFromRows(rowsToUse, { excludeWidgetId: w.id }),
                { emptyMessage: exportStrings.tocEmpty || 'Nenhuma entrada no sumário. Marque «Incluir no sumário» em Título âncora, Cabeçalho ou Card estilizado.' },
            );
        }
        if (w.type === 'chart-widget') {
            innerHtml = buildChartExportInnerHtml(w.content, w.id);
        }
        if (w.type === 'flowchart-widget') {
            innerHtml = buildFlowchartExportInnerHtml(w.content, w.id);
        }
        if (w.type === 'list-icon') {
            const IconComp = getIconComponent(w.content.icon) || LucideIcons.CheckCircle;
            const isRow = w.content.display === 'HORIZONTAL' || w.content.display === 'TWO COLUMNS';
            const deskAlign = w.content.align || 'NENHUM';
            const sAlignItems = listIconAlignToFlex(deskAlign);
            const sTextAlign = listIconAlignToTextAlign(deskAlign);
            const sJustify = isRow ? listIconRowJustify(deskAlign) : 'flex-start';
            const mobAlignCls = listIconMobileAlignClass(w.content);

            const iconSvgMarkup = renderToStaticMarkup(<IconComp size={`${w.content.iconSize ?? 2.65}rem`} color={w.content.iconColor || '#3b82f6'} />);
            const wf = parseInt(w.content.wrapperFont, 10);
            const lh = w.content.wrapperLineHeight;
            const bodyFont = Number.isFinite(wf) && wf > 0 ? `font-size: ${wf}px;` : '';
            const bodyLh = lh ? `line-height: ${lh};` : '';
            const rowTextAlign = deskAlign === 'RIGHT' ? 'right' : deskAlign === 'CENTER' ? 'center' : 'left';

            innerHtml = `<div class="madri-list-icon-root${isRow ? ' madri-list-icon-root--row' : ''} ${mobAlignCls}" style="display: flex; flex-direction: ${isRow ? 'row' : 'column'}; justify-content: ${sJustify}; align-items: ${isRow ? 'center' : sAlignItems}; gap: ${w.content.spaceBetween ?? 0.45}rem; text-align: ${isRow ? rowTextAlign : sTextAlign}; max-width: 100%; min-width: 0; width: 100%; box-sizing: border-box;">
          <div style="flex-shrink: 0; display: flex; justify-content: center; align-items: center; align-self: ${isRow ? 'center' : 'auto'};">${iconSvgMarkup}</div>
          <div class="madri-list-icon-body fake-text" style="flex: 1; min-width: 0; max-width: 100%; overflow-wrap: anywhere; word-break: break-word; color: ${w.content.textColor || 'inherit'};${bodyFont}${bodyLh}">${sanitizeHtml((w.content.html || '').replace(/&nbsp;/g, ' '))}</div>
        </div>`;
        }
        if (w.type === 'styled-button') {
            const { text, link, size, rounded, border, fullWidth, center, width, fontWeight, letterSpacing, wordSpacing, uppercase, colorsNormal, colorsHover, newWindow, customClass, buttonIconSizePx, iconColor } = w.content;
            let btnPad = '10px 20px'; let btnFont = '14px';
            if (size === 'S') { btnPad = '6px 12px'; btnFont = '12px'; }
            if (size === 'L') { btnPad = '14px 28px'; btnFont = '16px'; }
            if (size === 'XL') { btnPad = '18px 36px'; btnFont = '18px'; }
            const btnFontNum = parseInt(btnFont, 10) || 14;
            const iconPx = Number.isFinite(buttonIconSizePx) && buttonIconSizePx >= 12
                ? Math.min(48, Math.max(12, buttonIconSizePx))
                : btnFontNum;
            const iconColorExplicit = iconColor && String(iconColor).trim();

            let br = '4px';
            if (rounded === 'Yes (Pill)') br = '50px';
            if (rounded === 'No (Square)') br = '0px';

            let fw = 'normal';
            if (fontWeight === 'Light') fw = '300';
            if (fontWeight === 'Medium') fw = '500';
            if (fontWeight === 'Semi-bold') fw = '600';
            if (fontWeight === 'Bold') fw = 'bold';

            const cssMain = `
            display: ${fullWidth ? 'block' : 'inline-block'};
            width: ${fullWidth ? '100%' : `${width || 100}%`};
            text-align: center;
            padding: ${btnPad};
            font-size: ${btnFont};
            border-radius: ${br};
            background-color: ${border ? 'transparent' : (colorsNormal?.bg || '#f97316')};
            color: ${colorsNormal?.text || '#ffffff'};
            border: ${border ? `2px solid ${colorsNormal?.bg || '#f97316'}` : `1px solid ${colorsNormal?.border || 'transparent'}`};
            font-weight: ${fw};
            letter-spacing: ${letterSpacing || 0}px;
            word-spacing: ${wordSpacing || 0}px;
            text-transform: ${uppercase ? 'uppercase' : 'none'};
            text-decoration: none;
            transition: all 0.3s ease;
         `;

            // ID estável: Math.random() mudava a cada render do builder → srcDoc do preview sempre diferente → iframe em loop.
            const uid_btn = 'madri-sbtn-' + String(w.id || 'widget').replace(/[^a-zA-Z0-9_-]/g, '_');
            const hoverCss = `
           #${uid_btn}:hover {
              background-color: ${border && colorsHover ? colorsHover.bg : (colorsHover?.bg || '#ea580c')} !important;
              color: ${colorsHover?.text || '#ffffff'} !important;
              border-color: ${colorsHover?.border || 'transparent'} !important;
           }
         `;

            const IconCmp = getIconComponent(w.content?.icon);
            const iconProps = { size: iconPx, ...(iconColorExplicit ? { color: iconColorExplicit } : {}) };
            const iconHtml = w.content.showIcon ? (IconCmp ? renderToStaticMarkup(<IconCmp {...iconProps} />) : '<span>★</span>') : '';

            const escapeUrlForStyledBtn = (u) => String(u).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            const rawBtnLink = String(link || '#').trim();
            const rootRelBtn = isMoodleRootRelativeHref(rawBtnLink);
            const iconHoverCss = iconColorExplicit
                ? ''
                : ` #${uid_btn}:hover svg { color: ${colorsHover?.text || '#ffffff'} !important; }`;
            let anchorTag;
            if (rootRelBtn) {
                const escHref = escapeUrlForStyledBtn(rawBtnLink);
                if (newWindow) {
                    anchorTag = `<a id="${uid_btn}" href="#" role="link" data-madri-href="${escHref}" onclick="event.preventDefault();var u=this.getAttribute('data-madri-href');var t=(typeof madriResolveRedirectUrl==='function'?madriResolveRedirectUrl(u):u);window.open(t,'_blank');" rel="noopener noreferrer" style="${cssMain}" class="${elementAdvancedCss('', customClass).classNames}">`;
                } else {
                    anchorTag = `<a id="${uid_btn}" href="#" role="link" data-madri-href="${escHref}" onclick="event.preventDefault();var u=this.getAttribute('data-madri-href');if(u){if(typeof madriGoRedirect==='function'){madriGoRedirect(u);}else{try{(window.top||window).location.href=u;}catch(e){location.href=u;}}}" style="${cssMain}" class="${elementAdvancedCss('', customClass).classNames}">`;
                }
            } else {
                const safeHref = escapeUrlForStyledBtn(rawBtnLink || '#');
                const relAttr = newWindow ? ' rel="noopener noreferrer"' : '';
                anchorTag = `<a id="${uid_btn}" href="${safeHref}" target="${newWindow ? '_blank' : '_self'}"${relAttr} style="${cssMain}" class="${elementAdvancedCss('', customClass).classNames}">`;
            }

            innerHtml = `
           <style>${hoverCss}${iconHoverCss}</style>
           <div style="text-align: ${center ? 'center' : 'left'}; width: 100%;">
              ${anchorTag}
                  <div style="display: flex; align-items: center; justify-content: center; gap: 8px;">
                      ${iconHtml}
                      <span>${text || "Button"}</span>
                  </div>
              </a>
           </div>
         `;
        }
        if (w.type === 'tabs') {
            const tabs = w.content.tabs || [];
            const acc = w.content.accentColor || '#3b82f6';
            const tabId = 'tabs_' + w.id;
            const headerBtns = tabs.map((t, i) => `<button onclick="madriTabSwitch('${tabId}',${i})" id="${tabId}_btn_${i}" style="padding:10px 20px;border:none;background:none;cursor:pointer;font-weight:${i === 0 ? '700' : '400'};color:${i === 0 ? acc : '#64748b'};border-bottom:${i === 0 ? `2px solid ${acc}` : '2px solid transparent'};margin-bottom:-2px;transition:all 0.2s">${t.label}</button>`).join('');
            const bodies = tabs.map((t, i) => `<div id="${tabId}_body_${i}" class="fake-text" style="padding:1rem;${i !== 0 ? 'display:none' : ''}">${sanitizeHtml(t.body || '')}</div>`).join('');
            innerHtml = `<script>function madriTabSwitch(id,idx){var btns=document.querySelectorAll('[id^="'+id+'_btn_"]');var bodies=document.querySelectorAll('[id^="'+id+'_body_"]');var _acc=${jsStringLiteral(acc)};btns.forEach(function(b,i){b.style.fontWeight=i===idx?'700':'400';b.style.color=i===idx?_acc:'#64748b';b.style.borderBottom=i===idx?('2px solid '+_acc):'2px solid transparent';});bodies.forEach(function(b,i){b.style.display=i===idx?'block':'none';});}</script><div style="border:1px solid #e2e8f0;border-radius:8px;overflow:hidden"><div style="display:flex;border-bottom:2px solid #e2e8f0;background:#f8fafc">${headerBtns}</div>${bodies}</div>`;
        }
        if (w.type === 'accordion') {
            const items = w.content.items || [];
            const acc = w.content.accentColor || '#3b82f6';
            const accId = 'acc_' + w.id;
            const itemsHtml = items.map((item, i) => `<div style="border-bottom:${i < items.length - 1 ? '1px solid #e2e8f0' : 'none'}"><button onclick="madriAccToggle('${accId}',${i})" id="${accId}_btn_${i}" style="width:100%;text-align:left;padding:14px 16px;background:white;border:none;cursor:pointer;display:flex;justify-content:space-between;align-items:center;font-weight:600;color:#1e293b;transition:all 0.2s"><span>${item.question}</span><span id="${accId}_chevron_${i}" style="transition:transform 0.3s">▼</span></button><div id="${accId}_body_${i}" class="fake-text" style="display:none;padding:0 16px 14px;color:#475569;line-height:1.7">${sanitizeHtml(item.answer || '')}</div></div>`).join('');
            innerHtml = `<script>function madriAccToggle(id,idx){var bodies=document.querySelectorAll('[id^="'+id+'_body_"]');var chevrons=document.querySelectorAll('[id^="'+id+'_chevron_"]');bodies.forEach(function(b,i){var open=i===idx&&b.style.display==='none';b.style.display=open?'block':'none';chevrons[i].style.transform=open?'rotate(180deg)':'rotate(0)';});}</script><div style="border:1px solid #e2e8f0;border-radius:8px;overflow:hidden">${itemsHtml}</div>`;
        }
        if (w.type === 'modal-trigger') {
            const btnBg = w.content.btnBg || '#3b82f6';
            const btnText2 = w.content.btnText || '#ffffff';
            const bStyle = w.content.btnStyle || 'button';
            const trigStyle = bStyle === 'outline' ? `background:transparent;color:${btnBg};border:2px solid ${btnBg}` : bStyle === 'link' ? `background:transparent;color:${btnBg};padding:0;text-decoration:underline;border:none` : `background:${btnBg};color:${btnText2};border:none`;
            const modalId = 'modal_' + w.id;
            const modalPanelW = Math.min(1200, Math.max(280, parseInt(w.content.modalPanelWidthPx, 10) || 800));
            const modalPanelMaxH = Math.min(900, Math.max(200, parseInt(w.content.modalPanelMaxHeightPx, 10) || 600));
            innerHtml = `<div style="text-align:${w.content.align || 'left'}"><button onclick="document.getElementById('${modalId}').style.display='flex'" style="display:inline-flex;align-items:center;gap:8px;padding:10px 22px;border-radius:6px;cursor:pointer;font-weight:600;font-size:14px;${trigStyle}">${w.content.triggerLabel || 'Abrir Modal'}</button></div><div id="${modalId}" onclick="if(event.target===this)this.style.display='none'" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,0.55);z-index:99999;align-items:center;justify-content:center;padding:1rem"><div style="background:#fff;border-radius:12px;padding:2rem;width:min(${modalPanelW}px,calc(100vw - 32px));max-width:100%;max-height:min(${modalPanelMaxH}px,90vh);box-shadow:0 25px 60px rgba(0,0,0,.3);position:relative;display:flex;flex-direction:column;overflow:hidden;box-sizing:border-box"><button onclick="document.getElementById('${modalId}').style.display='none'" style="position:absolute;top:12px;right:14px;background:none;border:none;font-size:22px;cursor:pointer;color:#94a3b8;z-index:1">✕</button>${w.content.modalTitle ? `<h2 style="margin-top:0;margin-bottom:1rem;color:#1e293b;flex-shrink:0;padding-right:36px">${w.content.modalTitle}</h2>` : ''}<div class="fake-text" style="flex:1;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch">${sanitizeHtml(w.content.modalBody || '')}</div></div></div>`;
        }
        if (w.type === 'reinforcement-quiz') {
            hasMicroquizzes = true;
            innerHtml = buildMicroquizExportInnerHtml(w, quizList, {
                trueLabel: exportStrings.microquizTrue || 'Verdadeiro',
                falseLabel: exportStrings.microquizFalse || 'Falso',
            }, { rows: rowsToUse, checkpointIds: layoutCheckpointIds });
        }
        if (w.type === 'carousel') {
            const slides = w.content.slides || [];
            const acc = w.content.accentColor || '#3b82f6';
            const carId = 'car_' + w.id;
            const slidesHtml = slides.map((s, i) => `<div id="${carId}_slide_${i}" style="${i !== 0 ? 'display:none' : ''};width:100%">${s.url ? `<img src="${s.url}" style="width:100%;max-height:320px;object-fit:cover" alt="${s.caption || ''}"/>` : '<div style="width:100%;height:200px;background:#e2e8f0;display:flex;align-items:center;justify-content:center;font-size:2rem">🖼️</div>'}${s.caption ? `<div style="padding:10px 16px;text-align:center;color:#475569;font-style:italic;font-size:14px">${s.caption}</div>` : ''}</div>`).join('');
            const dotsHtml = slides.map((_, i) => `<span onclick="madriCarNav('${carId}',${i},${slides.length})" id="${carId}_dot_${i}" style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${i === 0 ? acc : '#cbd5e1'};cursor:pointer;margin:0 3px"></span>`).join('');
            innerHtml = `<script>function madriCarNav(id,idx,total){var _acc=${jsStringLiteral(acc)};for(var i=0;i<total;i++){document.getElementById(id+'_slide_'+i).style.display=i===idx?'block':'none';var d=document.getElementById(id+'_dot_'+i);if(d)d.style.background=i===idx?_acc:'#cbd5e1';}}</script><div style="border:1px solid #e2e8f0;border-radius:10px;overflow:hidden;position:relative;background:#f8fafc">${slidesHtml}${slides.length > 1 ? `<button onclick="var cur=0;for(var i=0;i<${slides.length};i++){if(document.getElementById('${carId}_slide_'+i).style.display!=='none')cur=i;}madriCarNav('${carId}',(cur-1+${slides.length})%${slides.length},${slides.length})" style="position:absolute;left:8px;top:50%;transform:translateY(-50%);background:${acc};color:#fff;border:none;border-radius:50%;width:32px;height:32px;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:18px">‹</button><button onclick="var cur=0;for(var i=0;i<${slides.length};i++){if(document.getElementById('${carId}_slide_'+i).style.display!=='none')cur=i;}madriCarNav('${carId}',(cur+1)%${slides.length},${slides.length})" style="position:absolute;right:8px;top:50%;transform:translateY(-50%);background:${acc};color:#fff;border:none;border-radius:50%;width:32px;height:32px;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:18px">›</button><div style="position:absolute;bottom:8px;left:50%;transform:translateX(-50%)">${dotsHtml}</div>` : ''}</div>`;
        }
        if (w.type === 'card-slider') {
            innerHtml = buildCardSliderInnerHtml(w.content, w.id);
        }
        if (w.type === 'card-shop') {
            innerHtml = buildCardShopInnerHtml(w.content, w.id);
        }
        if (w.type === 'video-card-slider') {
            innerHtml = buildVideoCardSliderInnerHtml(w.content, w.id);
        }
        if (w.type === 'image-card') {
            const { imageUrl, imageHeight, cardTag, cardTitle, cardText, showBtn, btnLabel, btnLink, btnBg: cBg, btnTextColor: cTxt, cardBg, titleColor, textColor: tColor } = w.content;
            const escImgHref = (u) => String(u).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            const rawImgBtnLink = String(btnLink || '#').trim();
            const btnStyleImg = `display:inline-block;padding:8px 18px;background:${cBg || '#3b82f6'};color:${cTxt || '#fff'};border-radius:6px;text-decoration:none;font-size:14px;font-weight:600`;
            let imgCardBtnHtml = '';
            if (showBtn) {
                if (isMoodleRootRelativeHref(rawImgBtnLink)) {
                    imgCardBtnHtml = `<a href="#" role="link" data-madri-href="${escImgHref(rawImgBtnLink)}" onclick="event.preventDefault();var u=this.getAttribute('data-madri-href');if(u){if(typeof madriGoRedirect==='function'){madriGoRedirect(u);}else{try{(window.top||window).location.href=u;}catch(e){location.href=u;}}}" style="${btnStyleImg}">${btnLabel || 'Saiba Mais'}</a>`;
                } else {
                    imgCardBtnHtml = `<a href="${escImgHref(rawImgBtnLink || '#')}" style="${btnStyleImg}">${btnLabel || 'Saiba Mais'}</a>`;
                }
            }
            innerHtml = `<div style="border:1px solid #e2e8f0;border-radius:10px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.07);background:${cardBg || '#fff'}">${imageUrl ? `<img src="${imageUrl}" style="width:100%;height:${imageHeight || 200}px;object-fit:cover" />` : '<div style="width:100%;height:180px;background:#e2e8f0;display:flex;align-items:center;justify-content:center;font-size:3rem">🖼️</div>'}<div style="padding:1.2rem">${cardTag ? `<span style="font-size:.75rem;font-weight:700;text-transform:uppercase;color:${cBg || '#3b82f6'};letter-spacing:1px">${cardTag}</span>` : ''}${cardTitle ? `<h3 style="margin-top:.4rem;margin-bottom:.6rem;color:${titleColor || '#1e293b'};font-size:1.1rem">${cardTitle}</h3>` : ''} ${cardText ? `<p style="color:${tColor || '#64748b'};font-size:.9rem;line-height:1.6;margin-bottom:${showBtn ? '1rem' : 0}">${cardText}</p>` : ''} ${imgCardBtnHtml}</div></div>`;
        }
        if (w.type === 'social-card') {
            const c = w.content || {};
            const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
            const btnBg = String(c.btnBg || '#2563eb').replace(/["'<>`\\]/g, '').trim() || '#2563eb';
            const cardBg = String(c.cardBg || '#ffffff').replace(/["'<>`\\]/g, '').trim() || '#ffffff';
            const tagColor = String(c.tagColor || btnBg).replace(/["'<>`\\]/g, '').trim() || btnBg;
            const titleColor = String(c.titleColor || '#0f172a').replace(/["'<>`\\]/g, '').trim() || '#0f172a';
            const subtitleColor = String(c.subtitleColor || '#64748b').replace(/["'<>`\\]/g, '').trim() || '#64748b';
            const textColor = String(c.textColor || '#64748b').replace(/["'<>`\\]/g, '').trim() || '#64748b';
            const btnTextColor = String(c.btnTextColor || '#ffffff').replace(/["'<>`\\]/g, '').trim() || '#ffffff';
            const iconsColor = String(c.socialIconsColor || '#334155').replace(/["'<>`\\]/g, '').trim() || '#334155';
            const tagFontPx = Math.min(22, Math.max(10, parseInt(c.tagFontPx, 10) || 11));
            const titleFontPx = Math.min(34, Math.max(12, parseInt(c.titleFontPx, 10) || 18));
            const subtitleFontPx = Math.min(26, Math.max(10, parseInt(c.subtitleFontPx, 10) || 14));
            const textFontPx = Math.min(22, Math.max(10, parseInt(c.textFontPx, 10) || 13));
            const iconsSizePx = Math.min(32, Math.max(12, parseInt(c.socialIconsSizePx, 10) || 18));
            const avSize = Math.min(120, Math.max(56, parseInt(c.avatarSize, 10) || 88));
            const bh = Math.max(80, parseInt(c.bannerHeight, 10) || 160);
            const zoomPct = Math.min(200, Math.max(50, parseInt(c.bannerZoomPct, 10) || 100));
            const posX = Math.min(50, Math.max(-50, parseInt(c.bannerPosX, 10) || 0));
            const posY = Math.min(50, Math.max(-50, parseInt(c.bannerPosY, 10) || 0));
            const mode = c.avatarMode || 'mockup';
            const mockSvg = encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96"><circle cx="48" cy="48" r="48" fill="#e2e8f0"/><circle cx="48" cy="36" r="16" fill="#94a3b8"/><ellipse cx="48" cy="82" rx="28" ry="22" fill="#94a3b8"/></svg>');
            const defaultMock = `data:image/svg+xml;charset=utf-8,${mockSvg}`;
            const mockUrl = String(c.mockupAvatarUrl || '').trim() || defaultMock;
            let avatarInner = '';
            if (mode === 'moodle_viewer_native' || mode === 'moodle_self') {
                avatarInner = '[[GEPPETTO_CURRENT_USER_PICTURE]]';
            } else if (mode === 'moodle_teacher_native') {
                avatarInner = '[[GEPPETTO_COURSE_TEACHER_PICTURE]]';
            } else if (mode === 'moodle_user_id') {
                const uid = Math.max(0, parseInt(String(c.avatarMoodleUserId ?? '').trim(), 10) || 0);
                avatarInner = `[[GEPPETTO_USER_PICTURE:${uid}]]`;
            } else if (mode === 'moodle_custom') {
                const raw = String(c.avatarFiltercodes || '').trim();
                avatarInner = raw || '[[GEPPETTO_CURRENT_USER_PICTURE]]';
            } else {
                const safeSrc = mockUrl.replace(/"/g, '&quot;');
                avatarInner = `<img src="${safeSrc}" alt="" width="96" height="96" style="width:100%;height:100%;object-fit:cover;display:block;" />`;
            }
            const halfAv = avSize / 2;
            const bodyPadTop = halfAv + 10;
            const btnOutline = (c.btnStyle || 'outline') === 'outline';
            const btnCss = btnOutline
                ? `display:inline-block;padding:10px 24px;background:transparent;color:${btnBg};border:2px solid ${btnBg};border-radius:9999px;text-decoration:none;font-size:14px;font-weight:600`
                : `display:inline-block;padding:10px 24px;background:${btnBg};color:${btnTextColor};border:none;border-radius:9999px;text-decoration:none;font-size:14px;font-weight:600`;
            const bannerUrlRaw = String(c.bannerUrl || '').trim();
            const bannerBlock = bannerUrlRaw
                ? `<img src="${bannerUrlRaw.replace(/"/g, '&quot;')}" alt="" style="width:100%;height:${bh}px;object-fit:cover;display:block;object-position:calc(50% + ${posX}%) calc(50% + ${posY}%);transform:scale(${zoomPct / 100});transform-origin:calc(50% + ${posX}%) calc(50% + ${posY}%);" />`
                : `<div style="width:100%;height:${bh}px;background:linear-gradient(135deg,#dc2626 0%,#b91c1c 100%);display:flex;align-items:center;justify-content:center;color:rgba(255,255,255,.85);font-size:14px;font-weight:600">Banner</div>`;
            const tagHtml = c.cardTag ? `<span style="font-size:${tagFontPx}px;font-weight:700;text-transform:uppercase;color:${tagColor};letter-spacing:.08em">${esc(c.cardTag)}</span>` : '';
            const titleHtml = c.cardTitle ? `<h3 class="madri-social-card-title" style="margin:${c.cardTag ? '6px 0 4px 0' : '0 0 4px 0'};color:${titleColor};font-size:${titleFontPx}px;font-weight:700">${esc(c.cardTitle)}</h3>` : '';
            const subHtml = c.cardSubtitle ? `<p class="madri-social-card-subtitle" style="margin:0 0 8px 0;color:${subtitleColor};font-size:${subtitleFontPx}px;line-height:1.45">${esc(c.cardSubtitle)}</p>` : '';
            const textHtml = c.cardText ? `<p class="madri-social-card-text" style="margin:0 0 14px 0;color:${textColor};font-size:${textFontPx}px;line-height:1.6">${esc(c.cardText)}</p>` : '';
            const rawSocBtnLink = String(c.btnLink || '#').trim();
            let btnHtml = '';
            if (c.showBtn) {
                if (isMoodleRootRelativeHref(rawSocBtnLink)) {
                    btnHtml = `<div style="text-align:center;margin-top:4px"><a href="#" role="link" data-madri-href="${esc(rawSocBtnLink)}" onclick="event.preventDefault();var u=this.getAttribute('data-madri-href');if(u){if(typeof madriGoRedirect==='function'){madriGoRedirect(u);}else{try{(window.top||window).location.href=u;}catch(e){location.href=u;}}}" style="${btnCss}">${esc(c.btnLabel || 'Perfil')}</a></div>`;
                } else {
                    btnHtml = `<div style="text-align:center;margin-top:4px"><a href="${esc(rawSocBtnLink || '#')}" style="${btnCss}">${esc(c.btnLabel || 'Perfil')}</a></div>`;
                }
            }
            const iconsEnabled = !!c.socialIconsEnabled;
            const iconsPos = c.socialIconsPosition === 'below' ? 'below' : 'above';
            const icons = iconsEnabled ? (Array.isArray(c.socialIcons) ? c.socialIcons : []).filter(it => (it?.url || '').trim()).slice(0, 5) : [];
            const renderIconSvg = (iconName) => {
                const ic = String(iconName || '').trim();
                const svg = renderFaMdIconSvg(ic, iconsColor, iconsSizePx);
                if (svg) return svg;
                try {
                    const luc = renderTimelineLucideIconSvg(ic, iconsColor, iconsSizePx);
                    if (luc) return luc;
                } catch (_) { /* ignore */ }
                return '';
            };
            const iconsHtml = icons.length ? `<div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;margin:10px 0 12px 0">${icons.map((it) => {
                const url = String(it.url || '').trim();
                const iconSvg = renderIconSvg(it.icon || 'Link');
                const target = it.newTab === false ? '_self' : '_blank';
                return `<a href="${url.replace(/"/g, '&quot;')}" target="${target}" rel="noopener noreferrer" style="width:34px;height:34px;border-radius:999px;border:1px solid #e2e8f0;background:#ffffff;display:inline-flex;align-items:center;justify-content:center;box-shadow:0 1px 4px rgba(15,23,42,0.08);text-decoration:none">${iconSvg || ''}</a>`;
            }).join('')}</div>` : '';
            const iconsAbove = iconsPos === 'above' ? iconsHtml : '';
            const iconsBelow = iconsPos === 'below' ? iconsHtml : '';
            innerHtml = `<div class="madri-social-card" style="border:1px solid #e2e8f0;border-radius:12px;box-shadow:0 2px 10px rgba(15,23,42,.08);background:${cardBg};max-width:100%;overflow:visible"><div style="border-radius:12px 12px 0 0;overflow:hidden;line-height:0">${bannerBlock}</div><div style="position:relative;padding:${bodyPadTop}px 20px 20px 20px"><div class="madri-social-card-avatar" style="position:absolute;left:20px;top:${-halfAv}px;width:${avSize}px;height:${avSize}px;border-radius:50%;overflow:hidden;border:3px solid #fff;box-shadow:0 2px 8px rgba(15,23,42,.15);background:#f1f5f9;z-index:2">${avatarInner}</div>${tagHtml}${titleHtml}${subHtml}${textHtml}${iconsAbove}${btnHtml}${iconsBelow}</div></div>`;
        }
        if (w.type === 'post-it') {
            const { iconColor: ic, html: piHtml, icon: piIcon } = w.content;
            const piIconColor = ic || '#854d0e';
            let postItIconHtml = renderFaMdIconSvg(piIcon, piIconColor, 24);
            if (!postItIconHtml) {
                const PiIconComp = getIconComponent(piIcon) || LucideIcons.StickyNote;
                postItIconHtml = renderToStaticMarkup(React.createElement(PiIconComp, { size: 24, color: piIconColor, style: { flexShrink: 0, marginTop: '2px' } }));
            }
            innerHtml = buildPostItInnerHtml(w.content, postItIconHtml, sanitizeHtml(piHtml || ''), gConfigSource);
        }
        if (w.type === 'blockquote') {
            const acc = w.content.accentColor || '#3b82f6';
            const qStyle = w.content.quoteStyle || 'classic';
            const txt = (w.content.text || '').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
            const auth = (w.content.author || '').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
            const fs = w.content.fontSize || 18;
            const tc = w.content.textColor || '#334155';
            const bg = w.content.bg ?? (w.content.wrapperBg && w.content.wrapperBg !== 'transparent' ? (w.content.computedBgRGBA || w.content.wrapperBg) : undefined) ?? '#f8fafc';
            const quoteSvg = `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="${acc}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="opacity:0.7;margin:0 auto 0.8rem;display:block"><path d="M3 21c3 0 7-4 7-9V8c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 1 1 2v5c0 1-1 2-2 2z"/><path d="M15 21c3 0 7-4 7-9V8c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 1 2 2 2z"/></svg>`;
            if (qStyle === 'bordered') {
                innerHtml = `<div style="border-left:5px solid ${acc};padding-left:1.5rem;margin:1rem 0;background:${bg};padding:1.2rem 1.5rem;border-radius:0 8px 8px 0"><p style="font-size:${fs}px;font-style:italic;color:${tc};line-height:1.8;margin:0">&ldquo;${txt}&rdquo;</p>${auth ? `<cite style="display:block;margin-top:0.8rem;font-size:0.85rem;color:${acc};font-weight:700">&mdash; ${auth}</cite>` : ''}</div>`;
            } else if (qStyle === 'card') {
                innerHtml = `<div style="background:${acc};border-radius:12px;padding:1.8rem;position:relative;overflow:hidden"><div style="position:absolute;top:-20px;left:10px;font-size:8rem;color:rgba(255,255,255,0.15);font-family:Georgia;line-height:1">&ldquo;</div><p style="font-size:${fs}px;font-style:italic;color:#fff;line-height:1.8;margin:0;position:relative;z-index:1">${txt}</p>${auth ? `<cite style="display:block;margin-top:0.8rem;font-size:0.85rem;color:rgba(255,255,255,0.8);font-weight:700;position:relative;z-index:1">&mdash; ${auth}</cite>` : ''}</div>`;
            } else {
                innerHtml = `<div style="text-align:center;padding:1.5rem 2rem;margin:1rem 0">${quoteSvg}<p style="font-size:${fs}px;font-style:italic;color:${tc};line-height:1.8;margin:0 0 1rem">${txt}</p>${auth ? `<cite style="font-size:0.9rem;color:${acc};font-weight:700">&mdash; ${auth}</cite>` : ''}</div>`;
            }
        }
        if (w.type === 'timeline') {
            const getTimelineIconHtml = (iconName, color, size) => {
                const ic = iconName || 'Circle';
                const svg = renderFaMdIconSvg(ic, color, size);
                if (svg) return svg;
                if (!String(ic).startsWith('fa:') && !String(ic).startsWith('md:')) {
                    try {
                        const luc = renderTimelineLucideIconSvg(ic, color, size);
                        if (luc) return luc;
                    } catch (_) { /* fallback estático */ }
                }
                return getTimelineIconSvg(ic, color, size);
            };
            const tlSafeColor = (v, fb) => {
                const s = String(v || fb || '').trim().replace(/[<>"'`;{}\\]/g, '').slice(0, 48);
                return s || fb;
            };
            const items = w.content.items || [];
            const acc = tlSafeColor(w.content.accentColor, '#3b82f6');
            const lineColor = tlSafeColor(w.content.lineColor, '#e2e8f0');
            const dateC = tlSafeColor(w.content.timelineDateColor, acc);
            const titleC = tlSafeColor(w.content.timelineTitleColor, '#1e293b');
            const descC = tlSafeColor(w.content.timelineDescColor, '#64748b');
            const cardBg = tlSafeColor(w.content.timelineCardBg, '#f8fafc');
            const rawCardBr = w.content.timelineCardBorder;
            const cardBorderCssExp = resolveTimelineCardBorderCss(
                rawCardBr == null || String(rawCardBr).trim() === '' ? '' : tlSafeColor(rawCardBr, '#e2e8f0'),
            );
            const dir = w.content.direction || 'vertical';
            const scalePct = Math.min(200, Math.max(50, parseInt(w.content.timelineFontScalePct, 10) || 100));
            const scale = scalePct / 100;
            const remDate = `${0.75 * scale}rem`;
            const remTitleH = `${0.9 * scale}rem`;
            const remDescH = `${0.8 * scale}rem`;
            const remTitleV = `${0.95 * scale}rem`;
            const remDescV = `${0.85 * scale}rem`;
            if (items.length === 0) {
                innerHtml = '<div style="padding:1rem;color:#94a3b8;font-style:italic">Nenhum evento na linha do tempo</div>';
            } else if (dir === 'horizontal') {
                const itemsHtml = items.map((item, i) => {
                    const d = (item.date || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                    const t = (item.title || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                    const desc = (item.desc || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                    const iconHtml = getTimelineIconHtml(item.icon || 'Circle', '#fff', 24);
                    const innerCard = `${d ? `<span class="madri-gc-timeline-date" style="font-size:${remDate};font-weight:700;color:${dateC};letter-spacing:0.5px">${d}</span>` : ''}<div class="madri-gc-timeline-title" style="font-weight:700;color:${titleC};font-size:${remTitleH};margin-top:2px">${t}</div>${desc ? `<p class="madri-gc-timeline-desc" style="font-size:${remDescH};color:${descC};margin-top:4px;line-height:1.5">${desc}</p>` : ''}`;
                    return `<div class="madri-gc-timeline-h-item" style="flex:1;display:flex;flex-direction:column;align-items:center;padding:0 8px;position:relative"><div class="madri-gc-timeline-h-icon" style="width:48px;height:48px;border-radius:50%;background:${acc};display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 0 0 2px ${acc};z-index:1;flex-shrink:0">${iconHtml}</div><div class="madri-gc-timeline-h-body" style="margin-top:0.8rem;width:100%;box-sizing:border-box"><div style="background:${cardBg};border-radius:8px;padding:0.9rem 1.1rem;border:${cardBorderCssExp};text-align:center;box-sizing:border-box">${innerCard}</div></div></div>`;
                }).join('');
                innerHtml = `<div class="madri-gc-timeline madri-gc-timeline--horizontal" style="--madri-tl-line:${lineColor};overflow-x:auto;padding-bottom:1rem"><div class="madri-gc-timeline-h-track" style="display:flex;align-items:flex-start;min-width:${items.length * 180}px;position:relative;padding:1rem 0"><div class="madri-gc-timeline-h-connector" style="position:absolute;top:36px;left:48px;right:48px;height:2px;background:${lineColor}"></div>${itemsHtml}</div></div>`;
            } else {
                const itemsHtml = items.map((item, i) => {
                    const d = (item.date || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                    const t = (item.title || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                    const desc = (item.desc || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                    const iconHtml = getTimelineIconHtml(item.icon || 'Circle', '#fff', 20);
                    return `<div style="display:flex;gap:1rem;margin-bottom:${i < items.length - 1 ? '1.8rem' : 0};position:relative"><div style="position:absolute;left:-38px;top:2px;width:36px;height:36px;border-radius:50%;background:${acc};display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 0 0 2px ${acc};flex-shrink:0">${iconHtml}</div><div style="background:${cardBg};border-radius:8px;padding:0.9rem 1.1rem;border:${cardBorderCssExp};flex:1">${d ? `<span class="madri-gc-timeline-date" style="font-size:${remDate};font-weight:700;color:${dateC};letter-spacing:0.5px">${d}</span>` : ''}<div class="madri-gc-timeline-title" style="font-weight:700;color:${titleC};font-size:${remTitleV};margin-top:${d ? '2px' : 0}">${t}</div>${desc ? `<p class="madri-gc-timeline-desc" style="font-size:${remDescV};color:${descC};margin-top:4px;line-height:1.6;margin-bottom:0">${desc}</p>` : ''}</div></div>`;
                }).join('');
                innerHtml = `<div style="position:relative;padding-left:2.5rem"><div style="position:absolute;left:14px;top:8px;bottom:8px;width:2px;background:${lineColor}"></div>${itemsHtml}</div>`;
            }
        }
        if (w.type === 'checkpoint') {
            innerHtml = '<div class="madri-ck-standalone-inner" aria-hidden="true" style="min-height:8px;width:100%;"></div>';
        }
        if (w.type === 'spacer') {
            innerHtml = buildSpacerInnerHtml(w.content);
        }
        if (w.type === 'responsive-table') {
            innerHtml = buildResponsiveTableInnerHtml(w.content);
        }
        if (w.type === 'image-gallery') {
            innerHtml = buildImageGalleryInnerHtml(w.content, w.id);
        }
        if (w.type === 'divider') {
            const style = w.content.dividerStyle || 'solid';
            const color = w.content.color || '#cbd5e1';
            const thickness = w.content.thickness || 2;
            const width = w.content.width || 100;
            const align = w.content.align || 'center';
            const jc = align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start';
            const borderStyle = style === 'dashed' ? 'dashed' : style === 'dotted' ? 'dotted' : 'solid';
            const isDotDash = style === 'dashed' || style === 'dotted';
            if (style === 'icon' || style === 'scissors') {
                const iconChar = style === 'scissors' ? '✂' : '★';
                innerHtml = `<div style="display:flex;align-items:center;gap:12px;width:${width}%;margin:0 auto;padding:12px 0;justify-content:${jc}"><div style="flex:1;height:${thickness}px;background:${color};border-radius:4px"></div><span style="color:${color};font-size:20px">${iconChar}</span><div style="flex:1;height:${thickness}px;background:${color};border-radius:4px"></div></div>`;
            } else if (style === 'double') {
                innerHtml = `<div style="width:${width}%;margin:0 auto;padding:12px 0;display:flex;flex-direction:column;gap:4px"><div style="width:100%;height:${thickness}px;background:${color}"></div><div style="width:100%;height:${thickness}px;background:${color}"></div></div>`;
            } else if (style === 'wavy') {
                innerHtml = `<div style="width:${width}%;margin:0 auto;padding:2px 0;overflow:hidden"><svg width="100%" height="20" viewBox="0 0 400 20" preserveAspectRatio="none"><path d="M0,10 Q25,0 50,10 T100,10 T150,10 T200,10 T250,10 T300,10 T350,10 T400,10" fill="none" stroke="${color}" stroke-width="${thickness}"/></svg></div>`;
            } else {
                const bgVal = style === 'gradient' ? `linear-gradient(to right, transparent, ${color}, transparent)` : (isDotDash ? 'none' : color);
                const borderVal = isDotDash ? `${thickness}px ${borderStyle} ${color}` : 'none';
                innerHtml = `<div style="width:${width}%;margin:0 auto;padding:12px 0"><div style="width:100%;height:${isDotDash ? 0 : thickness}px;background:${bgVal};border-top:${borderVal}"></div></div>`;
            }
        }
        if (w.type === 'site-menu') {
            innerHtml = buildSiteMenuInnerHtml(w.content, w.id);
        }
        if (w.type === 'site-footer') {
            innerHtml = buildSiteFooterInnerHtml(w.content);
        }
        if (w.type === 'site-login') {
            innerHtml = buildLoginPageInnerHtml(w.content, w.id);
        }
        if (w.type === 'site-maintenance') {
            innerHtml = buildMaintenancePageInnerHtml(w.content, w.id);
        }
        if (w.type === 'count-up') {
            innerHtml = buildCountUpInnerHtml(w.content);
        }
        if (w.type === 'header-menu') {
            const c = w.content || {};
            const po = c.paddingObj;
            const padInline = po ? `padding:${po.top || 0}px ${po.right || 0}px ${po.bottom || 0}px ${po.left || 0}px;` : '';
            const mbg = c.menuBg && c.menuBg !== 'transparent' ? `background:${c.menuBg};` : '';
            innerHtml = `<div class="madri-header-menu-host" style="${padInline}${mbg}border-radius:8px;width:100%;box-sizing:border-box;">${buildExportedHeaderMenuInnerHtml(c, w.id)}</div>`;
        }
        if (w.type === 'comments') {
            hasComments = true;
            innerHtml = `<div class="madri-geppetto-comments" data-cmid="[[GEPPETTO_CMID]]" data-sesskey="[[GEPPETTO_SESSKEY]]" data-element-key="${w.id}">
          <div class="madri-gc-composer"></div>
          <div class="madri-gc-list"></div>
        </div>`;
        }
        if (w.type === 'geppetto-backpack') {
            innerHtml = '';
        }
        if (w.type === 'asaas-payment') {
            innerHtml = siteExport
                ? buildSiteOmittedNoticeHtml(sitePaymentNotice)
                : buildAsaasPaymentExportInnerHtml(w, currentPageIdx, checkpointList, layoutCheckpointIds);
        }
        if (w.type === 'mercadopago-payment') {
            innerHtml = siteExport
                ? buildSiteOmittedNoticeHtml(sitePaymentNotice)
                : buildMercadopagoPaymentExportInnerHtml(w, currentPageIdx, checkpointList, layoutCheckpointIds);
        }
        if (w.type === 'moodle-activity' || w.type === 'moodle-quiz' || w.type === 'moodle-forum') {
            if (siteExport) {
                innerHtml = buildSiteOmittedNoticeHtml(siteOmittedNotice);
            } else {
            hasMoodleActivitySlots = true;
            const c = w.content || {};
            const slotId = String(w.id || '').replace(/["'<>]/g, '');
            const escBody = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            const escAttr = (s) => String(s || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            const rawHref = String(c.activityUrl ?? '').trim();
            let href = '';
            if (rawHref) {
                if (!/^\s*javascript:/i.test(rawHref) && rawHref.indexOf('..') < 0) {
                    if (/^https?:\/\//i.test(rawHref)) {
                        href = rawHref;
                    } else if (rawHref.slice(0, 2) !== '//') {
                        href = rawHref.startsWith('/') ? rawHref : `/${rawHref.replace(/^\/+/, '')}`;
                    }
                }
            } else {
                const cmidOnly = String(c.activityCmid != null ? c.activityCmid : '').replace(/\D/g, '').slice(0, 12);
                if (cmidOnly) {
                    const mod = w.type === 'moodle-forum' ? 'forum' : 'quiz';
                    href = mod === 'forum' ? `/mod/forum/view.php?id=${cmidOnly}` : `/mod/quiz/view.php?id=${cmidOnly}`;
                }
            }
            const title = escBody(c.activityTitle || '');
            const desc = escBody(c.activityDesc || '');
            const aria = escAttr(String(c.activityTitle || 'Atividade').slice(0, 220));
            const iconName = c.activityIcon || 'MessageCircle';
            const IconComp = getIconComponent(iconName) || LucideIcons.MessageCircle;
            const iconBg = safeCssColorToken(c.activityIconBg, '#e5f4f9');
            const iconFg = safeCssColorToken(c.activityIconColor, '#006378');
            const iconHtml = renderToStaticMarkup(React.createElement(IconComp, { size: 22, color: iconFg, strokeWidth: 2 }));
            const iconWrap = `<span class="gma-icon-wrap" style="background:${iconBg};">${iconHtml}</span>`;
            const titleHtml = title ? `<span class="gma-title">${title}</span>` : '';
            const metaHtml = desc ? `<span class="gma-meta">${desc}</span>` : '';
            const cmidParsed = extractMoodleActivityCmidFromHref(href);
            let badgeHtml = '';
            if (c.showActivityStatus) {
                const pendRaw = activityPendingDef;
                const compRaw = activityCompleteDef;
                if (cmidParsed) {
                    badgeHtml = `<span class="gma-badge gma-badge--live" data-cmid="${escAttr(cmidParsed)}" data-label-pending="${escAttr(pendRaw)}" data-label-complete="${escAttr(compRaw)}">${escBody(pendRaw)}</span>`;
                } else if (pendRaw) {
                    badgeHtml = `<span class="gma-badge">${escBody(pendRaw)}</span>`;
                }
            }
            const openNewTabAttr = c.activityOpenInNewTab === true ? '1' : '0';
            innerHtml = `<div class="geppetto-moodle-activity-row" role="link" tabindex="0" data-slot-id="${slotId}" data-activity-href="${escAttr(href)}" data-open-in-new-tab="${openNewTabAttr}" aria-label="${aria}">${iconWrap}<span class="gma-body">${titleHtml}${metaHtml}</span>${badgeHtml}</div>`;
            }
        }
        const marginStr = w.type === 'site-login' || w.type === 'site-maintenance' ? 'margin:0; ' : (w.content.marginObj ? `margin: ${w.content.marginObj.top || 0}px ${w.content.marginObj.right || 0}px ${w.content.marginObj.bottom || 0}px ${w.content.marginObj.left || 0}px; ` : 'margin: 0 0 20px 0; ');
        const paddingStr = (w.type === 'styled-card' || w.type === 'header-menu' || w.type === 'site-menu' || w.type === 'site-footer' || w.type === 'site-login' || w.type === 'site-maintenance' || w.type === 'geppetto-backpack' ? '' : (w.content.paddingObj ? `padding: ${w.content.paddingObj.top || 0}px ${w.content.paddingObj.right || 0}px ${w.content.paddingObj.bottom || 0}px ${w.content.paddingObj.left || 0}px; ` : ''));
        const bgStr = (w.type === 'header-menu' || w.type === 'site-menu' || w.type === 'site-footer' || w.type === 'site-login' || w.type === 'site-maintenance' || w.type === 'geppetto-backpack' ? '' : ((w.content.computedBgRGBA || w.content.wrapperBg) ? `background-color: ${w.content.computedBgRGBA || w.content.wrapperBg}; ` : ''));
        const exportWrapColor = resolveWidgetExportWrapperColor(w.type, w.content);
        const colorStr = exportWrapColor ? `color: ${exportWrapColor}; ` : '';
        const fontStr = w.content.wrapperFont ? `font-size: ${w.content.wrapperFont}px; ` : '';
        const lineHeightStr = w.content.wrapperLineHeight ? `line-height: ${w.content.wrapperLineHeight}; ` : '';
        const radiusStr = w.type === 'site-menu' || w.type === 'site-footer' || w.type === 'site-login' || w.type === 'site-maintenance' ? 'border-radius:0;overflow:visible;' : 'border-radius: 8px; ';
        const advPart = elementAdvancedCss(w.content.advancedCss, w.content.customClass);
        // Regras com seletor vão para a folha <style>. No atributo style só entram declarações simples.
        const advCssStr = advPart.inline ? ` ${advPart.inline.replace(/"/g, "'")}` : '';
        const customCls = advPart.classNames;
        const ELEMENTS_WITH_CHECKPOINT = ['video', 'panda-video', 'text', 'image', 'h5p', 'styled-card', 'section-header', 'title-basic', 'blockquote', 'alert'];
        const isStandaloneCheckpoint = w.type === 'checkpoint';
        const ck = isStandaloneCheckpoint ? (w.content || {}) : (w.content.checkpoint || {});
        const checkpointWrapEligible = isStandaloneCheckpoint
            ? !!innerHtml
            : (!!w.content.checkpoint?.enabled && ELEMENTS_WITH_CHECKPOINT.includes(w.type) && !!innerHtml);
        if (checkpointWrapEligible) {
            const isVideoMode = !isStandaloneCheckpoint && ['video', 'panda-video'].includes(w.type) && ck.completionMode === 'video';
            const dur = isVideoMode ? 0 : (ck.durationMs || 5000);
            const iconType = ck.iconType || 'check';
            const position = ck.position || 'top-right';
            const posCss = position === 'top-left' ? 'top:6px;left:6px' : position === 'top-right' ? 'top:6px;right:6px' : position === 'bottom-left' ? 'bottom:6px;left:6px' : 'bottom:6px;right:6px';
            let pandaIframeId = null;
            if (isVideoMode && w.type === 'panda-video') {
                const pv = extractPandaVideoIdFromUrl((w.content.url || '').trim());
                if (pv) pandaIframeId = `panda-${pv}`;
            }
            checkpointList.push(isVideoMode ? { id: w.id, duration: 0, page: currentPageIdx, mode: 'video', videoPercent: ck.videoPercent ?? 75, pandaIframeId, label: resolveCheckpointDisplayLabel(w, null, ckExportIndex) } : { id: w.id, duration: dur, page: currentPageIdx, label: resolveCheckpointDisplayLabel(w, null, ckExportIndex) });
            ckExportIndex += 1;
            const svgTypes = ['circle', 'square', 'badge', 'shield'];
            let iconHtml = '';
            if (svgTypes.includes(iconType)) {
                const s = 26;
                const notDone = iconType === 'circle' ? `<circle cx="12" cy="12" r="10" fill="none" stroke="#94a3b8" stroke-width="2"/><path d="M8 12l3 3 5-6" stroke="#94a3b8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>` : iconType === 'square' ? `<rect x="4" y="4" width="16" height="16" rx="2" fill="none" stroke="#94a3b8" stroke-width="2"/><path d="M8 12l3 3 5-6" stroke="#94a3b8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>` : iconType === 'badge' ? `<circle cx="12" cy="12" r="10" fill="#e2e8f0"/><path d="M8 12l3 3 5-6" stroke="#94a3b8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>` : `<path d="M12 2L4 5v7c0 5.5 3.8 9.6 8 11 4.2-1.4 8-5.5 8-11V5l-8-3z" fill="none" stroke="#94a3b8" stroke-width="2" stroke-linejoin="round"/><path d="M8 12l3 3 5-6" stroke="#94a3b8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>`;
                const done = iconType === 'circle' ? `<circle cx="12" cy="12" r="10" fill="#22c55e" stroke="#22c55e" stroke-width="2"/><path d="M8 12l3 3 5-6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>` : iconType === 'square' ? `<rect x="4" y="4" width="16" height="16" rx="2" fill="#22c55e" stroke="#22c55e" stroke-width="2"/><path d="M8 12l3 3 5-6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>` : iconType === 'badge' ? `<circle cx="12" cy="12" r="10" fill="#22c55e"/><path d="M8 12l3 3 5-6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>` : `<path d="M12 2L4 5v7c0 5.5 3.8 9.6 8 11 4.2-1.4 8-5.5 8-11V5l-8-3z" fill="#22c55e" stroke="#22c55e" stroke-width="2"/><path d="M8 12l3 3 5-6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>`;
                iconHtml = `<span class="madri-ck-icon madri-ck-notdone" style="position:absolute;${posCss};width:${s}px;height:${s}px"><svg width="${s}" height="${s}" viewBox="0 0 24 24">${notDone}</svg></span><span class="madri-ck-icon madri-ck-done" style="position:absolute;${posCss};width:${s}px;height:${s}px;display:none"><svg width="${s}" height="${s}" viewBox="0 0 24 24">${done}</svg></span>`;
            } else {
                const ico = { check: { d: '✓', n: '○' }, flag: { d: '🚩', n: '🏳' }, star: { d: '★', n: '☆' }, bookmark: { d: '🔖', n: '📑' }, trophy: { d: '🏆', n: '🎖' } }[iconType] || { d: '✓', n: '○' };
                iconHtml = `<span class="madri-ck-icon madri-ck-notdone" style="position:absolute;${posCss};font-size:1.3em;color:#94a3b8">${ico.n}</span><span class="madri-ck-icon madri-ck-done" style="position:absolute;${posCss};font-size:1.3em;color:#22c55e;display:none">${ico.d}</span>`;
            }
            innerHtml = `<div class="madri-checkpoint madri-ck-inline" id="madri-ck-${w.id}" data-duration="${dur}" data-page="${currentPageIdx}" data-gep-cmid="[[GEPPETTO_CMID]]" data-gep-sesskey="[[GEPPETTO_SESSKEY]]" style="position:relative">${innerHtml}${iconHtml}</div>`;
        }
        innerHtml = applyMotionToInnerHtml(innerHtml, w.content);
        const motionCls = motionClassName(w.content);
        const motionStyle = motionInlineStyle(w.content);
        const extraClasses = [customCls, isHiddenOnMobile(w.content) ? HIDE_ON_MOBILE_CLASS : '', motionCls].filter(Boolean).join(' ');
        pushWidgetMobileTypographyRules(typographyStyleRules, w.id, w.type, w.content);
        return `<div class="widget-block madri-widget-${w.id}${extraClasses ? ' ' + extraClasses : ''}" style="${marginStr}${paddingStr}${bgStr}${colorStr}${fontStr}${lineHeightStr}${radiusStr}${advCssStr}${motionStyle}">${innerHtml}</div>`;
    };

    // Pre-scan: progress-bar and generate headerHtml
    const headerToUse = headerWidgetsToUse || [];
    const extractProgressConfig = (w) => ({
        accentColor: w.content.accentColor || '#3b82f6',
        showPercent: w.content.showPercent !== false,
        barStyle: w.content.barStyle || 'simple',
        widthPct: w.content.widthPct ?? 100,
        barAlign: w.content.barAlign || 'center',
        progressBarBg: w.content.progressBarBg || '#f8fafc',
        trackColor: w.content.trackColor || '#e2e8f0',
        progressBarTextColor: w.content.progressBarTextColor || '#64748b',
        progressBarIcon: w.content.progressBarIcon || 'Gauge',
        showSegmentedCompletion: w.content.showSegmentedCompletion !== false,
        segmentedCompletionText: w.content.segmentedCompletionText || 'para completar',
        segmentedCompletionColor: w.content.segmentedCompletionColor || w.content.progressBarTextColor || '#64748b',
        progressBarFontSize: w.content.wrapperFont,
        progressBarLineHeight: w.content.wrapperLineHeight
    });
    headerToUse.forEach(w => {
        if (w.type === 'progress-bar') progressBarConfig = extractProgressConfig(w);
    });
    if (!progressBarConfig) {
        rowsToUse.forEach(r => {
            r.columns.forEach(c => {
                c.widgets.forEach(w => {
                    if (w.type === 'progress-bar') progressBarConfig = extractProgressConfig(w);
                });
            });
        });
    }

    const headerMenuWidget = headerToUse.find(w => w.type === 'header-menu');
    const headerWidgetsValid = headerToUse.filter(w => w.type !== 'progress-bar' && w.type !== 'header-menu');
    const globalHeaderHtml = headerWidgetsValid.length > 0
        ? `<div class="madri-global-header" style="margin-bottom: 20px;">` + headerWidgetsValid.map(w => renderWidgetHtml(w, 0)).join('\n') + `</div>`
        : '';

    /** Espelha utils/rowBackgroundImage.js (este ficheiro pode ser mesclado sem imports externos). */
    const buildRowBackgroundImageLayerHtmlInline = (rc) => {
        const raw = (rc && rc.bgImage && String(rc.bgImage).trim()) || '';
        if (!raw) return '';
        const urlCss = String(raw).replace(/\\/g, '\\\\').replace(/'/g, "\\'");
        const urlAttr = String(raw).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const op = (rc.bgImageOpacity != null ? rc.bgImageOpacity : 100) / 100;
        const mode = (rc && rc.bgConfig) || 'cover';
        const posX = rc.bgImagePositionX != null ? rc.bgImagePositionX : 50;
        const posY = rc.bgImagePositionY != null ? rc.bgImagePositionY : 50;
        if (['repeat', 'repeat-x', 'repeat-y'].includes(mode)) {
            const tile = rc.bgImageSize != null ? rc.bgImageSize : 100;
            return `<div class="madri-row-bg-image" style="position:absolute;inset:0;z-index:0;overflow:hidden;pointer-events:none;opacity:${op};background-image:url('${urlCss}');background-repeat:${mode};background-size:${tile}% ${tile}%;background-position:${posX}% ${posY}%"></div>`;
        }
        const rot = rc.bgImageRotate != null ? rc.bgImageRotate : 0;
        const pct = rc.bgImageSize != null ? rc.bgImageSize : 100;
        const ox = (rc.bgImagePositionX != null ? rc.bgImagePositionX : 50) + '%';
        const oy = (rc.bgImagePositionY != null ? rc.bgImagePositionY : 50) + '%';
        const objFit = mode === 'contain' ? 'contain' : mode === 'stretch' ? 'fill' : 'cover';
        return `<div class="madri-row-bg-image" style="position:absolute;inset:0;z-index:0;overflow:hidden;pointer-events:none;opacity:${op}"><div style="position:absolute;left:50%;top:50%;width:${pct}%;height:${pct}%;transform:translate(-50%,-50%) rotate(${rot}deg);transform-origin:center center"><img src="${urlAttr}" alt="" style="width:100%;height:100%;object-fit:${objFit};object-position:${ox} ${oy}" /></div></div>`;
    };

    const renderExportRowHtml = (row, currentPageIdx, isNested = false) => {
        const rc = row.config || {};
        const rowCols = row.columns || [];
        const rowBetweenGapPx = rowColumnsBetweenGapPx(rowCols);
        const columnsHtml = rowCols.map((c, colIdx) => {
            const otherWidgets = (c.widgets || []).filter(w => w.type !== 'page-break' && w.type !== 'progress-bar' && w.type !== 'header-menu');
            const widgetsHtml = otherWidgets.map(w => renderWidgetHtml(w, currentPageIdx)).join('\n');
            const nestedList = Array.isArray(c.nestedRows) ? c.nestedRows : [];
            const nestedHtml = nestedList.map(nr => renderExportRowHtml(nr, currentPageIdx, true)).join('\n');

            const isDynamic = c.width === 'dynamic';
            const pct = c.customWidth ?? 33.33;
            const cssClass = isDynamic ? 'madri-col-dynamic' : (
                c.width === '1/1' ? 'madri-col-12' :
                c.width === '1/2' ? 'madri-col-md-6' :
                c.width === '1/3' ? 'madri-col-md-4' :
                c.width === '2/3' ? 'madri-col-md-8' :
                c.width === '1/4' ? 'madri-col-md-3' : 'madri-col-12'
            );
            const cc = normalizeColumnConfig(c.config);
            const respCls = columnResponsiveClassNames(cc).join(' ').trim();
            const colAdv = elementAdvancedCss(cc.advancedCss, cc.customClass);
            const fullClass = [`madri-col-${c.id}`, cssClass, respCls, colAdv.classNames, isHiddenOnMobile(cc) ? HIDE_ON_MOBILE_CLASS : ''].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
            let colStyle = '';
            if (isNested && rowBetweenGapPx === 0) {
                colStyle = 'min-width: 0; position: relative; padding-right: 15px; padding-left: 15px; box-sizing: border-box; ';
            } else if (rowBetweenGapPx > 0) {
                colStyle = `min-width: 0; position: relative; padding-left: ${COLUMN_GRID_BASE_PADDING_PX}px; padding-right: ${COLUMN_GRID_BASE_PADDING_PX}px; box-sizing: border-box; `;
            } else if (isDynamic) {
                colStyle = `flex: 0 0 ${pct}%; max-width: ${pct}%; position: relative; padding-right: 15px; padding-left: 15px; box-sizing: border-box; min-width: 0;`;
            }
            const ai = columnAlignToFlex(cc.columnAlign);
            const flexPart = ai ? `display:flex;flex-direction:column;align-items:${ai};` : '';
            const taPart = cc.textAlign ? `text-align:${cc.textAlign};` : '';
            const hasVisibleBg = (cc.backgroundColor && cc.backgroundColor !== 'transparent') || cc.bgImage;
            const emptyCol = !widgetsHtml.trim() && !nestedHtml.trim();
            let hPart = '';
            if (cc.heightPx > 0) hPart = `min-height:${cc.heightPx}px;`;
            else if (hasVisibleBg && emptyCol) hPart = 'min-height:120px;';
            const ptPart = cc.paddingTop > 0 ? `padding-top:${cc.paddingTop}px;` : '';
            const pbPart = cc.paddingBottom > 0 ? `padding-bottom:${cc.paddingBottom}px;` : '';
            const bgCpart = (cc.backgroundColor && cc.backgroundColor !== 'transparent') ? `background-color: ${cc.backgroundColor};` : '';
            const bgImgSafe = (cc.bgImage || '').replace(/\\/g, '\\\\').replace(/'/g, "\\'");
            const bgImgPart = cc.bgImage ? `background-image: url('${bgImgSafe}'); background-size: cover; background-position: 50% 50%;` : '';
            const colAdvPart = colAdv.inline ? ` ${colAdv.inline.replace(/"/g, "'")}` : '';
            const colWaveHtml = getWaveHtml(cc.waveConfig) || '';
            pushColumnMobileLayoutRules(layoutStyleRules, row.id, c.id, cc);
            return `<div class="${fullClass}" style="${colStyle}${flexPart}${taPart}${hPart}${ptPart}${pbPart}${bgCpart}${bgImgPart}position:relative;${colAdvPart}">${colWaveHtml}${widgetsHtml}${nestedHtml}</div>`;
        }).join('\n');

        const rowMargin = rc.marginObj ? `margin: ${rc.marginObj.top || 0}px ${rc.marginObj.right || 0}px ${rc.marginObj.bottom || 0}px ${rc.marginObj.left || 0}px; ` : 'margin: 0; ';
        const rowHasSiteMenu = !isNested && (row.columns || []).some((col) => (col.widgets || []).some((w) => w && w.type === 'site-menu'));
        const rowHasSiteFooter = !isNested && (row.columns || []).some((col) => (col.widgets || []).some((w) => w && w.type === 'site-footer'));
        const rowHasSiteLogin = !isNested && (row.columns || []).some((col) => (col.widgets || []).some((w) => w && w.type === 'site-login'));
        const rowHasSiteMaintenance = !isNested && (row.columns || []).some((col) => (col.widgets || []).some((w) => w && w.type === 'site-maintenance'));
        const rowPadding = rc.paddingObj ? `padding: ${rc.paddingObj.top || 0}px ${rc.paddingObj.right || 0}px ${rc.paddingObj.bottom || 0}px ${rc.paddingObj.left || 0}px; ` : (rowHasSiteFooter || rowHasSiteLogin || rowHasSiteMaintenance ? 'padding: 0; ' : rowHasSiteMenu ? 'padding: 0 1rem 1rem 1rem; ' : 'padding: 1rem; ');
        const rowBgResolved = resolveRowBackgroundColor(rc);
        const rowBgColor = rowBgResolved !== 'transparent' ? 'background-color: ' + rowBgResolved + ';' : '';
        const rowBgLayerHtml = buildRowBackgroundImageLayerHtmlInline(rc);
        const rowAdv = elementAdvancedCss(rc.advancedCss, rc.customClass);
        const rowAdvCss = rowAdv.inline ? ` ${rowAdv.inline.replace(/"/g, "'")}` : '';
        const fiHtml = buildRowFloatingImageHtml(rc, row.id, layoutStyleRules);
        const waveHtml = getWaveHtml(rc.waveConfig) || '';
        const rowCustomCls = rowAdv.classNames;
        const pageIsFull = (gConfigSource || {}).layoutWidth === 'full-width';
        const rowBleeds = pageIsFull && !isNested && !!rc.contentBleed;
        const rowClasses = `madri-row madri-row-${row.id}${isNested ? ' madri-row--nested' : ''}${rowBleeds ? ' madri-row-bleed' : ''}${isHiddenOnMobile(rc) ? ' madri-hide-mobile' : ''}${rowCustomCls ? ' ' + rowCustomCls : ''}`;
        pushRowMobileLayoutRules(layoutStyleRules, row.id, rc);
        if (rowBetweenGapPx > 0) {
            pushRowGridGapStyleRules(layoutStyleRules, row);
        } else if (isNested) {
            pushRowGridGapStyleRules(layoutStyleRules, row, { always: true });
        }
        const nestedRowVar = isNested ? `--madri-row-gap-total:${rowBetweenGapPx}px;` : '';
        const rowContentHtml = (pageIsFull && !isNested)
            ? `<div class="madri-row-inner">${columnsHtml}</div>`
            : columnsHtml;
        return `<div class="${rowClasses}" style="position:relative;${nestedRowVar}${rowMargin}${rowPadding}${rowBgColor}${rowAdvCss}">${rowBgLayerHtml}${waveHtml}${fiHtml}${rowContentHtml}</div>`;
    };

    // Pre-processamento: Cortar as "Rows" exatamente onde tem o Widget de Quebra
    const processedRows = [];
    rowsToUse.forEach(r => {
        let hasPageBreak = false;
        r.columns.forEach(c => c.widgets.forEach(w => { if (w.type === 'page-break') hasPageBreak = true; }));

        if (!hasPageBreak) {
            processedRows.push(r);
            return;
        }

        let rBefore = { ...r, columns: r.columns.map(c => ({ ...c, widgets: [] })) };
        let rAfter = { ...r, columns: r.columns.map(c => ({ ...c, widgets: [] })) };
        let foundAnyBreakInRow = false;

        for (let cIdx = 0; cIdx < r.columns.length; cIdx++) {
            const col = r.columns[cIdx];
            let foundBreakInThisCol = false;

            for (const w of col.widgets) {
                if (w.type === 'page-break') {
                    foundBreakInThisCol = true;
                    foundAnyBreakInRow = true;
                } else {
                    if (foundBreakInThisCol || foundAnyBreakInRow) {
                        rAfter.columns[cIdx].widgets.push(w);
                    } else {
                        rBefore.columns[cIdx].widgets.push(w);
                    }
                }
            }
        }

        const hasBeforeWidgets = rBefore.columns.some(c => c.widgets.length > 0);
        if (hasBeforeWidgets) processedRows.push(rBefore);

        const hasAfterWidgets = rAfter.columns.some(c => c.widgets.length > 0);
        processedRows.push({ isMarker: true, noContentAfter: !hasAfterWidgets });

        if (hasAfterWidgets) processedRows.push(rAfter);
    });

    processedRows.forEach(r => {
        // 2. Após adicionar a ROW útil, se ela for marcadora de SPA, nós encerramos a página aqui
        if (r.isMarker) {
            if (currentPageHTML.length > 0) {
                pages.push(currentPageHTML.join('\n'));
                currentPageHTML = [];
            }
            // Não cria página vazia: o botão de redirect aparece na última página de conteúdo
            return;
        }

        // 1. Processa widgets normais ignorando os page-breaks na renderização visual
        const currentPageIdx = pages.length;
        currentPageHTML.push(renderExportRowHtml(r, currentPageIdx));
    });

    if (currentPageHTML.length > 0) {
        pages.push(currentPageHTML.join('\n'));
    }

    const pageCount = pages.length;
    let finalHtml = '';

    if (pageCount <= 1) {
        const moodleRedirectPrefix = layoutNeedsMadriMoodleRedirectScript(rowsToUse, headerWidgetsToUse) ? MADRI_MOODLE_REDIRECT_SCRIPT : '';
        finalHtml = moodleRedirectPrefix + MADRI_SCROLL_ANCHOR_SCRIPT + (pages[0] || '');
    } else {
        const pbRaw = collectPageBreakExportConfig(rowsToUse);
        const navStyle = pbRaw.navStyle;
        const btnBg = safeCssColorToken(pbRaw.btnBgColor, '#3b82f6');
        const btnText = safeCssColorToken(pbRaw.btnTextColor, '#ffffff');
        const textColor = safeCssColorToken(pbRaw.textColor, '#64748b');
        const lastPageAction = String(pbRaw.lastPageAction || 'none').toLowerCase();
        const lastPageUrl = (pbRaw.lastPageUrl || '').trim();
        const lastPageLabel = (pbRaw.lastPageLabel || 'Concluir e voltar ao curso').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
        // Escapa URL para atributo href: & quebra parsing em URLs como ?id=1&sesskey=...
        const escapeUrlForHref = (u) => String(u).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const lastPageUrlEscaped = lastPageUrl ? escapeUrlForHref(lastPageUrl) : '';

        // 3. Monta Script de Paginação JS
        const pagesHtml = pages.map((pageContent, idx) => {
            const paginationContent = buildSpaPaginationContentHtml({
                navStyle,
                idx,
                pageCount: pages.length,
                prev,
                next,
                pageLabel,
                btnBg,
                btnText,
                textColor,
                lastPageAction,
                lastPageUrlEscaped,
                lastPageLabel,
            });

            return `
        <div id="madri-page-${idx}" style="display: ${idx === 0 ? 'block' : 'none'}; animation: fadeIn 0.4s ease-out;">
          <div>
            ${pageContent}
          </div>
          <div class="madri-spa-pagination madri-spa-pagination--${navStyle}">
            ${paginationContent}
          </div>
        </div>
        `;
        }).join('\n');

        // Script de paginação ANTES do HTML das páginas: evita que "</script>" ou markup nas páginas
        // feche o &lt;script&gt; cedo e corrompa o JS (madriGoToPage não definido).
        finalHtml = `
        <style> @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } } </style>
        <script>
          function madriStripTrailingSlash(s) {
            var t = String(s || '');
            while (t.length > 0 && t.charAt(t.length - 1) === '/') { t = t.slice(0, -1); }
            return t;
          }
          function madriResolveRedirectUrl(u) {
            if (!u) return u;
            var raw = String(u).trim();
            if (!raw) return raw;
            var _absProto = new RegExp('^[a-zA-Z][a-zA-Z0-9+.-]*:');
            if (_absProto.test(raw) || raw.slice(0, 2) === '//') return raw;

            var ctx = null;
            try { ctx = window.top || window; } catch (e0) { ctx = window; }

            try {
              var m = ctx && ctx.M;
              var wwwroot = m && m.cfg && m.cfg.wwwroot ? String(m.cfg.wwwroot) : '';
              if (wwwroot && raw.charAt(0) === '/') {
                return madriStripTrailingSlash(wwwroot) + raw;
              }
            } catch (e1) {}

            try {
              return new URL(raw, (ctx && ctx.location && ctx.location.href) ? ctx.location.href : window.location.href).toString();
            } catch (e2) {
              return raw;
            }
          }

          function madriGoRedirect(u) {
            var target = madriResolveRedirectUrl(u);
            if (!target) return;
            try {
              (window.top || window).location.href = target;
            } catch (e3) {
              window.location.href = target;
            }
          }

          function madriScrollToContentTop() {
            try { window.scrollTo(0, 0); } catch (e0) {}
            try {
              if (document.documentElement) { document.documentElement.scrollTop = 0; }
              if (document.body) { document.body.scrollTop = 0; }
            } catch (e1) {}
            try {
              var pw = window.parent;
              if (pw && pw !== window && pw.document) {
                var pdoc = pw.document;
                var fe = null;
                try { fe = window.frameElement; } catch (eFe) {}
                if (!fe) {
                  fe = pdoc.getElementById('resourceobject')
                    || pdoc.querySelector('iframe.gepettoresource-html-embed, .gepettoresource-html-embed');
                }
                if (fe) {
                  var nav = pdoc.querySelector('.fixed-top.navbar, #page-header, header.navbar.fixed-top, .navbar.fixed-top');
                  var off = nav ? Math.ceil(nav.getBoundingClientRect().height || 0) : 0;
                  var rect = fe.getBoundingClientRect();
                  var se = pdoc.scrollingElement || pdoc.documentElement;
                  var cur = pw.pageYOffset || (se ? se.scrollTop : 0) || 0;
                  var targetY = Math.max(0, cur + rect.top - off - 8);
                  try { pw.scrollTo({ top: targetY, left: 0, behavior: 'auto' }); } catch (e2) { pw.scrollTo(0, targetY); }
                  if (se) { se.scrollTop = targetY; }
                  if (pdoc.body) { pdoc.body.scrollTop = targetY; }
                  var scrollers = pdoc.querySelectorAll('#page, #page-content, #region-main, [data-region="main-content"], main');
                  for (var si = 0; si < scrollers.length; si++) {
                    var sc = scrollers[si];
                    try {
                      if (sc && sc.contains && sc.contains(fe) && sc.scrollHeight > sc.clientHeight + 2) {
                        var cr = fe.getBoundingClientRect();
                        var sr = sc.getBoundingClientRect();
                        sc.scrollTop = sc.scrollTop + (cr.top - sr.top) - off - 8;
                      }
                    } catch (eSc) {}
                  }
                  try { fe.scrollIntoView({ block: 'start', behavior: 'auto' }); } catch (e3) {
                    try { fe.scrollIntoView(true); } catch (e4) {}
                  }
                }
              }
            } catch (e5) {}
            try {
              if (window.parent && window.parent !== window && window.parent.postMessage) {
                window.parent.postMessage({ type: 'madriPageScrollTop' }, '*');
              }
            } catch (e6) {}
          }

          function madriScheduleScrollAfterPageChange() {
            window.__madriRequestScrollTop = true;
            madriScrollToContentTop();
            [0, 50, 120, 250, 450].forEach(function(ms) {
              setTimeout(function() {
                madriScrollToContentTop();
                if (typeof window.sendHeightToMoodle === 'function') {
                  window.sendHeightToMoodle({ force: true });
                }
              }, ms);
            });
          }

          function madriGetCurrentPageIdx() {
            var total = ${pages.length};
            for (var i = 0; i < total; i++) {
              var pel = document.getElementById('madri-page-' + i);
              if (pel && pel.style.display !== 'none') return i;
            }
            return 0;
          }

          function madriGoToPage(targetIdx) {
            var total = ${pages.length};
            if (targetIdx < 0 || targetIdx >= total) return;
            if (madriGetCurrentPageIdx() === targetIdx) return;
            for (var i = 0; i < total; i++) {
              var el = document.getElementById('madri-page-' + i);
              if (el) el.style.display = (i === targetIdx) ? 'block' : 'none';
            }
            madriScheduleScrollAfterPageChange();
            function madriChartsReflow() {
              if (typeof window.__madriResizeEchartsCharts === 'function') {
                window.__madriResizeEchartsCharts();
              } else if (typeof echarts !== 'undefined') {
                var nodes = document.querySelectorAll('.madri-gc-chart-host');
                for (var j = 0; j < nodes.length; j++) {
                  var ii = echarts.getInstanceByDom(nodes[j]);
                  if (ii) { try { ii.resize(); } catch (e4) {} }
                }
              }
            }
            setTimeout(madriChartsReflow, 0);
            setTimeout(madriChartsReflow, 60);
            setTimeout(madriChartsReflow, 220);
          }

          function madriScrollToAnchor(rawId) {
            var id = String(rawId || '').replace(/^#/, '').trim();
            if (!id) return;
            try { id = decodeURIComponent(id); } catch (e0) {}
            id = String(id).replace(/[^a-zA-Z0-9_-]/g, '');
            if (!id) return;
            var el = document.getElementById(id);
            if (!el) return;
            var n = el;
            var pageIdx = -1;
            while (n && n !== document.body) {
              if (n.id && /^madri-page-\\d+$/.test(n.id)) {
                var mm = n.id.match(/^madri-page-(\\d+)$/);
                if (mm) pageIdx = parseInt(mm[1], 10);
                break;
              }
              n = n.parentElement;
            }
            var needSwitch = false;
            if (pageIdx >= 0) {
              needSwitch = madriGetCurrentPageIdx() !== pageIdx;
              if (needSwitch) { madriGoToPage(pageIdx); }
            }
            setTimeout(function() {
              var t = document.getElementById(id);
              if (t && t.scrollIntoView) {
                try { t.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e1) { t.scrollIntoView(true); }
              }
              if (typeof sendHeightToMoodle === 'function') setTimeout(sendHeightToMoodle, 80);
            }, needSwitch ? 60 : 0);
          }
          window.madriScrollToAnchor = madriScrollToAnchor;
        </script>
        ${pagesHtml}
      `;
    }

    const gConfig = gConfigSource || {};
    const effectiveBg = gConfig.bgAdvanced || gConfig.computedBgRGBA || gConfig.wrapperBg || 'transparent';
    const isGradient = effectiveBg.includes('gradient');
    const bgRepeat = gConfig.bgConfig === 'repeat' ? 'repeat' : gConfig.bgConfig === 'repeat-x' ? 'repeat-x' : gConfig.bgConfig === 'repeat-y' ? 'repeat-y' : 'no-repeat';
    const bgSize = (gConfig.bgConfig === 'cover' || gConfig.bgConfig === 'contain' || gConfig.bgConfig === 'stretch') ? (gConfig.bgConfig === 'stretch' ? '100% 100%' : gConfig.bgConfig) : 'auto';
    const bgImg = (gConfig.bgImage || '').trim();
    const isRepeat = ['repeat', 'repeat-x', 'repeat-y'].includes(gConfig.bgConfig);
    const bgImageCss = bgImg ? 'none' : (isGradient ? effectiveBg : 'none');

    const stripQuotesForStyleAttr = (s) => String(s ?? '').replace(/"/g, "'");
    const outerBgColor = bgImg ? 'transparent' : (isGradient ? 'transparent' : effectiveBg);
    const outerBgImageCss = bgImg ? 'none' : bgImageCss;
    const outerBgRepeat = bgImg ? 'no-repeat' : bgRepeat;
    const outerBgSize = bgImg ? 'auto' : bgSize;
    let inlineStyleHTML = `position:relative; min-height: 0; background-color: ${stripQuotesForStyleAttr(outerBgColor)}; background-image: ${stripQuotesForStyleAttr(outerBgImageCss)}; background-repeat: ${outerBgRepeat}; background-size: ${outerBgSize}; background-position: top center;`;
    if (gConfig.fontFamily) {
        inlineStyleHTML += ` font-family: ${gConfig.fontFamily.replace(/"/g, "'")};`;
    }
    inlineStyleHTML += ` --bg-card: ${stripQuotesForStyleAttr(gConfig.cardBg || '#ffffff')}; --text-main: ${stripQuotesForStyleAttr(gConfig.textColor || 'inherit')}; --course-primary: ${stripQuotesForStyleAttr(gConfig.colorPrimary || '#3b82f6')}; --course-secondary: ${stripQuotesForStyleAttr(gConfig.colorSecondary || '#8b5cf6')}; color: var(--text-main);`;

    const fontImportHtml = gConfig.googleFontUrl ? `\n<link href="${gConfig.googleFontUrl}" rel="stylesheet">` : '';

    const containerClass = gConfig.layoutWidth === 'full-width' ? 'container-fluid' : 'container';
    const wrapperId = rowsOverride ? 'preview-template' : (courseInfo?.id || 'course');
    const courseLabel = rowsOverride ? 'Preview' : (courseInfo?.name || 'Curso');

    const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    let bgImgLayer = '';
    if (bgImg) {
        const op = (gConfig.bgImageOpacity != null ? gConfig.bgImageOpacity : 100) / 100;
        if (isRepeat) {
            const tilePct = gConfig.bgImageSize != null ? gConfig.bgImageSize : 100;
            bgImgLayer = `<div style="position:absolute;inset:0;z-index:0;overflow:hidden;pointer-events:none;opacity:${op};background-image:url('${bgImg.replace(/'/g, "%27")}');background-repeat:${gConfig.bgConfig || 'repeat'};background-size:${tilePct}% ${tilePct}%"></div>`;
        } else {
            const rot = gConfig.bgImageRotate != null ? gConfig.bgImageRotate : 0;
            const pct = gConfig.bgImageSize != null ? gConfig.bgImageSize : 100;
            const corner = gConfig.bgImagePositionCorner || 'left-top';
            const baseX = corner === 'center' ? 50 : (corner.includes('right') ? 100 : 0);
            const baseY = corner === 'center' ? 50 : (corner.includes('bottom') ? 100 : 0);
            const posX = (baseX + (gConfig.bgImagePositionX != null ? gConfig.bgImagePositionX : 0)) + '%';
            const posY = (baseY + (gConfig.bgImagePositionY != null ? gConfig.bgImagePositionY : 0)) + '%';
            const objFit = gConfig.bgConfig === 'contain' ? 'contain' : gConfig.bgConfig === 'stretch' ? 'fill' : 'cover';
            bgImgLayer = `<div style="position:absolute;inset:0;z-index:0;overflow:hidden;pointer-events:none;opacity:${op}"><div style="position:absolute;left:50%;top:50%;width:${pct}%;height:${pct}%;transform:translate(-50%,-50%) rotate(${rot}deg);transform-origin:center center"><img src="${esc(bgImg)}" alt="" style="width:100%;height:100%;object-fit:${objFit};object-position:${posX} ${posY}" /></div></div>`;
        }
    }
    const bgOverlayLayer = bgImg
        ? `<div style="position:absolute;inset:0;z-index:1;pointer-events:none;background:${stripQuotesForStyleAttr(effectiveBg)}"></div>`
        : '';
    // ── Advanced CSS style block ─────────────────────────────────────────────
    // Collect advancedCss from all widgets and rows and emit a single <style>
    // tag so that full CSS (pseudo-classes like :hover, animations, etc.) works
    // in the exported HTML. Properties also remain in the inline style attribute
    // for backward compatibility.
    const advStyleRules = [];
    const collectWidgetStyles = (w) => {
        if (!w) return;
        appendElementAdvancedCss(advStyleRules, w.content && w.content.advancedCss, w.content && w.content.customClass, `.madri-widget-${w.id}`);
    };
    const collectRowStyles = (r) => {
        if (!r) return;
        appendElementAdvancedCss(advStyleRules, r.config && r.config.advancedCss, r.config && r.config.customClass, `.madri-row-${r.id}`);
        (r.columns || []).forEach((c) => {
            appendElementAdvancedCss(advStyleRules, c.config && c.config.advancedCss, c.config && c.config.customClass, `.madri-col-${c.id}`);
            (c.widgets || []).forEach(collectWidgetStyles);
            (c.nestedRows || []).forEach(collectRowStyles);
        });
    };
    headerToUse.forEach(collectWidgetStyles);
    rowsToUse.forEach(collectRowStyles);
    pushGlobalMobileTypographyRules(typographyStyleRules, gConfigSource);
    const advStyleBlock = (advStyleRules.length > 0 || layoutStyleRules.length > 0 || typographyStyleRules.length > 0)
        ? `<style>\n${[...layoutStyleRules, ...typographyStyleRules, ...advStyleRules].join('\n').replace(/<\/style>/gi, '\\3C \\2F style \\3E')}\n</style>`
        : '';
    // ─────────────────────────────────────────────────────────────────────────

    const hasCheckpoints = checkpointList.length > 0;
    const totalCheckpoints = checkpointList.length || 1;
    const accent = (progressBarConfig && progressBarConfig.accentColor) ? progressBarConfig.accentColor : '#3b82f6';
    const showPercent = progressBarConfig ? (progressBarConfig.showPercent !== false) : true;
    const barStyle = progressBarConfig?.barStyle || 'simple';
    const widthPct = progressBarConfig?.widthPct ?? 100;
    const barAlign = progressBarConfig?.barAlign || 'center';
    const bg = progressBarConfig?.progressBarBg || '#f8fafc';
    const track = progressBarConfig?.trackColor || '#e2e8f0';
    const textClr = progressBarConfig?.progressBarTextColor || '#64748b';
    const segSuffix = progressBarConfig?.segmentedCompletionText || 'para completar';
    const segClr = progressBarConfig?.segmentedCompletionColor || textClr;
    const showSegText = progressBarConfig ? (progressBarConfig.showSegmentedCompletion !== false) : true;
    const barHeight = barStyle === 'segmented' ? 10 : barStyle === 'thick' ? 16 : barStyle === 'pill' ? 14 : 6;
    const barRadius = barStyle === 'segmented' ? 5 : barStyle === 'pill' ? 999 : barStyle === 'thick' ? 10 : 3;
    const alignStyle = barAlign === 'left' ? 'flex-start' : barAlign === 'right' ? 'flex-end' : 'center';
    const showText = barStyle === 'segmented' ? showSegText : (showPercent && barStyle !== 'minimal' && barStyle !== 'stepper');
    const progressTextContent = barStyle === 'segmented' ? ('0% ' + segSuffix) : ('0 / ' + totalCheckpoints);
    const segmentMarkers = barStyle === 'segmented' ? [1, 2, 3, 4].map(function(i) {
        const segBg = i <= 2 ? 'rgba(255,255,255,0.9)' : accent;
        return '<div style="position:absolute;left:' + (i * 20) + '%;top:50%;transform:translateY(-50%);width:7px;height:7px;border-radius:5px;background:' + segBg + ';border:1px solid rgba(0,0,0,0.1)"></div>';
    }).join('') : '';
    const stepperHtml = barStyle === 'stepper' ? ('<div style="position:absolute;top:14px;left:0;right:0;height:2px;background:' + track + ';z-index:0"></div><div style="position:absolute;top:14px;left:0;width:33%;height:2px;background:' + accent + ';z-index:0"></div>' + [1,2,3,4].map(function(i) {
        var filled = i <= 2;
        return '<div style="display:flex;flex-direction:column;align-items:center;gap:4px;z-index:1"><div style="width:24px;height:24px;border-radius:50%;background:' + (filled ? accent : track) + ';display:flex;align-items:center;justify-content:center">' + (filled ? '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg>' : '') + '</div><span style="font-size:10px;color:' + (filled ? accent : textClr) + '">' + i + '</span></div>';
    }).join('')) : '';
    const barInner = barStyle === 'stepper'
      ? '<div style="position:relative;width:100%;display:flex;align-items:center;justify-content:space-between">' + stepperHtml + '</div>'
      : barStyle === 'segmented'
      ? `<div style="flex:1;min-width:0;height:${barHeight}px;background:${track};border-radius:${barRadius}px;overflow:hidden;position:relative"><div id="madri-progress-fill" style="width:0%;height:100%;background:${accent};border-radius:${barRadius}px;transition:width 0.3s"></div>${segmentMarkers}</div>`
      : `<div style="flex:1;min-width:0;height:${barHeight}px;background:${track};border-radius:${barRadius}px;overflow:hidden"><div id="madri-progress-fill" style="width:0%;height:100%;background:${accent};border-radius:${barRadius}px;transition:width 0.3s"></div></div>`;
    const progressTextColor = barStyle === 'segmented' ? segClr : textClr;
    const progressBarFontSize = progressBarConfig?.progressBarFontSize ? (progressBarConfig.progressBarFontSize + 'px') : '0.85em';
    const progressBarLineHeight = progressBarConfig?.progressBarLineHeight || 1.5;
    const progressRowInner = progressBarConfig ? `<div style="display:flex;align-items:center;justify-content:${alignStyle};gap:12px;width:100%"><div style="width:${widthPct}%;max-width:100%;display:flex;align-items:center;gap:12px">${barInner}${showText ? `<span id="madri-progress-text" style="font-size:${progressBarFontSize};line-height:${progressBarLineHeight};font-weight:600;color:${progressTextColor};min-width:70px">${progressTextContent}</span>` : ''}</div></div>` : '';

    const headerMenuC = headerMenuWidget && headerMenuWidget.content ? headerMenuWidget.content : null;
    const menuInnerExport = headerMenuWidget && headerMenuC ? buildExportedHeaderMenuInnerHtml(headerMenuC, headerMenuWidget.id) : '';
    const rel = String((headerMenuC && headerMenuC.relativeToProgress) || 'above').trim();
    const isHeaderMenuSideBySide = !!(progressBarConfig && menuInnerExport && (rel === 'left' || rel === 'right'));
    const menuBlockForSticky = menuInnerExport && headerMenuWidget
        ? (() => {
            const po = headerMenuC.paddingObj;
            const padInline = po ? `padding:${po.top || 0}px ${po.right || 0}px ${po.bottom || 0}px ${po.left || 0}px;` : '';
            const mbg = headerMenuC.menuBg && headerMenuC.menuBg !== 'transparent' ? `background:${headerMenuC.menuBg};` : '';
            const mo = headerMenuC.marginObj;
            const marginInner = mo ? `margin:${mo.top || 0}px ${mo.right || 0}px ${mo.bottom || 0}px ${mo.left || 0}px;` : '';
            const widthPart = isHeaderMenuSideBySide
                ? 'width:auto;max-width:min(100%,560px);flex:0 1 auto;align-self:center;'
                : 'width:100%;max-width:100%;';
            return `<div class="madri-header-menu-sticky-wrap madri-widget-${headerMenuWidget.id}" style="${marginInner}${padInline}${mbg}border-radius:8px;${widthPart}box-sizing:border-box;">${menuInnerExport}</div>`;
        })()
        : '';
    let innerStickyCombined = '';
    if (progressBarConfig && menuBlockForSticky) {
        if (rel === 'below') innerStickyCombined = progressRowInner + menuBlockForSticky;
        else if (rel === 'left') innerStickyCombined = `<div style="display:flex;align-items:center;gap:16px;width:100%;flex-wrap:wrap;box-sizing:border-box">${menuBlockForSticky}<div style="flex:1;min-width:0;min-height:0">${progressRowInner}</div></div>`;
        else if (rel === 'right') innerStickyCombined = `<div style="display:flex;align-items:center;gap:16px;width:100%;flex-wrap:wrap;box-sizing:border-box"><div style="flex:1;min-width:0;min-height:0">${progressRowInner}</div>${menuBlockForSticky}</div>`;
        else innerStickyCombined = menuBlockForSticky + progressRowInner;
    } else if (progressBarConfig) innerStickyCombined = progressRowInner;
    else if (menuBlockForSticky) innerStickyCombined = menuBlockForSticky;

    let stickyBg = '#f8fafc';
    if (progressBarConfig) stickyBg = bg;
    else if (headerMenuC && headerMenuC.menuBg && headerMenuC.menuBg !== 'transparent') stickyBg = headerMenuC.menuBg;

    const progressBarHtml = (progressBarConfig || menuBlockForSticky) ? `<div id="madri-progress-bar" style="position:sticky;top:0;z-index:100;background:${stickyBg};padding:10px 16px;margin:0 -15px 16px -15px;border-bottom:1px solid #e2e8f0">${innerStickyCombined}</div>` : '';
    const ckById = Object.fromEntries(checkpointList.map((c) => [c.id, c]));
    const exportCkIds = layoutCheckpointIds.length
        ? layoutCheckpointIds
        : checkpointList.map((c) => c.id);
    const exportCkPages = exportCkIds.map((id) => ckById[id]?.page || 0);
    const exportCkVideoPercents = exportCkIds.map((id) => ckById[id]?.videoPercent ?? 75);
    const exportCkModes = exportCkIds.map((id) => ckById[id]?.mode || 'time');
    const exportCkPandaIframeIds = exportCkIds.map((id) => ckById[id]?.pandaIframeId || null);
    // Checkpoints não podem depender exclusivamente do widget progress-bar (ver App.jsx).
    const checkpointScript = (progressBarConfig || hasCheckpoints) ? `
<script>
(function(){
  var wId=${jsStringLiteralForId(wrapperId)};
  function geppettoCkResolveMoodleCtx() {
    var cmid = '';
    var sk = '';
    var www = '';
    if (window.geppettoMoodleCmid) cmid = String(window.geppettoMoodleCmid);
    if (window.geppettoMoodleSesskey) sk = String(window.geppettoMoodleSesskey);
    if (window.geppettoMoodleWwwroot) www = String(window.geppettoMoodleWwwroot).replace(/\\/$/, '');
    if (!cmid || !sk) {
      var el = document.querySelector('[data-gep-cmid]');
      if (el) {
        cmid = String(el.getAttribute('data-gep-cmid') || '').trim();
        sk = String(el.getAttribute('data-gep-sesskey') || '').trim();
      }
    }
    if (cmid.indexOf('[') !== -1) cmid = '';
    if (sk.indexOf('[') !== -1) sk = '';
    return { cmid: cmid, sesskey: sk, wwwroot: www };
  }
  function moodleSaveCheckpointDirect(idx, attempt) {
    attempt = attempt || 0;
    var ctx = geppettoCkResolveMoodleCtx();
    if (!ctx.cmid || !ctx.sesskey) {
      return;
    }
    var dc = Array.from(new Set(doneArr)).length;
    var base = ctx.wwwroot || (window.geppettoMoodleWwwroot || '').replace(/\\/$/, '') || window.location.origin;
    var ajaxUrl = base + '/lib/ajax/service.php?sesskey=' + encodeURIComponent(ctx.sesskey);
    fetch(ajaxUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify([{
        index: 0,
        methodname: 'mod_gepettoresource_save_checkpoint',
        args: {
          cmid: parseInt(ctx.cmid, 10),
          checkpointindex: idx,
          total: total,
          donecount: dc
        }
      }])
    })
    .then(function(resp) { return resp.json(); })
    .then(function(data) {
      if (!data || !data[0]) {
        if (attempt < 2) {
          setTimeout(function() { moodleSaveCheckpointDirect(idx, attempt + 1); }, 700);
        }
        return;
      }
      if (data[0].error) {
        if (attempt < 2) {
          setTimeout(function() { moodleSaveCheckpointDirect(idx, attempt + 1); }, 700);
        }
        return;
      }
      try {
        document.dispatchEvent(new CustomEvent('geppettoCheckpointSaved', { detail: { index: idx } }));
      } catch(e) {}
      try {
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({ type: 'geppettoQuizStateRefresh' }, '*');
        }
      } catch(e) {}
    })
    .catch(function(err) {
      if (attempt < 2) {
        setTimeout(function() { moodleSaveCheckpointDirect(idx, attempt + 1); }, 700);
      }
    });
  }
  var total=Math.max(1,${checkpointList.length});
  var isSegmented=${barStyle === 'segmented' ? 'true' : 'false'};
  var segSuffix=${JSON.stringify(segSuffix || 'para completar')};
  var ckIds=${JSON.stringify(exportCkIds)};
  var ckPages=${JSON.stringify(exportCkPages)};
  var ckVideoPercents=${JSON.stringify(exportCkVideoPercents)};
  var ckModes=${JSON.stringify(exportCkModes)};
  var ckPandaIframeIds=${JSON.stringify(exportCkPandaIframeIds)};
  // Progresso: no Moodle só banco (boot PHP + AJAX). Fora do Moodle, só memória da sessão (sem localStorage).
  var doneArr = [];

  function getDoneArr() {
    return Array.from(new Set(doneArr));
  }

  function mergeDoneIndices(incoming) {
    if (!Array.isArray(incoming)) return;
    var changed = false;
    incoming.forEach(function(i) {
      var n = parseInt(i, 10);
      if (!isNaN(n) && !doneArr.includes(n)) {
        doneArr.push(n);
        changed = true;
      }
    });
    update();
    if (changed) {
      try {
        document.dispatchEvent(new CustomEvent('geppettoCheckpointDone', { detail: { source: 'sync' } }));
      } catch(e) {}
      try {
        document.dispatchEvent(new CustomEvent('geppettoCheckpointSaved', { detail: { source: 'sync' } }));
      } catch(e) {}
    }
  }

  function bootstrapProgressFromBoot() {
    if (Array.isArray(window.geppettoSavedCheckpointIndices)) {
      mergeDoneIndices(window.geppettoSavedCheckpointIndices);
    }
  }

  function hydrateProgressFromServer() {
    var ctx = geppettoCkResolveMoodleCtx();
    if (!ctx.cmid || !ctx.sesskey) return Promise.resolve();
    var base = ctx.wwwroot || (window.geppettoMoodleWwwroot || '').replace(/\\/$/, '') || window.location.origin;
    var ajaxUrl = base + '/lib/ajax/service.php?sesskey=' + encodeURIComponent(ctx.sesskey);
    return fetch(ajaxUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify([{
        index: 0,
        methodname: 'mod_gepettoresource_get_progress',
        args: { cmid: parseInt(ctx.cmid, 10) }
      }])
    })
    .then(function(resp) { return resp.json(); })
    .then(function(data) {
      if (!data || !data[0] || data[0].error) return;
      var payload = data[0].data;
      if (payload && Array.isArray(payload.done)) {
        mergeDoneIndices(payload.done);
      }
    })
    .catch(function() {});
  }

  function requestParentProgress() {
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type: 'madriRequestProgress', courseId: wId }, '*');
      }
    } catch(e) {}
  }

  function update() {
    var c = Array.from(new Set(doneArr)).length;
    var f = document.getElementById("madri-progress-fill");
    var t = document.getElementById("madri-progress-text");
    if(f) f.style.width=(c/total*100)+"%";
    if(t) t.innerText=isSegmented ? (Math.round(c/total*100)+"% "+segSuffix) : (c+" / "+total);
    // Mark icons visually
    ckIds.forEach(function(pid, i) {
      if(doneArr.includes(i)) {
         var el = document.getElementById("madri-ck-"+pid);
         if(el) {
            var n = el.querySelector(".madri-ck-notdone");
            var d = el.querySelector(".madri-ck-done");
            if(n) n.style.display="none";
            if(d) d.style.display="inline-block";
         }
      }
    });
  }

  function markDone(idx) {
    if(!doneArr.includes(idx)) {
       doneArr.push(idx);
       update();
       try {
         document.dispatchEvent(new CustomEvent('geppettoCheckpointDone', { detail: { index: idx, widgetId: ckIds[idx] } }));
       } catch(e) {}
       // Notify parent (Moodle plugin) via postMessage so it can persist to the DB.
       try {
         if (window.parent && window.parent !== window) {
           window.parent.postMessage({
             type: 'madriCheckpointDone',
             checkpointIndex: idx,
             courseId: wId,
             doneCount: Array.from(new Set(doneArr)).length,
             total: total
           }, '*');
         }
       } catch(e) {}
       moodleSaveCheckpointDirect(idx);
    }
  }

  // ── Moodle progress sync ─────────────────────────────────────────────────
  // Fonte da verdade no Moodle: banco (boot PHP + AJAX + postMessage do pai). Sem localStorage.
  window.addEventListener('message', function(event) {
    if (!event.data || event.data.type !== 'madriProgressData') return;
    mergeDoneIndices(event.data.doneArr);
  });
  bootstrapProgressFromBoot();
  hydrateProgressFromServer().then(function() {
    requestParentProgress();
  });
  var _progressSyncRetries = 0;
  var _progressSyncTimer = setInterval(function() {
    if (_progressSyncRetries >= 12) {
      clearInterval(_progressSyncTimer);
      return;
    }
    _progressSyncRetries++;
    requestParentProgress();
    if (_progressSyncRetries === 3 || _progressSyncRetries === 8) {
      hydrateProgressFromServer();
    }
  }, 500);

  window.__geppettoCkProgress = {
    ckIds: ckIds,
    getDone: getDoneArr,
    mergeDone: mergeDoneIndices
  };
  // ─────────────────────────────────────────────────────────────────────────

  function loadScript(src, id, cb) {
    if (document.getElementById(id)) { if(cb) cb(); return; }
    var s = document.createElement('script');
    s.id = id; s.src = src;
    if (cb) s.onload = cb;
    document.head.appendChild(s);
  }

  // Detect which video APIs are needed.
  var reqYtb = false, reqVimeo = false, reqPanda = false;
  ckIds.forEach(function(pid, idx) {
    if (ckModes[idx] === 'video') {
      var pandaFrameId = ckPandaIframeIds[idx];
      var vEl = document.getElementById(pandaFrameId || ('madri-video-' + pid));
      if (vEl) {
        if (vEl.src.includes('youtube') || vEl.src.includes('youtu.be')) reqYtb = true;
        if (vEl.src.includes('vimeo')) reqVimeo = true;
        if (pandaFrameId || vEl.src.includes('player-vz-') || vEl.src.includes('b-vz-') || vEl.src.includes('pandavideo.com')) reqPanda = true;
      }
    }
  });
  if (reqYtb)   loadScript('https://www.youtube.com/iframe_api',          'ytb-api');
  if (reqVimeo) loadScript('https://player.vimeo.com/api/player.js',      'vimeo-api');

  function initVideos() {
    var pandaBootFns = [];
    ckIds.forEach(function(pid, idx) {
      if (ckModes[idx] !== "video") return;
      var pandaFrameId = ckPandaIframeIds[idx];
      var vEl = document.getElementById(pandaFrameId || ('madri-video-' + pid));
      if (!vEl) return;
      var p            = ckVideoPercents[idx] / 100;
      var isYoutubeUrl = vEl.src.includes('youtube') || vEl.src.includes('youtu.be');
      var isVimeoUrl   = vEl.src.includes('vimeo');
      var isPandaUrl   = vEl.src.includes('player-vz-') || vEl.src.includes('b-vz-') || vEl.src.includes('pandavideo.com');

      if (isYoutubeUrl && typeof window !== 'undefined' && 'YT' in window && window['YT'].Player) {
        var checkInt = null;
        var player = new window['YT'].Player(vEl.id, {
          events: {
            'onReady': function() {
              // Player is ready; state tracking will begin on first PLAYING event.
            },
            'onStateChange': function(e) {
              if (e.data === window['YT'].PlayerState.PLAYING) {
                // Clear any previous polling interval before starting a new one
                // to avoid multiple concurrent intervals when play/pause/play.
                if (checkInt) { clearInterval(checkInt); checkInt = null; }
                checkInt = setInterval(function() {
                  if (player && player.getCurrentTime) {
                    var cur = player.getCurrentTime();
                    var tot = player.getDuration();
                    if (tot > 0 && (cur / tot) >= p) {
                      markDone(idx);
                      clearInterval(checkInt);
                      checkInt = null;
                    }
                  }
                }, 1000);
              } else {
                // Paused, ended, buffering – stop polling.
                if (checkInt) { clearInterval(checkInt); checkInt = null; }
              }
            }
          }
        });
      } else if (isVimeoUrl && window.Vimeo) {
        var vimeoPlayer = new window.Vimeo.Player(vEl.id);
        vimeoPlayer.on('timeupdate', function(data) {
          if (data.percent >= p) {
            markDone(idx);
            vimeoPlayer.off('timeupdate');
          }
        });
      } else if (pandaFrameId) {
        (function(threshold, markIdx, frameId) {
          pandaBootFns.push(function() {
            try {
              var pl = new window.PandaPlayer(frameId, {
                onReady: function() {
                  pl.onEvent(function(ev) {
                    if (ev.message !== 'panda_timeupdate' || ev.isMutedIndicator) return;
                    var D = pl.getDuration();
                    if (D > 0 && ev.currentTime / D >= threshold) markDone(markIdx);
                  });
                }
              });
            } catch (ex) {}
          });
        })(p, idx, pandaFrameId);
      } else if (isPandaUrl) {
        var pandaDoneFb = false;
        window.addEventListener('message', function(event) {
          if (pandaDoneFb || p < 0.999) return;
          try {
            var raw = event.data;
            var data = typeof raw === 'string' ? JSON.parse(raw) : raw;
            if (data && data.message === 'panda_ended') {
              pandaDoneFb = true;
              markDone(idx);
            }
          } catch (err) {}
        });
      } else {
        // Generic fallback: mark after 5 s if API not available.
        setTimeout(function() { markDone(idx); }, 5000);
      }
    });
    if (pandaBootFns.length) {
      window.pandascripttag = window.pandascripttag || [];
      var pandaApiUrl = 'https://player.pandavideo.com.br/api.v2.js';
      if (!document.getElementById('panda-api-v2')) {
        pandaBootFns.forEach(function(fn) { window.pandascripttag.push(fn); });
        var injectPanda = function() {
          var se = document.createElement('script');
          se.id = 'panda-api-v2';
          se.async = true;
          se.src = pandaApiUrl;
          document.head.appendChild(se);
        };
        if (window.__madriLoadUmd) {
          window.__madriLoadUmd(pandaApiUrl, injectPanda);
        } else {
          var od = window.define;
          try { window.define = undefined; } catch (eP0) {}
          injectPanda();
          try { if (od) window.define = od; } catch (eP1) {}
        }
      } else if (window.PandaPlayer) {
        pandaBootFns.forEach(function(fn) { try { fn(); } catch (e) {} });
      } else {
        pandaBootFns.forEach(function(fn) { window.pandascripttag.push(fn); });
      }
    }
  }

  window.addEventListener("load", function() {
    update();

    // Wait for YouTube / Vimeo APIs to be ready before initialising video tracking.
    var attempts = 0;
    var waitForApi = setInterval(function() {
      var ytGlobal    = typeof window !== 'undefined' && 'YT' in window ? window['YT'] : null;
      var isYtbReady  = !reqYtb  || (ytGlobal && ytGlobal.Player);
      var vimeoReady  = !reqVimeo || window.Vimeo;
      attempts++;
      if ((isYtbReady && vimeoReady) || attempts > 50) {
        clearInterval(waitForApi);
        initVideos();
      }
    }, 200);

    // Time-based (non-video) checkpoints.
    ckIds.forEach(function(pid, idx) {
      if (ckModes[idx] === "video" || ckModes[idx] === "payment") return;
      var el = document.getElementById("madri-ck-" + pid);
      if (!el) return;
      var ms   = parseInt(el.getAttribute("data-duration") || "5000");
      var page = parseInt(el.getAttribute("data-page") || "0");
      var checkInt = setInterval(function() {
        var curP = 0;
        for (var b = 0; b < total; b++) {
          var pe = document.getElementById("madri-page-" + b);
          if (pe && pe.style.display !== "none") curP = b;
        }
        if (curP === page) {
          setTimeout(function() { markDone(idx); }, ms);
          clearInterval(checkInt);
        }
      }, 1000);
    });
${GEPPETTO_ASAAS_PAYMENT_CHECKPOINT_SNIPPET}
${GEPPETTO_MERCADOPAGO_PAYMENT_CHECKPOINT_SNIPPET}
  });
})();
</script>` : '';

    // Menu flutuante (sanduíche) - quando há paginação e está habilitado
    const fm = gConfig.floatingMenu || {};
    const fmItems = (fm.items || []).filter(it => (it.url || '').trim());
    const showFloatingMenu = fm.enabled && fmItems.length > 0;
    const floatingMenuHtml = showFloatingMenu ? (() => {
        const getIconHtml = (iconName) => {
            const ic = iconName || 'Home';
            const svg = renderFaMdIconSvg(ic, '#475569', 18);
            if (svg) return svg;
            return getFloatingMenuIconSvg(ic, '#475569', 18);
        };
        const cdnLinks = '';
        const itemsHtml = fmItems.map(it => {
            const url = (it.url || '').trim();
            const label = esc(it.label || '');
            const iconHtml = getIconHtml(it.icon || 'Home');
            const target = (it.linkType || 'moodle') === 'external' ? '_blank' : '_top';
            return `<a href="${url.replace(/"/g, '&quot;')}" target="${target}" class="madri-fm-item" style="display:flex;align-items:center;gap:10px;padding:10px 14px;color:#334155;text-decoration:none;font-size:0.9rem;border-radius:6px;transition:background 0.2s;white-space:nowrap;">${iconHtml}<span>${label}</span></a>`;
        }).join('');
        return `
<style>.madri-fm-item:hover{background:#f1f5f9 !important;}#madri-floating-menu{transition:top 0.12s ease-out;}</style>
<div id="madri-floating-menu" style="position:fixed;right:16px;top:50%;transform:translateY(-50%);z-index:9999;font-family:inherit;">
  <button id="madri-fm-toggle" onclick="toggleMadriFloatingMenu()" aria-label="Menu" style="width:48px;height:48px;border-radius:50%;background:#fff;border:1px solid #e2e8f0;box-shadow:0 2px 8px rgba(0,0,0,0.1);cursor:pointer;display:flex;align-items:center;justify-content:center;transition:0.2s;">
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#475569" stroke-width="2"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/></svg>
  </button>
  <div id="madri-fm-panel" style="display:none;position:absolute;right:0;top:100%;margin-top:8px;background:#fff;border:1px solid #e2e8f0;border-radius:8px;box-shadow:0 4px 12px rgba(0,0,0,0.15);padding:8px;min-width:200px;">
    ${itemsHtml}
  </div>
</div>
<script>
function toggleMadriFloatingMenu(){var p=document.getElementById('madri-fm-panel');p.style.display=p.style.display==='none'?'block':'none';}
document.addEventListener('click',function(e){var m=document.getElementById('madri-floating-menu');if(m&&!m.contains(e.target)){var p=document.getElementById('madri-fm-panel');if(p)p.style.display='none';}});
(function(){
  var m=document.getElementById('madri-floating-menu');if(!m)return;
  if(window.self===window.top)return;
  function updatePos(){
    try{
      var frame=window.frameElement;var p=window.parent;
      if(!frame||!p)return;
      var r=frame.getBoundingClientRect();
      var vh=p.innerHeight||p.document.documentElement.clientHeight;
      var targetY=vh/2;
      var topPx=targetY-r.top;
      var maxTop=r.height-24;
      topPx=Math.max(12,Math.min(maxTop-12,topPx));
      m.style.top=topPx+'px';
      m.style.transform='translateY(-50%)';
    }catch(e){}
  }
  try{
    var p=window.parent;
    p.addEventListener('scroll',updatePos,{passive:true});
    p.addEventListener('resize',updatePos);
  }catch(e){}
  updatePos();
  setTimeout(updatePos,50);
  setInterval(updatePos,50);
})();
</script>`;
    })() : '';

    const commentsScript = hasComments && !siteExport ? GEPPETTO_COMMENTS_EXPORT_SCRIPT : '';
    const moodleLabelsScript = hasMoodleActivitySlots
        ? `<script>window.geppettoMoodleActivityNoUrl=${jsStringLiteral(exportStrings.moodleNoActivityUrl ?? 'Configure a URL relativa da atividade nas propriedades do bloco no Geppetto.')};window.geppettoMoodleActivityPopupBlocked=${jsStringLiteral(exportStrings.moodleActivityPopupBlocked ?? 'Não foi possível abrir em nova aba. Permita pop-ups para este site.')}\n</script>`
        : '';
    const moodleActivityScript = hasMoodleActivitySlots ? (moodleLabelsScript + GEPPETTO_MOODLE_ACTIVITY_EXPORT_SCRIPT) : '';
    const quizManifestScript = quizList.length > 0
        ? `<script>var quizManifest=${JSON.stringify(quizList)};</script>`
        : '';
    const microquizScript = quizList.length > 0 ? GEPPETTO_MICROQUIZ_EXPORT_SCRIPT : '';
    const geppettoExportMarkerHtml = siteExport ? '' : buildGeppettoExportMarkerBundle(exportStrings);
    const siteRuntimeHtml = siteExport ? buildSiteRuntimeBundle({
        wrapperId,
        hasComments,
        strings: {
            commentName: exportStrings.siteCommentName,
            commentPlaceholder: exportStrings.siteCommentPlaceholder,
            commentSend: exportStrings.siteCommentSend,
            commentEmpty: exportStrings.siteCommentEmpty,
            linkNotice: exportStrings.siteLinkNotice,
        },
    }) : '';
    const headerBlock = `${progressBarHtml}\n${globalHeaderHtml}`;
    const headerHtml = (gConfig.layoutWidth === 'full-width' && headerBlock.trim())
        ? `<div class="madri-row-inner">${headerBlock}</div>`
        : headerBlock;
    const contentWrapper = `<div style="position:relative;z-index:2"><div class="${containerClass}">${headerHtml}\n${finalHtml}\n  </div></div>${checkpointScript}${quizManifestScript}${floatingMenuHtml}${commentsScript}${moodleActivityScript}${microquizScript}`;

    const iframeResizerHtml = siteExport ? '' : `
<script>
(function() {
  if (window.self === window.top) return;
  var lastSent = 0, MAX_H = 50000, TOLERANCE = 5, debounceTimer;
  var courseWrapId = 'madri-course-wrapper-' + ${jsStringLiteralForId(wrapperId)};
  ${GEPPETTO_IFRAME_PARENT_FIT_JS}

  function measureContentHeight() {
    var w = document.getElementById(courseWrapId);
    if (!w) return 1;
    var rectH = 0;
    try { rectH = Math.ceil(w.getBoundingClientRect().height || 0); } catch (e) {}
    var h = Math.max(w.scrollHeight || 0, w.offsetHeight || 0, rectH);
    return Math.min(MAX_H, Math.max(h, 1));
  }

  function sendHeightToMoodle(opts) {
    if (!window.parent || !window.parent.postMessage) return;
    var force = opts === true || (opts && opts.force);
    var scrollToTop = !!window.__madriRequestScrollTop;
    if (scrollToTop) { window.__madriRequestScrollTop = false; }
    var h = measureContentHeight();
    var width = Math.max(document.documentElement.scrollWidth, document.body.scrollWidth, window.innerWidth || 0);
    if (!force && !scrollToTop && Math.abs(h - lastSent) <= TOLERANCE) return;
    lastSent = h;
    var payload = { type: 'mLabIframeResize', height: h, width: width, id: courseWrapId, scrollToTop: scrollToTop };
    window.parent.postMessage(payload, '*');
    window.parent.postMessage({ type: 'moodle-iframe-resize', height: h, width: width, scrollToTop: scrollToTop }, '*');
    window.parent.postMessage({ subject: 'lti.frameResize', height: h, width: width }, '*');
    // mod_resource: o Moodle não escuta postMessage — ajusta o iframe pai (mesma origem).
    // mod_gepettoresource / Visualizar do builder: geppettoApplyParentIframeFit não faz nada.
    try { geppettoApplyParentIframeFit(h, width); } catch (eFit) {}
  }

  window.sendHeightToMoodle = sendHeightToMoodle;

  function debouncedSend() { clearTimeout(debounceTimer); debounceTimer = setTimeout(sendHeightToMoodle, 200); }

  sendHeightToMoodle();
  window.addEventListener('load', sendHeightToMoodle);
  window.addEventListener('resize', debouncedSend);
  if (typeof MutationObserver !== 'undefined') {
    var mo = new MutationObserver(debouncedSend);
    window.addEventListener('DOMContentLoaded', function() {
      sendHeightToMoodle();
      var w = document.getElementById(courseWrapId);
      if (w) mo.observe(w, { attributes: true, childList: true, subtree: true });
    });
  }
  if (typeof ResizeObserver !== 'undefined') {
    window.addEventListener('DOMContentLoaded', function() {
      var w = document.getElementById(courseWrapId);
      var ro = new ResizeObserver(debouncedSend);
      if (w) ro.observe(w);
    });
  }
  setInterval(sendHeightToMoodle, 2000);
})();
</script>`;

    const runtimeAttr = siteExport ? ' data-geppetto-runtime="site"' : '';
    const layoutFullClass = gConfig.layoutWidth === 'full-width' ? ' madri-layout-full' : '';
    let exportedHtml = `<!-- Geppetto Export -->${fontImportHtml}\n${advStyleBlock}\n<div id="madri-course-wrapper-${wrapperId}" class="madri-moodle-layout${layoutFullClass}" lang="${htmlLang}" data-geppetto-export="1"${runtimeAttr} style="${inlineStyleHTML}">\n${geppettoExportMarkerHtml}${bgImgLayer}${bgOverlayLayer}${contentWrapper}\n${siteRuntimeHtml}</div>\n${iframeResizerHtml}`;
    if (siteExport) exportedHtml = finalizeSiteExportHtml(exportedHtml);
    if (gConfig.layoutWidth === 'full-width') {
      exportedHtml = `<style data-geppetto-layout-width="full">.madri-moodle-layout.madri-layout-full{max-width:none !important;width:100% !important;margin:0 !important;padding:0 !important;overflow-x:clip !important;overflow-y:visible !important}.madri-moodle-layout.madri-layout-full .madri-row{margin-left:0 !important;margin-right:0 !important;width:100%}.madri-moodle-layout.madri-layout-full .madri-row-inner{max-width:1200px;width:100%;margin-left:auto;margin-right:auto;padding-left:24px;padding-right:24px;box-sizing:border-box;position:relative;z-index:0}.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner{display:flex !important;flex-wrap:wrap !important;align-items:flex-start}@media (min-width:768px){.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner{flex-wrap:nowrap !important}}.madri-moodle-layout.madri-layout-full .madri-row-bleed .madri-row-inner{max-width:none;padding-left:0;padding-right:0}@media (min-width:768px){.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner > .madri-col-md-6{flex:0 1 50% !important;max-width:50% !important}.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner > .madri-col-md-4{flex:0 1 33.333333% !important;max-width:33.333333% !important}.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner > .madri-col-md-8{flex:0 1 66.666667% !important;max-width:66.666667% !important}.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner > .madri-col-md-3{flex:0 1 25% !important;max-width:25% !important}}@media (max-width:767px){.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner > .madri-col-12,.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner > .madri-col-md-6,.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner > .madri-col-md-4,.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner > .madri-col-md-8,.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner > .madri-col-md-3,.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner > .madri-col-dynamic,.madri-moodle-layout.madri-layout-full .madri-row > .madri-row-inner > [class*="madri-col-"]{flex:0 0 100% !important;max-width:100% !important;width:100% !important}}</style>\n` + exportedHtml;
    }
    return exportedHtml;
};


  // Reordenação do Drag and Drop
  const onDragEnd = (result) => {
    if (isCanvasReadOnly) return;
    const { source, destination, type } = result;
    if (!destination) return;
    pushHistory();

    if (type === 'row') {
      const items = Array.from(rows);
      const [reorderedItem] = items.splice(source.index, 1);
      items.splice(destination.index, 0, reorderedItem);
      setRows(items);
      return;
    }

    if (type === 'widget') {
      const data = JSON.parse(JSON.stringify(rows));
      const srcCol = findColByIdInTree(data, source.droppableId);
      const dstCol = findColByIdInTree(data, destination.droppableId);
      if (!srcCol || !dstCol) return;
      const srcW = srcCol.widgets;
      const [movedWidget] = srcW.splice(source.index, 1);
      const dstW = srcCol === dstCol ? srcW : dstCol.widgets;
      dstW.splice(destination.index, 0, movedWidget);
      setRows(data);
      return;
    }
  };

  // Página pública da API do Relatório Geppetto — sem login.
  if (isLearningReportDocsPath()) {
    return <LearningReportDocs />;
  }

  // ============================================
  // VERIFICANDO SESSÃO (token no storage; aguardando /auth/me)
  // ============================================
  if (appState === 'checking') {
    const LoaderIcon = LucideIcons.Loader2 || (() => null);
    const checkingInvite = !!(getInviteTokenFromUrl() || (() => { try { return sessionStorage.getItem('gepetto_invite_token'); } catch { return null; } })());
    const checkingMessage = checkingInvite
      ? t('login:inviteAccepting', { defaultValue: 'Aceitando convite e abrindo o projeto...' })
      : (t('common:loading') || 'Carregando...');
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="flex flex-col items-center gap-4">
          {LoaderIcon && <LoaderIcon size={40} className="animate-spin text-blue-600 dark:text-blue-400" />}
          <p className="text-slate-600 dark:text-slate-400">{checkingMessage}</p>
        </div>
      </div>
    );
  }

  // ============================================
  // TELA DE PLANOS (usuário sem tenant obrigado a escolher)
  // ============================================
  if (appState === 'plans') {
    return (
      <>
        <ProfilePage
          user={currentUser}
          authToken={authToken}
          darkMode={darkMode}
          onToggleDarkMode={() => setDarkMode((v) => !v)}
          onBack={null}
          requiresPlanSelection
          onPlanSelected={(newUser, newToken) => {
            skipNextAuthCheckRef.current = true; // não rodar o efeito de /auth/me agora (evita deslogar)
            setCurrentUser(newUser);
            setAuthToken(newToken);
            // Salvar nos dois storages para não perder sessão (ex.: plano free)
            localStorage.setItem('gepetto_user', JSON.stringify(newUser));
            localStorage.setItem('gepetto_token', newToken);
            sessionStorage.setItem('gepetto_user', JSON.stringify(newUser));
            sessionStorage.setItem('gepetto_token', newToken);
            setAppState('builder');
          }}
          onLogout={logoutAndClear}
        />
        {currentUser && currentUser.role !== 'super_admin' && authToken && (
          <FeedbackFloatingWidget authToken={authToken} pageContext="plans" />
        )}
      </>
    );
  }

  // ============================================
  // TELA DE PERFIL (com Planos dentro)
  // ============================================
  if (appState === 'profile') {
    return (
      <>
        <ProfilePage
          user={currentUser}
          authToken={authToken}
          darkMode={darkMode}
          onToggleDarkMode={() => setDarkMode((v) => !v)}
          entryTab={profileEntryTab}
          initialPaymentGatewayProvider={profilePaymentGatewayProvider}
          onBack={() => {
            setProfileEntryTab(null);
            setProfilePaymentGatewayProvider(null);
            setAppState('builder');
          }}
          onOpenProjectById={(layoutId) => {
            if (!layoutId || !authToken) return;
            fetch(`${API_BASE}/layouts/${layoutId}`, { headers: { Authorization: `Bearer ${authToken}` } })
              .then(r => r.json())
              .then(data => {
                const project = data.project;
                if (project) openProject({ id: project.id, course_name: project.course_name });
              })
              .catch(() => {});
          }}
          onUserUpdate={(u) => setCurrentUser(u)}
          onLogout={logoutAndClear}
        />
        {currentUser && currentUser.role !== 'super_admin' && authToken && (
          <FeedbackFloatingWidget authToken={authToken} pageContext="profile" />
        )}
      </>
    );
  }

  // ============================================
  // TELA MASTER ADMIN
  // ============================================
  if (appState === 'master') {
    return (
      <MasterDashboard
        user={currentUser}
        onLogout={logoutAndClear}
        onLoginAsTenant={(user, token) => {
          clearAppSessionId();
          setCurrentUser(user);
          setAuthToken(token);
          sessionStorage.setItem('gepetto_user', JSON.stringify(user));
          sessionStorage.setItem('gepetto_token', token);
          setAppState('builder');
        }}
      />
    );
  }

  // ============================================
  // TELA DE LOGIN
  // ============================================
  if (appState === 'login') {
    const publicFile = (name) => {
      const base = import.meta.env.BASE_URL || '/';
      return base.endsWith('/') ? `${base}${name}` : `${base}/${name}`;
    };
    const loginBgUrl = publicFile('back_log.png');
    const loginLogoUrl = publicFile('Geppetto-white-v.png');
    const loginInputClass =
      'w-full rounded-xl border border-white/15 bg-slate-950/40 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-400 shadow-inner shadow-black/20 backdrop-blur-md focus:border-blue-400/40 focus:outline-none focus:ring-2 focus:ring-blue-400/50';

    return (
      <>
        <div className="login-page dark relative flex min-h-[100dvh] w-full flex-col items-center justify-center bg-slate-900">
          <div
            className="pointer-events-none absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${loginBgUrl})` }}
            aria-hidden
          />
          <TubesCursor />
          <div className="pointer-events-none absolute inset-0 login-screen-overlay" aria-hidden />
          <div className="absolute top-4 right-4 z-20">
            <LanguageSwitcher />
          </div>
          <div className="relative z-10 mx-4 w-full max-w-md py-10">
            <div className="mb-8 flex flex-col items-center text-center">
              <img
                src={loginLogoUrl}
                alt={t('login:title')}
                className="h-28 w-auto max-w-[min(100%,560px)] object-contain object-center sm:h-32"
                decoding="async"
              />
              <p className="mt-3 max-w-md text-sm leading-snug text-slate-200/95 drop-shadow-[0_1px_3px_rgba(0,0,0,0.75)]">
                {t('login:subtitle')}
              </p>
            </div>

            <div className="login-glass-card rounded-2xl p-8 ring-1 ring-white/20">
              <h2 className="mb-6 text-xl font-semibold text-slate-100">
                {loginMode === 'login' ? t('login:signIn') : t('login:createAccount')}
              </h2>

              {loginError && (
                <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-400/25 bg-red-950/45 p-3 text-sm text-red-200 backdrop-blur-md">
                  <AlertCircle size={16} /> {loginError}
                </div>
              )}
              {loginSuccessMessage && (
                <div className="mb-4 flex items-center gap-2 rounded-lg border border-emerald-400/25 bg-emerald-950/40 p-3 text-sm text-emerald-200 backdrop-blur-md">
                  <CheckCircle size={16} /> {loginSuccessMessage}
                </div>
              )}

              <form onSubmit={loginMode === 'login' ? handleLogin : handleRegister} className="flex flex-col gap-4">
                {loginMode === 'register' && (
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-200">{t('login:fullName')}</label>
                    <input
                      type="text"
                      value={loginName}
                      onChange={(e) => setLoginName(e.target.value)}
                      placeholder={t('login:fullNamePlaceholder')}
                      className={loginInputClass}
                      required
                    />
                  </div>
                )}
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-200">{t('login:email')}</label>
                  <input
                    autoFocus={loginMode !== 'register'}
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder={t('login:emailPlaceholder')}
                    className={loginInputClass}
                    required
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-200">{t('login:password')}</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      placeholder={t('login:passwordPlaceholder')}
                      className={`${loginInputClass} pr-12`}
                      required
                      minLength={4}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-200"
                      aria-label={showPassword ? t('login:hidePassword') : t('login:showPassword')}
                    >
                      {showPassword ? <LucideIcons.EyeOff size={20} /> : <LucideIcons.Eye size={20} />}
                    </button>
                  </div>
                </div>
                {loginMode === 'login' && (
                  <div className="space-y-1">
                    <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="rounded border-slate-500 text-blue-500 focus:ring-blue-400"
                      />
                      {t('login:rememberMe')}
                    </label>
                    <p className="pl-7 text-xs leading-snug text-slate-400">{t('login:rememberMeGoogleHint')}</p>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loginLoading}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 font-semibold text-white shadow-lg shadow-blue-900/30 transition-all hover:bg-blue-700 disabled:opacity-60"
                >
                  {loginLoading ? (
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    <>{loginMode === 'login' ? t('login:enter') : t('login:createAccountBtn')}</>
                  )}
                </button>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/12" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="rounded-md bg-slate-950/50 px-2 text-slate-300 backdrop-blur-md">{t('login:or')}</span>
                  </div>
                </div>

                <a
                  href={`${API_BASE.replace(/\/api$/, '')}/api/auth/google?persist=${loginMode === 'register' ? '1' : rememberMe ? '1' : '0'}`}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/15 bg-slate-950/40 py-3 font-semibold text-slate-100 backdrop-blur-md transition-colors hover:bg-slate-900/55"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden>
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  {t('login:signInWithGoogle')}
                </a>
              </form>

              <div className="mt-6 text-center text-sm text-slate-400">
                {(() => {
                  try {
                    const hasInvite = !!sessionStorage.getItem('gepetto_invite_token');
                    if (hasInvite && loginMode === 'login') return null;
                  } catch { /* ignore */ }
                  return loginMode === 'login' ? (
                    <p>
                      <button
                        type="button"
                        onClick={() => {
                          setLoginMode('register');
                          setLoginError('');
                          setLoginSuccessMessage('');
                        }}
                        className="font-semibold text-sky-300 hover:underline"
                      >
                        {t('login:registerFreeCta')}
                      </button>
                    </p>
                  ) : (
                    <p>
                      {t('login:hasAccount')}{' '}
                      <button
                        type="button"
                        onClick={() => {
                          setLoginMode('login');
                          setLoginError('');
                          setLoginSuccessMessage('');
                        }}
                        className="font-semibold text-sky-300 hover:underline"
                      >
                        {t('login:doLogin')}
                      </button>
                    </p>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Dashboard removido — projetos são acessados via modal no builder

  if (appState === 'cricket') {
    if (!authToken) {
      return (
        <div className="flex min-h-[100dvh] items-center justify-center bg-slate-50 dark:bg-slate-900">
          <p className="text-sm text-slate-600 dark:text-slate-400">{t('cricket:sessionRequired')}</p>
        </div>
      );
    }
    if (!cricketBoardLayoutId) {
      return (
        <div className="flex min-h-[100dvh] items-center justify-center bg-slate-50 dark:bg-slate-900">
          <LucideIcons.Loader2 className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-400" aria-hidden />
        </div>
      );
    }
    return (
      <>
        <CricketBoardPage
          layoutId={cricketBoardLayoutId}
          authToken={authToken}
          freeStandaloneUserId={
            isCricketFreeStandaloneLayout(cricketBoardLayoutId) ? currentUser?.id : undefined
          }
          darkMode={darkMode}
          onToggleDarkMode={() => setDarkMode((v) => !v)}
          onBack={() => {
            setCricketBoardLayoutId(null);
            setAppState('builder');
          }}
          showToast={showToast}
          onFocusLinkedWidget={(widgetId) => {
            if (widgetId == null || String(widgetId).trim() === '') return;
            setPendingCricketFocusWidgetId(String(widgetId));
            setCricketBoardLayoutId(null);
            setAppState('builder');
          }}
        />
        {currentUser && currentUser.role !== 'super_admin' && authToken && (
          <FeedbackFloatingWidget
            authToken={authToken}
            pageContext="cricket"
            layoutId={isCricketFreeStandaloneLayout(cricketBoardLayoutId) ? null : cricketBoardLayoutId}
          />
        )}
      </>
    );
  }

  return (
    <div className="flex bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 font-sans" style={{ height: '100vh', width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      {/* MODAL CONFIG ROW PRINCIPAL */}
      {activeRowSettings && (() => {
        const currentRow = findRowInTree(rows, activeRowSettings);
        const rowCtx = findRowContext(rows, activeRowSettings);
        if (!currentRow || !rowCtx) return null;

        let currentLayout = '1/1';
        if (currentRow.columns[0]?.width === 'dynamic') {
          currentLayout = 'dynamic';
        } else if (currentRow.columns.length === 2) {
          if (currentRow.columns[0].width === '2/3') currentLayout = '2/3';
          else if (currentRow.columns[0].width === '1/3') currentLayout = '33/66';
          else currentLayout = '1/2';
        } else if (currentRow.columns.length === 3) {
          currentLayout = '1/3';
        } else if (currentRow.columns.length === 4) {
          currentLayout = '1/4';
        }

        const adaptedContent = {
          ...currentRow.config,
          wrapperBg: currentRow.config.wrapperBg || currentRow.config.backgroundColor || 'transparent',
          layoutType: currentLayout,
          dynamicColumns: currentRow.config.dynamicColumns ?? currentRow.columns.map(c => c.customWidth ?? 33.33)
        };

        const handleRowUpdate = (config) => {
          const finalConfig = { ...config, backgroundColor: config.computedBgRGBA || config.wrapperBg };
          const { layoutType, ...cleanConfig } = finalConfig;

          let newColumns = [...currentRow.columns];

          if (layoutType && layoutType !== currentLayout) {
            let nextColumns = [];
            if (layoutType === '1/1') {
              nextColumns = [{ id: currentRow.columns[0]?.id || uid(), width: '1/1', widgets: [] }];
            } else if (layoutType === '1/2') {
              nextColumns = [
                { id: currentRow.columns[0]?.id || uid(), width: '1/2', widgets: [] },
                { id: currentRow.columns[1]?.id || uid(), width: '1/2', widgets: [] }
              ];
            } else if (layoutType === '1/3') {
              nextColumns = [
                { id: currentRow.columns[0]?.id || uid(), width: '1/3', widgets: [] },
                { id: currentRow.columns[1]?.id || uid(), width: '1/3', widgets: [] },
                { id: currentRow.columns[2]?.id || uid(), width: '1/3', widgets: [] }
              ];
            } else if (layoutType === '2/3') {
              nextColumns = [
                { id: currentRow.columns[0]?.id || uid(), width: '2/3', widgets: [] },
                { id: currentRow.columns[1]?.id || uid(), width: '1/3', widgets: [] }
              ];
            } else if (layoutType === '33/66') {
              nextColumns = [
                { id: currentRow.columns[0]?.id || uid(), width: '1/3', widgets: [] },
                { id: currentRow.columns[1]?.id || uid(), width: '2/3', widgets: [] }
              ];
            } else if (layoutType === '1/4') {
              nextColumns = [
                { id: currentRow.columns[0]?.id || uid(), width: '1/4', widgets: [] },
                { id: currentRow.columns[1]?.id || uid(), width: '1/4', widgets: [] },
                { id: currentRow.columns[2]?.id || uid(), width: '1/4', widgets: [] },
                { id: currentRow.columns[3]?.id || uid(), width: '1/4', widgets: [] }
              ];
            } else if (layoutType === 'dynamic') {
              const dynCols = config.dynamicColumns || [34, 33, 33];
              nextColumns = dynCols.map((pct, index) => ({
                id: currentRow.columns[index]?.id || uid(),
                width: 'dynamic',
                customWidth: pct,
                widgets: []
              }));
            }

            // Migra os widgets com segurança (preserva config por coluna quando o slot continua existindo)
            currentRow.columns.forEach((oldCol, idx) => {
              if (idx < nextColumns.length) {
                nextColumns[idx].widgets = oldCol.widgets;
                if (oldCol.nestedRows) nextColumns[idx].nestedRows = oldCol.nestedRows;
                if (oldCol.config && typeof oldCol.config === 'object') {
                  nextColumns[idx].config = oldCol.config;
                }
              } else {
                // Se a coluna deixou de existir (ex: 3 cols pra 2 cols), joga na última coluna nova p/ não perder dado
                const lastI = nextColumns.length - 1;
                nextColumns[lastI].widgets.push(...oldCol.widgets);
                if (oldCol.nestedRows?.length) {
                  nextColumns[lastI].nestedRows = [...(nextColumns[lastI].nestedRows || []), ...oldCol.nestedRows];
                }
              }
            });

            newColumns = nextColumns;
          } else if (layoutType === 'dynamic' && currentLayout === 'dynamic' && config.dynamicColumns) {
            // Atualiza colunas dinâmicas: mesmo número = atualiza customWidth; número diferente = reconstrói
            if (config.dynamicColumns.length === currentRow.columns.length) {
              newColumns = currentRow.columns.map((col, idx) => ({
                ...col,
                customWidth: config.dynamicColumns[idx] ?? col.customWidth
              }));
            } else {
              newColumns = config.dynamicColumns.map((pct, index) => ({
                id: currentRow.columns[index]?.id || uid(),
                width: 'dynamic',
                customWidth: pct,
                widgets: index < currentRow.columns.length ? currentRow.columns[index].widgets : [],
                ...(index < currentRow.columns.length && currentRow.columns[index].nestedRows
                  ? { nestedRows: currentRow.columns[index].nestedRows }
                  : {}),
              }));
              if (config.dynamicColumns.length < currentRow.columns.length) {
                currentRow.columns.slice(config.dynamicColumns.length).forEach(c => {
                  const li = newColumns.length - 1;
                  newColumns[li].widgets.push(...c.widgets);
                  if (c.nestedRows?.length) {
                    newColumns[li].nestedRows = [...(newColumns[li].nestedRows || []), ...c.nestedRows];
                  }
                });
              }
            }
          }

          setRows(rList => replaceRowInTree(rList, activeRowSettings, (old) => ({ ...old, config: cleanConfig, columns: newColumns })));
        };

        const currentPosition = rowCtx.index + 1;
        const totalRowPositions = rowCtx.parentList.length;

        return (
          <WidgetConfigModal
            widgetType="row"
            initialContent={adaptedContent}
            onChange={handleRowUpdate}
            onSave={(config) => {
              handleRowUpdate(config);
              releaseWidgetLock(`row:${activeRowSettings}`);
              setActiveEditKey(prev => (prev === `row:${activeRowSettings}` ? null : prev));
              setActiveRowSettings(null);
              schedulePersistLayoutAfterModal();
            }}
            onClose={() => {
              releaseWidgetLock(`row:${activeRowSettings}`);
              setActiveEditKey(prev => (prev === `row:${activeRowSettings}` ? null : prev));
              setActiveRowSettings(null);
            }}
            currentPosition={currentPosition}
            totalCount={totalRowPositions}
            onPositionChange={
              rowCtx.scope === 'nested'
                ? (v) => moveNestedRowToPosition(activeRowSettings, v)
                : (v) => moveRowToPosition(activeRowSettings, v)
            }
            editBreakpoint={editBreakpoint}
            pageLayoutWidth={courseInfo.globalConfig?.layoutWidth || 'boxed'}
            rowIsNested={rowCtx.scope === 'nested'}
          />
        );
      })()}

      {/* MODAL CONFIG COLUNA (slot do grid) */}
      {activeColumnSettings && (() => {
        const owner = findColumnOwner(rows, activeColumnSettings.colId);
        const rowForCol = owner?.row;
        const colTarget = owner?.col;
        if (!rowForCol || !colTarget) return null;

        const colLockKey = `column:${rowForCol.id}:${colTarget.id}`;
        const colLockInfo = presenceLocks[colLockKey];
        const colLockedByOther = !!(colLockInfo?.user_id && String(colLockInfo.user_id) !== String(currentUserId || ''));
        if (colLockedByOther) {
          return null;
        }

        const patchColumnConfig = (cfg) => {
          const norm = normalizeColumnConfig(cfg);
          setRows(rList => patchColumnInTree(rList, colTarget.id, (c) => ({ ...c, config: norm })));
        };

        return (
          <WidgetConfigModal
            widgetType="column"
            initialContent={{ ...DEFAULT_COLUMN_CONFIG, ...(colTarget.config || {}) }}
            onChange={patchColumnConfig}
            onSave={(cfg) => {
              patchColumnConfig(cfg);
              releaseWidgetLock(colLockKey);
              setActiveEditKey(prev => (prev === colLockKey ? null : prev));
              setActiveColumnSettings(null);
              schedulePersistLayoutAfterModal();
            }}
            onClose={() => {
              releaseWidgetLock(colLockKey);
              setActiveEditKey(prev => (prev === colLockKey ? null : prev));
              setActiveColumnSettings(null);
            }}
            editBreakpoint={editBreakpoint}
          />
        );
      })()}

      {/* MODAL GLOBAL CONFIG */}
      {showGlobalSettings && (
        <WidgetConfigModal
          widgetType="global"
          initialContent={courseInfo.globalConfig || {}}
          onChange={(config) => setCourseInfo(prev => ({ ...prev, globalConfig: config }))}
          onSave={(config) => {
            setCourseInfo(prev => ({ ...prev, globalConfig: config }));
            setShowGlobalSettings(false);
            schedulePersistLayoutAfterModal();
          }}
          onClose={() => setShowGlobalSettings(false)}
        />
      )}

      {/* SIDEBAR NEUTRAL TAILWIND (Nova) */}
      <aside className="w-20 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 flex flex-col items-center py-3 pb-4 gap-2 z-20 shadow-sm flex-shrink-0" style={{ height: '100vh' }}>
        <button
          type="button"
          onClick={() => setAppState('profile')}
          className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-md cursor-pointer overflow-hidden bg-blue-600 hover:opacity-90 transition-opacity shrink-0"
          title={t('profile:profile', { defaultValue: 'Perfil' })}
        >
          {currentUser?.avatar_url ? (
            <img
              src={currentUser.avatar_url}
              alt={t('profile:profile', { defaultValue: 'Perfil' })}
              className="w-full h-full object-cover"
            />
          ) : (
            <span>{(currentUser?.name || 'U').trim().charAt(0).toUpperCase()}</span>
          )}
        </button>

        <nav className="flex flex-col gap-1 w-full px-2 items-center flex-1 min-h-0 scrollbar-hide pt-1" style={{ overflowY: 'auto', paddingBottom: '12px' }}>
          <SidebarItem
            icon={<FolderOpen size={20} />}
            label={t('builder:projects')}
            isActive={activePanel === "projetos"}
            onClick={() => togglePanel("projetos")}
          />
          <SidebarItem
            icon={<LayoutTemplate size={20} />}
            label={t('builder:models')}
            isActive={activePanel === "modelos"}
            onClick={() => togglePanel("modelos")}
          />
          <SidebarItem
            icon={<Shapes size={20} />}
            label={t('builder:elements')}
            isActive={activePanel === "elementos"}
            onClick={() => togglePanel("elementos")}
          />
          <SidebarItem
            icon={<LayoutGrid size={20} />}
            label={t('builder:templates')}
            isActive={activePanel === "templates"}
            onClick={() => togglePanel("templates")}
          />
          <SidebarItem
            icon={<UploadCloud size={20} />}
            label={t('builder:uploads')}
            isActive={activePanel === "uploads"}
            onClick={() => togglePanel("uploads")}
            tier={moduleTierMap.upload || 'basic'}
            locked={!planUploadsModuleEnabled}
            lockedTitle={t('builder:moduleUploadLockedHint')}
          />
          {!!currentUser?.aiLibraryCurator && (
            <SidebarItem
              icon={<Sparkles size={20} />}
              label={t('builder:aiLibrary', { defaultValue: 'Biblioteca IA' })}
              isActive={activePanel === 'ai-library'}
              onClick={() => togglePanel('ai-library')}
            />
          )}
          {/* Painel «Mídias» (MidiasPanel) desativado — reativar: importar MidiasPanel e descomentar o bloco abaixo
          {currentUser?.plan?.features?.modules?.midias !== false && (
            <SidebarItem
              icon={<ImageIcon size={22} />}
              label={t('builder:media')}
              isActive={activePanel === "midias"}
              onClick={() => togglePanel("midias")}
              tier={moduleTierMap.midias || 'basic'}
            />
          )}
          */}
          <SidebarItem
            icon={<Puzzle size={20} />}
            label={t('builder:plugins')}
            isActive={activePanel === "plugin"}
            onClick={() => togglePanel("plugin")}
            tier={moduleTierMap.plugin || 'basic'}
            locked={!planPluginModuleEnabled}
            lockedTitle={t('builder:modulePluginLockedHint')}
            showUpdateDot={pluginUpdateDot && planPluginModuleEnabled}
          />

          <SidebarMoreMenu
            showPlans={currentUser?.tenant_access !== 'collab'}
            onOpenPlans={() => setAppState('profile')}
            onOpenMyApi={() => togglePanel('myapi')}
            myApiLocked={!planMyApiMenuEnabled}
            myApiActive={activePanel === 'myapi'}
            diskStorage={sidebarDiskStorage}
            onOpenDiskInfo={() => setAppState('profile')}
            onOpenReportApi={() => { window.location.assign(LEARNING_REPORT_DOCS_PATH); }}
          />

          <div className="flex-1 min-h-2" />
          <div className="w-full flex justify-center px-1 mb-1">
            <LanguageSwitcher compact />
          </div>
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="flex flex-col items-center justify-center w-full rounded-xl py-2 gap-1 transition-all duration-200 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50"
            title={darkMode ? t('common:light') : t('common:dark')}
          >
            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            <span className="text-[10px] font-medium tracking-wide">{darkMode ? t('common:light') : t('common:dark')}</span>
          </button>
        </nav>
      </aside>

      {/* MAIN CONTENT AREA */}
      <main className="flex-1 flex flex-col" style={{ minWidth: 0, height: '100vh', overflow: 'hidden' }}>
        {/* TOPBAR */}
        <header className="h-14 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between px-6 max-md:px-3 z-10 text-slate-800 dark:text-slate-200 flex-shrink-0 min-w-0">
          <div className="flex items-center gap-2 md:gap-3 text-sm min-w-0 flex-1 truncate mr-2">
            {courseInfo.id ? (
              <>
                <button
                  onClick={goBackToDashboard}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-all shrink-0"
                  title={t('builder:closeProject')}
                >
                  <ArrowLeft size={16} />
                </button>
                <span className="text-slate-400 max-md:hidden">|</span>
                <span className="text-slate-500 max-md:hidden">{isCanvasReadOnly ? t('builder:viewingProject') : t('builder:editing')}</span>
                <span className="font-semibold truncate">{courseInfo.name}</span>
                {presenceOnline.length > 0 && (
                  <span className="ml-3 text-xs px-2 py-1 rounded bg-sky-100 text-sky-700 max-md:hidden">
                    {t('builder:online', { defaultValue: 'Online' })}: {presenceOnline.map(u => u.user_name || u.user_id).join(', ')}
                  </span>
                )}
                {isSaving && <span className="ml-4 text-xs bg-slate-100 text-slate-500 px-2 py-1 rounded animate-pulse max-md:hidden">{t('builder:saving')}</span>}
              </>
            ) : (
              <>
                <button
                  onClick={() => setAppState('profile')}
                  className="flex items-center gap-2 text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  <span>{t('builder:hello')}, <strong className="text-slate-700 dark:text-slate-200">{currentUser?.name}</strong></span>
                  {currentUser?.plan && (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                      {t('builder:yourPlanIs', { defaultValue: 'Seu plano é' })} {currentUser.plan.name}
                    </span>
                  )}
                </button>
              </>
            )}
          </div>

          <BuilderTopbarActions
            appState={appState}
            isCanvasReadOnly={isCanvasReadOnly}
            historyState={historyState}
            hasStageContent={rows.length > 0}
            currentUser={currentUser}
            canSave={canSaveLayout()}
            onUndo={undo}
            onRedo={redo}
            onNewProject={() => {
              const hasCurrent = rows.length > 0 || headerWidgets.length > 0 || courseInfo.id;
              if (hasCurrent) {
                setShowNewProjectConfirmModal(true);
              } else {
                goBackToDashboard();
                setShowNewProjectModal(true);
              }
            }}
            onResetStage={() => setShowResetStageConfirmModal(true)}
            onGlobalSettings={() => { pushHistory(); setShowGlobalSettings(true); }}
            onClearCache={clearBuilderBrowserCacheAndReload}
            onSave={saveLayoutToDB}
            onPublishSite={handlePublishSite}
            isPublishingSite={isPublishingSite}
            onPublishMoodle={handlePublishMoodle}
            isPublishingMoodle={isPublishingMoodle}
            onPreview={() => { setPreviewViewport('desktop'); setShowPreviewModal(true); }}
            onExport={() => { setExportModalKind('moodle'); setShowExportModal(true); }}
            onExportSite={() => { setExportModalKind('site'); setShowExportModal(true); }}
            onGallery={() => setShowGallerySubmitModal(true)}
            onSaveTemplate={() => setShowSaveAsTemplateModal(true)}
            onLogout={handleLogout}
            editBreakpoint={editBreakpoint}
            onEditBreakpointChange={setEditBreakpoint}
            highlightPlanRestricted={highlightPlanRestricted}
            onToggleHighlightPlanRestricted={toggleHighlightPlanRestricted}
            showHighlightPlanRestricted={!!(currentUser && currentUser.role !== 'super_admin' && (planAllowedElementTypes || planAllowedGridIds))}
            canHighlightPlanRestricted={stageHasPlanRestrictedWidgets}
          />
        </header>

        <div className="flex-1 flex flex-row relative" style={{ overflow: 'hidden', minHeight: 0 }}>
          {/* Slide-out Panels (Tailwind) */}
          {activePanel && (
            <>
            <button
              type="button"
              className="md:hidden absolute inset-0 z-20 bg-slate-900/40 border-0 p-0 cursor-pointer"
              aria-label={t('builder:close')}
              onClick={() => { setActivePanel(null); setActiveInsertIndex(null); setGridInsertTarget(null); }}
            />
            <div
              className={`${activePanel === 'ai-library' ? 'w-[26rem] max-md:max-w-[min(26rem,calc(100vw-5rem))]' : 'w-80 max-md:max-w-[min(20rem,calc(100vw-5rem))]'} max-md:absolute max-md:left-0 max-md:top-0 max-md:bottom-0 max-md:z-30 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 shadow-[4px_0_24px_rgba(0,0,0,0.05)] dark:shadow-[4px_0_24px_rgba(0,0,0,0.2)] flex flex-col z-10 flex-shrink-0 min-h-0 animate-in slide-in-from-left-4 duration-200 overflow-x-hidden transition-[box-shadow,ring-color,width] duration-300 ${guidedGridPickerActive ? 'ring-2 ring-inset ring-blue-500/55 dark:ring-blue-400/50 shadow-[4px_0_32px_rgba(59,130,246,0.22)]' : ''}`}
            >
              <div className="flex items-center justify-between p-6 border-b border-slate-100 dark:border-slate-700">
                <h2 className="font-semibold text-slate-800 dark:text-slate-200 capitalize">
                  {activePanel === 'ai-library'
                    ? t('builder:aiLibrary', { defaultValue: 'Biblioteca IA' })
                    : t('builder:' + ({ projetos: 'projects', modelos: 'models', elementos: 'elements', templates: 'templates', uploads: 'uploads', midias: 'media', plugin: 'plugins', myapi: 'myApiPanelTitle', indique: 'indiqueTitle' }[activePanel] || activePanel))}
                </h2>
                <button
                  onClick={() => { setActivePanel(null); setActiveInsertIndex(null); setGridInsertTarget(null); }}
                  className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors"
                >
                  <XCircle size={18} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6 content-scrollbar">
                {activePanel === "modelos" && (
                  <ModelosPanel
                    featureFlags={featureFlags}
                    onSelect={(gridId, options) => {
                      const finish = () => {
                        setActiveInsertIndex(null);
                        setGridInsertTarget(null);
                        setActivePanel(null);
                      };
                      const pct = options?.customPercentages;
                      if (gridInsertTarget?.type === 'nested') {
                        const { hostColId, index } = gridInsertTarget;
                        if (gridId === '1/1') addNestedRowToColumn(hostColId, index, '1/1');
                        else if (gridId === '1/2') addNestedRowToColumn(hostColId, index, '1/2');
                        else if (gridId === '1/3') addNestedRowToColumn(hostColId, index, '1/3');
                        else if (gridId === '1/4') addNestedRowToColumn(hostColId, index, '1/4');
                        else if (gridId === '2/3') addNestedRowToColumn(hostColId, index, '2/3');
                        else if (gridId === '33/66') addNestedRowToColumn(hostColId, index, '33/66');
                        else if (gridId === 'dynamic' && pct) addNestedRowToColumn(hostColId, index, 'dynamic', pct);
                        finish();
                        return;
                      }
                      const idx = activeInsertIndex ?? rows.length;
                      if (gridId === '1/1') addRowAt(idx, '1/1');
                      else if (gridId === '1/2') addRowAt(idx, '1/2');
                      else if (gridId === '1/3') addRowAt(idx, '1/3');
                      else if (gridId === '1/4') addRowAt(idx, '1/4');
                      else if (gridId === '2/3') addRowAt(idx, '2/3');
                      else if (gridId === '33/66') addRowAt(idx, '33/66');
                      else if (gridId === 'dynamic' && pct) addRowAt(idx, 'dynamic', pct);
                      finish();
                    }}
                  />
                )}
                {activePanel === "elementos" && (
                  <ElementosPanel
                    onSelect={addWidgetToColumn}
                    hasSelectedSlot={!!selectedSlotId}
                    headerElements={HEADER_ELEMENTS}
                    featureFlags={featureFlags}
                  />
                )}
                {activePanel === "templates" && (
                  <TemplatesPanel
                    suppressTemplateGalleryEscape={!!previewTemplateItem}
                    onApply={(rows, globalConfig, headerW = []) => {
                      const hasContent = rows.length > 0 || headerWidgets.length > 0;
                      if (hasContent && !confirm(t('builder:templateReplaceConfirm'))) return;
                      pushHistory();
                      const cloned = cloneTemplateRows(rows);
                      setRows(cloned);
                      setHeaderWidgets(headerW || []);
                      if (globalConfig) setCourseInfo(prev => ({ ...prev, globalConfig }));
                      setActivePanel(null);
                      setPreviewTemplateItem(null);
                    }}
                    onPreview={(item) => setPreviewTemplateItem(item)}
                    buildTemplateGalleryPreviewSrcDoc={(rows, globalConfig, headerWidgets, opts = {}) => {
                      try {
                        if (!rows?.length) return null;
                        const scale =
                          typeof opts?.scale === 'number' && opts.scale > 0 && opts.scale <= 1 ? opts.scale : 0.14;
                        const wPct = `${(100 / scale).toFixed(4)}%`;
                        const previewHtml = getSimulatedCode(rows, globalConfig || {}, headerWidgets || [], moodleSimulatedExportStrings);
                        const safeCss = exportCss.replace(/<\/style/gi, '<\\/style');
                        return `<!DOCTYPE html><html lang="${i18n?.language === 'en' ? 'en' : 'pt-BR'}"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css"><link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet"><style>html,body{margin:0;padding:0;background:#e2e8f0;overflow:hidden;width:100%;height:100%;}#_gpMini{transform:scale(${scale});transform-origin:top left;width:${wPct};min-height:720px;}</style><style>${safeCss}</style></head><body><div id="_gpMini">${previewHtml}</div></body></html>`;
                      } catch (_) {
                        return null;
                      }
                    }}
                  />
                )}
                {activePanel === "plugin" && (
                  <PluginPanel
                    authToken={authToken}
                    pluginsLocked={!planPluginModuleEnabled}
                    onOpenPlans={() => setAppState('profile')}
                  />
                )}
                {activePanel === "myapi" && (
                  <MyApiPanel
                    authToken={authToken}
                    myApiLocked={!planMyApiMenuEnabled}
                    onOpenPlans={() => setAppState('profile')}
                    onConfigurePaymentGateways={() => setAppState('profile')}
                  />
                )}
                {activePanel === "uploads" && (
                  <UploadsPanel
                    authToken={authToken}
                    uploadsLocked={!planUploadsModuleEnabled}
                    canCurateAiLibrary={!!currentUser?.aiLibraryCurator}
                    onOpenPlans={() => setAppState('profile')}
                    onCopyUrl={() => showToast(t('builder:urlCopied', { defaultValue: 'URL copiada para a área de transferência.' }), 'success', 2000)}
                  />
                 )}
                {activePanel === 'ai-library' && !!currentUser?.aiLibraryCurator && (
                  <AiLibraryPanel
                    authToken={authToken}
                    onInsertSection={handleInsertAiLibrarySection}
                    onOpenGenerateLayout={() => setShowAiLayoutModal(true)}
                  />
                )}
                {activePanel === "projetos" && <ProjetosPanel
                  projectsList={projectsList}
                  onOpenProject={(proj) => { if (proj.type !== 'folder') { openProject(proj); setActivePanel(null); } }}
                  onDeleteProject={deleteProject}
                  onNewProject={() => { setNewProjectParentId(null); setShowNewProjectModal(true); }}
                  onNewFolder={() => { setNewFolderParentId(null); setNewFolderName(''); setShowNewFolderModal(true); }}
                  onRenameProject={(proj) => { setProjectToRename(proj); setRenameProjectName(proj.course_name || ''); setShowRenameModal(true); }}
                  onReuseProject={(proj) => { if (proj.type !== 'folder') { reuseProject(proj); setActivePanel(null); } }}
                  onMoveProject={moveProject}
                />}
                {/* {activePanel === "midias" && (
                  <MidiasPanel
                    onCopyUrl={() => showToast(t('builder:urlCopied', { defaultValue: 'URL copiada para a área de transferência.' }), 'success', 2000)}
                  />
                )} */}
                {activePanel === "indique" && <IndiquePanel />}
              </div>
            </div>
            </>
          )}

          {/* WORKSPACE CANVAS - fundo segue tema; conteúdo do curso (course-wrapper) fica isolado */}
          <div className="builder-workspace-canvas flex-1 overflow-auto p-8 max-md:px-0 max-md:py-2 relative bg-slate-100 dark:bg-slate-900 min-w-0">
            {courseInfo.globalConfig?.googleFontUrl && (
              <link href={courseInfo.globalConfig.googleFontUrl} rel="stylesheet" />
            )}
            {postItFontUrls.map((url) => (
              <link key={url} href={url} rel="stylesheet" />
            ))}
            <style>{`
              ${stageGlobalConfig?.h1Size ? `.course-wrapper h1:not(.madri-gc-section-header__heading), #madri-course-wrapper-${courseInfo.id} h1:not(.madri-gc-section-header__heading) { font-size: ${stageGlobalConfig.h1Size}px !important; }` : ''}
              ${stageGlobalConfig?.h2Size ? `.course-wrapper h2:not(.madri-gc-section-header__heading), #madri-course-wrapper-${courseInfo.id} h2:not(.madri-gc-section-header__heading) { font-size: ${stageGlobalConfig.h2Size}px !important; }` : ''}
              ${stageGlobalConfig?.h3Size ? `.course-wrapper h3:not(.madri-gc-section-header__heading), #madri-course-wrapper-${courseInfo.id} h3:not(.madri-gc-section-header__heading) { font-size: ${stageGlobalConfig.h3Size}px !important; }` : ''}
              ${stageGlobalConfig?.pSize ? `.course-wrapper p:not(.madri-gc-timeline-desc):not(.madri-gc-section-header__sub), #madri-course-wrapper-${courseInfo.id} p:not(.madri-gc-timeline-desc):not(.madri-gc-section-header__sub) { font-size: ${stageGlobalConfig.pSize}px !important; }` : ''}
            `}</style>
            {/* Moodle Canvas Fake Environment */}
            <div
              className={`geppetto-edit-stage-outer w-full flex justify-center${editBreakpoint === EDIT_BREAKPOINT_MOBILE ? ' geppetto-edit-stage-outer--mobile' : ''}`}
            >
            <div ref={courseWrapperRef} className={`course-wrapper${editBreakpoint === EDIT_BREAKPOINT_MOBILE ? ' geppetto-edit-bp-mobile' : ''}`} style={(() => {
              const gc = courseInfo.globalConfig || {};
              const hasBgImg = !!(gc.bgImage || '').trim();
              const effectiveBg = gc.bgAdvanced || gc.computedBgRGBA || gc.wrapperBg || '#ffffff';
              const isGrad = String(effectiveBg).includes('gradient');
              const bgCfg = gc.bgConfig;
              const bgRepeat = bgCfg === 'repeat' ? 'repeat' : bgCfg === 'repeat-x' ? 'repeat-x' : bgCfg === 'repeat-y' ? 'repeat-y' : 'no-repeat';
              const bgSize = (bgCfg === 'cover' || bgCfg === 'contain') ? bgCfg : bgCfg === 'stretch' ? '100% 100%' : 'auto';
              const mobileStageWidth = editBreakpoint === EDIT_BREAKPOINT_MOBILE
                ? `min(100%, ${MOBILE_STAGE_WIDTH_PX}px)`
                : (gc.layoutWidth === 'full-width' ? '100%' : '1100px');
              return {
                position: 'relative',
                minHeight: hasBgImg ? '80vh' : 'fit-content',
                paddingBottom: '10rem',
                width: editBreakpoint === EDIT_BREAKPOINT_MOBILE ? mobileStageWidth : undefined,
                maxWidth: mobileStageWidth,
                fontFamily: gc.fontFamily || 'inherit',
                '--bg-card': gc.cardBg || '#ffffff',
                '--text-main': gc.textColor || '#0f172a',
                '--course-primary': gc.colorPrimary || '#3b82f6',
                '--course-secondary': gc.colorSecondary || '#8b5cf6',
                color: gc.textColor || '#0f172a',
                backgroundColor: hasBgImg ? 'transparent' : (isGrad ? 'transparent' : (gc.computedBgRGBA || gc.wrapperBg || '#ffffff')),
                backgroundImage: hasBgImg ? 'none' : (() => {
                  const bgAdvanced = (gc.bgAdvanced || '').trim();
                  if (bgAdvanced.includes('gradient')) return bgAdvanced;
                  const computedBg = gc.computedBgRGBA || gc.wrapperBg || '';
                  if (computedBg.includes('gradient')) return computedBg;
                  return 'none';
                })(),
                backgroundRepeat: hasBgImg ? 'no-repeat' : bgRepeat,
                backgroundSize: hasBgImg ? 'auto' : bgSize,
                backgroundPosition: 'top center',
              };
            })()}>
              {guidedGridPickerActive && (
                <div
                  className="pointer-events-none absolute left-0 right-0 top-0 z-[20] flex justify-center px-3 pt-3"
                  role="status"
                  aria-live="polite"
                >
                  <div className="flex max-w-[min(100%,28rem)] items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-center text-xs font-medium text-white shadow-lg sm:text-sm animate-in fade-in zoom-in-95 duration-200 dark:bg-blue-500">
                    <PanelLeft className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                    <span>{t('builder:gridPickerStageBanner')}</span>
                  </div>
                </div>
              )}
              {/* Camada de imagem de fundo por URL */}
              {(courseInfo.globalConfig?.bgImage || '').trim() && (() => {
                const bgImg = (courseInfo.globalConfig?.bgImage || '').trim();
                const gc = courseInfo.globalConfig || {};
                const isRepeat = ['repeat', 'repeat-x', 'repeat-y'].includes(gc.bgConfig);
                if (isRepeat) {
                  const op = (gc.bgImageOpacity != null ? gc.bgImageOpacity : 100) / 100;
                  const tilePct = gc.bgImageSize != null ? gc.bgImageSize : 100;
                  return (
                    <div style={{ position: 'absolute', inset: 0, zIndex: 0, width: '100%', minHeight: '100%', overflow: 'hidden', pointerEvents: 'none', opacity: op, backgroundImage: `url("${bgImg.replace(/"/g, '%22')}")`, backgroundRepeat: gc.bgConfig || 'repeat', backgroundSize: `${tilePct}% ${tilePct}%` }} />
                  );
                }
                const op = (gc.bgImageOpacity != null ? gc.bgImageOpacity : 100) / 100;
                const rot = gc.bgImageRotate != null ? gc.bgImageRotate : 0;
                const pct = gc.bgImageSize != null ? gc.bgImageSize : 100;
                const corner = gc.bgImagePositionCorner || 'left-top';
                const baseX = corner === 'center' ? 50 : (corner.includes('right') ? 100 : 0);
                const baseY = corner === 'center' ? 50 : (corner.includes('bottom') ? 100 : 0);
                const posX = (baseX + (gc.bgImagePositionX != null ? gc.bgImagePositionX : 0)) + '%';
                const posY = (baseY + (gc.bgImagePositionY != null ? gc.bgImagePositionY : 0)) + '%';
                const objFit = gc.bgConfig === 'contain' ? 'contain' : gc.bgConfig === 'stretch' ? 'fill' : 'cover';
                return (
                  <div style={{ position: 'absolute', inset: 0, zIndex: 0, width: '100%', minHeight: '100%', overflow: 'hidden', pointerEvents: 'none', opacity: op }}>
                    <div style={{ position: 'absolute', left: '50%', top: '50%', width: `${pct}%`, height: `${pct}%`, transform: `translate(-50%, -50%) rotate(${rot}deg)`, transformOrigin: 'center center' }}>
                      <img src={bgImg} alt="" style={{ width: '100%', height: '100%', objectFit: objFit, objectPosition: `${posX} ${posY}` }} />
                    </div>
                  </div>
                );
              })()}
              {(courseInfo.globalConfig?.bgImage || '').trim() && (() => {
                const gc = courseInfo.globalConfig || {};
                const effectiveBg = gc.bgAdvanced || gc.computedBgRGBA || gc.wrapperBg || '#ffffff';
                return (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: 1,
                      pointerEvents: 'none',
                      background: effectiveBg,
                    }}
                  />
                );
              })()}
              <div style={{ position: 'relative', zIndex: 2, isolation: 'isolate' }}>

                {rows.length === 0 && !courseInfo.id && (
                  <div className="flex flex-col items-center justify-center py-20 px-8">
                    <div className="w-20 h-20 bg-blue-50 rounded-2xl flex items-center justify-center mb-6">
                      <Layout size={36} className="text-blue-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-700 mb-2">{t('builder:welcomeTitle')}</h3>
                    <p className="text-slate-500 text-sm mb-8 text-center max-w-sm">{t('builder:welcomeHint')}</p>
                    <div className="flex gap-4">
                      <button
                        onClick={() => setShowNewProjectModal(true)}
                        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-all shadow-lg shadow-blue-200 text-sm"
                      >
                        <Plus size={18} /> {t('builder:newProject')}
                      </button>
                      {projectsList.length > 0 && (
                        <button
                          onClick={() => setShowProjectsModal(true)}
                          className="flex items-center gap-2 px-6 py-3 bg-white hover:bg-slate-50 text-slate-700 font-medium rounded-xl transition-all border border-slate-200 shadow-sm text-sm"
                        >
                          <FolderOpen size={18} /> {t('builder:viewProjects')}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Zona Header - Posição fixa no topo, apenas elementos com tag header */}
                {(rows.length > 0 || headerWidgets.length > 0) && (
                  <div className="mb-4 rounded-xl border-2 border-dashed border-sky-300 bg-sky-50/50 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-bold uppercase tracking-wider text-sky-600">{t('builder:headerFixed')}</span>
                      {headerWidgets.length === 0 && (
                        <span className="text-xs text-sky-500">{t('builder:headerAddProgress')}</span>
                      )}
                    </div>
                    <div className="flex flex-col gap-3 w-full">
                      {(() => {
                        const barW = headerWidgets.find((x) => x.type === 'progress-bar');
                        const menuW = headerWidgets.find((x) => x.type === 'header-menu');
                        const others = headerWidgets.filter((x) => x.type !== 'progress-bar' && x.type !== 'header-menu');
                        const rel = String(menuW?.content?.relativeToProgress || 'above').trim();
                        const hdrBox = 'relative group/elem rounded-lg border border-sky-200 shadow-sm';

                        const renderHdrWidget = (w, opts = {}) => {
                          const { fullWidth = true, className = '', style = {} } = opts;
                          return (
                            <div
                              key={w.id}
                              className={`${hdrBox} ${fullWidth ? 'w-full min-w-full' : ''} ${className}`.trim()}
                              style={{ margin: toCssStr(w.content.marginObj, '0 0 20px 0'), ...style }}
                            >
                              <WidgetRenderer
                                widget={w}
                                dragHandleProps={{}}
                                globalCardBg={courseInfo.globalConfig?.cardBg}
                                globalTypographyConfig={stageGlobalConfig}
                                onRemove={(e) => { if (e) e.stopPropagation(); removeHeaderWidget(w.id); }}
                                onUpdate={(wId, newContent) => updateHeaderWidget(wId, newContent)}
                                onDuplicate={() => { }}
                                onAfterConfigSave={schedulePersistLayoutAfterModal}
                                onBeforeEdit={pushHistory}
                                onRequestEdit={acquireWidgetLock}
                                onReleaseEdit={releaseWidgetLock}
                                lockInfo={presenceLocks[w.id]}
                                currentUserId={currentUserId}
                                rowLockInfo={null}
                                parentRowId={null}
                                activeEditKey={activeEditKey}
                                setActiveEditKey={setActiveEditKey}
                                isHeaderWidget
                                titleBasicAnchorsForMenu={titleBasicAnchorsList}
                                tocAvailableEntries={tocEntriesList}
                                readOnly={isCanvasReadOnly}
                                showToast={showToast}
                                asaasGatewayConfigured={asaasGatewayConfigured}
                                onOpenAsaasGatewaySettings={openBuilderPaymentGatewaySettings}
                                mercadopagoGatewayConfigured={mercadopagoGatewayConfigured}
                                onOpenMercadoPagoGatewaySettings={() => openBuilderPaymentGatewaySettings('mercadopago')}
                                cricketLinkEnabled={cricketWidgetLinkEnabled}
                                cricketLayoutId={cricketWidgetLinkEnabled ? String(courseInfo.id) : null}
                                cricketAuthToken={cricketWidgetLinkEnabled ? authToken : null}
                                cricketStageBadge={cricketWidgetLinkEnabled ? cricketWidgetBadgeByWidgetId[String(w.id)] ?? null : null}
                                cricketStageLampsHidden={cricketStageLampsHidden}
                                onCricketOverlayInvalidate={loadCricketOverlaySnapshot}
                                cricketFocusHighlight={cricketFocusHighlightWidgetId === String(w.id)}
                                onCricketLampClick={cricketWidgetLinkEnabled ? focusCricketLinkedWidgetOnStage : null}
                                editBreakpoint={editBreakpoint}
                                microquizCheckpointOptions={microquizCheckpointOptionsList}
                                planRestrictedHighlight={!!(highlightPlanRestricted && planAllowedElementTypes && !planAllowedElementTypes.has(w.type))}
                                planAllowedDimOthers={!!(highlightPlanRestricted && planAllowedElementTypes)}
                              />
                            </div>
                          );
                        };

                        const nodes = [];
                        others.forEach((w) => nodes.push(renderHdrWidget(w)));

                        if (barW && menuW) {
                          const barEl = renderHdrWidget(barW, { fullWidth: true, className: 'min-w-0', style: { margin: 0, width: '100%' } });
                          const menuEl = renderHdrWidget(menuW, {
                            fullWidth: !(rel === 'left' || rel === 'right'),
                            className: rel === 'left' || rel === 'right' ? 'shrink-0 max-w-full' : '',
                            style: {
                              margin: 0,
                              ...(rel === 'left' || rel === 'right' ? { width: 'auto', maxWidth: 'min(100%, 560px)' } : {}),
                            },
                          });
                          let pair;
                          if (rel === 'below') pair = <div className="flex flex-col gap-3 w-full">{barEl}{menuEl}</div>;
                          else if (rel === 'left') pair = <div className="flex flex-row flex-wrap items-stretch gap-3 w-full">{menuEl}<div className="flex-1 min-w-0">{barEl}</div></div>;
                          else if (rel === 'right') pair = <div className="flex flex-row flex-wrap items-stretch gap-3 w-full"><div className="flex-1 min-w-0">{barEl}</div>{menuEl}</div>;
                          else pair = <div className="flex flex-col gap-3 w-full">{menuEl}{barEl}</div>;
                          nodes.push(
                            <div key="hdr-progress-menu-group" className="w-full min-w-full">
                              {pair}
                            </div>
                          );
                        } else {
                          if (barW) nodes.push(renderHdrWidget(barW));
                          if (menuW && !barW) nodes.push(renderHdrWidget(menuW));
                        }

                        return nodes;
                      })()}
                    </div>
                  </div>
                )}

                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="builder-canvas" type="row">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef}>
                        {rows.length === 0 && courseInfo.id && (
                          <div
                            key="empty-stage-cta"
                            className="flex flex-col items-center justify-center py-14 px-6 text-center rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-600 bg-slate-50/80 dark:bg-slate-800/40 mb-4"
                          >
                            <div className="w-14 h-14 rounded-2xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center mb-4">
                              <LayoutGrid size={28} className="text-blue-600 dark:text-blue-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-2">{t('builder:stageEmpty')}</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 max-w-md leading-relaxed">{isCanvasReadOnly ? t('builder:readOnlyCollaborator') : t('builder:stageEmptyHint')}</p>
                            {!isCanvasReadOnly && (
                            <button
                              type="button"
                              onClick={() => openGridCatalogForStageInsert({ insertIndex: 0 })}
                              className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 rounded-xl shadow-md shadow-blue-200/50 dark:shadow-none transition-all"
                            >
                              <Plus size={18} /> {t('builder:addGrid', { defaultValue: 'Adicionar Grid' })}
                            </button>
                            )}
                          </div>
                        )}
                        {rows.flatMap((row, index) => [
                          !isCanvasReadOnly && (
                          <div key={`add-before-${index}`} className="flex justify-center py-2">
                            <button
                              type="button"
                              onClick={() => openGridCatalogForStageInsert({ insertIndex: index })}
                              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 border-2 border-dashed border-slate-200 hover:border-blue-400 dark:border-slate-600 dark:hover:border-blue-500 rounded-lg transition-all"
                            >
                              <Plus size={16} /> {t('builder:addGrid', { defaultValue: 'ADD Grid' })}
                            </button>
                          </div>
                          ),
                          <Draggable key={row.id} draggableId={row.id} index={index} isDragDisabled={isCanvasReadOnly}>
                            {(provided, snapshot) => {
                              const rowLockInfo = presenceLocks[`row:${row.id}`];
                              const rowLockedByOther = !!(rowLockInfo?.user_id && String(rowLockInfo.user_id) !== String(currentUserId || ''));
                              const rowLockLabel = rowLockInfo?.user_name || rowLockInfo?.user_id || t('builder:anotherUser', { defaultValue: 'Outro usuário' });
                              const rowCss = elementAdvancedCss(row.config.advancedCss, row.config.customClass);
                              const hasRowBgImg = !!(row.config.bgImage || '').trim();
                              // Conteúdo fica em 0 (acima do fundo, mesma ordem da pré-visualização).
                              // Camada +1/+2 da imagem flutuante precisa continuar na frente.
                              const rowStackZ = 0;
                              const rowBgCanvas = resolveRowBackgroundColor(row.config);
                              const rowConfigForStage = resolveRowConfigForEdit(row.config, editBreakpoint);
                              const rowHasSiteMenu = (row.columns || []).some((col) => (col.widgets || []).some((w) => w && w.type === 'site-menu'));
                              const rowHasSiteFooter = (row.columns || []).some((col) => (col.widgets || []).some((w) => w && w.type === 'site-footer'));
                              const rowHasSiteLogin = (row.columns || []).some((col) => (col.widgets || []).some((w) => w && w.type === 'site-login'));
                              const rowHasSiteMaintenance = (row.columns || []).some((col) => (col.widgets || []).some((w) => w && w.type === 'site-maintenance'));
                              const topGridRestricted = !!(highlightPlanRestricted && isPlanGridRestricted(row));
                              const topGridDim = !!(highlightPlanRestricted && planAllowedGridIds && !isPlanGridRestricted(row));
                              return (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`builder-row madri-row-${row.id} ${rowCss.classNames} ${snapshot.isDragging ? 'is-dragging' : ''}${topGridRestricted ? ' gepetto-plan-restricted' : ''}${isHiddenOnMobile(row.config) ? ` ${HIDE_ON_MOBILE_CLASS}` : ''}`.trim()}
                                data-geppetto-plan-restricted={topGridRestricted ? '1' : undefined}
                                style={{
                                  position: 'relative',
                                  border: rowLockedByOther ? '1px solid #ef4444' : undefined,
                                  padding: toCssStr(rowConfigForStage.paddingObj, rowHasSiteFooter || rowHasSiteLogin || rowHasSiteMaintenance ? '0' : rowHasSiteMenu ? '0 1rem 1rem' : '1rem'),
                                  margin: toCssStr(rowConfigForStage.marginObj, '0'),
                                  ...cssStringToObject(rowCss.inline),
                                  ...provided.draggableProps.style,
                                  ...(rowBgCanvas !== 'transparent' ? { backgroundColor: rowBgCanvas } : {}),
                                  ...(topGridRestricted ? getPlanRestrictedHighlightStyle() : {}),
                                  ...(topGridDim ? { opacity: 0.55, transition: 'opacity 0.25s ease' } : {}),
                                }}
                              >
                                <AdvancedCssStyleTag styleId={`row-${row.id}`} css={rowCss.stylesheet} />
                                {isHiddenOnMobile(row.config) && (
                                  <div className="madri-hide-mobile-note">{t('builder:hiddenOnMobileNote', { defaultValue: 'Oculto no celular' })}</div>
                                )}
                                {hasRowBgImg && <RowBackgroundImageLayer rc={row.config} />}
                                {rowLockedByOther && (
                                  <div style={{ position: 'absolute', right: 8, top: 8, zIndex: 6, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
                                    {rowLockLabel} {t('builder:isEditing', { defaultValue: 'editando' })}
                                  </div>
                                )}
                                {/* Ondas na borda (superior ou inferior) */}
                                {row.config.waveConfig?.enabled && getWaveSvgDataUrl(row.config.waveConfig) && (
                                  <div style={{ position: 'absolute', left: 0, right: 0, width: '100%', height: row.config.waveConfig.height || 60, [row.config.waveConfig.direction === 'top' ? 'top' : 'bottom']: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
                                    <div style={{ width: '100%', height: '100%', backgroundImage: getWaveSvgDataUrl(row.config.waveConfig), backgroundSize: '100% 100%', backgroundRepeat: 'no-repeat', transform: row.config.waveConfig.direction === 'top' ? 'scaleY(-1)' : 'none' }} />
                                  </div>
                                )}
                                {/* Imagem flutuante da seção (limitada ao bounds da row) */}
                                {(row.config.floatingImage?.imageUrl || '').trim() && (() => {
                                  const fi = resolveFloatingImage(row.config.floatingImage, editBreakpoint === EDIT_BREAKPOINT_MOBILE);
                                  const url = (fi.imageUrl || '').trim();
                                  if (!url || fi.hidden) return null;
                                  const op = (fi.opacity != null ? fi.opacity : 100) / 100;
                                  const rot = fi.rotate != null ? fi.rotate : 0;
                                  const sz = (fi.size != null ? fi.size : 100) / 100;
                                  const corner = fi.positionCorner || 'left-top';
                                  const baseX = corner === 'center' ? 50 : (corner.includes('right') ? 100 : 0);
                                  const baseY = corner === 'center' ? 50 : (corner.includes('bottom') ? 100 : 0);
                                  const posX = (baseX + (fi.positionX != null ? fi.positionX : 0)) + '%';
                                  const posY = (baseY + (fi.positionY != null ? fi.positionY : 0)) + '%';
                                  const layer = fi.layer != null ? fi.layer : 0;
                                  return (
                                    <div className="madri-row-float" style={{ position: 'absolute', inset: 0, zIndex: layer, overflow: 'hidden', pointerEvents: 'none' }}>
                                      <img src={url} alt="" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: `${posX} ${posY}`, opacity: op, transform: `rotate(${rot}deg) scale(${sz})`, transformOrigin: `${posX} ${posY}` }} />
                                    </div>
                                  );
                                })()}
                                {!isCanvasReadOnly && (
                                <div className="builder-row-controls" style={{ position: 'relative', zIndex: 10 }}>
                                  <div className="row-control-btn" title={t('builder:btnDrag')} {...provided.dragHandleProps}><GripVertical size={16} /></div>
                                  <div
                                    className="row-control-btn"
                                    title={rowLockedByOther ? `${rowLockLabel} ${t('builder:isEditing', { defaultValue: 'editando' })}` : t('builder:btnConfig')}
                                    onClick={async () => {
                                      if (rowLockedByOther) return;
                                      const rowWidgetLocks = row.columns
                                        .flatMap(c => c.widgets || [])
                                        .map(w => presenceLocks[w.id])
                                        .filter(Boolean);
                                      if (rowWidgetLocks.length > 0) {
                                        const who = rowWidgetLocks[0]?.user_name || rowWidgetLocks[0]?.user_id || t('builder:anotherUser', { defaultValue: 'Outro usuário' });
                                        showToast(`${who} ${t('builder:isEditing', { defaultValue: 'editando' })} ${t('builder:element', { defaultValue: 'um elemento' })}.`, 'warning');
                                        return;
                                      }
                                      if (activeEditKey && activeEditKey !== `row:${row.id}`) {
                                        showToast(t('builder:finishCurrentEditFirst', { defaultValue: 'Finalize a edição atual antes de abrir outra configuração.' }), 'warning');
                                        return;
                                      }
                                      const ok = await acquireWidgetLock(`row:${row.id}`);
                                      if (!ok) return;
                                      pushHistory();
                                      setActiveEditKey(`row:${row.id}`);
                                      setActiveRowSettings(row.id);
                                    }}
                                  >
                                    <Pencil size={16} color={BUILDER_EDIT_PENCIL.row} strokeWidth={2.2} />
                                  </div>
                                  <div className="row-control-btn" title={t('builder:btnDuplicateRow')} onClick={() => duplicateRowById(row.id)}>
                                    <CopyPlus size={16} />
                                  </div>
                                  {planSectionsLibraryEnabled && (
                                    <>
                                      <div
                                        className="row-control-btn"
                                        title={t('builder:sectionPresetSaveBtn', { defaultValue: 'Salvar seção na biblioteca' })}
                                        onClick={() => openSectionLibraryModal('save', row.id)}
                                      >
                                        <Save size={16} />
                                      </div>
                                      <div
                                        className="row-control-btn"
                                        title={t('builder:sectionPresetInsertBtn', { defaultValue: 'Inserir seção da biblioteca' })}
                                        onClick={() => openSectionLibraryModal('insert', row.id)}
                                      >
                                        <Download size={16} />
                                      </div>
                                    </>
                                  )}
                                  <div className="row-control-btn danger" title={t('builder:btnDeleteRow')} onClick={() => removeRow(row.id)}>
                                    <Trash2 size={16} />
                                  </div>
                                </div>
                                )}

                                {(() => {
                                  const masterRowGap = rowColumnsBetweenGapPx(row.columns);
                                  const masterContainerGapStyle = buildRowContainerGapStyle(row.columns);
                                  const pageIsFull = courseInfo.globalConfig?.layoutWidth === 'full-width';
                                  const rowBleeds = pageIsFull && !!row.config?.contentBleed;
                                  const columnsEl = (
                                <div
                                  className={`builder-columns-container ${masterRowGap > 0 ? 'builder-columns-container--col-gap md:flex-nowrap' : ''}`}
                                  style={{
                                    position: 'relative',
                                    zIndex: rowStackZ,
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    width: '100%',
                                    ...(masterContainerGapStyle || {}),
                                  }}
                                >
                                  {row.columns.map((col, colIdx) => renderColumnCell(row, col, colIdx, rowLockedByOther))}
                                </div>
                                  );
                                  if (!pageIsFull) return columnsEl;
                                  return (
                                    <div
                                      className={rowBleeds ? 'madri-row-inner madri-row-inner--bleed' : 'madri-row-inner'}
                                      style={{
                                        maxWidth: rowBleeds ? 'none' : '1200px',
                                        width: '100%',
                                        marginLeft: 'auto',
                                        marginRight: 'auto',
                                        paddingLeft: rowBleeds ? 0 : 24,
                                        paddingRight: rowBleeds ? 0 : 24,
                                        boxSizing: 'border-box',
                                        position: 'relative',
                                        zIndex: rowStackZ,
                                      }}
                                    >
                                      {columnsEl}
                                    </div>
                                  );
                                })()}
                              </div>
                            );
                            }}
                          </Draggable>
                        ])}
                        {!isCanvasReadOnly && rows.length > 0 && (
                          <div key="add-after-last" className="flex justify-center py-2">
                            <button
                              type="button"
                              onClick={() => openGridCatalogForStageInsert({ insertIndex: rows.length })}
                              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 border-2 border-dashed border-slate-200 hover:border-blue-400 dark:border-slate-600 dark:hover:border-blue-500 rounded-lg transition-all"
                            >
                              <Plus size={16} /> {t('builder:addGrid', { defaultValue: 'ADD Grid' })}
                            </button>
                          </div>
                        )}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>

              </div>
            </div>
            </div>
          </div>
        </div>
      </main>

      {/* MODAL EXPORT ZIP */}
      {showExportModal && (
        <div className="export-modal-overlay">
          <div className="export-modal" style={{ maxWidth: '560px' }}>
            <div className="export-modal-header">
              <h3>{exportModalKind === 'site' ? t('builder:siteExportTitle') : t('builder:exportZipModalTitle')}</h3>
              <button
                type="button"
                className="export-modal-close"
                onClick={() => !isExporting && setShowExportModal(false)}
                aria-label={t('builder:close')}
              >
                <XCircle size={24} strokeWidth={2} color="var(--text-muted)" />
              </button>
            </div>

            <div className="export-modal-body">
              {exportModalKind === 'site' ? (
              <section className="export-option">
                <p className="export-modal-intro">{t('builder:siteExportDesc')}</p>
                <button
                  type="button"
                  onClick={handleExportSiteZip}
                  className="btn btn-success"
                  disabled={isExporting}
                >
                  {isExporting && exportKind === 'site' ? `${t('builder:generatingPackage')} ${exportProgress}% ` : <><Download size={16} /> {t('builder:siteExportConfirm')}</>}
                </button>
              </section>
              ) : (
              <section className="export-option">
                <h4 className="export-option-title">{t('builder:exportZipMoodleTitle')}</h4>
                <p className="export-modal-intro">{t('builder:exportZipDesc')}</p>
                {microquizLayoutSummary.total > 0 && (
                  <div style={{ padding: '12px 14px', borderRadius: 8, background: 'var(--bg-muted, #f1f5f9)', fontSize: 14, lineHeight: 1.5, color: '#334155' }}>
                    <div>{t('builder:microquizExportSummary', { total: microquizLayoutSummary.total, graded: microquizLayoutSummary.gradedCount })}</div>
                    {microquizLayoutSummary.gradedCount > 0 && (
                      <div style={{ marginTop: 4, color: microquizLayoutSummary.weightsOk ? '#15803d' : '#b45309' }}>
                        {microquizLayoutSummary.weightsOk
                          ? t('builder:microquizExportWeightsOk')
                          : microquizLayoutSummary.weightSum > 100
                            ? t('builder:microquizExportWeightsOver', { pct: microquizLayoutSummary.weightSum })
                            : t('builder:microquizExportWeightsMissing', { pct: microquizLayoutSummary.weightRemaining })}
                      </div>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleExportZip}
                  className="btn btn-success"
                  disabled={isExporting}
                >
                  {isExporting && exportKind !== 'site' ? `${t('builder:generatingPackage')} ${exportProgress}% ` : <><Download size={16} /> {t('builder:exportZipConfirm')}</>}
                </button>
              </section>
              )}

            </div>

            <div className="export-modal-footer" style={{ flexDirection: 'column', gap: '12px' }}>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setShowExportModal(false)}
                disabled={isExporting}
                style={{ width: '100%' }}
              >
                {t('builder:exportModalClose')}
              </button>

              <div style={{ alignSelf: 'flex-end', fontSize: '12px', marginTop: '4px' }}>
                <button
                  type="button"
                  onClick={() => {
                    const helpBox = document.getElementById('zip-troubleshoot');
                    if (helpBox) helpBox.style.display = helpBox.style.display === 'none' ? 'block' : 'none';
                  }}
                  className="export-modal-link"
                >
                  {t('builder:zipDownloadTrouble')}
                </button>
              </div>
            </div>

            <div id="zip-troubleshoot" className="zip-troubleshoot-panel" style={{ display: 'none' }}>
              <h4 className="zip-troubleshoot-title"><LucideIcons.AlertTriangle size={14} style={{ verticalAlign: 'middle' }} /> {t('builder:zipCorruptTitle')}</h4>
              <p className="zip-troubleshoot-text">
                {t('builder:zipCorruptDesc')}
                <br /><br />
                <b>{t('builder:zipRenameHint')}</b>
              </p>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const zip = new JSZip();
                    const exportHtml = getSimulatedCode(undefined, undefined, undefined, moodleSimulatedExportStrings);
                    const exportCssLocal = await (await fetch('/assets/preview-base.css')).text().catch(() => "/* Base não carregada */");

                    zip.file("index.html", `<!DOCTYPE html>\n<html lang="${i18n?.language === 'en' ? 'en' : 'pt-BR'}">\n<head>\n<meta charset="utf-8">\n<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css">\n<link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet">\n<link rel="stylesheet" href="style.css">\n</head>\n<body>\n${exportHtml}\n</body>\n</html>`);
                    zip.file("style.css", exportCssLocal);
                    zip.folder("assets");

                    const contentBlob = await zip.generateAsync({ type: "blob" });
                    const blobTextual = new Blob([contentBlob], { type: 'text/plain' });
                    const filenameTxt = `${courseInfo.name.replace(/\s+/g, "_")} _RENOMEIE_PARA_ZIP.txt`;

                    const blobUrl = URL.createObjectURL(blobTextual);
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = filenameTxt;
                    link.style.display = 'none';
                    document.body.appendChild(link);
                    link.click();
                    setTimeout(() => { document.body.removeChild(link); URL.revokeObjectURL(blobUrl); }, 1000);
                    alert(t('builder:txtFileGenerated'));
                  } catch (error) {
                    console.error("Erro na geração de TXT:", error);
                    alert(t('builder:txtExportError'));
                  }
                }}
                className="btn btn-outline zip-troubleshoot-btn"
              >
                <Download size={14} style={{ marginRight: '6px' }} /> {t('builder:downloadSafeTxt')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL LIVE PREVIEW MOODLE */}
      {showPreviewModal && (
        <div className="export-modal-overlay" style={{ zIndex: 99999 }}>
          <div className="export-modal" style={{ maxWidth: (courseInfo.globalConfig?.layoutWidth === 'full-width' && previewViewport !== 'mobile') ? '1600px' : '1200px', width: (courseInfo.globalConfig?.layoutWidth === 'full-width' && previewViewport !== 'mobile') ? '96vw' : '95%', height: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="export-modal-header preview-modal-header">
              <h3><LucideIcons.Eye size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} /> {t('builder:previewTitle')}</h3>
              <div className="preview-modal-header-actions">
                <div className="preview-device-toggle" role="group" aria-label={t('builder:previewDeviceToggleLabel')}>
                  <button
                    type="button"
                    className={`preview-device-toggle__btn${previewViewport === 'desktop' ? ' is-active' : ''}`}
                    onClick={() => setPreviewViewport('desktop')}
                    title={t('builder:previewDeviceDesktopTitle')}
                    aria-label={t('builder:previewDeviceDesktopTitle')}
                    aria-pressed={previewViewport === 'desktop'}
                  >
                    <LucideIcons.Monitor size={18} aria-hidden />
                    <span className="preview-device-toggle__label">{t('builder:previewDeviceDesktop')}</span>
                  </button>
                  <button
                    type="button"
                    className={`preview-device-toggle__btn${previewViewport === 'mobile' ? ' is-active' : ''}`}
                    onClick={() => setPreviewViewport('mobile')}
                    title={t('builder:previewDeviceMobileTitle')}
                    aria-label={t('builder:previewDeviceMobileTitle')}
                    aria-pressed={previewViewport === 'mobile'}
                  >
                    <LucideIcons.Smartphone size={18} aria-hidden />
                    <span className="preview-device-toggle__label">{t('builder:previewDeviceMobile')}</span>
                  </button>
                </div>
                <button type="button" className="export-modal-close" onClick={() => setShowPreviewModal(false)} aria-label={t('builder:close')}>
                  <XCircle size={24} strokeWidth={2} color="var(--text-muted)" />
                </button>
              </div>
            </div>

            <div
              className={`export-modal-body preview-modal-body${previewViewport === 'mobile' ? ' preview-modal-body--mobile' : ''}`}
              style={{ flex: 1, padding: 0, backgroundColor: '#f0f2f5' }}
            >
              <div className={`preview-device-frame${previewViewport === 'mobile' ? ' preview-device-frame--mobile' : ''}`}>
                <div className={`preview-device-screen${previewViewport === 'mobile' ? '' : ' preview-device-screen--desktop'}`}>
                  <iframe
                    key={previewViewport}
                    title={t('builder:moodlePreview')}
                    className="preview-device-iframe"
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      overflow: 'auto',
                      backgroundColor: previewViewport === 'mobile' ? '#fff' : '#f0f2f5',
                    }}
                    scrolling="yes"
                    srcDoc={`
          <!DOCTYPE html>
            <html lang="${i18n?.language === 'en' ? 'en' : 'pt-BR'}">
              <head>
                <meta charset="utf-8">
                  <meta name="viewport" content="width=device-width, initial-scale=1">
                    <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css">
                    <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet">
                    <style>
                      html, body {
                        margin: 0;
                        padding: 0;
                        overflow-x: hidden;
                        ${previewViewport === 'mobile' ? 'height: 100%;' : ''}
                      }
                      ${previewViewport === 'mobile' ? `html { overflow: hidden; }
                      body { height: 100%; overflow-y: auto; -webkit-overflow-scrolling: touch; }` : `html, body { height: auto; overflow-y: auto; }`}
                      body {
                        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
                      padding: ${(courseInfo.globalConfig?.layoutWidth === 'full-width' || previewViewport === 'mobile') ? (previewViewport === 'mobile' ? '12px 8px' : '0') : '40px 20px'};
                      background-color: transparent;
                          }
                      .moodle-block-fake {
                        background: #fff;
                      max-width: ${(previewViewport === 'mobile' || courseInfo.globalConfig?.layoutWidth === 'full-width') ? '100%' : '1100px'};
                      margin: 0 auto;
                      border-radius: ${(previewViewport === 'mobile' || courseInfo.globalConfig?.layoutWidth === 'full-width') ? '0' : '8px'};
                      box-shadow: ${(previewViewport === 'mobile' || courseInfo.globalConfig?.layoutWidth === 'full-width') ? 'none' : '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)'};
                      overflow: visible;
                          }
                      ${exportCss.replace(/<\/style>/gi, '\\3C \\2F style \\3E')}
                    </style>
                  </head>
                  <body>
                    <div class="moodle-block-fake">
                      <div class="course-wrapper">
                        ${getSimulatedCode(undefined, undefined, undefined, moodleSimulatedExportStrings)}
                      </div>
                    </div>
                  </body>
                </html>
                    `}
                  />
                </div>
              </div>
            </div>

            <div className="export-modal-footer">
              <button className="btn btn-outline" onClick={() => setShowPreviewModal(false)}>{t('builder:closePreview')}</button>
            </div>
          </div>
        </div>
      )}
      {(isSaving || uiToast) && (
        <div className="fixed bottom-4 right-4 z-[3001] flex max-w-sm flex-col-reverse items-end gap-2">
          {isSaving && (
            <div
              className="flex items-center gap-2.5 rounded-xl border-2 border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900 shadow-lg ring-2 ring-amber-400/35 dark:border-amber-600 dark:bg-amber-950/50 dark:text-amber-100 dark:ring-amber-500/30"
              role="status"
              aria-live="polite"
            >
              <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
              <span className="leading-snug">{t('builder:saving')}</span>
            </div>
          )}
          {uiToast && (
            <div
              className={`rounded-xl px-4 py-3 shadow-lg border text-sm font-medium ${
                uiToast.type === 'error'
                  ? 'bg-red-50 border-red-200 text-red-700 dark:bg-red-950/40 dark:border-red-800 dark:text-red-200'
                  : uiToast.type === 'success'
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-200'
                    : uiToast.type === 'info'
                      ? 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-950/50 dark:border-blue-800 dark:text-blue-100'
                      : 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-100'
              }`}
            >
              {uiToast.message}
            </div>
          )}
        </div>
      )}
      {/* MODAL: Confirmar reset do palco (RESET / Cancelar) */}
      {showResetStageConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/70 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4 border border-slate-200 dark:border-slate-700">
            <p className="text-slate-700 dark:text-slate-200 mb-6">{t('builder:resetConfirm')}</p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowResetStageConfirmModal(false);
                  pushHistory();
                  setRows([]);
                }}
                className="w-full px-4 py-2.5 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-all"
              >
                RESET
              </button>
              <button
                type="button"
                onClick={() => setShowResetStageConfirmModal(false)}
                className="w-full px-4 py-2.5 text-sm font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-xl transition-all"
              >
                {t('builder:cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* MODAL: Confirmar sair para novo projeto (Sim / Não / Cancelar) */}
      {showNewProjectConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/70 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4 border border-slate-200 dark:border-slate-700">
            <p className="text-slate-700 dark:text-slate-200 mb-6">{t('builder:newProjectConfirm')}</p>
            <div className="flex flex-col gap-2">
              <button
                onClick={async () => {
                  setShowNewProjectConfirmModal(false);
                  await saveLayoutToDB();
                  goBackToDashboard();
                  setShowNewProjectModal(true);
                }}
                className="w-full px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-all"
              >
                {t('builder:newProjectConfirmYes')}
              </button>
              <button
                onClick={() => {
                  setShowNewProjectConfirmModal(false);
                  goBackToDashboard();
                  setShowNewProjectModal(true);
                }}
                className="w-full px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-xl transition-all"
              >
                {t('builder:newProjectConfirmNo')}
              </button>
              <button
                onClick={() => setShowNewProjectConfirmModal(false)}
                className="w-full px-4 py-2.5 text-sm font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-xl transition-all"
              >
                {t('builder:cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* MODAL: Novo Projeto */}
      {showNewProjectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 w-full max-w-md mx-4 border border-slate-200 dark:border-slate-700">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-4">{t('builder:newProject')}</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">{t('builder:courseName')}</label>
              <input
                autoFocus
                type="text"
                value={newProjectName}
                onChange={e => setNewProjectName(e.target.value)}
                placeholder={t('builder:courseNamePlaceholder')}
                className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                onKeyDown={e => e.key === 'Enter' && createNewProject()}
              />
            </div>
            {projectsList.filter(p => p.type === 'folder').length > 0 && (
              <div className="mb-5">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">{t('builder:projectLocation', { defaultValue: 'Local' })}</label>
                <select
                  value={newProjectParentId || ''}
                  onChange={e => setNewProjectParentId(e.target.value || null)}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-700 text-sm"
                >
                  <option value="">{t('builder:rootFolder', { defaultValue: 'Raiz' })}</option>
                  {projectsList.filter(p => p.type === 'folder').map(f => (
                    <option key={f.id} value={f.id}>{f.course_name || t('builder:untitled')}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => { setShowNewProjectModal(false); setNewProjectName(''); setNewProjectParentId(null); }}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-xl transition-all"
              >
                {t('builder:cancel')}
              </button>
              <button
                onClick={createNewProject}
                disabled={!newProjectName.trim()}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-all disabled:opacity-40 flex items-center justify-center gap-2"
              >
                <Plus size={16} /> {t('builder:create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Nova Pasta */}
      {showNewFolderModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 w-full max-w-md mx-4 border border-slate-200 dark:border-slate-700">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-4">{t('builder:newFolder', { defaultValue: 'Nova pasta' })}</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">{t('builder:folderName', { defaultValue: 'Nome da pasta' })}</label>
              <input
                autoFocus
                type="text"
                value={newFolderName}
                onChange={e => setNewFolderName(e.target.value)}
                placeholder={t('builder:folderNamePlaceholder', { defaultValue: 'Ex: Projeto Moodle' })}
                className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                onKeyDown={e => e.key === 'Enter' && createNewFolder()}
              />
            </div>
            {projectsList.filter(p => p.type === 'folder').length > 0 && (
              <div className="mb-5">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">{t('builder:projectLocation', { defaultValue: 'Local' })}</label>
                <select
                  value={newFolderParentId || ''}
                  onChange={e => setNewFolderParentId(e.target.value || null)}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-700 text-sm"
                >
                  <option value="">{t('builder:rootFolder', { defaultValue: 'Raiz' })}</option>
                  {projectsList.filter(p => p.type === 'folder').map(f => (
                    <option key={f.id} value={f.id}>{f.course_name || t('builder:untitled')}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => { setShowNewFolderModal(false); setNewFolderName(''); setNewFolderParentId(null); }}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-xl transition-all"
              >
                {t('builder:cancel')}
              </button>
              <button
                onClick={createNewFolder}
                disabled={!newFolderName.trim()}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-xl transition-all disabled:opacity-40 flex items-center justify-center gap-2"
              >
                <FolderPlus size={16} /> {t('builder:create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Ver Projetos */}
      {showProjectsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/70 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between p-6 border-b border-slate-100 dark:border-slate-700">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">{t('builder:myProjects')}</h3>
              <button
                onClick={() => setShowProjectsModal(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              >
                <XCircle size={20} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <ProjectsTreeModal
                projectsList={projectsList}
                expandedIds={projectsModalExpanded}
                onToggleExpand={(id) => setProjectsModalExpanded(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; })}
                onOpenProject={(proj) => { if (proj.type !== 'folder') { openProject(proj); setShowProjectsModal(false); } }}
                onRenameProject={(proj) => { setProjectToRename(proj); setRenameProjectName(proj.course_name || ''); setShowRenameModal(true); }}
                onReuseProject={(proj) => { if (proj.type !== 'folder') reuseProject(proj); }}
                onDeleteProject={deleteProject}
                onMoveProject={moveProject}
                t={t}
                locale={i18n?.language === 'pt' ? 'pt-BR' : 'en-US'}
              />
            </div>
            <div className="p-4 border-t border-slate-100 dark:border-slate-700 flex justify-between flex-wrap gap-2">
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowProjectsModal(false); setNewProjectParentId(null); setShowNewProjectModal(true); }}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/40 rounded-xl transition-all"
                >
                  <Plus size={16} /> {t('builder:newProject')}
                </button>
                <button
                  onClick={() => { setShowProjectsModal(false); setNewFolderParentId(null); setNewFolderName(''); setShowNewFolderModal(true); }}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/40 rounded-xl transition-all"
                >
                  <FolderPlus size={16} /> {t('builder:newFolder', { defaultValue: 'Nova pasta' })}
                </button>
              </div>
              <button
                onClick={() => setShowProjectsModal(false)}
                className="px-4 py-2 text-sm font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-all"
              >
                {t('builder:close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Renomear Projeto */}
      {showRenameModal && projectToRename && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 dark:bg-black/70 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 border border-slate-200 dark:border-slate-700">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-4">{t('builder:renameProject')}</h3>
            <input
              type="text"
              value={renameProjectName}
              onChange={e => setRenameProjectName(e.target.value)}
              placeholder={t('builder:newProjectNamePlaceholder')}
              className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  renameProject(projectToRename.id, renameProjectName);
                  setShowRenameModal(false);
                  setProjectToRename(null);
                  setRenameProjectName('');
                }
                if (e.key === 'Escape') {
                  setShowRenameModal(false);
                  setProjectToRename(null);
                  setRenameProjectName('');
                }
              }}
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => { setShowRenameModal(false); setProjectToRename(null); setRenameProjectName(''); }}
                className="px-4 py-2 text-sm font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
              >
                {t('builder:cancel')}
              </button>
              <button
                onClick={() => {
                  if (renameProjectName.trim()) {
                    renameProject(projectToRename.id, renameProjectName);
                    setShowRenameModal(false);
                    setProjectToRename(null);
                    setRenameProjectName('');
                  }
                }}
                disabled={!renameProjectName.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
              >
                {t('builder:save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Ver Planos */}
      {false && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/70 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between p-6 border-b border-slate-100 dark:border-slate-700">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">{t('builder:viewPlans')}</h3>
              <button
                onClick={() => setShowPlansModal(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              >
                <XCircle size={20} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {plansList.length === 0 ? (
                <div className="text-center py-10 text-slate-400 dark:text-slate-500">
                  <CreditCard size={40} className="mx-auto mb-3 text-slate-300 dark:text-slate-600" />
                  <p>{t('builder:noPlansAvailable')}</p>
                  <p className="text-sm mt-2">{t('builder:contactForOptions')}</p>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {plansList.map(plan => (
                    <div
                      key={plan.id}
                      className="p-4 rounded-xl border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-all"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold text-slate-800 dark:text-slate-200">{plan.name}</h4>
                          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                            {plan.max_projects || 3} {t('builder:projectsCount')}
                          </p>
                        </div>
                        <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                          R$ {parseFloat(plan.price_brl || 0).toFixed(2).replace('.', ',')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-slate-100 dark:border-slate-700">
              <button
                onClick={() => setShowPlansModal(false)}
                className="w-full px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-xl transition-all"
              >
                {t('builder:close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPremiumModal && <PremiumAlertModal />}
      <style>{`
        @keyframes gepettoPlanRestrictedPulse {
          0%, 100% { box-shadow: 0 0 0 1px rgba(239, 68, 68, 0.45), 0 0 8px rgba(239, 68, 68, 0.12); }
          50% { box-shadow: 0 0 0 1px rgba(239, 68, 68, 0.7), 0 0 12px rgba(239, 68, 68, 0.22); }
        }
      `}</style>
      {showFreeSaveModal && <FreeSaveModal />}
      {showPaidWarning && <PaidWarningToast />}
      {showPluginUpdateBalloon && pluginManifest && planPluginModuleEnabled && (
        <PluginUpdateBalloon
          manifest={pluginManifest}
          onDismiss={dismissPluginUpdateNotice}
          onOpenPlugins={openPluginPanelFromUpdateNotice}
        />
      )}

      {previewTemplateItem && (() => {
        let schema = { rows: [], globalConfig: {}, headerWidgets: [] };
        try {
          const raw = previewTemplateItem.schema_json;
          const parsed = typeof raw === 'string' ? JSON.parse(raw || '{}') : raw;
          if (parsed && typeof parsed === 'object') schema = parsed;
        } catch (_) {
          /* schema inválido: preview vazio em vez de derrubar o React */
        }
        const previewRows = Array.isArray(schema.rows) ? schema.rows : [];
        const previewGlobalConfig = schema.globalConfig && typeof schema.globalConfig === 'object' ? schema.globalConfig : {};
        const previewHeaderWidgets = Array.isArray(schema.headerWidgets) ? schema.headerWidgets : [];
        let previewHtml = `<p style="padding:2rem;color:#94a3b8;">${t('builder:layoutEmpty')}</p>`;
        if (previewRows.length > 0) {
          try {
            previewHtml = getSimulatedCode(previewRows, previewGlobalConfig, previewHeaderWidgets, moodleSimulatedExportStrings);
          } catch (_) {
            previewHtml = `<p style="padding:2rem;color:#b45309;">${t('builder:templatePreviewRenderError', { defaultValue: 'Não foi possível gerar a pré-visualização deste template.' })}</p>`;
          }
        }
        return (
          <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-[100030] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-lg font-bold text-slate-800">Visualizar: {previewTemplateItem.name}</h3>
                <div className="flex gap-2">
                  <button onClick={() => {
                    const hasContent = rows.length > 0 || headerWidgets.length > 0;
                    if (hasContent && !confirm(t('builder:templateReplaceConfirm'))) return;
                    pushHistory();
                    const cloned = cloneTemplateRows(previewRows);
                    setRows(cloned);
                    setHeaderWidgets(previewHeaderWidgets || []);
                    if (Object.keys(previewGlobalConfig).length) setCourseInfo(prev => ({ ...prev, globalConfig: previewGlobalConfig }));
                    setActivePanel(null);
                    setPreviewTemplateItem(null);
                  }} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2">
                    <Plus size={18} /> {t('builder:templatesUse')}
                  </button>
                  <button onClick={() => setPreviewTemplateItem(null)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg">
                    <XCircle size={20} />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-auto bg-slate-100" style={{ minHeight: '400px' }}>
                <iframe title="Preview template" className="w-full h-full min-h-[500px] border-0" srcDoc={`<!DOCTYPE html><html lang="${i18n?.language === 'en' ? 'en' : 'pt-BR'}"><head><meta charset="utf-8"><link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css"><link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet"><style>body{margin:0;padding:20px;background:#f1f5f9;}${exportCss}</style></head><body>${previewHtml}</body></html>`} />
              </div>
            </div>
          </div>
        );
      })()}

      {showSaveAsTemplateModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-slate-200">
            <div className="p-6">
              <h3 className="text-xl font-bold text-slate-800 mb-2">{t('builder:saveAsTemplateTitle')}</h3>
              <p className="text-sm text-slate-500 mb-4">{t('builder:templateDesc')}</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">{t('builder:templateNameLabel')} *</label>
                  <input value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder={t('builder:templateNamePlaceholder')} required
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">{t('builder:descriptionOptional')}</label>
                  <input value={templateDesc} onChange={e => setTemplateDesc(e.target.value)} placeholder={t('builder:templateDescPlaceholder')}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => { setShowSaveAsTemplateModal(false); setTemplateName(''); setTemplateDesc(''); }}
                  className="flex-1 py-2.5 text-slate-600 font-semibold rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">{t('builder:cancel')}</button>
                <button onClick={async () => {
                  if (!templateName.trim()) { alert(t('builder:templateNameRequired') || 'Informe o nome do template.'); return; }
                  setTemplateSaving(true);
                  try {
                    const schema = { rows, headerWidgets, globalConfig: courseInfo.globalConfig || {} };
                    const r = await fetch(`${API_BASE}/master/templates`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
                      body: JSON.stringify({ name: templateName.trim(), description: templateDesc.trim(), schema_json: JSON.stringify(schema) })
                    });
                    const d = await r.json();
                    if (r.ok) {
                      setShowSaveAsTemplateModal(false);
                      setTemplateName('');
                      setTemplateDesc('');
                      alert('✅ ' + t('builder:templateCreateSuccess'));
                    } else alert('❌ ' + (d.error || t('builder:templateCreateError')));
                  } catch (e) { alert('❌ ' + t('common:connectionError')); }
                  setTemplateSaving(false);
                }} disabled={templateSaving}
                  className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  <LayoutGrid size={16} />
                  {templateSaving ? t('builder:saving') : t('builder:createTemplate')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCricketDrawerInBuilder && (
        <>
          <CricketDrawer
            open={cricketDrawerOpen}
            onClose={() => {
              setCricketDrawerOpen(false);
              if (cricketWidgetLinkEnabled) loadCricketOverlaySnapshot();
            }}
            layoutId={
              cricketWidgetLinkEnabled ? String(courseInfo.id) : CRICKET_FREE_STANDALONE_LAYOUT_ID
            }
            authToken={authToken}
            standaloneUserId={planFreeCricketDrawerStandalone ? currentUser.id : undefined}
            onOpenFullBoard={() => {
              setCricketBoardLayoutId(
                cricketWidgetLinkEnabled
                  ? String(courseInfo.id)
                  : CRICKET_FREE_STANDALONE_LAYOUT_ID,
              );
              setAppState('cricket');
            }}
            showToast={showToast}
            stageLampsHidden={cricketStageLampsHidden}
            onToggleStageLamps={
              cricketWidgetLinkEnabled
                ? () => setCricketStageLampsHiddenPersist(!cricketStageLampsHidden)
                : undefined
            }
            onFocusLinkedWidget={
              cricketWidgetLinkEnabled
                ? (widgetId) => {
                    if (widgetId == null || String(widgetId).trim() === '') return;
                    setCricketDrawerOpen(false);
                    window.setTimeout(() => focusCricketLinkedWidgetOnStage(widgetId), 160);
                  }
                : undefined
            }
          />
          {!cricketDrawerOpen && (
            <button
              type="button"
              onClick={() => setCricketDrawerOpen(true)}
              className="fixed right-4 top-1/2 z-[1410] flex h-14 w-14 -translate-y-1/2 items-center justify-center rounded-full bg-amber-500 text-white shadow-lg transition hover:bg-amber-600 hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900"
              title={
                planFreeCricketDrawerStandalone
                  ? t('cricket:freeStandaloneFabTitle')
                  : t('cricket:fabOpenTitle')
              }
              aria-label={
                planFreeCricketDrawerStandalone
                  ? t('cricket:freeStandaloneFabTitle')
                  : t('cricket:fabOpenTitle')
              }
              aria-haspopup="dialog"
            >
              <Lamp size={26} aria-hidden />
            </button>
          )}
        </>
      )}

      {currentUser && currentUser.role !== 'super_admin' && authToken && !cricketDrawerOpen && (
        <FeedbackFloatingWidget
          authToken={authToken}
          pageContext="builder"
          layoutId={courseInfo?.id || null}
        />
      )}

      {sectionsModal && (
        <SectionsLibraryModal
          open
          mode={sectionsModal.mode}
          targetRowId={sectionsModal.rowId}
          layoutId={courseInfo?.id || null}
          authToken={authToken}
          onClose={() => setSectionsModal(null)}
          onSave={sectionsModal.mode === 'save' ? handleSaveSectionPreset : undefined}
          onInsert={sectionsModal.mode === 'insert' ? handleInsertSectionPreset : undefined}
          canDelete={!isCanvasReadOnly}
          canManageFolders={!isCanvasReadOnly}
          canCurateAiLibrary={!!currentUser?.aiLibraryCurator}
        />
      )}

      {showAiLayoutModal && !!currentUser?.aiLibraryCurator && (
        <AiLayoutGenerateModal
          open
          authToken={authToken}
          onClose={() => setShowAiLayoutModal(false)}
          onApply={handleApplyAiAssembledLayout}
        />
      )}

      {showGallerySubmitModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-slate-200">
            <div className="p-6">
              <h3 className="text-xl font-bold text-slate-800 mb-2">{t('builder:shareToGallery')}</h3>
              <p className="text-sm text-slate-500 mb-4">{t('builder:galleryDesc')}</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">{t('builder:layoutName')} *</label>
                  <input value={gallerySubmitName} onChange={e => setGallerySubmitName(e.target.value)} placeholder={t('builder:galleryNamePlaceholder')} required
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">{t('builder:descriptionOptional')}</label>
                  <textarea value={gallerySubmitDesc} onChange={e => setGallerySubmitDesc(e.target.value)} placeholder={t('builder:galleryDescPlaceholder')}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" rows={3} />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => { setShowGallerySubmitModal(false); setGallerySubmitName(''); setGallerySubmitDesc(''); }}
                  className="flex-1 py-2.5 text-slate-600 font-semibold rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">{t('builder:cancel')}</button>
                <button onClick={async () => {
                  if (!gallerySubmitName.trim()) { alert(t('builder:galleryNameRequired') || 'Informe o nome do layout.'); return; }
                  setGallerySubmitting(true);
                  try {
                    const schema = { rows, headerWidgets, globalConfig: courseInfo.globalConfig || {} };
                    const r = await fetch(`${API_BASE}/gallery/submit`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
                      body: JSON.stringify({ name: gallerySubmitName.trim(), description: gallerySubmitDesc.trim(), schema_json: JSON.stringify(schema), layout_id: courseInfo.id })
                    });
                    const d = await r.json();
                    if (r.ok) {
                      setShowGallerySubmitModal(false);
                      setGallerySubmitName('');
                      setGallerySubmitDesc('');
                      alert('✅ ' + t('builder:gallerySubmitted'));
                    } else alert('❌ ' + (d.error || t('builder:gallerySubmitError')));
                  } catch (e) { alert('❌ ' + t('common:connectionError')); }
                  setGallerySubmitting(false);
                }} disabled={gallerySubmitting}
                  className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  <Share2 size={16} />
                  {gallerySubmitting ? t('builder:submitting') : t('builder:submit')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
